// ============================================================
// FILAMENT BREWERY — React App v2.1 (JSX source)
// Build: 2026-05-11
// ============================================================
// Compile with: npx babel filament-brewery.jsx -o filament-brewery.js
// ============================================================

const {
  useState,
  useEffect,
  useRef
} = React;

// Load html5-qrcode scanner library eagerly
(function() {
  if (window.Html5Qrcode) return;
  var s = document.createElement("script");
  s.src = "https://cdnjs.cloudflare.com/ajax/libs/html5-qrcode/2.3.8/html5-qrcode.min.js";
  document.head.appendChild(s);
})();

// ============================================================
// API
// ============================================================
const BREWERY_API = "https://script.google.com/macros/s/AKfycbxIYSBY5qaywyI0mrEQNt1Ay91ckruFNhQ2meQ0U_AbhMuPS161fOgtFtSCTkzRk9dI/exec";
// fetchJson — one place that turns a response into data or a plain-English error.
// - Reads the body as text first. Apps Script answers with an HTML page (not JSON)
//   when it times out or Google is busy; calling r.json() on that is what showed
//   Safari's "The string did not match the expected pattern".
// - timeoutMs: reads only. Writes pass 0 — aborting in the browser does not stop
//   the sheet write, so a timed-out save could be re-done and duplicated.
// - retries: reads get one automatic retry after 3s. Writes never retry here.
function fetchJson(url, opts, timeoutMs, retries) {
  function once() {
    var ctrl = (timeoutMs && typeof AbortController !== "undefined") ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function() { ctrl.abort(); }, timeoutMs) : null;
    var o = Object.assign({ redirect: "follow" }, opts || {});
    if (ctrl) o.signal = ctrl.signal;
    return fetch(url, o).then(function(r) {
      return r.text().then(function(txt) {
        var t = (txt || "").trim();
        if (!t) throw new Error("The spreadsheet sent back nothing. It may be busy \u2014 try again in a minute.");
        if (t.charAt(0) === "<") throw new Error("The spreadsheet sent a page instead of data. It is usually busy or timed out \u2014 try again in a minute.");
        try { return JSON.parse(t); }
        catch (e) { throw new Error("The spreadsheet sent data the app couldn't read \u2014 try again in a minute."); }
      });
    }, function(err) {
      if (err && err.name === "AbortError") throw new Error("The spreadsheet took too long to answer (over " + Math.round(timeoutMs / 1000) + "s). It may be busy \u2014 try again in a minute.");
      throw new Error("Couldn't reach the spreadsheet \u2014 check the wifi and try again.");
    }).finally(function() { if (timer) clearTimeout(timer); });
  }
  function attempt(left) {
    return once().catch(function(err) {
      if (left <= 0) throw err;
      return new Promise(function(res) { setTimeout(res, 3000); }).then(function() { return attempt(left - 1); });
    });
  }
  return attempt(retries || 0);
}
const API_READ_TIMEOUT_MS = 45000;
const api = {
  load() {
    return fetchJson(BREWERY_API + "?action=loadAll", null, API_READ_TIMEOUT_MS, 1);
  },
  get(action) {
    return fetchJson(BREWERY_API + "?action=" + encodeURIComponent(action), null, API_READ_TIMEOUT_MS, 1);
  },
  post(action, data) {
    return fetchJson(BREWERY_API, {
      method: "POST",
      headers: {
        "Content-Type": "text/plain;charset=utf-8"
      },
      body: JSON.stringify({
        action,
        ...data
      })
    }, 0, 0);
  }
};


// ============================================================
// FILL SESSION PERSISTENCE (localStorage)
// Survives page reloads / iPad app switches
// ============================================================
var FK_SESSION_KEY = "filament_fill_session";
function saveFillSession(data) {
  try { localStorage.setItem(FK_SESSION_KEY, JSON.stringify(Object.assign({ ts: Date.now() }, data))); } catch(e) {}
}
function loadFillSession() {
  try {
    var raw = localStorage.getItem(FK_SESSION_KEY);
    if (!raw) return null;
    var s = JSON.parse(raw);
    // Never silently discard work. Only drop a truly ancient session (>48h),
    // which almost certainly means a forgotten/abandoned fill. Sessions older
    // than 12h are flagged stale so the UI can offer Resume / Discard instead.
    if (s.ts && Date.now() - s.ts > 48 * 60 * 60 * 1000) { clearFillSession(); return null; }
    if (s.ts && Date.now() - s.ts > 12 * 60 * 60 * 1000) { s._stale = true; }
    return s;
  } catch(e) { return null; }
}
function clearFillSession() {
  try { localStorage.removeItem(FK_SESSION_KEY); } catch(e) {}
}

// ============================================================
// PENDING FILL (localStorage) — a "receipt" for a submitted-but-unconfirmed
// final Save. If the iPad is closed before the server replies, we re-send this
// on next open. Safe because the backend is idempotent on requestId, so a
// re-send can only confirm or complete the save, never duplicate it.
// ============================================================
var FK_PENDING_KEY = "filament_pending_fill";
function savePendingFill(data) {
  try { localStorage.setItem(FK_PENDING_KEY, JSON.stringify(Object.assign({ ts: Date.now() }, data))); } catch(e) {}
}
function loadPendingFill() {
  try {
    var raw = localStorage.getItem(FK_PENDING_KEY);
    if (!raw) return null;
    var p = JSON.parse(raw);
    if (p.ts && Date.now() - p.ts > 48 * 60 * 60 * 1000) { clearPendingFill(); return null; }
    return p;
  } catch(e) { return null; }
}
function clearPendingFill() {
  try { localStorage.removeItem(FK_PENDING_KEY); } catch(e) {}
}

// ============================================================
// USERS & CONSTANTS
// ============================================================
const USERS = [{
  username: "brendan",
  password: "filament1",
  name: "Brendan",
  initials: "BG"
}, {
  username: "aaron",
  password: "filament2026",
  name: "Aaron",
  initials: "AE"
}, {
  username: "marty",
  password: "shipton1204",
  name: "Marty",
  initials: "MS"
}, {
  username: "ollie",
  password: "nolan2026",
  name: "Ollie",
  initials: "ON"
}];
function userInitials(name) {
  var u = USERS.find(function(x) { return x.name === name; });
  return u ? u.initials : (name || "?").charAt(0).toUpperCase();
}
const PC = {
  "Classic": "#C43E2A",
  "Iced Espresso": "#2563A8",
  "Choc Banana": "#F0B816",
  "Lamington": "#6B3A2A",
  "Choc Mint": "#2A8B6A",
  "Choc Orange": "#D4870A",
  "Summer": "#D4870A"
};
const AC = {
  "on": "#17B892",
  "off": "#fa688e"
};
const PS = {
  "Classic": "Classic",
  "Iced Espresso": "Iced Esp.",
  "Choc Banana": "Choc Ban.",
  "Lamington": "Lamington",
  "Choc Mint": "Choc Mint",
  "Choc Orange": "Choc Orange"
};
const PKG = {
  "Classic": "34",
  "Iced Espresso": "55"
};
// Normalise ops-style product names ("Classic (Nitro)") to brewery-style ("Classic")
function normProduct(p) {
  if (!p) return p;
  return String(p).replace(/\s*\(Nitro\)|\s*\(N\)/gi, '').trim();
}
// Reverse: brewery-style ("Classic") -> ops-style ("Classic (Nitro)") for sheet writes.
// Iced Espresso is not a nitro product so it stays as-is. Anything already containing "(Nitro)" is left untouched.
function opsProduct(p) {
  if (!p) return p;
  var s = String(p).trim();
  if (/\(Nitro\)/i.test(s)) return s;
  if (s === "Iced Espresso") return s;
  return s + " (Nitro)";
}
const TANK_DEF = [{
  id: "BBT01",
  cap: 500
}, {
  id: "BBT02",
  cap: 500
}, {
  id: "BBT03",
  cap: 1000
}];
const QCT = {
  "Classic": 1.45,
  "Iced Espresso": 2.90
};
const QCFT = {
  "Classic": 423
};
const PRESERV = {
  nagardo: 0.025,
  sorbate: 0.01
};

// ============================================================
// HELPERS
// ============================================================
function todayHdr() {
  const d = new Date(),
    D = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"],
    M = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return D[d.getDay()] + " " + d.getDate() + " " + M[d.getMonth()];
}
function nowT() {
  const d = new Date();
  return String(d.getHours()).padStart(2, "0") + ":" + String(d.getMinutes()).padStart(2, "0");
}
function nowT45() {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 45);
  return String(d.getHours()).padStart(2, "0") + ":" + String(d.getMinutes()).padStart(2, "0");
}
function nowTplus(mins) {
  const d = new Date();
  d.setMinutes(d.getMinutes() + mins);
  return String(d.getHours()).padStart(2, "0") + ":" + String(d.getMinutes()).padStart(2, "0");
}
function bbDef() {
  const d = new Date();
  d.setDate(d.getDate() + 70);
  var _M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return d.getDate() + " " + _M[d.getMonth()] + " " + d.getFullYear();
}
function toISODate(s) {
  if (!s) return "";
  var _M = {Jan:"01",Feb:"02",Mar:"03",Apr:"04",May:"05",Jun:"06",Jul:"07",Aug:"08",Sep:"09",Oct:"10",Nov:"11",Dec:"12"};
  // Already ISO
  if (typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // d MMM yyyy
  var m = String(s).match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
  if (m) return m[3] + "-" + (_M[m[2]] || "01") + "-" + String(m[1]).padStart(2, "0");
  // fallback — try Date parse
  var d = new Date(s);
  if (!isNaN(d.getTime())) return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0");
  return "";
}
function fmtDate(s) {
  if (!s) return "";
  var M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  // Handle YYYY-MM-DD, DD MMM YYYY, or Date objects
  var d;
  if (typeof s === "string" && s.indexOf("-") > -1 && s.length >= 10) {
    var p = s.split("-"); d = new Date(parseInt(p[0]), parseInt(p[1]) - 1, parseInt(p[2]));
  } else {
    d = new Date(s);
  }
  if (isNaN(d.getTime())) return String(s);
  return d.getDate() + " " + M[d.getMonth()] + " " + d.getFullYear();
}
function fmtDateShort(s) {
  if (!s) return "";
  var d;
  if (typeof s === "string" && s.indexOf("-") > -1 && s.length >= 10) {
    var p = s.split("-"); d = new Date(parseInt(p[0]), parseInt(p[1]) - 1, parseInt(p[2]));
  } else { d = new Date(s); }
  if (isNaN(d.getTime())) return String(s);
  var _M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return String(d.getDate()).padStart(2, "0") + "-" + _M[d.getMonth()] + "-" + String(d.getFullYear()).slice(2);
}
function tdsF(v) {
  let d = v.replace(/[^0-9]/g, "");
  if (d.length > 3) d = d.slice(0, 3);
  return d.length <= 1 ? d : d[0] + "." + d.slice(1);
}
function calcAvg(a) {
  const v = a.filter(x => x !== "").map(Number).filter(n => !isNaN(n));
  return v.length === 0 ? null : v.reduce((a, b) => a + b, 0) / v.length;
}
function tankSS(s) {
  if (s === "Ready") return {
    bg: "rgba(42,139,106,.1)",
    c: "var(--success)"
  };
  if (s === "Brewing") return {
    bg: "rgba(29,78,216,.08)",
    c: "var(--blue)"
  };
  if (s === "Grinding") return {
    bg: "rgba(29,78,216,.08)",
    c: "var(--blue)"
  };
  if (s === "Filling") return {
    bg: "rgba(42,139,106,.1)",
    c: "var(--success)"
  };
  if (s === "Filtering") return {
    bg: "rgba(196,62,42,.08)",
    c: "#C43E2A"
  };
  return {
    bg: "var(--input-bg)",
    c: "var(--ghost)"
  };
}

// ============================================================
// CSS
// ============================================================
const _css = document.createElement("style");
_css.textContent = `
:root{--bg:#F8F8F6;--rule:#ddd;--rule-light:#eee;--rule-heavy:#111;--input-bg:#f0f0ec;--text:#111;--dim:#999;--ghost:#aaa;--off:#bbb;--faint:#ddd;--blue:#1D4ED8;--classic:#C43E2A;--choc-banana:#C49A1A;--lamington:#6B3A2A;--choc-mint:#2A8B6A;--summer:#D4598A;--iced-espresso:#2563A8;--danger:#C43E2A;--success:#2A8B6A;--sans:-apple-system,BlinkMacSystemFont,'SF Pro Display','Segoe UI',system-ui,sans-serif}
*{margin:0;padding:0;box-sizing:border-box;-webkit-tap-highlight-color:transparent}
body{font-family:var(--sans);background:var(--bg);color:var(--text);min-height:100vh;max-width:430px;margin:0 auto;overflow-x:hidden;padding-bottom:96px}
::-webkit-scrollbar{display:none}::placeholder{color:var(--ghost)!important}
.brand{font-size:9px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--blue)}
.date-hdr{font-size:9px;color:var(--ghost);letter-spacing:1px}
.page-title{font-size:32px;font-weight:800;letter-spacing:-1px;line-height:1;margin-top:14px}
.h2{font-size:22px;font-weight:700;letter-spacing:-.3px}
.lbl{font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase}
.product{font-size:15px;font-weight:600}.product-sm{font-size:13px;font-weight:600}
.hdr{padding:16px 24px 12px}.hdr-top{display:flex;justify-content:space-between;align-items:baseline}
.scr{padding:0 24px 24px}
@keyframes fu{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
.sect{border-top:2px solid var(--rule-heavy);margin-top:22px;padding-top:10px}
.sect-blue{border-top-color:var(--blue)}
.sect-top{display:flex;justify-content:space-between;align-items:baseline}
.rule{border-top:1px solid var(--rule);margin-top:10px;padding-top:12px}
.rule-light{border-top-color:var(--rule-light)}
.cols{display:flex}.col{flex:1}
.dot{width:6px;height:6px;border-radius:50%;flex-shrink:0}.dot-lg{width:8px;height:8px}
.row-i{display:flex;align-items:center;gap:6px}.ml{margin-left:auto}
.ftbl{display:grid;align-items:center}
.ftbl-hd{border-top:1px solid var(--rule);margin-top:10px;padding-top:8px}
.ftbl-row{border-top:1px solid var(--rule-light);padding:10px 0}.ftbl-row:first-of-type{margin-top:6px}
.ftbl-total{border-top:1.5px solid var(--rule-heavy);padding:8px 0}
.ftbl-c{text-align:center}
.fridge-tap{border-radius:6px;cursor:pointer;transition:background .15s}.fridge-tap:active{background:rgba(29,78,216,.08)}
.fsect-title{font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--dim);margin-bottom:10px}
.frow{display:flex;gap:10px;margin-bottom:12px}.frow:last-child{margin-bottom:0}
.ffield{flex:1;display:flex;flex-direction:column;gap:5px}
.flabel{font-size:9px;font-weight:700;color:var(--ghost);text-transform:uppercase;letter-spacing:1px}
.input{background:var(--input-bg);border:2px solid transparent;border-radius:8px;padding:11px 12px;font-size:15px;font-family:var(--sans);color:var(--text);outline:none;width:100%;transition:border-color .2s}
.input:focus{border-color:var(--blue)}
.input-mono{font-size:22px;font-weight:700;text-align:center;padding:12px}
.pgrid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}
.popt{border-radius:8px;padding:12px 8px;text-align:center;font-size:10px;font-weight:700;cursor:pointer;border:3px solid transparent;text-transform:uppercase;letter-spacing:.5px;color:#fff;opacity:.6;transition:all .2s}
.popt.sel{opacity:1;border-color:rgba(255,255,255,.6);transform:scale(1.04);box-shadow:0 4px 16px rgba(0,0,0,.25)}
.dgrid{display:flex;gap:6px;flex-wrap:wrap;margin-top:8px}
.dchip{background:var(--input-bg);border-radius:6px;padding:8px 14px;font-size:14px;font-weight:700;min-width:56px;text-align:center}
.dchip.on{background:var(--blue);color:#fff}
.dadd{background:none;border:2px dashed var(--rule);border-radius:6px;padding:8px 14px;font-size:18px;color:var(--ghost);cursor:pointer;min-width:44px;text-align:center}
.steps{display:flex;gap:6px;margin-bottom:18px}
.step{flex:1;height:3px;border-radius:2px;background:var(--rule);transition:background .3s}
.step.done{background:var(--blue)}.step.cur{background:var(--blue);animation:pu 2s ease-in-out infinite}
@keyframes pu{0%,100%{opacity:1}50%{opacity:.4}}
.btn{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;padding:14px;border:none;border-radius:8px;font-size:13px;font-weight:700;font-family:var(--sans);cursor:pointer;text-transform:uppercase;letter-spacing:1px;transition:transform .1s}
.btn svg{width:16px;height:16px;stroke:currentColor;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;flex-shrink:0}
.btn:active{transform:scale(.97)}
.btn-blue{background:var(--blue);color:#fff}
.btn-out{background:transparent;color:var(--text);border:1.5px solid var(--rule)}
.btn-red{background:var(--danger);color:#fff}
.btn-row{display:flex;gap:10px;margin-top:18px}.btn-row .btn{flex:1}
.tpair{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;gap:10px;margin:8px 0}
.tbox{background:var(--input-bg);border-radius:8px;padding:12px;text-align:center}
.tdsavg{background:var(--blue);color:#fff;border-radius:8px;padding:12px 16px;display:flex;align-items:center;gap:10px;margin-top:10px}
.kgrid{display:grid;grid-template-columns:repeat(4,1fr);gap:6px;margin-top:10px}
.kchip{border-radius:6px;padding:7px 4px;text-align:center;font-size:8px;font-weight:700;background:var(--input-bg);border:2px solid transparent}
.kchip.on{border-color:var(--blue);background:rgba(29,78,216,.08)}
.ksz{font-size:12px;font-weight:800;display:block;margin-bottom:1px}
.tag{display:flex;align-items:center;justify-content:center;gap:8px;padding:13px 18px;border-radius:8px;font-size:13px;font-weight:800;cursor:pointer;border:3px solid transparent;text-transform:uppercase;letter-spacing:.5px;position:relative;overflow:hidden;flex:1;color:#fff;transition:all .2s}
.tag.sel{border-color:rgba(255,255,255,.6);box-shadow:0 4px 16px rgba(0,0,0,.25);transform:scale(1.03)}
.tag-s{position:absolute;top:50%;left:0;right:0;height:6px;background:#fff;opacity:.6;transform:translateY(-50%)}
.tag-t{position:relative;z-index:1}
.sbar{height:3px;background:var(--input-bg);border-radius:2px;margin-top:4px;overflow:hidden}
.sfill{height:100%;border-radius:2px;transition:width .6s ease}
.map-floor{border:1.5px solid var(--rule);border-radius:12px;padding:14px 18px 10px;margin-top:10px;background:#fff}
.map-tr{display:flex;justify-content:space-around;align-items:flex-start;padding-top:8px}
.mt{display:flex;flex-direction:column;align-items:center;cursor:pointer}.mt:active{transform:scale(.95)}
.mt-c{position:relative;border-radius:50%;border:2.5px solid var(--rule);display:flex;flex-direction:column;align-items:center;justify-content:center;overflow:hidden;background:#fff}
.mt-f{position:absolute;bottom:0;left:0;right:0;opacity:.85}
.mt-v{font-size:18px;font-weight:800;position:relative;z-index:1}
.mt-p{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;position:relative;z-index:1;margin-top:1px}
.mt-n{font-size:11px;color:var(--dim);margin-top:4px;font-weight:600}
.mt-s{margin-top:2px;padding:3px 10px;border-radius:100px;font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:.8px}
.mfridge{border:1.5px solid var(--rule);border-radius:10px;padding:14px 16px;margin-top:14px;margin-bottom:14px}
.flow{text-align:center;padding:2px 0;color:var(--ghost)}
.bsel{flex:1;background:var(--input-bg);border:2px solid var(--rule);border-radius:8px;padding:11px;text-align:center;font-weight:700;font-size:14px;color:var(--ghost);cursor:pointer}
.bsel.on{background:rgba(29,78,216,.08);border-color:var(--blue);color:var(--blue)}
.brew-entry{border-top:1px solid var(--rule-light);padding:12px 0;cursor:pointer}
.brew-entry:first-child{border-top:1px solid var(--rule);margin-top:8px}.brew-entry:active{opacity:.6}
.kstat{border:1.5px solid var(--rule);border-radius:10px;padding:14px 16px;transition:transform .1s}
.kstat:active{transform:scale(.97)}.kstat+.kstat{margin-top:8px}
.kstat-top{display:flex;justify-content:space-between;align-items:center}
.kstat-n{font-size:24px;font-weight:800}
.kstat-lbl{font-size:11px;font-weight:700;color:var(--dim);text-transform:uppercase;letter-spacing:1px}
.kstat-sub{font-size:11px;color:var(--ghost);margin-top:2px}
.prep-item{display:flex;align-items:center;gap:12px;padding:12px 0;border-bottom:1px solid var(--rule-light);font-size:14px;font-weight:600;cursor:pointer}
.prep-item:last-child{border-bottom:none}.prep-item:active{opacity:.6}
.prep-tick{width:24px;height:24px;border-radius:6px;border:2px solid var(--rule);flex-shrink:0;display:flex;align-items:center;justify-content:center;transition:all .15s}
.nav{position:fixed;bottom:0;left:50%;transform:translateX(-50%);width:100%;max-width:430px;background:rgba(248,248,246,.94);backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);border-top:1px solid #e0e0dc;display:flex;padding:6px 4px 26px;z-index:100}
.ni{flex:1;display:flex;flex-direction:column;align-items:center;gap:2px;padding:6px 0;cursor:pointer;color:var(--off);font-size:8px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;border:none;background:none;font-family:var(--sans)}
.ni.on{color:var(--blue)}
.ni svg{width:22px;height:22px;stroke:currentColor;fill:none;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}
.ico{width:16px;height:16px;stroke:currentColor;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;vertical-align:middle}
.pulse{display:inline-block;width:6px;height:6px;border-radius:50%;background:var(--success);animation:blink 2s ease-in-out infinite}
@keyframes blink{0%,100%{opacity:1}50%{opacity:.3}}
.rcp-tabs{display:flex;gap:0;border-bottom:2px solid var(--rule);margin-bottom:16px}
.rcp-tab{flex:1;text-align:center;padding:10px 6px;font-size:11px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;cursor:pointer;color:var(--ghost);border-bottom:2.5px solid transparent;margin-bottom:-2px;transition:all .15s}
.rcp-tab.on{color:var(--text);border-bottom-color:var(--blue)}
.rcp-card{border:1.5px solid var(--rule);border-radius:10px;padding:16px;margin-bottom:10px;cursor:pointer;transition:transform .1s}
.rcp-card:active{transform:scale(.97)}
.rcp-field{display:flex;justify-content:space-between;align-items:center;padding:9px 0;border-bottom:1px solid var(--rule-light)}
.rcp-field:last-child{border-bottom:none}
.rcp-field-lbl{font-size:12px;color:var(--dim)}
.rcp-detail{display:none;margin-top:10px;padding-top:10px;border-top:1px solid var(--rule)}
.rcp-card.open .rcp-detail{display:block}.rcp-card.open{border-color:var(--blue)}
.ls-opt{border:1.5px solid var(--rule);border-radius:10px;padding:14px 16px;cursor:pointer;transition:all .15s;display:flex;align-items:center;gap:12px}
.ls-opt:active{transform:scale(.97)}.ls-opt+.ls-opt{margin-top:8px}
.ls-opt.sel{border-color:var(--blue);background:rgba(29,78,216,.04)}
.login-wrap{min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:24px}
.toast-box{position:fixed;top:24px;left:50%;transform:translateX(-50%);z-index:999;padding:12px 20px;border-radius:10px;font-size:13px;font-weight:700;color:#fff;visibility:hidden;opacity:0;transition:opacity .3s,visibility .3s}
.toast-box.show{visibility:visible;opacity:1}
.modal-overlay{position:fixed;inset:0;z-index:200;background:rgba(0,0,0,.45);display:flex;align-items:flex-end;justify-content:center;padding:24px}
.modal-card{background:var(--bg);border-radius:14px;width:100%;max-width:400px;max-height:70vh;overflow:auto;padding:20px;margin-bottom:40px}
@keyframes spin{to{transform:rotate(360deg)}}
.spinner{display:inline-block;width:18px;height:18px;border:2.5px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:50%;animation:spin .6s linear infinite}

/* ── Brewery Scanner ── */
#bsc-overlay{position:fixed;inset:0;z-index:300;background:rgba(0,0,0,.95);display:flex;flex-direction:column;overflow:auto;-webkit-overflow-scrolling:touch}
#bsc-top{padding:16px 20px 12px;display:flex;align-items:center;justify-content:space-between}
#bsc-label{font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:rgba(255,255,255,.6)}
#bsc-close{width:36px;height:36px;border-radius:50%;border:1.5px solid rgba(255,255,255,.2);background:none;color:#fff;font-size:20px;display:flex;align-items:center;justify-content:center;cursor:pointer}
#bsc-reader-wrap{margin:0 20px;border-radius:14px;background:#111;overflow:hidden;position:relative}
#bsc-reader{width:100%;min-height:280px;position:relative}
#bsc-reader video{width:100%!important;height:auto!important;display:block!important;object-fit:cover!important}
#bsc-reader img{display:none!important}
#bsc-reader>div{width:100%!important}
#bsc-reader__scan_region{border:2px solid rgba(255,255,255,.3)!important;border-radius:4px!important;box-shadow:0 0 0 4000px rgba(0,0,0,.3)!important}
#bsc-reader__dashboard_section{display:none!important}
#bsc-pending{display:none;margin:12px 20px;padding:18px;border-radius:12px;text-align:center;color:#fff}
#bsc-pending .bsc-p-id{font-size:22px;font-weight:800;letter-spacing:.5px}
#bsc-pending .bsc-p-size{font-size:12px;opacity:.7;margin-top:4px;font-weight:600}
#bsc-pending .bsc-p-btn{display:block;width:100%;padding:14px;border:none;border-radius:8px;font-size:14px;font-weight:800;letter-spacing:1px;text-transform:uppercase;cursor:pointer;margin-top:12px;color:#fff;background:rgba(255,255,255,.2);font-family:var(--sans)}
#bsc-pending .bsc-p-cancel{display:block;width:100%;padding:10px;border:none;background:none;color:rgba(255,255,255,.5);font-size:12px;font-weight:700;cursor:pointer;margin-top:6px;font-family:var(--sans);letter-spacing:1px;text-transform:uppercase}
#bsc-reject{display:none;margin:0 20px;padding:12px 16px;border-radius:10px;background:#C43E2A;color:#fff;font-size:13px;font-weight:700;text-align:center}
#bsc-list{padding:12px 20px 80px;flex:1;overflow:auto}
#bsc-list .bsc-keg-row{display:flex;align-items:center;gap:10px;padding:12px 14px;background:rgba(255,255,255,.06);border-radius:10px;margin-bottom:6px}
#bsc-list .bsc-keg-bar{width:4px;height:28px;border-radius:2px;flex-shrink:0}
#bsc-list .bsc-keg-bar-ok{background:#2A8B6A}
#bsc-list .bsc-keg-id{font-size:14px;font-weight:800;color:#fff;letter-spacing:.3px}
#bsc-list .bsc-keg-sz{font-size:10px;font-weight:600;color:rgba(255,255,255,.4);margin-left:auto}
#bsc-list .bsc-keg-rm{width:28px;height:28px;border-radius:50%;border:1px solid rgba(255,255,255,.15);background:none;color:rgba(255,255,255,.35);font-size:14px;display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;margin-left:6px}
#bsc-list .bsc-keg-rm:active{background:rgba(255,0,0,.2);color:#fff}
#bsc-done{position:fixed;bottom:0;left:0;right:0;padding:16px 20px 34px;background:linear-gradient(transparent,rgba(0,0,0,.9) 30%);z-index:301;display:flex;gap:10px}
#bsc-done .bsc-done-btn{flex:1;padding:16px;border-radius:10px;border:none;font-size:14px;font-weight:800;letter-spacing:.5px;cursor:pointer;text-align:center;font-family:var(--sans)}
#bsc-count{position:absolute;top:12px;right:12px;background:var(--success);color:#fff;min-width:28px;height:28px;border-radius:14px;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:800;z-index:2;padding:0 8px}
@keyframes bscPop{from{transform:scale(.8);opacity:0}to{transform:scale(1);opacity:1}}
@keyframes bscFlash{0%{opacity:1}100%{opacity:0}}
#bsc-flash{position:fixed;inset:0;z-index:302;pointer-events:none;opacity:0}
`;
document.head.appendChild(_css);

// ============================================================
// SVG ICON COMPONENTS
// ============================================================
const IcoHome = () => /*#__PURE__*/React.createElement("svg", {
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("path", {
  d: "M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"
}), /*#__PURE__*/React.createElement("polyline", {
  points: "9 22 9 12 15 12 15 22"
}));
const IcoLive = () => /*#__PURE__*/React.createElement("svg", {
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("circle", {
  cx: "12",
  cy: "12",
  r: "3"
}), /*#__PURE__*/React.createElement("path", {
  d: "M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"
}));
const IcoBrew = () => /*#__PURE__*/React.createElement("svg", {
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("path", {
  d: "M12 2.69l5.66 5.66a8 8 0 11-11.31 0z"
}));
const IcoKeg = () => /*#__PURE__*/React.createElement("svg", {
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("rect", {
  x: "6",
  y: "3",
  width: "12",
  height: "18",
  rx: "3"
}), /*#__PURE__*/React.createElement("line", {
  x1: "6",
  y1: "7",
  x2: "18",
  y2: "7"
}), /*#__PURE__*/React.createElement("line", {
  x1: "6",
  y1: "17",
  x2: "18",
  y2: "17"
}), /*#__PURE__*/React.createElement("line", {
  x1: "12",
  y1: "7",
  x2: "12",
  y2: "17"
}));
const IcoStock = () => /*#__PURE__*/React.createElement("svg", {
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("path", {
  d: "M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"
}), /*#__PURE__*/React.createElement("polyline", {
  points: "3.27 6.96 12 12.01 20.73 6.96"
}), /*#__PURE__*/React.createElement("line", {
  x1: "12",
  y1: "22.08",
  x2: "12",
  y2: "12"
}));
const IcoCheck = () => /*#__PURE__*/React.createElement("svg", {
  className: "ico",
  stroke: "#fff",
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("polyline", {
  points: "20 6 9 17 4 12"
}));
const IcoBack = () => /*#__PURE__*/React.createElement("svg", {
  style: {
    width: 20,
    height: 20,
    stroke: "var(--text)",
    fill: "none",
    strokeWidth: 2.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  },
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("polyline", {
  points: "15 18 9 12 15 6"
}));
const IcoArrow = () => /*#__PURE__*/React.createElement("svg", {
  className: "ico",
  stroke: "#fff",
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("line", {
  x1: "5",
  y1: "12",
  x2: "19",
  y2: "12"
}), /*#__PURE__*/React.createElement("polyline", {
  points: "12 5 19 12 12 19"
}));
const IcoPlus = () => /*#__PURE__*/React.createElement("svg", {
  className: "ico",
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("line", {
  x1: "12",
  y1: "5",
  x2: "12",
  y2: "19"
}), /*#__PURE__*/React.createElement("line", {
  x1: "5",
  y1: "12",
  x2: "19",
  y2: "12"
}));
const IcoClose = () => /*#__PURE__*/React.createElement("svg", {
  style: {
    width: 20,
    height: 20,
    stroke: "var(--ghost)",
    fill: "none",
    strokeWidth: 2
  },
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("line", {
  x1: "18",
  y1: "6",
  x2: "6",
  y2: "18"
}), /*#__PURE__*/React.createElement("line", {
  x1: "6",
  y1: "6",
  x2: "18",
  y2: "18"
}));
const IcoWash = () => /*#__PURE__*/React.createElement("svg", {
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("path", {
  d: "M2 15s2-2 4-2 4 2 6 2 4-2 6-2 4 2 4 2"
}), /*#__PURE__*/React.createElement("path", {
  d: "M2 19s2-2 4-2 4 2 6 2 4-2 6-2 4 2 4 2"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12 3v6"
}), /*#__PURE__*/React.createElement("circle", {
  cx: "12",
  cy: "11",
  r: "2"
}));
const IcoBook = () => /*#__PURE__*/React.createElement("svg", {
  className: "ico",
  stroke: "#fff",
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("path", {
  d: "M4 19.5A2.5 2.5 0 016.5 17H20"
}), /*#__PURE__*/React.createElement("path", {
  d: "M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"
}));
const IcoChevR = () => /*#__PURE__*/React.createElement("svg", {
  className: "ico",
  style: {
    width: 12,
    height: 12,
    marginLeft: 2
  },
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("polyline", {
  points: "9 18 15 12 9 6"
}));
const IcoChevD = () => /*#__PURE__*/React.createElement("svg", {
  className: "ico",
  style: {
    transition: "transform .2s"
  },
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("polyline", {
  points: "6 9 12 15 18 9"
}));
const Spinner = () => /*#__PURE__*/React.createElement("span", {
  className: "spinner"
});
const ChevArrow = () => /*#__PURE__*/React.createElement("svg", {
  style: {
    width: 16,
    height: 16,
    stroke: "var(--ghost)",
    fill: "none",
    strokeWidth: 2
  },
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("polyline", {
  points: "9 18 15 12 9 6"
}));
const DownArrow = () => /*#__PURE__*/React.createElement("svg", {
  style: {
    width: 16,
    height: 16,
    stroke: "var(--ghost)",
    fill: "none",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  },
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("line", {
  x1: "12",
  y1: "5",
  x2: "12",
  y2: "19"
}), /*#__PURE__*/React.createElement("polyline", {
  points: "19 12 12 19 5 12"
}));

// Tick SVG for checklists
const TickSvg = ({
  size = 14,
  color = "#fff"
}) => /*#__PURE__*/React.createElement("svg", {
  style: {
    width: size,
    height: size,
    stroke: color,
    fill: "none",
    strokeWidth: 3,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  },
  viewBox: "0 0 24 24"
}, /*#__PURE__*/React.createElement("polyline", {
  points: "20 6 9 17 4 12"
}));

// Checkbox component for prep checklists
const CheckBox = ({
  checked,
  onClick,
  label
}) => /*#__PURE__*/React.createElement("div", {
  style: { display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid var(--rule-light)", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  onClick: onClick
}, /*#__PURE__*/React.createElement("div", {
  style: { width: 24, height: 24, borderRadius: 6, border: "2px solid " + (checked ? "var(--success)" : "var(--rule)"), background: checked ? "var(--success)" : "#fff", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", transition: "all .15s" }
}, checked && /*#__PURE__*/React.createElement(TickSvg, null)), /*#__PURE__*/React.createElement("span", { style: { color: checked ? "var(--success)" : "var(--text)" } }, label));

// Confirm checkbox (bigger, standalone)
const ConfirmCheck = ({
  checked,
  onClick,
  label
}) => /*#__PURE__*/React.createElement("div", {
  style: {
    marginTop: 12,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 14,
    border: "2px solid " + (checked ? "var(--success)" : "var(--rule)"),
    borderRadius: 8,
    cursor: "pointer"
  },
  onClick: onClick
}, /*#__PURE__*/React.createElement("div", {
  style: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: "2px solid " + (checked ? "var(--success)" : "var(--rule)"),
    background: checked ? "var(--success)" : "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  }
}, checked && /*#__PURE__*/React.createElement(TickSvg, {
  size: 18
})), /*#__PURE__*/React.createElement("div", {
  style: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--dim)"
  }
}, label));

// Last-keg adjustment — on the FINAL fill phase, used when emptying a tank and
// the last keg ended up a different size, or there was overflow into an extra keg
const LastKegAdjust = ({
  color,
  phase,
  n18, n9, n7,
  isIcedEsp,
  setFk18, setFk9, setFk7,
  fkScanned, setFkScanned
}) => {
  const [open, setOpen] = React.useState(false);
  // Drop the last scanned ID of a given size when reducing the count (so totals stay in sync)
  const dropLastOfSize = (sizeStr) => {
    const list = fkScanned && fkScanned.length > 0 && fkScanned[0] !== "confirmed" ? fkScanned : [];
    if (list.length === 0) return;
    for (let i = list.length - 1; i >= 0; i--) {
      const m = list[i].match(/^KEG-(\d+)-/i);
      if (m && parseInt(m[1], 10) + "L" === sizeStr) {
        const next = list.slice(0, i).concat(list.slice(i + 1));
        setFkScanned(next);
        return;
      }
    }
  };
  const swap = (fromSize, toSize) => {
    // Decrease "from", increase "to"
    if (fromSize === "18L" && n18 > 0) { setFk18(String(n18 - 1)); dropLastOfSize("18L"); }
    else if (fromSize === "9L" && n9 > 0) { setFk9(String(n9 - 1)); dropLastOfSize("9L"); }
    else if (fromSize === "7L" && n7 > 0) { setFk7(String(n7 - 1)); dropLastOfSize("7L"); }
    if (toSize === "18L") setFk18(String(n18 + (fromSize === "18L" ? 0 : 1)));
    if (toSize === "9L") setFk9(String(n9 + (fromSize === "9L" ? 0 : 1)));
    if (toSize === "7L") setFk7(String(n7 + (fromSize === "7L" ? 0 : 1)));
  };
  const addExtra = (size) => {
    if (size === "18L") setFk18(String(n18 + 1));
    if (size === "9L") setFk9(String(n9 + 1));
    if (size === "7L") setFk7(String(n7 + 1));
  };
  const total = n18 + n9 + n7;
  if (!open) {
    return /*#__PURE__*/React.createElement("div", {
      style: { textAlign: "center", marginTop: 8, marginBottom: 4 }
    }, /*#__PURE__*/React.createElement("button", {
      style: { background: "none", border: "none", fontSize: 11, fontWeight: 700, color: color || "var(--blue)", textDecoration: "underline", cursor: "pointer", padding: 4 },
      onClick: () => setOpen(true)
    }, "Last keg different size? Adjust →"));
  }
  return /*#__PURE__*/React.createElement("div", {
    style: { marginTop: 10, marginBottom: 4, padding: 12, background: "var(--input-bg)", borderRadius: 10, border: "1px solid var(--rule-light)" }
  },
    /*#__PURE__*/React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 } },
      /*#__PURE__*/React.createElement("div", { style: { fontSize: 12, fontWeight: 700 } }, "Adjust last keg"),
      /*#__PURE__*/React.createElement("button", {
        style: { background: "none", border: "none", fontSize: 11, color: "var(--ghost)", cursor: "pointer" },
        onClick: () => setOpen(false)
      }, "Close")
    ),
    /*#__PURE__*/React.createElement("div", { style: { fontSize: 10, color: "var(--ghost)", marginBottom: 10, lineHeight: 1.3 } },
      "Use when the tank ran short or over on the last keg. Current: ",
      /*#__PURE__*/React.createElement("strong", null, n18 + "× 18L"),
      n9 > 0 ? /*#__PURE__*/React.createElement("strong", null, " + " + n9 + "× 9L") : null,
      isIcedEsp && n7 > 0 ? /*#__PURE__*/React.createElement("strong", null, " + " + n7 + "× 7L") : null
    ),
    // Swap row
    /*#__PURE__*/React.createElement("div", { style: { fontSize: 10, fontWeight: 700, color: "var(--dim)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 } }, "Swap last keg"),
    /*#__PURE__*/React.createElement("div", { style: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 } },
      n9 > 0 && /*#__PURE__*/React.createElement("button", { className: "btn btn-out", style: { fontSize: 10, padding: "6px 10px", flex: "1 1 auto" }, onClick: () => swap("9L", "18L") }, "9L → 18L"),
      n18 > 0 && /*#__PURE__*/React.createElement("button", { className: "btn btn-out", style: { fontSize: 10, padding: "6px 10px", flex: "1 1 auto" }, onClick: () => swap("18L", "9L") }, "18L → 9L"),
      isIcedEsp && n7 > 0 && /*#__PURE__*/React.createElement("button", { className: "btn btn-out", style: { fontSize: 10, padding: "6px 10px", flex: "1 1 auto" }, onClick: () => swap("7L", "9L") }, "7L → 9L"),
      isIcedEsp && n9 > 0 && /*#__PURE__*/React.createElement("button", { className: "btn btn-out", style: { fontSize: 10, padding: "6px 10px", flex: "1 1 auto" }, onClick: () => swap("9L", "7L") }, "9L → 7L")
    ),
    // Add extra row
    /*#__PURE__*/React.createElement("div", { style: { fontSize: 10, fontWeight: 700, color: "var(--dim)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 } }, "Add an extra keg (overflow)"),
    /*#__PURE__*/React.createElement("div", { style: { display: "flex", gap: 6 } },
      /*#__PURE__*/React.createElement("button", { className: "btn btn-out", style: { fontSize: 10, padding: "6px 10px", flex: 1 }, onClick: () => addExtra("18L") }, "+ 1× 18L"),
      /*#__PURE__*/React.createElement("button", { className: "btn btn-out", style: { fontSize: 10, padding: "6px 10px", flex: 1 }, onClick: () => addExtra("9L") }, "+ 1× 9L"),
      isIcedEsp && /*#__PURE__*/React.createElement("button", { className: "btn btn-out", style: { fontSize: 10, padding: "6px 10px", flex: 1 }, onClick: () => addExtra("7L") }, "+ 1× 7L")
    ),
    /*#__PURE__*/React.createElement("div", { style: { fontSize: 9, color: "var(--ghost)", marginTop: 8, lineHeight: 1.3 } },
      "Note: swap removes the last scanned keg ID of that size. You'll need to scan or enter the new one."
    )
  );
};

// Step indicator
const Steps = ({
  total,
  current
}) => /*#__PURE__*/React.createElement("div", {
  className: "steps"
}, Array.from({
  length: total
}, (_, i) => {
  let cls = "step";
  if (i + 1 < current) cls += " done";
  if (i + 1 === current) cls += " cur";
  return /*#__PURE__*/React.createElement("div", {
    key: i,
    className: cls
  });
}));

// ============================================================
// APP
// ============================================================
function App() {
  // AUTH — restore from localStorage if available
  const [user, setUser] = useState(function() {
    try {
      var saved = localStorage.getItem("filament_brew_user");
      if (saved) {
        var parsed = JSON.parse(saved);
        // Validate against current USERS list
        var match = USERS.find(function(u) { return u.username === parsed.username; });
        if (match) return match;
      }
    } catch(e) {}
    return null;
  });
  const [loginU, setLoginU] = useState("");
  const [loginP, setLoginP] = useState("");
  const [loginErr, setLoginErr] = useState("");
  // Item 4: keg reconcile (Brendan-only, read-only) state
  const [reconData, setReconData] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconErr, setReconErr] = useState("");

  // NAV
  const [screen, setScreen] = useState("home");
  const [sub, setSub] = useState(null);
  const TITLES = {
    home: "Dashboard",
    live: "Live",
    brew: "Brew Log",
    kegs: "Kegs",
    stock: "Stock"
  };

  // LOADING
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [loadWarn, setLoadWarn] = useState(null);   // partial-load notes from the backend
  const hasLoadedRef = useRef(false);               // true once any load has succeeded
  const [syncing, setSyncing] = useState(false);

  // TOAST
  const toastTimer = useRef(null);
  function toast(msg, color) {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    const el = document.getElementById("brewery-toast");
    if (!el) return;
    el.textContent = msg;
    el.style.background = color || "var(--blue)";
    el.className = "toast-box show";
    toastTimer.current = setTimeout(() => {
      el.className = "toast-box";
    }, 2500);
  }

  // DATA
  const [brewLog, setBrewLog] = useState([]);
  const [tankState, setTankState] = useState([]);
  const [kegFilling, setKegFilling] = useState([]);
  const [washLog, setWashLog] = useState([]);
  const [stock, setStock] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [limitedRecipes, setLimitedRecipes] = useState([]);
  const [fridgeStock, setFridgeStock] = useState([]);
  const [recipeLog, setRecipeLog] = useState([]);
  const [rcpEditing, setRcpEditing] = useState(null);
  const [rcpDraft, setRcpDraft] = useState({});
  const [rcpReason, setRcpReason] = useState("");
  const [kegFleetCounts, setKegFleetCounts] = useState({ atCustomer: { n18: 0, n9: 0 }, toBeWashed: { n18: 0, n9: 0 }, clean: { n18: 0, n9: 0 }, staffMarket: { n18: 0, n9: 0 }, inFridge: { n18: 0, n9: 0 } });
  const [kegRegister, setKegRegister] = useState([]);
  const [mvKeg, setMvKeg] = useState(null);
  const [mvDest, setMvDest] = useState(null);
  const [mvSize, setMvSize] = useState("18");
  const [mvDigits, setMvDigits] = useState("");
  const [mvNotes, setMvNotes] = useState("");
  const [mvLog, setMvLog] = useState([]);
  // Tab state
  const [mvTab, setMvTab] = useState("move"); // 'move' | 'transfer'
  // Transfer-specific state (when mvTab === 'transfer')
  const [mvTfKind, setMvTfKind] = useState(null); // null | 'split' | 'transfer'
  const [mvTfDests, setMvTfDests] = useState([]); // [{ kegId, size, litres, kegIdInput, sizeInput }] — sizeInput/kegIdInput are draft entries
  const [mvTfResidual, setMvTfResidual] = useState(null); // 'wash' | 'stockResidual' | 'staff'
  const [mvTfProduct, setMvTfProduct] = useState(null); // null = keep source product; or a Limited product name to relabel destinations
  const [addFleetChecks, setAddFleetChecks] = useState([false,false,false,false,false,false]);
  const [addFleetSize, setAddFleetSize] = useState("18");
  const [addFleetDigits, setAddFleetDigits] = useState("");
  const [addFleetLog, setAddFleetLog] = useState([]);
  const [addFleetStaged, setAddFleetStaged] = useState([]); // [{kegId, size}, ...] pending submit
  // Fill Log review (Aaron's keg-fill summary view)
  const [flFilter, setFlFilter] = useState("week"); // today | week | fortnight | all
  const [flExpanded, setFlExpanded] = useState(null); // session key that's expanded

  // ============================================================
  // BREWERY SCANNER MODULE (bsc-)
  // ============================================================
  // Reusable QR scanner for keg operations. Uses direct DOM manipulation
  // (not React state) to avoid iOS Safari scroll-jump issues.
  // Pattern ported from filament-ops.js scanner.
  //
  // Usage: bscOpen({ mode, expected, onConfirm, onDone, label, color })
  //   mode: "fill" | "move" | "addFleet"
  //   expected: number of kegs expected (0 = unlimited)
  //   onConfirm: function(kegId, size) — called per keg on confirm
  //   onDone: function(kegIds) — called when user taps Done
  //   label: string — header label e.g. "SCAN KEGS TO FILL"
  //   color: string — accent colour for the session
  // ============================================================

  const bscRef = useRef({ scanner: null, audioCtx: null, scannedIds: [], onConfirm: null, onDone: null, pendingId: null, cooldownKeg: null, cooldownUntil: 0, lastDupeId: null, rejectTimer: null, mode: null, expected: 0 });

  function parseSizeFromId(id) { var m = id.match(/^KEG-(\d+)-/i); if (!m) return "18L"; var n = parseInt(m[1], 10); return n + "L"; }

  function bscEnsureLib(cb) {
    if (window.Html5Qrcode) { cb(); return; }
    var existing = document.querySelector('script[src*="html5-qrcode"]');
    if (existing) {
      var prev = existing.onload;
      existing.onload = function() { if (prev) prev(); cb(); };
      existing.onerror = function() { toast("Scanner library failed to load", "var(--danger)"); };
      return;
    }
    var s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/html5-qrcode/2.3.8/html5-qrcode.min.js";
    s.onload = cb;
    s.onerror = function() { toast("Scanner library failed to load", "var(--danger)"); };
    document.head.appendChild(s);
  }

  function bscPrimeAudio() {
    try {
      if (bscRef.current.audioCtx) return;
      bscRef.current.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      var buf = bscRef.current.audioCtx.createBuffer(1, 1, 22050);
      var s = bscRef.current.audioCtx.createBufferSource();
      s.buffer = buf; s.connect(bscRef.current.audioCtx.destination); s.start(0);
    } catch(e) {}
  }

  function bscBeep(ok) {
    try {
      var ctx = bscRef.current.audioCtx;
      if (!ctx) return;
      if (ctx.state === "suspended") ctx.resume();
      var osc = ctx.createOscillator();
      var gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = ok ? 880 : 220; osc.type = "sine";
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (ok ? 0.15 : 0.3));
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + (ok ? 0.15 : 0.3));
    } catch(e) {}
  }

  function bscFlash(color) {
    var fl = document.getElementById("bsc-flash");
    if (!fl) return;
    fl.style.background = color;
    fl.style.opacity = "1";
    fl.style.animation = "none";
    void fl.offsetWidth;
    fl.style.animation = "bscFlash .4s ease forwards";
  }

  function bscOpen(opts) {
    var ref = bscRef.current;
    ref.scannedIds = [];
    ref.onConfirm = opts.onConfirm || null;
    ref.onDone = opts.onDone || null;
    ref.pendingId = null;
    ref.cooldownKeg = null;
    ref.cooldownUntil = 0;
    ref.lastDupeId = null;
    ref.mode = opts.mode || "fill";
    ref.expected = opts.expected || 0;

    // Build scanner overlay DOM
    var overlay = document.getElementById("bsc-overlay");
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.id = "bsc-overlay";
      overlay.innerHTML =
        '<div id="bsc-top">' +
          '<div id="bsc-label"></div>' +
          '<button id="bsc-close">\u2715</button>' +
        '</div>' +
        '<div id="bsc-reader-wrap" style="position:relative">' +
          '<div id="bsc-reader"></div>' +
          '<div id="bsc-count" style="display:none">0</div>' +
        '</div>' +
        '<div id="bsc-reject"></div>' +
        '<div id="bsc-pending"></div>' +
        '<div id="bsc-list"></div>' +
        '<div id="bsc-done">' +
          '<button class="bsc-done-btn" id="bsc-cancel-btn" style="background:rgba(255,255,255,.1);color:rgba(255,255,255,.6)">Cancel</button>' +
          '<button class="bsc-done-btn" id="bsc-done-btn" style="background:var(--success);color:#fff">Done</button>' +
        '</div>' +
        '<div id="bsc-flash"></div>';
      document.body.appendChild(overlay);
    }
    overlay.style.display = "flex";
    document.body.style.overflow = "hidden";

    // Set label and color
    document.getElementById("bsc-label").textContent = opts.label || "SCAN KEGS";
    var rw = document.getElementById("bsc-reader-wrap");
    if (rw) rw.style.borderBottom = "3px solid " + (opts.color || "var(--success)");

    // Reset list + pending
    document.getElementById("bsc-list").innerHTML = '<div style="text-align:center;padding:24px;color:rgba(255,255,255,.25);font-size:12px;font-weight:600">Scanned kegs appear here</div>';
    document.getElementById("bsc-pending").style.display = "none";
    document.getElementById("bsc-reject").style.display = "none";
    var cnt = document.getElementById("bsc-count");
    if (cnt) { cnt.style.display = "none"; cnt.textContent = "0"; }

    // Wire close / done / cancel
    document.getElementById("bsc-close").onclick = function() { bscClose(false); };
    document.getElementById("bsc-cancel-btn").onclick = function() { bscClose(false); };
    document.getElementById("bsc-done-btn").onclick = function() { bscClose(true); };

    // Start camera
    bscEnsureLib(function() { bscStartCamera(opts.color); });
  }

  function bscStartCamera(accentColor) {
    bscPrimeAudio();
    try {
      var ref = bscRef.current;
      ref.scanner = new window.Html5Qrcode("bsc-reader");
      ref.scanner.start(
        { facingMode: "environment" },
        {
          fps: 15,
          qrbox: { width: 240, height: 240 },
          videoConstraints: {
            facingMode: "environment",
            advanced: [{ focusMode: "continuous" }]
          }
        },
        function(text) { bscOnScan(text, accentColor); },
        function() {}
      ).catch(function(err) { toast("Camera: " + err, "var(--danger)"); bscClose(false); });
    } catch(e) { toast("Scanner: " + e.message, "var(--danger)"); bscClose(false); }
  }

  function bscStopCamera() {
    var ref = bscRef.current;
    if (ref.scanner) {
      ref.scanner.stop().then(function() { ref.scanner.clear(); ref.scanner = null; }).catch(function() { ref.scanner = null; });
    }
  }

  function bscClose(commit) {
    var ref = bscRef.current;
    bscStopCamera();
    bscDismissPending();
    var overlay = document.getElementById("bsc-overlay");
    if (overlay) overlay.style.display = "none";
    document.body.style.overflow = "";
    if (commit && ref.onDone) {
      ref.onDone(ref.scannedIds.slice());
    }
    ref.scannedIds = [];
    ref.onConfirm = null;
    ref.onDone = null;
  }

  function bscOnScan(raw, accentColor) {
    var ref = bscRef.current;
    var id = raw.trim().toUpperCase();
    if (!id.match(/^KEG-\d+-\d+/i)) return;
    // Ignore while pending confirmation showing
    if (ref.pendingId) return;
    // Cooldown: silently ignore re-scans of just-confirmed keg
    if (ref.cooldownKeg === id && Date.now() < ref.cooldownUntil) return;
    // Duplicate: silently ignore but remember for tap-to-reveal
    if (ref.scannedIds.indexOf(id) !== -1) { ref.lastDupeId = id; bscBeep(false); bscShowReject(id + " already scanned"); return; }

    // Mode-specific validation
    if (ref.mode === "fill" || ref.mode === "move") {
      var keg = kegLookup(id);
      if (!keg && ref.mode === "move") {
        // Move flow still requires the keg to exist — you can't move something that's not tracked
        bscBeep(false);
        bscShowReject(id + " not in keg register");
        return;
      }
      // Fill mode: unregistered kegs are allowed — backend upsertKegRegister will
      // auto-register them as part of the fill. Matches Marty's sticker-then-fill workflow.
    }
    if (ref.mode === "addFleet") {
      // For add-to-fleet, keg must NOT already exist
      var existing = kegLookup(id);
      if (existing) {
        bscBeep(false);
        bscShowReject(id + " already in fleet");
        return;
      }
    }

    ref.lastDupeId = null;
    var size = parseSizeFromId(id);
    bscBeep(true);
    bscFlash(accentColor || "var(--success)");
    bscShowPending(id, size, accentColor);
  }

  function bscShowPending(id, size, accentColor) {
    var ref = bscRef.current;
    ref.pendingId = id;
    var pe = document.getElementById("bsc-pending");
    if (!pe) return;
    // Lookup product info if in register
    var keg = kegLookup(id);
    var productInfo = keg && keg.product ? " \u00B7 " + keg.product : "";
    var statusInfo = keg && keg.status ? keg.status : "";
    pe.style.background = accentColor || "var(--success)";
    pe.innerHTML =
      '<div class="bsc-p-id">' + id + '</div>' +
      '<div class="bsc-p-size">' + size + productInfo + (statusInfo ? ' \u00B7 ' + statusInfo : '') + '</div>' +
      '<button class="bsc-p-btn" id="bsc-confirm">\u2713 CONFIRM</button>' +
      '<button class="bsc-p-cancel" id="bsc-pcancel">SKIP</button>';
    pe.style.display = "block";
    pe.style.animation = "bscPop .15s ease";
    document.getElementById("bsc-confirm").onclick = function() { bscConfirmPending(accentColor); };
    document.getElementById("bsc-pcancel").onclick = function() { bscDismissPending(); };
  }

  function bscDismissPending() {
    bscRef.current.pendingId = null;
    var pe = document.getElementById("bsc-pending");
    if (pe) pe.style.display = "none";
  }

  function bscConfirmPending(accentColor) {
    var ref = bscRef.current;
    var id = ref.pendingId;
    if (!id) return;
    var size = parseSizeFromId(id);

    ref.scannedIds.push(id);
    if (ref.onConfirm) ref.onConfirm(id, size);

    bscDismissPending();

    // Cooldown: ignore re-scans of this keg for 2s
    ref.cooldownKeg = id;
    ref.cooldownUntil = Date.now() + 2000;

    // Update list
    bscRenderList(accentColor);

    // Update count badge
    var cnt = document.getElementById("bsc-count");
    if (cnt) {
      cnt.style.display = "flex";
      cnt.textContent = String(ref.scannedIds.length);
      cnt.style.animation = "bscPop .2s ease";
    }

    // Auto-close if expected count reached
    if (ref.expected > 0 && ref.scannedIds.length >= ref.expected) {
      setTimeout(function() { bscClose(true); }, 600);
    }
  }

  function bscShowReject(msg) {
    var ref = bscRef.current;
    var rw = document.getElementById("bsc-reject");
    if (!rw) return;
    rw.textContent = "\u26A0\uFE0F " + msg;
    rw.style.display = "block";
    rw.style.animation = "bscPop .15s ease";
    if (ref.rejectTimer) clearTimeout(ref.rejectTimer);
    ref.rejectTimer = setTimeout(function() { rw.style.display = "none"; }, 2500);
  }

  function bscRenderList(accentColor) {
    var ref = bscRef.current;
    var container = document.getElementById("bsc-list");
    if (!container) return;
    if (ref.scannedIds.length === 0) {
      container.innerHTML = '<div style="text-align:center;padding:24px;color:rgba(255,255,255,.25);font-size:12px;font-weight:600">Scanned kegs appear here</div>';
      return;
    }
    var html = '';
    for (var i = ref.scannedIds.length - 1; i >= 0; i--) {
      var kid = ref.scannedIds[i];
      var sz = parseSizeFromId(kid);
      var keg = kegLookup(kid);
      var prod = keg && keg.product ? keg.product : "";
      var prodHtml = prod ? '<span style="font-size:10px;color:rgba(255,255,255,.35);margin-left:6px">' + prod + '</span>' : '';
      html += '<div class="bsc-keg-row" style="' + (i === ref.scannedIds.length - 1 ? 'animation:bscPop .2s ease' : '') + '">' +
        '<div class="bsc-keg-bar bsc-keg-bar-ok"></div>' +
        '<div><span class="bsc-keg-id">' + kid + '</span>' + prodHtml + '</div>' +
        '<span class="bsc-keg-sz">' + sz + '</span>' +
        '<button class="bsc-keg-rm" data-kid="' + kid + '">\u2715</button>' +
        '</div>';
    }
    container.innerHTML = html;
    // Wire remove buttons
    var btns = container.querySelectorAll(".bsc-keg-rm");
    btns.forEach(function(btn) {
      btn.onclick = function() {
        var removeId = btn.getAttribute("data-kid");
        ref.scannedIds = ref.scannedIds.filter(function(k) { return k !== removeId; });
        bscRenderList(accentColor);
        var cnt = document.getElementById("bsc-count");
        if (cnt) {
          cnt.textContent = String(ref.scannedIds.length);
          if (ref.scannedIds.length === 0) cnt.style.display = "none";
        }
      };
    });
  }


  // BREW ON
  const [onStep, setOnStep] = useState(1);
  const [onProduct, setOnProduct] = useState("Classic");
  const [onTotalKg, setOnTotalKg] = useState("34");
  const [onGrindSize, setOnGrindSize] = useState("");
  const [onRoastDate, setOnRoastDate] = useState("");
  const [onDoses, setOnDoses] = useState(["8.5", "8.5", "8.5", "8.5"]);
  const [onGrindStart, setOnGrindStart] = useState("");
  const [onGrindFinish, setOnGrindFinish] = useState("");
  const [onTank, setOnTank] = useState("");
  const [onFillTarget, setOnFillTarget] = useState("");
  const [onTimeOn, setOnTimeOn] = useState("");
  const [onTargetOff, setOnTargetOff] = useState("");
  const [onAmbient, setOnAmbient] = useState("");
  const [onStartTemp, setOnStartTemp] = useState("");
  const [onTargetTemp, setOnTargetTemp] = useState("8.3");
  const [onParticle, setOnParticle] = useState(["", "", ""]);
  const [onBrewDate, setOnBrewDate] = useState(function() { var d = new Date(); var _M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]; return d.getDate() + " " + _M[d.getMonth()] + " " + d.getFullYear(); }());
  const [onTimePerDose, setOnTimePerDose] = useState("");
  const [onDoseDone, setOnDoseDone] = useState([false, false, false, false]);
  const [onGrindStartOk, setOnGrindStartOk] = useState(false);
  const [onGrindFinishOk, setOnGrindFinishOk] = useState(false);
  const [onChecklist, setOnChecklist] = useState([false, false, false, false, false, false, false, false]);
  const [onPhotoFlow, setOnPhotoFlow] = useState(false);
  const [onPhotoTank, setOnPhotoTank] = useState(false);
  const [onInProgress, setOnInProgress] = useState(false);
  const [onPreGrind, setOnPreGrind] = useState([false, false]);

  // BREW OFF
  const [offStep, setOffStep] = useState(1);
  const [offInProgress, setOffInProgress] = useState(false);
  const [offLtS, setOffLtS] = useState("");
  const [offLtL, setOffLtL] = useState("");
  const [offBtS, setOffBtS] = useState("");
  const [offBtL, setOffBtL] = useState("");
  const [offFS, setOffFS] = useState("");
  const [offFF, setOffFF] = useState("");
  const [offPS, setOffPS] = useState("5");
  const [offPE, setOffPE] = useState("15");
  const [offFL, setOffFL] = useState("");
  const [offConc, setOffConc] = useState(["", "", "", "", ""]);
  const [offDil, setOffDil] = useState(["", "", "", "", ""]);
  const [offWaterOk, setOffWaterOk] = useState(false);
  const [offNagardoOk, setOffNagardoOk] = useState(false);
  const [offSorbateOk, setOffSorbateOk] = useState(false);
  const [offNagardoRate, setOffNagardoRate] = useState("");
  const [offSorbateRate, setOffSorbateRate] = useState("");
  const [offOverride, setOffOverride] = useState("");
  const [offTag, setOffTag] = useState("");
  const [offBB, setOffBB] = useState("");
  const [offFlavour, setOffFlavour] = useState("");

  // FILL KEGS
  const [fkTank, setFkTank] = useState(null);
  const [fk18, setFk18] = useState("");
  const [fk9, setFk9] = useState("");
  const [fk7, setFk7] = useState("");
  const [fkScanned, setFkScanned] = useState([]);
  const [fkChecks, setFkChecks] = useState([false, false, false, false, false]);
  const [fkLimited, setFkLimited] = useState(null); // null = no limited, or "Choc Mint" etc
  const [fkLtd18, setFkLtd18] = useState("");
  const [fkLtd9, setFkLtd9] = useState("");
  const [fkLtdScanned, setFkLtdScanned] = useState([]);
  const [fkLtdChecks, setFkLtdChecks] = useState([false, false, false, false, false]);
  const [fkRecipeConfirmed, setFkRecipeConfirmed] = useState(false);
  const [fkLtdTag, setFkLtdTag] = useState("");
  const [fkPhase, setFkPhase] = useState("setup"); // setup | classic | limited
  const [fkPhotoCalcs, setFkPhotoCalcs] = useState(false);
  const [fkLtdPhotoCalcs, setFkLtdPhotoCalcs] = useState(false);
  const [fkSaving, setFkSaving] = useState(false);
  // Save progress overlay state — { steps: [{label, status: "pending"|"active"|"done"}], message, done: bool }
  const [saveProgress, setSaveProgress] = useState(null);
  const [fkDrainOpen, setFkDrainOpen] = useState(false);
  const [fkResumeSession, setFkResumeSession] = useState(null);
  const [cancelFillTank, setCancelFillTank] = useState(null);
  const [cancelFillSaving, setCancelFillSaving] = useState(false);
  // Empty Tank (from Live view) — discards the residual left in a bright tank and
  // returns it to Empty so it's free for the next brew. Reason is mandatory and is
  // written to the Stock Log as a Drain, same as the in-fill "Empty Tank" button.
  const [emptyTank, setEmptyTank] = useState(null); // { id, product, litres, brewId }
  const [emptyReason, setEmptyReason] = useState("");
  const [emptySaving, setEmptySaving] = useState(false);
  const [fkDrainReason, setFkDrainReason] = useState("");
  const [fkRemainOverride, setFkRemainOverride] = useState(""); // "" = use calculated, otherwise user's actual measured remainder
  // Last keg adjustment (handles tank-empty scenarios where the final keg's volume differs)
  const [fkLastKegOpen, setFkLastKegOpen] = useState(false); // modal open?
  const [fkLastKegInput, setFkLastKegInput] = useState(""); // user-entered actual volume
  const [fkLastKegPartial, setFkLastKegPartial] = useState(null); // { size: "18L"|"9L"|"7L", litres: number } when last keg is partial -> staff fridge
  const [fkLastKegScannedId, setFkLastKegScannedId] = useState(""); // the kegId of the partial keg (separate from fkScanned)
  // Manual keg entry modal (within fill flow)
  const [fkManualOpen, setFkManualOpen] = useState(null); // { size: "18"|"09"|"07", remaining: n, phase: "classic"|"limited", color }
  const [fkManualDigits, setFkManualDigits] = useState("");
  const fkManualInputRef = useRef(null);
  // Independent "in fridge" tick states for classic + limited phases (so scan IDs and tick are decoupled)
  const [fridgeChecked, setFkFridgeChecked] = useState(false);
  const [ltdFridgeChecked, setFkLtdFridgeChecked] = useState(false);

  // WASH
  const [washChecks, setWashChecks] = useState([false, false]);
  const [addDirtyOpen, setAddDirtyOpen] = useState(false);
  const [addDirtySize, setAddDirtySize] = useState("18L");
  const [staffFridgeOpen, setStaffFridgeOpen] = useState(false);
  const [staffFridgeSize, setStaffFridgeSize] = useState("18L");
  const [staffFridgeSource, setStaffFridgeSource] = useState("stock");
  const [staffFridgeProduct, setStaffFridgeProduct] = useState("Classic");
  const [staffRemoveOpen, setStaffRemoveOpen] = useState(false);
  const [staffRemoveSize, setStaffRemoveSize] = useState("18L");
  const [staffAdjustOpen, setStaffAdjustOpen] = useState(false);
  const [w18, setW18] = useState("");
  const [w9, setW9] = useState("");

  // MODALS
  const [brewDetail, setBrewDetail] = useState(null);
  const [brewEditing, setBrewEditing] = useState(false);
  const [brewDraft, setBrewDraft] = useState({});
  const [fridgeModal, setFridgeModal] = useState(null);
  const [fleetExpanded, setFleetExpanded] = useState(null); // status label that's expanded
  const [rcpTabSt, setRcpTabSt] = useState("classic");
  const [rcpOpen, setRcpOpen] = useState(null);
  const [rcpLogOpen, setRcpLogOpen] = useState(null);

  // SCHEDULE
  const [schedule, setSchedule] = useState([]);
  // Live schedule updates (Oct 2026). The backend keeps a version number that
  // moves whenever Aaron's planning sheet is re-synced with a change. The app
  // polls it (cheap — no spreadsheet read) and, when it moves, refetches the
  // schedule and shows a banner naming what changed.
  const scheduleRef = useRef([]);
  const schedVersionRef = useRef(null);
  const [schedBanner, setSchedBanner] = useState(null); // { at, changes: [label] }
  const [schedChecking, setSchedChecking] = useState(false);
  const [schedWeek, setSchedWeek] = useState(0); // 0 = this week, 1 = next week
  const [noteExpanded, setNoteExpanded] = useState({}); // key: date string -> bool
  const [tankCleanDone, setTankCleanDone] = useState({}); // key: "date|detail|who" -> true (visual only, resets on refresh)

  // Scroll top on nav
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "instant"
    });
  }, [screen, sub]);

  // Reset fill-flow fridge ticks when leaving the fillKegs sub-screen
  useEffect(() => {
    if (sub !== "fillKegs") {
      setFkFridgeChecked(false);
      setFkLtdFridgeChecked(false);
    }
  }, [sub]);

  // Safety: if we land on classic phase but classic has 0 kegs and limited has counts, jump to limited
  useEffect(function() {
    if (sub !== "fillKegs" || fkPhase !== "classic" || !fkTank) return;
    var classicCount = (parseInt(fk18) || 0) + (parseInt(fk9) || 0) + (parseInt(fk7) || 0);
    var ltdCount = (parseInt(fkLtd18) || 0) + (parseInt(fkLtd9) || 0);
    if (classicCount === 0 && fkLimited && ltdCount > 0) {
      setFkPhase("limited");
      setFkLtdChecks([false, false, false, false, false]);
    }
  }, [sub, fkPhase, fkTank, fk18, fk9, fk7, fkLtd18, fkLtd9, fkLimited]);


  // Auto-save fill session on any state change
  useEffect(function() {
    if (sub === "fillKegs" && fkTank) {
      persistFillSession();
    }
  }, [fkTank, fk18, fk9, fk7, fkScanned, fkChecks, fkPhase, fkLimited, fkLtd18, fkLtd9, fkLtdScanned, fkLtdChecks, fkRecipeConfirmed, fkLtdTag, fkPhotoCalcs, fkLtdPhotoCalcs, fkRemainOverride, fkLastKegPartial]);

  // LOAD DATA
  // A failed BACKGROUND refresh (after a save, or a replayed fill) must not
  // replace a working screen with "Connection Error" — the brewer keeps what
  // they had and gets a toast. Only the first load / a Retry shows the full
  // error screen.
  function loadData(showSpinner) {
    var background = !showSpinner && hasLoadedRef.current;
    if (showSpinner) setLoading(true);
    if (!background) setLoadErr(null);
    function fail(msg) {
      if (background) {
        toast("Couldn't refresh: " + msg, "var(--warning, #C17817)");
        return;
      }
      setLoadErr(msg);
      setLoading(false);
    }
    api.load().then(data => {
      if (!data || data.error) {
        fail((data && data.error) || "No data came back");
        return;
      }
      hasLoadedRef.current = true;
      setLoadErr(null);
      setLoadWarn(data.warnings && data.warnings.length ? data.warnings : null);
      if (data.brewLog) setBrewLog(data.brewLog);
      if (data.tankState) setTankState(data.tankState);
      if (data.kegFilling) setKegFilling(data.kegFilling);
      if (data.washLog) setWashLog(data.washLog);
      if (data.stock) setStock(data.stock);
      if (data.recipes) setRecipes(data.recipes);
      if (data.limitedRecipes) setLimitedRecipes(data.limitedRecipes.map(function(r) {
        var ings = [];
        if (r.ingredient1) ings.push({ name: r.ingredient1, rate: r.rate1 });
        if (r.ingredient2) ings.push({ name: r.ingredient2, rate: r.rate2 });
        if (r.ingredient3) ings.push({ name: r.ingredient3, rate: r.rate3 });
        r.ingredients = ings;
        return r;
      }));
      if (data.fridgeStock) setFridgeStock(data.fridgeStock);
      if (data.recipeLog) setRecipeLog(data.recipeLog);
      if (data.kegFleetCounts) setKegFleetCounts(data.kegFleetCounts);
      if (data.schedule) {
        // A background refresh can pick up a newer schedule before the poll does —
        // still tell the user, rather than changing their day silently.
        if (hasLoadedRef.current && data.scheduleVersion && schedVersionRef.current &&
            data.scheduleVersion !== schedVersionRef.current) {
          applyScheduleUpdate(data.schedule, { version: data.scheduleVersion });
        } else {
          setSchedule(data.schedule);
        }
      }
      if (data.scheduleVersion) schedVersionRef.current = data.scheduleVersion;
      if (data.kegRegister) setKegRegister(data.kegRegister);
      setLoading(false);
    }).catch(err => {
      fail((err && err.message) || "Network error");
    });
  }
  useEffect(() => {
    if (user) loadData(true);
  }, [user]);


  // Restore an interrupted fill from localStorage — network-INDEPENDENT.
  // Runs as soon as the user is known, without waiting for (or being blocked by)
  // the data load. The fill lives entirely in local storage until the final Save,
  // so we can drop the brewer back exactly where they left off even if the network
  // is slow or down. Only the final commit needs the server.
  var fillRestoredRef = useRef(false);
  function applyFillSession(saved) {
    var snap = (saved && saved.tankSnapshot) || tankState.find(function(t) { return t.tankId === (saved && saved.tankId); }) || null;
    if (snap) setFkTank(snap);
    setFk18(saved.fk18 || ""); setFk9(saved.fk9 || ""); setFk7(saved.fk7 || "");
    setFkScanned(saved.fkScanned || []);
    setFkChecks(saved.fkChecks || [false, false, false, false, false]);
    setFkPhase(saved.fkPhase || "setup");
    setFkLimited(saved.fkLimited || null);
    setFkLtd18(saved.fkLtd18 || ""); setFkLtd9(saved.fkLtd9 || "");
    setFkLtdScanned(saved.fkLtdScanned || []);
    setFkLtdChecks(saved.fkLtdChecks || [false, false, false, false, false]);
    setFkRecipeConfirmed(saved.fkRecipeConfirmed || false);
    setFkLtdTag(saved.fkLtdTag || "");
    setFkPhotoCalcs(saved.fkPhotoCalcs || false);
    setFkLtdPhotoCalcs(saved.fkLtdPhotoCalcs || false);
    setFkRemainOverride(saved.fkRemainOverride || "");
    setFkLastKegPartial(saved.fkLastKegPartial || null);
    setFkLastKegScannedId(saved.fkLastKegScannedId || "");
    return snap;
  }
  useEffect(function() {
    if (!user || fillRestoredRef.current) return;
    // A submitted-but-unconfirmed fill is handled by the replay effect below —
    // don't also restore it here as an "in-progress" fill.
    if (loadPendingFill()) { fillRestoredRef.current = true; return; }
    var saved = loadFillSession();
    if (!saved || !saved.tankId) return;
    fillRestoredRef.current = true;
    var snap = applyFillSession(saved);
    // If the brewer was actively mid-fill when the app was killed, take them
    // straight back into the fill. If they deliberately saved & closed, or the
    // session is stale (>12h), show the resume banner and let them choose.
    if (saved.wasActive && !saved._stale && snap) {
      setScreen("kegs"); setSub("fillKegs");
    } else {
      setFkResumeSession(saved);
    }
  }, [user]);

  // Foreground reconciliation: when the app returns from the background (iPad
  // unlocked / app re-opened), make sure any saved fill is re-armed in memory.
  // Cheap (localStorage only) so it can run on every unlock without hammering
  // the network. The fill data is local, so this never blocks the UI.
  useEffect(function() {
    function onForeground() {
      if (document.visibilityState && document.visibilityState !== "visible") return;
      if (!user) return;
      if (loadPendingFill()) return; // replay effect reconciles a pending save
      var saved = loadFillSession();
      if (saved && saved.tankId && !fkTank && sub !== "fillKegs" && sub !== "fillKegsLimited") {
        applyFillSession(saved);
        setFkResumeSession(saved);
      }
    }
    document.addEventListener("visibilitychange", onForeground);
    window.addEventListener("pageshow", onForeground);
    return function() {
      document.removeEventListener("visibilitychange", onForeground);
      window.removeEventListener("pageshow", onForeground);
    };
  }, [user, fkTank, sub]);

  // Replay a fill that was submitted but never confirmed (e.g. the iPad was
  // closed right after Save, before the server replied). Re-sending is safe: the
  // backend is idempotent on requestId, so it either confirms the earlier save
  // or completes it now — never a duplicate.
  // ── Live schedule: diff, apply, poll ──
  useEffect(function() { scheduleRef.current = schedule; }, [schedule]);

  function schedKey(s) { return s.date + "|" + s.task + "|" + s.sortOrder; }
  function schedSig(s) { return [s.detail, s.who, s.time, s.notes].join("|"); }
  // Returns short labels like "Thu Keg Up" for items added, removed or edited.
  // Status-only changes (Done ticks) are ignored — they aren't Aaron's plan changing.
  function diffSchedule(oldList, newList) {
    var DN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    function label(s) {
      var p = String(s.date || "").split("-");
      var d = p.length === 3 ? new Date(+p[0], +p[1] - 1, +p[2]) : null;
      var day = d && !isNaN(d.getTime()) ? DN[d.getDay()] + " " + d.getDate() : s.date;
      return day + " " + (s.task === "Note" ? "note" : s.task);
    }
    var oldMap = {}, newMap = {}, out = [];
    (oldList || []).forEach(function(s) { oldMap[schedKey(s)] = s; });
    (newList || []).forEach(function(s) { newMap[schedKey(s)] = s; });
    (newList || []).forEach(function(s) {
      var o = oldMap[schedKey(s)];
      if (!o || schedSig(o) !== schedSig(s)) out.push({ date: s.date, text: label(s) });
    });
    (oldList || []).forEach(function(s) {
      if (!newMap[schedKey(s)]) out.push({ date: s.date, text: label(s) + " removed" });
    });
    out.sort(function(a, b) { return a.date < b.date ? -1 : a.date > b.date ? 1 : 0; });
    var seen = {}, labels = [];
    out.forEach(function(c) { if (!seen[c.text]) { seen[c.text] = 1; labels.push(c.text); } });
    return labels;
  }
  function applyScheduleUpdate(items, ver, opts) {
    var changes = diffSchedule(scheduleRef.current, items);
    scheduleRef.current = items;
    setSchedule(items);
    if (ver && ver.version) schedVersionRef.current = ver.version;
    if (!(opts && opts.silent) && changes.length) {
      setSchedBanner({ at: (ver && ver.updatedAt) || nowT(), changes: changes });
    }
    return changes;
  }
  // Poll every 90s while the app is on screen, and straight away when the iPad
  // is unlocked / the app comes back to the foreground.
  useEffect(function() {
    if (!user) return;
    var busy = false;
    function check() {
      if (busy) return;
      if (document.visibilityState && document.visibilityState !== "visible") return;
      if (!schedVersionRef.current) return; // first load not done yet
      busy = true;
      fetchJson(BREWERY_API + "?action=scheduleVersion", null, 15000, 0).then(function(v) {
        if (!v || !v.version || v.version === schedVersionRef.current) return null;
        return fetchJson(BREWERY_API + "?action=loadSchedule", null, 30000, 1).then(function(items) {
          if (Array.isArray(items)) applyScheduleUpdate(items, v);
        });
      }).catch(function() { /* quiet — try again next tick */ }).then(function() { busy = false; });
    }
    var timer = setInterval(check, 90000);
    function onVis() { if (document.visibilityState === "visible") check(); }
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pageshow", onVis);
    return function() {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pageshow", onVis);
    };
  }, [user]);
  // "Check for changes" — pulls Aaron's sheet now rather than waiting for the
  // 5-minute sync. For whoever presses it the new schedule just appears (toast,
  // no banner); everyone else gets the banner on their next poll.
  function syncScheduleNow() {
    if (schedChecking) return;
    setSchedChecking(true);
    api.get("syncScheduleNow").then(function(res) {
      if (res && res.error) { setSchedChecking(false); toast("Couldn't check: " + res.error, "var(--danger)"); return; }
      if (res && res.busy) { setSchedChecking(false); toast("A check is already running \u2014 try again in a moment", "var(--warning, #C17817)"); return; }
      return api.get("loadSchedule").then(function(items) {
        setSchedChecking(false);
        if (!Array.isArray(items)) { toast("Couldn't load the schedule", "var(--danger)"); return; }
        var changes = applyScheduleUpdate(items, res, { silent: true });
        setSchedBanner(null);
        toast(changes.length ? "Schedule updated \u2014 " + changes.length + " change" + (changes.length !== 1 ? "s" : "") : "Schedule is up to date \u2713", "var(--success)");
      });
    }).catch(function(err) {
      setSchedChecking(false);
      toast("Couldn't check: " + ((err && err.message) || "network error"), "var(--danger)");
    });
  }

  var pendingReplayRef = useRef(false);
  useEffect(function() {
    if (!user || pendingReplayRef.current) return;
    var pending = loadPendingFill();
    if (!pending || !pending.data) return;
    pendingReplayRef.current = true;
    api.post(pending.action || "fillKegs", { data: pending.data }).then(function(res) {
      if (res && res.error) {
        toast("Finishing previous fill\u2026 will retry (" + res.error + ")", "var(--warning, #C17817)");
        return; // keep the receipt for another attempt
      }
      clearPendingFill();
      clearFillSession();
      toast(res && res.duplicate ? "Previous fill already saved \u2713" : "Previous fill saved \u2713", "var(--success)");
      loadData();
    }).catch(function() {
      // Still offline — keep the receipt and retry next time the app opens.
    });
  }, [user]);

  // DERIVED
  const getTank = id => tankState.find(t => t.tankId === id) || null;
  const fridgeSummary = () => {
    const s = {};
    fridgeStock.forEach(k => {
      const p = normProduct(k.product) || "Unknown";
      if (!s[p]) s[p] = {
        "18L": 0,
        "9L": 0,
        "7L": 0
      };
      var sz = String(k.size);
      if (sz.indexOf("7") > -1) s[p]["7L"]++;
      else if (sz.indexOf("9") > -1) s[p]["9L"]++;
      else s[p]["18L"]++;
    });
    return s;
  };
  const has7LKegs = () => {
    var fs = fridgeSummary();
    return Object.keys(fs).some(function(p) { return fs[p]["7L"] > 0; });
  };
  const fridgeTotal = () => fridgeStock.length;

  // Look up keg by ID from the register
  function kegLookup(kegId) {
    kegId = String(kegId).toUpperCase().trim();
    return kegRegister.find(function(k) { return k.kegId.toUpperCase() === kegId; }) || null;
  }
  // True if this register status is a "safe to fill" state (Clean, Unregistered, or blank).
  // Any other status (At Customer, In Fridge, To Be Washed, Staff/Market) means filling
  // would overwrite an active record — the user should be warned and asked to confirm.
  function isKegFillable(status) {
    var s = String(status || "").trim();
    return s === "Clean" || s === "Unregistered" || s === "";
  }
  // Compute next available keg IDs
  function nextKegIds() {
    // Find the lowest keg ID with status "Unregistered" (labels printed but not yet added to fleet)
    // If none found, fall back to max+1
    var unreg18 = [], unreg9 = [];
    var max18 = 0, max9 = 0;
    kegRegister.forEach(function(k) {
      var m = k.kegId.match(/^KEG-(\d+)-(\d+)$/i);
      if (!m) return;
      var sz = parseInt(m[1]);
      var num = parseInt(m[2]);
      if (sz === 18) {
        if (num > max18) max18 = num;
        if (k.status === 'Unregistered') unreg18.push(num);
      }
      if (sz === 9) {
        if (num > max9) max9 = num;
        if (k.status === 'Unregistered') unreg9.push(num);
      }
    });
    // Exclude any added this session
    addFleetLog.forEach(function(k) {
      var m = k.kegId.match(/^KEG-(\d+)-(\d+)$/i);
      if (!m) return;
      var sz = parseInt(m[1]);
      var num = parseInt(m[2]);
      if (sz === 18) { unreg18 = unreg18.filter(function(n) { return n !== num; }); if (num > max18) max18 = num; }
      if (sz === 9) { unreg9 = unreg9.filter(function(n) { return n !== num; }); if (num > max9) max9 = num; }
    });
    // Sort and pick lowest unregistered, or fall back to max+1
    unreg18.sort(function(a, b) { return a - b; });
    unreg9.sort(function(a, b) { return a - b; });
    return {
      next18: String(unreg18.length > 0 ? unreg18[0] : max18 + 1).padStart(3, '0'),
      next9: String(unreg9.length > 0 ? unreg9[0] : max9 + 1).padStart(3, '0')
    };
  }
  const activeBrew = () => brewLog.find(b => b.status === "ON" || b.status === "Brewing") || null;
  const offProd = () => {
    const ab = activeBrew();
    return ab && ab.product ? ab.product : "Classic";
  };
  const offNeedsDil = () => offProd() !== "Iced Espresso";
  const offTgt = () => QCT[offProd()] || 1.45;
  const offFT = () => {
    const ov = parseFloat(offOverride);
    return ov > 0 ? ov : QCFT[offProd()] || 423;
  };

  // LOGIN
  function handleLogin() {
    const u = loginU.trim().toLowerCase();
    const found = USERS.find(x => x.username === u && x.password === loginP);
    if (found) {
      setUser(found);
      try { localStorage.setItem("filament_brew_user", JSON.stringify({ username: found.username })); } catch(e) {}
      setLoginErr("");
      setLoginU("");
      setLoginP("");
    } else setLoginErr("Invalid username or password");
  }
  if (!user) return /*#__PURE__*/React.createElement("div", {
    className: "login-wrap"
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      textAlign: "center",
      marginBottom: 40
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "brand",
    style: {
      fontSize: 11,
      letterSpacing: 4
    }
  }, "Filament Brewery"), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 28,
      fontWeight: 800,
      letterSpacing: -1,
      marginTop: 12
    }
  }, "Sign in")), /*#__PURE__*/React.createElement("div", {
    style: {
      width: "100%",
      maxWidth: 280
    }
  }, /*#__PURE__*/React.createElement("input", {
    className: "input",
    type: "text",
    placeholder: "Username",
    value: loginU,
    onChange: e => setLoginU(e.target.value),
    onKeyDown: e => {
      if (e.key === "Enter") document.getElementById("brew-pw").focus();
    },
    style: {
      marginBottom: 10
    }
  }), /*#__PURE__*/React.createElement("input", {
    id: "brew-pw",
    className: "input",
    type: "password",
    placeholder: "Password",
    value: loginP,
    onChange: e => setLoginP(e.target.value),
    onKeyDown: e => {
      if (e.key === "Enter") handleLogin();
    },
    style: {
      marginBottom: 10
    }
  }), loginErr && /*#__PURE__*/React.createElement("div", {
    style: {
      color: "var(--danger)",
      fontSize: 12,
      fontWeight: 600,
      marginBottom: 10,
      textAlign: "center"
    }
  }, loginErr), /*#__PURE__*/React.createElement("button", {
    className: "btn btn-blue",
    onClick: handleLogin
  }, "Sign In")));
  if (loading && sub !== "fillKegs" && sub !== "fillKegsLimited") return /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "60vh",
      gap: 16
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: 32,
      height: 32,
      border: "3px solid var(--rule)",
      borderTopColor: "var(--blue)",
      borderRadius: "50%",
      animation: "spin .6s linear infinite"
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 13,
      color: "var(--dim)"
    }
  }, "Loading brewery data\u2026"));
  if (loadErr && sub !== "fillKegs" && sub !== "fillKegsLimited") return /*#__PURE__*/React.createElement("div", {
    style: {
      padding: 40,
      textAlign: "center"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 18,
      fontWeight: 700,
      marginBottom: 12
    }
  }, "Connection Error"), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 13,
      color: "var(--dim)",
      marginBottom: 20
    }
  }, loadErr), /*#__PURE__*/React.createElement("button", {
    className: "btn btn-blue",
    style: {
      maxWidth: 200,
      margin: "0 auto"
    },
    onClick: function() { loadData(true); }
  }, "Retry"));
  function nav(tab) {
    setScreen(tab);
    setSub(null);
  }

  // ============================================================
  // SAVE FUNCTIONS
  // ============================================================
  function saveBrewOn() {
    // Required-field check. Runs before the post, so a blocked save writes nothing.
    // Starting Temp is the one that kept coming back blank — it sits on step 3 beside
    // the finish checklist and is easy to walk straight past.
    var onMissing = [];
    if (!String(onProduct || "").trim()) onMissing.push("Product");
    if (!String(onTank || "").trim()) onMissing.push("Bright Tank");
    if (!String(onTotalKg || "").trim()) onMissing.push("Total KG");
    if (!String(onGrindSize || "").trim()) onMissing.push("Grind Size");
    if (!String(onRoastDate || "").trim()) onMissing.push("Roast Date");
    if (!String(onTimePerDose || "").trim()) onMissing.push("Time Per Batch");
    if (!String(onFillTarget || "").trim()) onMissing.push("Fill Target L");
    if (!String(onTimeOn || "").trim()) onMissing.push("Time On");
    if (!String(onAmbient || "").trim()) onMissing.push("Ambient Temp");
    if (!String(onStartTemp || "").trim()) onMissing.push("Starting Temp");
    if (!String(onTargetTemp || "").trim()) onMissing.push("Target Temp");
    if (onMissing.length) {
      toast("Missing: " + onMissing.join(", "), "var(--danger)");
      return;
    }
    setSyncing(true);
    api.post("saveBrewOn", {
      data: {
        date: onBrewDate,
        product: onProduct,
        totalKg: onTotalKg,
        timePerBatch: onTimePerDose,
        grindSize: onGrindSize,
        roastDate: onRoastDate,
        grindBatches: onDoses.filter(d => d).join(", "),
        grindStart: onGrindStart,
        grindFinish: onGrindFinish,
        brightTank: onTank,
        fillTargetL: onFillTarget,
        timeOn: onTimeOn,
        targetTimeOff: onTargetOff,
        ambientTemp: onAmbient,
        startingTemp: onStartTemp,
        targetTemp: onTargetTemp,
        particle0_400: onParticle[0],
        particle400_800: onParticle[1],
        particle800: onParticle[2],
        photoFlowMeter: onPhotoFlow ? "Yes" : "",
        photoFilledTank: onPhotoTank ? "Yes" : "",
        finishChecklist: onChecklist.every(function(c){return c;}) ? "Complete" : onChecklist.filter(function(c){return c;}).length + "/8",
        loggedBy: user.name
      }
    }).then(res => {
      setSyncing(false);
      if (res.error) {
        toast(res.error, "var(--danger)");
        return;
      }
      toast("Brew On saved — " + (res.brewId || ""), "var(--success)");
      setOnInProgress(false);
      setSub(null);
      loadData();
    }).catch(err => {
      setSyncing(false);
      toast("Error: " + err.message, "var(--danger)");
    });
  }
  function saveBrewOff() {
    const ab = activeBrew();
    if (!ab) {
      toast("No active brew", "var(--danger)");
      return;
    }
    // Required-field check — same pattern as Brew On.
    var offMissing = [];
    if (!String(offFL || "").trim()) offMissing.push("Filtered Litres");
    if (offConc.filter(function(v) { return String(v || "").trim(); }).length === 0) offMissing.push("Concentrate TDS");
    if (offNeedsDil() && offDil.filter(function(v) { return String(v || "").trim(); }).length === 0) offMissing.push("Diluted TDS");
    if (!String(offLtS || "").trim()) offMissing.push("LT End Temp Start");
    if (!String(offBtS || "").trim()) offMissing.push("BT End Temp Start");
    if (!String(offTag || "").trim()) offMissing.push("Keg Tag");
    if (!String(offBB || "").trim()) offMissing.push("Best Before");
    if (!String(offFlavour || "").trim()) offMissing.push("Flavour Notes");
    if (offMissing.length) {
      toast("Missing: " + offMissing.join(", "), "var(--danger)");
      return;
    }
    setSyncing(true);
    const cA = calcAvg(offConc),
      dA = offNeedsDil() ? calcAvg(offDil) : null,
      ft = offFT();
    // Target TDS comes off the recipe for this brew's product. Backend col AM has
    // always been wired to d.targetTds — the payload just never carried it.
    var _offRcpT = recipes.find(function(r) { return r.product === ab.product; });
    api.post("saveBrewOff", {
      data: {
        brewId: ab.brewId,
        targetTds: _offRcpT && _offRcpT.targetTds ? String(_offRcpT.targetTds) : "",
        ltEndTempStart: offLtS,
        ltEndTempLow: offLtL,
        btEndTempStart: offBtS,
        btEndTempLow: offBtL,
        filterStart: offFS,
        filterFinish: offFF,
        pumpSpeedStart: offPS,
        pumpSpeedEnd: offPE,
        filteredLitres: offFL,
        dilutionFinalL: offNeedsDil() ? ft : offFL,
        tdsConcentrate: offConc.filter(v => v).join(", "),
        tdsConcAvg: cA ? cA.toFixed(2) : "",
        tdsDiluted: offNeedsDil() ? offDil.filter(v => v).join(", ") : "",
        tdsDilAvg: dA ? dA.toFixed(2) : "",
        totalLitres: offNeedsDil() ? ft : offFL,
        dilutionStartL: offFL,
        dilutionAddL: offNeedsDil() ? Math.max(0, ft - (parseFloat(offFL) || 0)) : 0,
        nagardoG: ((offNeedsDil() ? ft : (parseFloat(offFL) || 0)) * (parseFloat(offNagardoRate) || 0)).toFixed(1),
        sorbateG: ((offNeedsDil() ? ft : (parseFloat(offFL) || 0)) * (parseFloat(offSorbateRate) || 0)).toFixed(1),
        kegTag: offTag,
        bestBefore: offBB,
        flavourNotes: offFlavour,
        loggedBy: user.name
      }
    }).then(res => {
      setSyncing(false);
      if (res.error) {
        toast(res.error, "var(--danger)");
        return;
      }
      toast("Brew Off saved", "var(--success)");
      setOffInProgress(false);
      setSub(null);
      loadData();
    }).catch(err => {
      setSyncing(false);
      toast("Error: " + err.message, "var(--danger)");
    });
  }


  function persistFillSession(activeOverride) {
    if (!fkTank) return;
    saveFillSession({
      tankId: fkTank.tankId,
      tankSnapshot: fkTank,
      wasActive: (typeof activeOverride === "boolean") ? activeOverride : (sub === "fillKegs" || sub === "fillKegsLimited"),
      fk18: fk18,
      fk9: fk9,
      fk7: fk7,
      fkScanned: fkScanned,
      fkChecks: fkChecks,
      fkPhase: fkPhase,
      fkLimited: fkLimited,
      fkLtd18: fkLtd18,
      fkLtd9: fkLtd9,
      fkLtdScanned: fkLtdScanned,
      fkLtdChecks: fkLtdChecks,
      fkRecipeConfirmed: fkRecipeConfirmed,
      fkLtdTag: fkLtdTag,
      fkPhotoCalcs: fkPhotoCalcs,
      fkLtdPhotoCalcs: fkLtdPhotoCalcs,
      fkRemainOverride: fkRemainOverride,
      fkLastKegPartial: fkLastKegPartial,
      fkLastKegScannedId: fkLastKegScannedId,
      user: user ? user.name : "",
    });
  }

  // Save & Close: persist current session, arm resume banner, exit to kegs screen.
  // The autosave already runs on state changes — this is a defensive explicit save + exit.
  function saveAndCloseFill() {
    if (!fkTank) { setSub(null); return; }
    persistFillSession(false);
    // Re-load what we just wrote so the banner has the freshest data
    var saved = loadFillSession();
    if (saved && saved.tankId) setFkResumeSession(saved);
    setSub(null);
    setScreen("kegs");
    toast("Fill session saved \u2014 resume from banner", "var(--success)");
  }

  function resumeFillSession(saved) {
    // Prefer the live tank, but fall back to the stored snapshot so resume works
    // even when the network hasn't loaded (or has failed) on reopen.
    var ts = tankState.find(function(t) { return t.tankId === saved.tankId; }) || saved.tankSnapshot || null;
    if (!ts) { clearFillSession(); setFkResumeSession(null); toast("Tank no longer available", "var(--danger)"); return; }
    setFkTank(ts);
    setFk18(saved.fk18 || "");
    setFk9(saved.fk9 || "");
    setFk7(saved.fk7 || "");
    setFkScanned(saved.fkScanned || []);
    setFkChecks(saved.fkChecks || [false,false,false,false,false]);
    setFkPhase(saved.fkPhase || "setup");
    setFkLimited(saved.fkLimited || null);
    setFkLtd18(saved.fkLtd18 || "");
    setFkLtd9(saved.fkLtd9 || "");
    setFkLtdScanned(saved.fkLtdScanned || []);
    setFkLtdChecks(saved.fkLtdChecks || [false,false,false,false,false]);
    setFkRecipeConfirmed(saved.fkRecipeConfirmed || false);
    setFkLtdTag(saved.fkLtdTag || "");
    setFkPhotoCalcs(saved.fkPhotoCalcs || false);
    setFkLtdPhotoCalcs(saved.fkLtdPhotoCalcs || false);
    setFkRemainOverride(saved.fkRemainOverride || "");
    setFkLastKegPartial(saved.fkLastKegPartial || null);
    setFkLastKegScannedId(saved.fkLastKegScannedId || "");
    setFkResumeSession(null);
    setScreen("kegs");
    setSub("fillKegs");
    toast("Fill session restored", "var(--success)");
  }
  function dismissFillSession() {
    // Discard = wipe the fill completely. Clear BOTH the saved session and the
    // in-memory state, otherwise re-entering via "Fill Kegs" (which reuses
    // fkTank/fkScanned when present) brings the discarded kegs straight back.
    clearFillSession();
    setFkResumeSession(null);
    setFkTank(null); setFkScanned([]); setFk18(""); setFk9(""); setFk7("");
    setFkChecks([false, false, false, false, false]);
    setFkLimited(null); setFkLtd18(""); setFkLtd9("");
    setFkLtdScanned([]); setFkLtdChecks([false, false, false, false, false]);
    setFkRecipeConfirmed(false); setFkLtdTag(""); setFkPhase("setup");
    setFkRemainOverride(""); setFkLastKegPartial(null); setFkLastKegScannedId("");
    setFkPhotoCalcs(false); setFkLtdPhotoCalcs(false);
  }

  // Save progress overlay helpers — drive a timed sequence of status messages while a background save runs.
  // Steps display sequentially (each shown for ~stepMs ms) so the user sees forward motion even though we
  // don't get real progress events from the backend. On success we fast-forward to "done"; on error we close.
  function startSaveProgress(steps, stepMs) {
    var ms = stepMs || 2200;
    var initial = steps.map(function(label, i) { return { label: label, status: i === 0 ? "active" : "pending" }; });
    setSaveProgress({ steps: initial, done: false, error: null });
    var timers = [];
    for (var i = 1; i < steps.length; i++) {
      (function(idx) {
        timers.push(setTimeout(function() {
          setSaveProgress(function(prev) {
            if (!prev || prev.done || prev.error) return prev;
            var next = prev.steps.map(function(s, j) {
              if (j < idx) return { label: s.label, status: "done" };
              if (j === idx) return { label: s.label, status: "active" };
              return s;
            });
            return Object.assign({}, prev, { steps: next });
          });
        }, idx * ms));
      })(i);
    }
    return {
      finish: function(opts) {
        // Mark all steps done, show "Done" briefly, then dismiss
        timers.forEach(function(t) { clearTimeout(t); });
        setSaveProgress(function(prev) {
          if (!prev) return null;
          var allDone = prev.steps.map(function(s) { return { label: s.label, status: "done" }; });
          return { steps: allDone, done: true, error: null, doneMessage: (opts && opts.message) || "Done" };
        });
        setTimeout(function() { setSaveProgress(null); }, 1500);
      },
      fail: function(msg) {
        timers.forEach(function(t) { clearTimeout(t); });
        setSaveProgress(function(prev) {
          if (!prev) return null;
          return Object.assign({}, prev, { error: msg || "Sync error" });
        });
        setTimeout(function() { setSaveProgress(null); }, 3500);
      }
    };
  }

  function saveFillKegs() {
    // Allow save if either regular kegs OR a last-keg partial is set
    if (!fkTank || fkSaving) return;
    if (fkScanned.length === 0 && !fkLastKegScannedId) return;
    var _isIE = fkTank.product === "Iced Espresso";
    var n18v = parseInt(fk18) || 0;
    var n9v = parseInt(fk9) || 0;
    var n7v = _isIE ? (parseInt(fk7) || 0) : 0;
    var totalKegs = n18v + n9v + n7v;
    var requestId = "fill-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8);
    // Build kegs array — regulars from fkScanned, partial (if any) from fkLastKegScannedId
    var kegsArr = [];
    var hasRealIds = fkScanned.length > 0 && fkScanned[0] !== "confirmed";
    if (hasRealIds) {
      fkScanned.forEach(function(kid) { kegsArr.push({ kegId: kid, size: parseSizeFromId(kid) }); });
    } else if (totalKegs > 0) {
      // Manual placeholder mode (no real IDs) — emit one keg per slot. The partial slot
      // (if set) is filled with a placeholder marked for staff routing.
      var partialSize = fkLastKegPartial ? fkLastKegPartial.size : null;
      var n18ToFill = n18v - (partialSize === "18L" ? 1 : 0);
      var n9ToFill = n9v - (partialSize === "9L" ? 1 : 0);
      var n7ToFill = n7v - (partialSize === "7L" ? 1 : 0);
      for (var ki = 0; ki < n18ToFill; ki++) kegsArr.push({ kegId: "MANUAL-18-" + String(ki + 1).padStart(3, "0"), size: "18L" });
      for (var kj = 0; kj < n9ToFill; kj++) kegsArr.push({ kegId: "MANUAL-09-" + String(kj + 1).padStart(3, "0"), size: "9L" });
      for (var kk = 0; kk < n7ToFill; kk++) kegsArr.push({ kegId: "MANUAL-07-" + String(kk + 1).padStart(3, "0"), size: "7L" });
    }
    // Append the partial last keg (if set), tagged for Staff/Market fridge routing.
    if (fkLastKegPartial && fkLastKegPartial.size && fkLastKegPartial.litres > 0) {
      var pSize = fkLastKegPartial.size;
      var pCode = pSize === "18L" ? "18" : pSize === "9L" ? "09" : "07";
      var pId = fkLastKegScannedId || ("MANUAL-" + pCode + "-LAST");
      kegsArr.push({
        kegId: pId,
        size: pSize,
        destination: "staff",
        lastKegLitres: fkLastKegPartial.litres
      });
    }
    // Optimistic: immediately show success + lock button
    setFkSaving(true);
    // Build progress overlay steps based on what's being saved
    var fridgeCount = kegsArr.filter(function(k) { return k.destination !== "staff"; }).length;
    var staffCount = kegsArr.filter(function(k) { return k.destination === "staff"; }).length;
    var stepLabels = [
      "Writing to Keg Filling log",
      fridgeCount > 0 ? ("Adding " + fridgeCount + " keg" + (fridgeCount !== 1 ? "s" : "") + " to fridge") : null,
      staffCount > 0 ? ("Routing " + staffCount + " partial keg to Staff/Market") : null,
      "Updating Keg Register",
      fkLimited ? "Preparing " + fkLimited + " phase" : "Cleaning tank " + fkTank.tankId,
      "Refreshing fleet counts"
    ].filter(function(s) { return !!s; });
    var progress = startSaveProgress(stepLabels, 2200);

    if (fkLimited) {
      setFkPhase("limited");
      setFkLtdPhotoCalcs(false);
    } else {
      // Navigate to kegs screen immediately
      setSub(null);
      setScreen("kegs");
    }
    // Background save to spreadsheet. Record a "pending fill" receipt FIRST so
    // that if the iPad is closed before the server confirms, we re-send it on
    // next open. The backend is idempotent on requestId, so re-sending can never
    // create a duplicate — it only confirms or completes the save.
    var fillPayload = {
      brightTank: fkTank.tankId,
      product: opsProduct(fkTank.product),
      brewId: fkTank.brewId || "",
      kegTag: fkTank.kegTag || "",
      bestBefore: fkTank.bestBefore || "",
      kegs: kegsArr,
      loggedBy: user.name,
      requestId: requestId
    };
    savePendingFill({ action: "fillKegs", data: fillPayload });
    api.post("fillKegs", { data: fillPayload }).then(res => {
      setFkSaving(false);
      if (res.error) { progress.fail(res.error); toast("Sync error: " + res.error, "var(--danger)"); return; }
      clearPendingFill();
      progress.finish({ message: fkLimited ? "Saved — continuing to " + fkLimited : (totalKegs + " kegs saved") });
      if (!fkLimited) {
        setSub(null);
        setFkTank(null); setFkScanned([]); setFk18(""); setFk9(""); setFk7(""); setFkRemainOverride(""); setFkLastKegPartial(null); setFkLastKegScannedId("");
        setFkChecks([false, false, false, false, false]);
        setFkPhotoCalcs(false);
        clearFillSession();
        loadData();
      } else {
        loadData();
      }
    }).catch(err => {
      setFkSaving(false);
      progress.fail(err.message || "Network error");
      toast("Sync error: " + err.message + " — data may not have saved, check spreadsheet", "var(--danger)");
    });
  }
  function saveFillKegsLimited() {
    if (!fkTank || fkSaving) return;
    if (fkLtdScanned.length === 0 && !fkLastKegScannedId) return;
    var ltdN18v = parseInt(fkLtd18) || 0;
    var ltdN9v = parseInt(fkLtd9) || 0;
    var ltdTotalKegs = ltdN18v + ltdN9v;
    var requestId = "fill-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8);
    var ltdKegsArr = [];
    var hasLtdRealIds = fkLtdScanned.length > 0 && fkLtdScanned[0] !== "confirmed";
    if (hasLtdRealIds) {
      fkLtdScanned.forEach(function(kid) { ltdKegsArr.push({ kegId: kid, size: parseSizeFromId(kid) }); });
    } else if (ltdTotalKegs > 0) {
      var partialSize2 = fkLastKegPartial ? fkLastKegPartial.size : null;
      var n18ToFill2 = ltdN18v - (partialSize2 === "18L" ? 1 : 0);
      var n9ToFill2 = ltdN9v - (partialSize2 === "9L" ? 1 : 0);
      for (var ki2 = 0; ki2 < n18ToFill2; ki2++) ltdKegsArr.push({ kegId: "MANUAL-18-" + String(ki2 + 1).padStart(3, "0"), size: "18L" });
      for (var kj2 = 0; kj2 < n9ToFill2; kj2++) ltdKegsArr.push({ kegId: "MANUAL-09-" + String(kj2 + 1).padStart(3, "0"), size: "9L" });
    }
    // Append the partial last keg (if set), tagged for Staff/Market fridge
    if (fkLastKegPartial && fkLastKegPartial.size && fkLastKegPartial.litres > 0) {
      var pSize2 = fkLastKegPartial.size;
      var pCode2 = pSize2 === "18L" ? "18" : "09";
      var pId2 = fkLastKegScannedId || ("MANUAL-" + pCode2 + "-LAST");
      ltdKegsArr.push({
        kegId: pId2,
        size: pSize2,
        destination: "staff",
        lastKegLitres: fkLastKegPartial.litres
      });
    }
    // Optimistic: immediately show success + lock button
    setFkSaving(true);
    var ltdFridgeCount = ltdKegsArr.filter(function(k) { return k.destination !== "staff"; }).length;
    var ltdStaffCount = ltdKegsArr.filter(function(k) { return k.destination === "staff"; }).length;
    var ltdStepLabels = [
      "Writing to Keg Filling log",
      ltdFridgeCount > 0 ? ("Adding " + ltdFridgeCount + " keg" + (ltdFridgeCount !== 1 ? "s" : "") + " to fridge") : null,
      ltdStaffCount > 0 ? ("Routing " + ltdStaffCount + " partial keg to Staff/Market") : null,
      "Updating Keg Register",
      "Cleaning tank " + fkTank.tankId,
      "Refreshing fleet counts"
    ].filter(function(s) { return !!s; });
    var ltdProgress = startSaveProgress(ltdStepLabels, 2200);
    // Navigate to kegs screen immediately
    setSub(null);
    setScreen("kegs");
    // Background save to spreadsheet (with pending-fill receipt — see saveFillKegs).
    var ltdFillPayload = {
      brightTank: fkTank.tankId,
      product: opsProduct(fkLimited),
      brewId: fkTank.brewId || "",
      kegTag: fkLtdTag || fkTank.kegTag || "",
      bestBefore: fkTank.bestBefore || "",
      kegs: ltdKegsArr,
      loggedBy: user.name,
      requestId: requestId
    };
    savePendingFill({ action: "fillKegs", data: ltdFillPayload });
    api.post("fillKegs", { data: ltdFillPayload }).then(res => {
      setFkSaving(false);
      if (res.error) { ltdProgress.fail(res.error); toast("Sync error: " + res.error, "var(--danger)"); return; }
      clearPendingFill();
      ltdProgress.finish({ message: ltdTotalKegs + " " + fkLimited + " kegs saved" });
      setSub(null);
      setFkTank(null); setFkScanned([]); setFk18(""); setFk9(""); setFk7(""); setFkRemainOverride(""); setFkLastKegPartial(null); setFkLastKegScannedId("");
      setFkChecks([false, false, false, false, false]);
      setFkLimited(null); setFkLtd18(""); setFkLtd9("");
      setFkLtdScanned([]); setFkLtdChecks([false, false, false, false, false]);
      setFkRecipeConfirmed(false); setFkLtdTag(""); setFkPhase("setup");
      setFkPhotoCalcs(false); setFkLtdPhotoCalcs(false);
      clearFillSession();
      loadData();
    }).catch(err => {
      setFkSaving(false);
      ltdProgress.fail(err.message || "Network error");
      toast("Sync error: " + err.message + " — data may not have saved, check spreadsheet", "var(--danger)");
    });
  }

  function cancelFillFromLive(tankId) {
    setCancelFillSaving(true);
    api.post("cancelFill", { data: { tankId: tankId } }).then(function(res) {
      setCancelFillSaving(false);
      setCancelFillTank(null);
      if (res.error) { toast("Error: " + res.error, "var(--danger)"); return; }
      toast(tankId + " reset to " + (res.newStatus || "Ready"), "var(--success)");
      clearFillSession();
      // Reset fill state if this was the active tank
      if (fkTank && fkTank.tankId === tankId) {
        setFkTank(null); setFkScanned([]); setFk18(""); setFk9(""); setFk7(""); setFkRemainOverride(""); setFkLastKegPartial(null); setFkLastKegScannedId("");
        setFkChecks([false,false,false,false,false]); setFkPhase("setup");
        setFkLimited(null); setFkLtd18(""); setFkLtd9("");
        setFkLtdScanned([]); setFkLtdChecks([false,false,false,false,false]);
        setFkRecipeConfirmed(false); setFkLtdTag("");
        setFkPhotoCalcs(false); setFkLtdPhotoCalcs(false);
        setSub(null);
      }
      loadData();
    }).catch(function(err) {
      setCancelFillSaving(false);
      toast("Error: " + err.message, "var(--danger)");
    });
  }

  // Empty a bright tank from the Live view. Drains the full remaining volume,
  // which the backend logs to Stock Log (Direction = "Drain") and then flips the
  // tank to Empty, clearing product / brew ID / keg tag / BB / TDS.
  function emptyTankFromLive() {
    if (!emptyTank || !emptyReason.trim()) return;
    var litres = Number(emptyTank.litres) || 0;
    if (litres <= 0) return;
    var tankId = emptyTank.id;
    setEmptySaving(true);
    api.post("drainTank", {
      data: {
        tankId: tankId,
        litres: litres,
        product: opsProduct(emptyTank.product),
        brewId: emptyTank.brewId || "",
        reason: emptyReason.trim(),
        loggedBy: user.name
      }
    }).then(function(res) {
      setEmptySaving(false);
      if (res.error) { toast("Error: " + res.error, "var(--danger)"); return; }
      setEmptyTank(null);
      setEmptyReason("");
      toast(tankId + " emptied — " + litres + "L drained", "var(--success)");
      // The tank's contents are gone, so any fill session against it is void.
      if (fkTank && fkTank.tankId === tankId) {
        clearFillSession();
        setFkResumeSession(null);
        setFkTank(null); setFkScanned([]); setFk18(""); setFk9(""); setFk7(""); setFkRemainOverride(""); setFkLastKegPartial(null); setFkLastKegScannedId("");
        setFkChecks([false,false,false,false,false]); setFkPhase("setup");
        setFkLimited(null); setFkLtd18(""); setFkLtd9("");
        setFkLtdScanned([]); setFkLtdChecks([false,false,false,false,false]);
        setFkRecipeConfirmed(false); setFkLtdTag("");
        setFkPhotoCalcs(false); setFkLtdPhotoCalcs(false);
        setSub(null);
      }
      loadData();
    }).catch(function(err) {
      setEmptySaving(false);
      toast("Error: " + err.message, "var(--danger)");
    });
  }

  function drainTankRemaining(drainL) {
    if (!fkTank || !fkDrainReason.trim() || drainL <= 0) return;
    setSyncing(true);
    api.post("drainTank", {
      data: {
        tankId: fkTank.tankId,
        litres: drainL,
        product: opsProduct(fkTank.product),
        brewId: fkTank.brewId || "",
        reason: fkDrainReason.trim(),
        loggedBy: user.name
      }
    }).then(function(res) {
      setSyncing(false);
      if (res.error) { toast(res.error, "var(--danger)"); return; }
      toast("Drained " + drainL + "L from " + fkTank.tankId, "var(--success)");
      setFkDrainOpen(false);
      setFkDrainReason("");
      loadData();
    }).catch(function(err) {
      setSyncing(false);
      toast("Error: " + err.message, "var(--danger)");
    });
  }
  function washKegsOptimistic(n18v, n9v) {
    // Optimistic: move kegs from "To Be Washed" → "Clean" in local state
    var moved18 = 0, moved9 = 0;
    setKegRegister(function(prev) {
      return prev.map(function(k) {
        if (k.status !== "To Be Washed") return k;
        var is18 = String(k.size).indexOf("18") > -1;
        if (is18 && moved18 < n18v) { moved18++; return Object.assign({}, k, { status: "Clean", product: "", kegTag: "", bestBefore: "", lastMovement: new Date().toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }), lastMovedBy: user.name, notes: "Washed" }); }
        if (!is18 && moved9 < n9v) { moved9++; return Object.assign({}, k, { status: "Clean", product: "", kegTag: "", bestBefore: "", lastMovement: new Date().toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }), lastMovedBy: user.name, notes: "Washed" }); }
        return k;
      });
    });
    setKegFleetCounts(function(prev) {
      var u = JSON.parse(JSON.stringify(prev));
      u.toBeWashed.n18 = Math.max(0, u.toBeWashed.n18 - n18v);
      u.toBeWashed.n9 = Math.max(0, u.toBeWashed.n9 - n9v);
      u.clean.n18 = (u.clean.n18 || 0) + n18v;
      u.clean.n9 = (u.clean.n9 || 0) + n9v;
      return u;
    });
  }
  function saveWash() {
    const n18 = parseInt(w18) || 0,
      n9 = parseInt(w9) || 0;
    if (n18 + n9 === 0) return;
    // Optimistic UI
    washKegsOptimistic(n18, n9);
    toast("Washed " + (n18 + n9) + " kegs", "var(--success)");
    setSub(null);
    setWashChecks([false, false]);
    setW18("");
    setW9("");
    // Background sync
    setSyncing(true);
    api.post("washKegsByCount", {
      data: {
        count18: n18,
        count9: n9,
        loggedBy: user.name
      }
    }).then(res => {
      setSyncing(false);
      if (res.error) { toast("Sync warning: " + res.error, "var(--danger)"); }
      loadData();
    }).catch(err => {
      setSyncing(false);
      toast("Sync error: " + err.message, "var(--danger)");
    });
  }
  function saveWashAll() {
    var n18 = kegFleetCounts.toBeWashed.n18 || 0;
    var n9 = kegFleetCounts.toBeWashed.n9 || 0;
    if (n18 + n9 === 0) return;
    // Optimistic UI
    washKegsOptimistic(n18, n9);
    toast("Washed " + (n18 + n9) + " kegs", "var(--success)");
    // Background sync
    setSyncing(true);
    api.post("washKegsByCount", {
      data: {
        count18: n18,
        count9: n9,
        loggedBy: user.name
      }
    }).then(function(res) {
      setSyncing(false);
      if (res.error) { toast("Sync warning: " + res.error, "var(--danger)"); }
      loadData();
    }).catch(function(err) {
      setSyncing(false);
      toast("Sync error: " + err.message, "var(--danger)");
    });
  }
  function saveAddDirty() {
    setSyncing(true);
    api.post("addDirtyKegs", {
      data: {
        n18: addDirtySize === "18L" ? 1 : 0,
        n9: addDirtySize === "9L" ? 1 : 0
      }
    }).then(function(res) {
      setSyncing(false);
      if (res.error) { toast(res.error, "var(--danger)"); return; }
      toast("Added 1 × " + addDirtySize + " to wash", "var(--success)");
      setAddDirtyOpen(false);
      loadData();
    }).catch(function(err) {
      setSyncing(false);
      toast("Error: " + err.message, "var(--danger)");
    });
  }

  function saveAddToStaffFridge() {
    setSyncing(true);
    api.post("addToStaffFridge", {
      data: {
        n18: staffFridgeSize === "18L" ? 1 : 0,
        n9: staffFridgeSize === "9L" ? 1 : 0,
        source: staffFridgeSource,
        product: staffFridgeSource === "stock" ? staffFridgeProduct : ""
      }
    }).then(function(res) {
      setSyncing(false);
      if (res.error) { toast(res.error, "var(--danger)"); return; }
      toast("Added 1 × " + staffFridgeSize + " to staff/market fridge" + (staffFridgeSource === "stock" ? " (from stock)" : " (from tank)"), "var(--success)");
      setStaffFridgeOpen(false);
      loadData();
    }).catch(function(err) {
      setSyncing(false);
      toast("Error: " + err.message, "var(--danger)");
    });
  }

  function saveRemoveFromStaffFridge() {
    setSyncing(true);
    api.post("removeFromStaffFridge", {
      data: {
        n18: staffRemoveSize === "18L" ? 1 : 0,
        n9: staffRemoveSize === "9L" ? 1 : 0
      }
    }).then(function(res) {
      setSyncing(false);
      if (res.error) { toast(res.error, "var(--danger)"); return; }
      toast("Moved 1 × " + staffRemoveSize + " to wash", "var(--success)");
      setStaffRemoveOpen(false);
      loadData();
    }).catch(function(err) {
      setSyncing(false);
      toast("Error: " + err.message, "var(--danger)");
    });
  }

  // ── MOVE KEG (scan-based) ──
  function mvLookupKeg() {
    var kegId = "KEG-" + mvSize + "-" + mvDigits.padStart(3, "0");
    var keg = kegLookup(kegId);
    if (!keg) { toast("Not found: " + kegId, "var(--danger)"); return; }
    setMvKeg(keg);
    setMvDest(null);
    setMvNotes("");
  }
  function saveMoveKeg() {
    if (!mvKeg || !mvDest) return;
    var destNames = { wash: "To Be Washed", clean: "Clean", fridge: "In Fridge", staff: "Staff / Market" };
    var statusKeys = { "To Be Washed": "toBeWashed", "Clean": "clean", "In Fridge": "inFridge", "Staff/Market": "staffMarket", "Staff / Market": "staffMarket", "At Customer": "atCustomer" };
    var destToStatus = { wash: "To Be Washed", clean: "Clean", fridge: "In Fridge", staff: "Staff/Market" };
    var newStatus = destToStatus[mvDest];
    var oldStatus = mvKeg.status;
    var is18 = mvKeg.size === "18L";
    var capturedKeg = Object.assign({}, mvKeg);
    var capturedDest = mvDest;
    var capturedNotes = mvNotes;

    // ── Optimistic: instant UI update ──
    toast(mvKeg.kegId + " \u2192 " + (destNames[mvDest] || mvDest), "var(--success)");
    setMvLog(function(prev) { return [{ kegId: capturedKeg.kegId, size: capturedKeg.size, from: oldStatus, to: destNames[capturedDest] || capturedDest, product: capturedKeg.product, notes: capturedNotes }].concat(prev); });

    // Update local keg register immediately
    setKegRegister(function(prev) {
      return prev.map(function(k) {
        if (k.kegId === capturedKeg.kegId) {
          var clearProduct = (capturedDest === "wash" || capturedDest === "clean");
          return Object.assign({}, k, {
            status: newStatus,
            location: "Brewery",
            product: clearProduct ? "" : k.product,
            lastMovement: new Date().toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }),
            lastMovedBy: user.name,
            notes: capturedNotes || ("Moved from " + oldStatus + " to " + newStatus)
          });
        }
        return k;
      });
    });

    // Update local fleet counts immediately
    setKegFleetCounts(function(prev) {
      var updated = JSON.parse(JSON.stringify(prev));
      var oldKey = statusKeys[oldStatus];
      var newKey = statusKeys[newStatus];
      var sizeKey = is18 ? "n18" : "n9";
      if (oldKey && updated[oldKey]) updated[oldKey][sizeKey] = Math.max(0, (updated[oldKey][sizeKey] || 0) - 1);
      if (newKey && updated[newKey]) updated[newKey][sizeKey] = (updated[newKey][sizeKey] || 0) + 1;
      return updated;
    });

    // Update local fridge stock if moving FROM fridge
    if (oldStatus === "In Fridge") {
      setFridgeStock(function(prev) {
        return prev.filter(function(f) { return String(f.kegId || "").toUpperCase() !== capturedKeg.kegId.toUpperCase(); });
      });
    }

    // Reset form immediately
    setMvKeg(null);
    setMvDest(null);
    setMvDigits("");
    setMvNotes("");

    // ── Background sync to server ──
    api.post("moveKeg", {
      data: {
        kegId: capturedKeg.kegId,
        destination: capturedDest,
        notes: capturedNotes,
        loggedBy: user.name
      }
    }).then(function(res) {
      if (res.error) { toast("Sync warning: " + res.error + " — check spreadsheet", "var(--danger)"); }
      // Quiet background refresh to reconcile with server
      loadData();
    }).catch(function(err) {
      toast("Sync error: " + err.message + " — keg may not have saved, check spreadsheet", "var(--danger)");
    });
  }

  // ── TRANSFER KEG (tab-based, source -> 1+ destinations) ──
  // Helper: look up the most recent fill record for a keg to estimate its volume
  function lookupSourceVolume(kegId) {
    if (!kegId) return null;
    var up = String(kegId).toUpperCase();
    for (var i = kegFilling.length - 1; i >= 0; i--) {
      var f = kegFilling[i];
      if (!f || !f.kegId) continue;
      if (String(f.kegId).toUpperCase() === up) {
        if (f.lastKegLitres !== "" && f.lastKegLitres !== undefined && f.lastKegLitres !== null) {
          var n = Number(f.lastKegLitres);
          if (!isNaN(n) && n > 0) return n;
        }
        if (f.size === "18L") return 18;
        if (f.size === "9L") return 9;
        if (f.size === "7L") return 7;
      }
    }
    return null;
  }

  // Initialise destination rows when a transfer "kind" is chosen
  function initTransferDests(kind) {
    if (!mvKeg) return;
    setMvTfProduct(null); // reset any product relabel each time a kind is chosen
    var srcVol = lookupSourceVolume(mvKeg.kegId);
    var srcNominal = mvKeg.size === "18L" ? 18 : (mvKeg.size === "9L" ? 9 : 0);
    var totalL = srcVol != null ? srcVol : srcNominal;

    if (kind === "split") {
      // Default to 2 destinations; if source is 18L split into 2x 9L; if 9L split into 2x 9L (half each)
      var splitSize = mvKeg.size === "18L" ? "9L" : "9L";
      var per = totalL / 2;
      // Round to 1dp
      per = Math.round(per * 10) / 10;
      setMvTfDests([
        { id: 1, sizeInput: "09", digitsInput: "", litres: String(per), size: splitSize, destination: "fridge" },
        { id: 2, sizeInput: "09", digitsInput: "", litres: String(per), size: splitSize, destination: "fridge" }
      ]);
      setMvTfResidual("wash"); // split default: original keg to wash
    } else if (kind === "transfer") {
      // Default to 1 destination of same size as source, prefilled with full source vol
      var destSize = mvKeg.size === "18L" ? "18" : "09";
      setMvTfDests([
        { id: 1, sizeInput: destSize, digitsInput: "", litres: String(totalL), size: mvKeg.size, destination: "fridge" }
      ]);
      setMvTfResidual("staff"); // transfer default: residual to staff/market
    }
  }

  function tfUpdateDest(rowId, field, value) {
    setMvTfDests(function(prev) {
      return prev.map(function(r) {
        if (r.id !== rowId) return r;
        var u = Object.assign({}, r);
        u[field] = value;
        if (field === "sizeInput") {
          u.size = value === "18" ? "18L" : "9L";
        }
        return u;
      });
    });
  }
  function tfAddDest() {
    setMvTfDests(function(prev) {
      var nextId = prev.reduce(function(m, r) { return Math.max(m, r.id); }, 0) + 1;
      // Default new destination = 9L, 9L litres, fridge stock
      return prev.concat([{ id: nextId, sizeInput: "09", digitsInput: "", litres: "9", size: "9L", destination: "fridge" }]);
    });
  }
  function tfRemoveDest(rowId) {
    setMvTfDests(function(prev) {
      if (prev.length <= 1) return prev; // keep at least one row
      return prev.filter(function(r) { return r.id !== rowId; });
    });
  }
  function tfScanDest(rowId) {
    bscOpen({
      mode: "move", expected: 1, label: "SCAN CLEAN DESTINATION KEG", color: "#7C3AED",
      onDone: function(ids) {
        if (ids.length === 0) return;
        var id = ids[0];
        var m = id.match(/^KEG-(\d+)-(\d+)$/i);
        if (m) {
          tfUpdateDest(rowId, "sizeInput", m[1]);
          tfUpdateDest(rowId, "digitsInput", m[2]);
        }
      }
    });
  }

  function saveTransferKeg() {
    if (!mvKeg) return;
    if (mvTfDests.length === 0) { toast("Add at least one destination keg", "var(--danger)"); return; }
    if (!mvTfResidual) { toast("Choose what happens to the source keg", "var(--danger)"); return; }

    // Validate each row + resolve each into a full keg record
    var resolved = [];
    var seen = {};
    for (var i = 0; i < mvTfDests.length; i++) {
      var r = mvTfDests[i];
      if (!r.digitsInput || r.digitsInput.length === 0) {
        toast("Destination " + (i + 1) + " needs a keg number", "var(--danger)"); return;
      }
      var destId = "KEG-" + r.sizeInput + "-" + r.digitsInput.padStart(3, "0");
      if (destId === mvKeg.kegId) { toast("Destination cannot be the source keg", "var(--danger)"); return; }
      if (seen[destId]) { toast(destId + " listed twice", "var(--danger)"); return; }
      seen[destId] = true;
      var litres = parseFloat(r.litres);
      if (isNaN(litres) || litres <= 0) { toast(destId + " needs litres > 0", "var(--danger)"); return; }
      var dKeg = kegLookup(destId);
      if (!dKeg) { toast("Not found: " + destId, "var(--danger)"); return; }
      if (dKeg.status !== "Clean") { toast(destId + " must be Clean (currently: " + dKeg.status + ")", "var(--danger)"); return; }
      var cap = dKeg.size === "18L" ? 18 : (dKeg.size === "9L" ? 9 : (dKeg.size === "7L" ? 7 : 0));
      if (cap > 0 && litres > cap + 0.5) { toast(destId + " is " + dKeg.size + " — can't hold " + litres + "L", "var(--danger)"); return; }
      resolved.push({ kegId: destId, size: dKeg.size, litres: litres, destination: r.destination || "fridge" });
    }

    var capturedSrc = Object.assign({}, mvKeg);
    var capturedDests = resolved;
    var capturedResidual = mvTfResidual;
    var capturedDestProduct = mvTfProduct || capturedSrc.product;
    var totalTransferred = capturedDests.reduce(function(s, d) { return s + d.litres; }, 0);
    var requestId = "transfer-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8);

    var srcNewStatus = capturedResidual === "wash" ? "To Be Washed"
                     : capturedResidual === "staff" ? "Staff/Market"
                     : capturedResidual === "stockResidual" ? "In Fridge"
                     : capturedSrc.status; // unreachable now, but safe fallback

    // ── Optimistic UI ──
    toast("Transferred " + totalTransferred + "L from " + capturedSrc.kegId + " to " + capturedDests.length + " keg" + (capturedDests.length !== 1 ? "s" : ""), "var(--success)");

    setMvLog(function(prev) {
      var entries = [];
      entries.push({
        kegId: capturedSrc.kegId, size: capturedSrc.size,
        from: capturedSrc.status, to: srcNewStatus,
        product: capturedResidual === "wash" ? "" : capturedSrc.product,
        notes: "Transferred " + totalTransferred + "L to " + capturedDests.map(function(d){ return d.kegId; }).join(", ")
      });
      capturedDests.forEach(function(d) {
        var destStatus = d.destination === "staff" ? "Staff/Market" : "In Fridge";
        entries.push({
          kegId: d.kegId, size: d.size,
          from: "Clean", to: destStatus,
          product: capturedDestProduct,
          notes: "Filled " + d.litres + "L from " + capturedSrc.kegId + (d.destination === "staff" ? " \u2192 Staff/Market" : "")
        });
      });
      return entries.concat(prev);
    });

    var todayStr = new Date().toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
    setKegRegister(function(prev) {
      return prev.map(function(k) {
        if (k.kegId === capturedSrc.kegId) {
          var clear = capturedResidual === "wash";
          return Object.assign({}, k, {
            status: srcNewStatus,
            location: "Brewery",
            product: clear ? "" : k.product,
            kegTag: clear ? "" : k.kegTag,
            bestBefore: clear ? "" : k.bestBefore,
            lastMovement: todayStr,
            lastMovedBy: user.name,
            notes: "Transferred " + totalTransferred + "L to " + capturedDests.map(function(d){ return d.kegId; }).join(", ")
          });
        }
        var match = capturedDests.find(function(d) { return d.kegId === k.kegId; });
        if (match) {
          var destStatus = match.destination === "staff" ? "Staff/Market" : "In Fridge";
          return Object.assign({}, k, {
            status: destStatus,
            location: "Brewery",
            product: capturedDestProduct,
            kegTag: capturedSrc.kegTag,
            bestBefore: capturedSrc.bestBefore,
            lastMovement: todayStr,
            lastMovedBy: user.name,
            notes: "Transferred from " + capturedSrc.kegId + " (" + match.litres + "L)" + (match.destination === "staff" ? " \u2192 Staff/Market" : "")
          });
        }
        return k;
      });
    });

    setKegFleetCounts(function(prev) {
      var u = JSON.parse(JSON.stringify(prev));
      var srcSizeKey = capturedSrc.size === "18L" ? "n18" : "n9";
      var statusKeys = { "To Be Washed": "toBeWashed", "Clean": "clean", "In Fridge": "inFridge", "Staff/Market": "staffMarket", "Staff / Market": "staffMarket" };
      var oldK = statusKeys[capturedSrc.status];
      var newK = statusKeys[srcNewStatus];
      if (oldK && u[oldK]) u[oldK][srcSizeKey] = Math.max(0, (u[oldK][srcSizeKey] || 0) - 1);
      if (newK && u[newK]) u[newK][srcSizeKey] = (u[newK][srcSizeKey] || 0) + 1;
      capturedDests.forEach(function(d) {
        var dSizeKey = d.size === "18L" ? "n18" : "n9";
        if (u.clean) u.clean[dSizeKey] = Math.max(0, (u.clean[dSizeKey] || 0) - 1);
        if (d.destination === "staff") {
          if (u.staffMarket) u.staffMarket[dSizeKey] = (u.staffMarket[dSizeKey] || 0) + 1;
        } else {
          if (u.inFridge) u.inFridge[dSizeKey] = (u.inFridge[dSizeKey] || 0) + 1;
        }
      });
      return u;
    });

    setFridgeStock(function(prev) {
      var next = prev.slice();
      // Remove source from fridge stock if it was there (we'll re-add below if stockResidual)
      if (capturedSrc.status === "In Fridge") {
        next = next.filter(function(f) { return String(f.kegId || "").toUpperCase() !== capturedSrc.kegId.toUpperCase(); });
      }
      // Add source back to fridge stock if outcome is stockResidual (regardless of where it was)
      if (capturedResidual === "stockResidual") {
        next.push({
          kegId: capturedSrc.kegId, size: capturedSrc.size, product: capturedSrc.product,
          brewId: "", kegTag: capturedSrc.kegTag, bestBefore: capturedSrc.bestBefore,
          dateFilled: todayStr, dateAdded: todayStr, status: "Available"
        });
      }
      // Add destinations to fridge stock (skip staff-routed ones)
      capturedDests.forEach(function(d) {
        if (d.destination === "staff") return;
        next.push({
          kegId: d.kegId, size: d.size, product: capturedDestProduct,
          brewId: "", kegTag: capturedSrc.kegTag, bestBefore: capturedSrc.bestBefore,
          dateFilled: todayStr, dateAdded: todayStr, status: "Available"
        });
      });
      return next;
    });

    // Reset form
    setMvKeg(null); setMvDest(null); setMvDigits(""); setMvNotes("");
    setMvTfKind(null); setMvTfDests([]); setMvTfResidual(null); setMvTfProduct(null);

    api.post("transferKeg", {
      data: {
        sourceKegId: capturedSrc.kegId,
        destinations: capturedDests.map(function(d) { return { kegId: d.kegId, litres: d.litres, destination: d.destination || "fridge" }; }),
        sourceOutcome: capturedResidual,
        destProduct: mvTfProduct || "",
        loggedBy: user.name,
        requestId: requestId
      }
    }).then(function(res) {
      if (res.error) { toast("Sync warning: " + res.error + " — check spreadsheet", "var(--danger)"); }
      loadData();
    }).catch(function(err) {
      toast("Sync error: " + err.message + " — check spreadsheet", "var(--danger)");
    });
  }
  // Stage a keg into the bulk list (validates context-aware: 'register' vs 'add').
  // mode = "register" → must exist as Unregistered. mode = "add" → must NOT exist.
  function stageKegForBulk(mode) {
    if (!addFleetDigits || addFleetDigits.length === 0) { toast("Enter a keg number", "var(--danger)"); return; }
    var kegId = "KEG-" + addFleetSize + "-" + addFleetDigits.padStart(3, "0");
    var size = addFleetSize + "L";

    if (addFleetStaged.find(function(k) { return k.kegId === kegId; })) {
      toast(kegId + " already in list", "var(--danger)"); return;
    }
    if (addFleetLog.find(function(k) { return k.kegId === kegId; })) {
      toast(kegId + " already added this session", "var(--danger)"); return;
    }

    var existing = kegLookup(kegId);
    if (mode === "register") {
      if (!existing) { toast(kegId + " not in register — use Add Keg", "var(--danger)"); return; }
      if (String(existing.status || "").trim().toLowerCase() !== "unregistered") {
        toast(kegId + " already registered (" + existing.status + ")", "var(--danger)"); return;
      }
    } else {
      // add mode — must NOT exist
      if (existing) { toast(kegId + " already exists", "var(--danger)"); return; }
    }

    setAddFleetStaged(function(prev) { return prev.concat([{ kegId: kegId, size: size }]); });
    setAddFleetDigits("");
  }

  // Remove a single staged entry by index
  function unstageKeg(idx) {
    setAddFleetStaged(function(prev) { return prev.filter(function(_, i) { return i !== idx; }); });
  }

  // Submit all staged kegs via the appropriate endpoint, sequentially.
  function submitStagedKegs(mode) {
    var list = addFleetStaged.slice();
    if (list.length === 0) { toast("No kegs in list", "var(--danger)"); return; }

    var endpoint = mode === "register" ? "registerKeg" : "addKegToFleet";
    setSyncing(true);
    var added = [];
    var errors = [];

    function next(idx) {
      if (idx >= list.length) {
        setSyncing(false);
        var verb = mode === "register" ? "registered" : "added";
        if (added.length > 0) {
          toast(added.length + " keg" + (added.length > 1 ? "s" : "") + " " + verb + (errors.length > 0 ? " (" + errors.length + " failed)" : ""), "var(--success)");
        } else {
          toast("All failed", "var(--danger)");
        }
        setAddFleetStaged([]);
        loadData();
        return;
      }
      var k = list[idx];
      var payload = mode === "register"
        ? { kegId: k.kegId, loggedBy: user.name }
        : { kegId: k.kegId, size: k.size, loggedBy: user.name };
      api.post(endpoint, { data: payload }).then(function(res) {
        if (res && res.error) {
          errors.push(k.kegId);
        } else {
          added.push(k);
          setAddFleetLog(function(prev) { return [k].concat(prev); });
        }
        next(idx + 1);
      }).catch(function() {
        errors.push(k.kegId);
        next(idx + 1);
      });
    }
    next(0);
  }

  // Product pill helper
  function ProductPill(props) {
    var p = props.name;
    var key = normProduct(p);
    var col = PC[key] || PC[p] || "#bbb";
    var label = PS[key] || PS[p] || p;
    return React.createElement("span", {
      style: { display: "inline-block", padding: "2px 10px", borderRadius: 100, background: col, color: "#fff", fontSize: props.size || 12, fontWeight: 700, letterSpacing: 0.3, whiteSpace: "nowrap" }
    }, label);
  }

  // ============================================================
  // HOME
  // ============================================================

  // ============================================================
  // SCHEDULE HELPERS
  // ============================================================
  const TASK_COLOURS = {
    "Brew Off": "#fa688e",
    "Brew On": "#17B892",
    "Keg Up": "var(--blue)",
    "Keg Clean": "#7B61FF",
    "Tank Clean": "#E67E22",
    "Deliveries": "#3498DB",
    "Maintenance": "#95A5A6",
    "Other": "#95A5A6"
  };
  const TASK_ICONS = {
    "Brew Off": "\u2B07",
    "Brew On": "\u2B06",
    "Keg Up": "\u25CF",
    "Keg Clean": "\u25CB",
    "Tank Clean": "\u25CB",
    "Deliveries": "\u25B6",
    "Maintenance": "\u2699",
    "Other": "\u2022"
  };

  function todayISO() {
    var d = new Date();
    var mm = d.getMonth() + 1;
    var dd = d.getDate();
    return d.getFullYear() + "-" + (mm < 10 ? "0" + mm : mm) + "-" + (dd < 10 ? "0" + dd : dd);
  }

  function getWeekDates(offset) {
    var now = new Date();
    var day = now.getDay(); // 0 = Sunday
    var sunday = new Date(now);
    sunday.setDate(now.getDate() - day + (offset * 7));
    var dates = [];
    for (var i = 0; i < 7; i++) {
      var d = new Date(sunday);
      d.setDate(sunday.getDate() + i);
      var mm = d.getMonth() + 1;
      var dd = d.getDate();
      dates.push(d.getFullYear() + "-" + (mm < 10 ? "0" + mm : mm) + "-" + (dd < 10 ? "0" + dd : dd));
    }
    return dates;
  }

  var DAYNAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  function formatSchedTime(t) {
    if (!t) return "";
    var s = String(t).trim();
    // Handle text values
    if (s === "COB" || s === "AM" || s === "PM" || s === "TBC") return s;
    // Extract hours and minutes from any format: "13:00", "13:00:00", "1:00 PM", etc
    var match = s.match(/^(\d{1,2}):(\d{2})/);
    if (match) {
      var h = parseInt(match[1], 10);
      var m = match[2];
      if (h === 0) return "12:" + m + " AM";
      if (h < 12) return h + ":" + m + " AM";
      if (h === 12) return "12:" + m + " PM";
      return (h - 12) + ":" + m + " PM";
    }
    return s;
  }

  function schedToday() {
    var iso = todayISO();
    return schedule.filter(function(s) { return s.date === iso; });
  }

  // Convert "5 May 2026" to "2026-05-05" for matching
  var MONTH_MAP = { Jan:"01", Feb:"02", Mar:"03", Apr:"04", May:"05", Jun:"06", Jul:"07", Aug:"08", Sep:"09", Oct:"10", Nov:"11", Dec:"12" };
  function dateToISO(dateStr) {
    if (!dateStr) return "";
    // Already ISO
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.substring(0, 10);
    // "5 May 2026" format
    var parts = dateStr.split(" ");
    if (parts.length === 3 && MONTH_MAP[parts[1]]) {
      var dd = parseInt(parts[0], 10);
      return parts[2] + "-" + MONTH_MAP[parts[1]] + "-" + (dd < 10 ? "0" + dd : dd);
    }
    return "";
  }

  function normaliseTank(t) {
    if (!t) return "";
    // "BBT1" -> "BBT01", "BBT01" -> "BBT01", " BBT1" -> "BBT01"
    var s = t.trim().toUpperCase();
    var m = s.match(/^BBT(\d+)$/);
    if (m) return "BBT" + (m[1].length === 1 ? "0" + m[1] : m[1]);
    return s;
  }

  function isSchedDone(item) {
    var iso = item.date;

    // Tank Clean uses local-only done state (visual, resets on refresh)
    if (item.task === "Tank Clean") {
      var key = item.date + "|" + (item.detail || "") + "|" + (item.who || "");
      return !!tankCleanDone[key];
    }

    // All other tasks: check spreadsheet Done status first
    if (item.status === "Done") return true;

    if (item.task === "Brew Off") {
      var detail = item.detail || "";
      return brewLog.some(function(b) {
        if (b.status !== "Ready") return false;
        if (dateToISO(b.filterDate) !== iso) return false;
        if (detail) {
          var dp = detail.split(":");
          var product = dp[0] ? dp[0].trim() : "";
          var tank = dp[1] ? dp[1].trim() : "";
          if (product && b.product !== product) return false;
          if (tank && normaliseTank(b.brightTank) !== normaliseTank(tank)) return false;
        }
        return true;
      });
    }
    if (item.task === "Brew On") {
      var detail = item.detail || "";
      return brewLog.some(function(b) {
        if (b.status !== "Brewing" && b.status !== "Ready") return false;
        if (dateToISO(b.date) !== iso) return false;
        if (detail) {
          var dp = detail.split(":");
          var product = dp[0] ? dp[0].trim() : "";
          var tank = dp[1] ? dp[1].trim() : "";
          if (product && b.product !== product) return false;
          if (tank && normaliseTank(b.brightTank) !== normaliseTank(tank)) return false;
        }
        return true;
      });
    }
    if (item.task === "Keg Up") {
      return kegFilling.some(function(f) { return dateToISO(f.date) === iso; });
    }
    if (item.task === "Keg Clean") {
      return washLog.some(function(w) { return dateToISO(w.date) === iso; });
    }
    if (item.task === "Deliveries") {
      // Auto-fade 1 hour after scheduled start
      if (iso !== todayISO()) return iso < todayISO();
      if (!item.time || item.time === "TBC") return false;
      var tp = item.time.match(/^(\d{1,2}):(\d{2})/);
      if (!tp) return false;
      var schedMinutes = parseInt(tp[1], 10) * 60 + parseInt(tp[2], 10);
      var now = new Date();
      var nowMinutes = now.getHours() * 60 + now.getMinutes();
      return nowMinutes >= schedMinutes + 60;
    }
    return false;
  }

  function markScheduleDone(item) {
    // Tank Clean done is visual-only (resets on refresh) — no API call
    var key = item.date + "|" + (item.detail || "") + "|" + (item.who || "");
    setTankCleanDone(function(prev) {
      var next = {};
      for (var k in prev) next[k] = prev[k];
      next[key] = true;
      return next;
    });
    toast(item.task + " marked done", "#17B892");
  }


  // ============================================================
  // SCHEDULE SCREEN
  // ============================================================
  function schedDetail(item) {
    // Suppress "Yes" as a detail (Aaron's checkbox format)
    var d = (item.detail || "").trim();
    if (d === "Yes" || d === "No" || d === "") return "";
    return d;
  }

  function renderSchedule() {
    var weekDates = getWeekDates(schedWeek);
    var weekLabel = schedWeek === 0 ? "This Week" : schedWeek === 1 ? "Next Week" : schedWeek === -1 ? "Last Week" : "Week " + schedWeek;
    var today = todayISO();

    return React.createElement("div", {
      className: "scr",
      style: { animation: "fu .2s ease" }
    },
    // Check for changes — pulls Aaron's planning sheet now
    React.createElement("button", {
      className: "btn btn-out",
      style: { width: "100%", marginBottom: 12, fontSize: 12, padding: "10px 0", opacity: schedChecking ? 0.6 : 1 },
      onClick: schedChecking ? null : syncScheduleNow
    }, schedChecking ? React.createElement(React.Fragment, null, React.createElement("span", { className: "spinner", style: { borderColor: "var(--rule)", borderTopColor: "var(--blue)" } }), " Checking Aaron\u2019s sheet\u2026") : "\u21bb Check for changes"),
    // Week switcher
    React.createElement("div", {
      style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }
    },
      React.createElement("button", {
        className: "btn-sm",
        style: { background: "var(--input-bg)", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, color: "var(--dim)", cursor: "pointer" },
        onClick: function() { setSchedWeek(schedWeek - 1); }
      }, "\u2039 Prev"),
      React.createElement("span", { style: { fontSize: 15, fontWeight: 700 } }, weekLabel),
      React.createElement("button", {
        className: "btn-sm",
        style: { background: "var(--input-bg)", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, color: "var(--dim)", cursor: "pointer" },
        onClick: function() { setSchedWeek(schedWeek + 1); }
      }, "Next \u203a")
    ),
    // Days
    weekDates.map(function(dateStr, dayIdx) {
      var dayItems = schedule.filter(function(s) { return s.date === dateStr; });
      var isToday = dateStr === today;
      var isPast = dateStr < today;
      var parts = dateStr.split("-");
      var dayNum = parseInt(parts[2], 10);
      var monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      var monthStr = monthNames[parseInt(parts[1], 10) - 1];
      var isWeekend = dayIdx >= 5;

      // Skip empty weekends
      if (isWeekend && dayItems.length === 0) return null;

      return React.createElement("div", {
        key: dateStr,
        style: {
          marginBottom: 14,
          border: isToday ? "2px solid var(--blue)" : "1px solid var(--rule)",
          borderRadius: 14,
          overflow: "hidden",
          opacity: isPast && !isToday ? 0.5 : 1
        }
      },
        // Day header bar
        React.createElement("div", {
          style: {
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 14px",
            background: isToday ? "var(--blue)" : "var(--input-bg)"
          }
        },
          React.createElement("span", {
            style: { fontSize: 13, fontWeight: 700, color: isToday ? "#fff" : "var(--dim)", letterSpacing: 1 }
          }, DAYNAMES[dayIdx]),
          React.createElement("span", {
            style: { fontSize: 18, fontWeight: 700, color: isToday ? "#fff" : "var(--text)" }
          }, dayNum),
          React.createElement("span", {
            style: { fontSize: 12, color: isToday ? "rgba(255,255,255,.7)" : "var(--ghost)" }
          }, monthStr),
          isToday && React.createElement("span", {
            style: { marginLeft: "auto", fontSize: 10, fontWeight: 700, color: "#fff", letterSpacing: 1.5, background: "rgba(255,255,255,.2)", padding: "2px 8px", borderRadius: 100 }
          }, "TODAY")
        ),
        // Day items
        dayItems.length === 0
          ? React.createElement("div", { style: { padding: "14px", fontSize: 13, color: "var(--ghost)" } }, "Nothing scheduled")
          : React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 0 } },
              dayItems.map(function(item, itemIdx) {
                // Day Note — render as a banner above tasks
                if (item.task === "Note") {
                  var lines = (item.detail || "").split("\n").filter(function(l) { return l.trim(); });
                  var firstLine = lines[0] || "";
                  var hasMore = lines.length > 1;
                  var expanded = noteExpanded[item.date];
                  return React.createElement("div", {
                    key: itemIdx,
                    onClick: hasMore ? function() {
                      var d = item.date;
                      var next = Object.assign({}, noteExpanded);
                      next[d] = !expanded;
                      setNoteExpanded(next);
                    } : undefined,
                    style: {
                      padding: "10px 14px",
                      background: "var(--warn-bg, #FFF8E1)",
                      borderLeft: "3px solid var(--warn, #F9A825)",
                      borderTop: itemIdx > 0 ? "1px solid var(--rule)" : "none",
                      fontSize: 12, color: "var(--dim)", lineHeight: 1.5,
                      cursor: hasMore ? "pointer" : "default"
                    }
                  },
                    expanded
                      ? React.createElement("div", { style: { display: "flex", gap: 8 } },
                          React.createElement("div", { style: { whiteSpace: "pre-line", flex: 1 } }, item.detail),
                          React.createElement("span", { style: { fontSize: 16, color: "var(--dim)", alignSelf: "flex-end", lineHeight: 1 } }, "▴")
                        )
                      : React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6 } },
                          React.createElement("span", { style: { flex: 1 } }, firstLine),
                          hasMore && React.createElement("span", { style: { fontSize: 16, color: "var(--dim)", whiteSpace: "nowrap", lineHeight: 1 } }, "▾")
                        )
                  );
                }

                var col = TASK_COLOURS[item.task] || "#95A5A6";
                var done = isSchedDone(item);
                var detail = schedDetail(item);
                var detailParts = detail ? detail.split(":") : [];
                var product = detailParts[0] ? detailParts[0].trim() : "";
                var rest = detailParts.length > 1 ? detailParts.slice(1).join(":").trim() : "";
                var hasProduct = product && PC[product];

                var isTankClean = item.task === "Tank Clean" && !done;

                return React.createElement("div", {
                  key: itemIdx,
                  style: {
                    display: "flex", alignItems: "center",
                    padding: "8px 14px",
                    borderTop: itemIdx > 0 ? "1px solid var(--rule)" : "none",
                    opacity: done ? 0.5 : 1
                  }
                },
                  // Left colour bar
                  React.createElement("div", { style: {
                    width: 4, height: 32, borderRadius: 2,
                    background: done ? "#17B892" : col,
                    marginRight: 12, flexShrink: 0
                  }}),
                  // Task content
                  React.createElement("div", { style: { flex: 1, minWidth: 0 } },
                    React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6 } },
                      React.createElement("span", { style: {
                        fontSize: 13, fontWeight: 700,
                        color: done ? "var(--ghost)" : col,
                        textDecoration: done ? "line-through" : "none"
                      }}, item.task),
                      hasProduct && React.createElement("span", { style: {
                        display: "inline-block", padding: "1px 8px", borderRadius: 100,
                        background: PC[product], color: "#fff", fontSize: 10, fontWeight: 700
                      }}, product),
                      rest && React.createElement("span", { style: { fontSize: 12, color: "var(--dim)" } }, rest),
                      !hasProduct && detail && React.createElement("span", { style: { fontSize: 12, color: "var(--dim)" } }, detail)
                    ),
                    item.notes && !done && React.createElement("div", { style: { fontSize: 11, color: "var(--ghost)", fontStyle: "italic", marginTop: 2 } }, item.notes)
                  ),
                  // Right side: done pill or time + who + mark done button
                  React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0, marginLeft: 8 } },
                    done && React.createElement("span", { style: {
                      display: "inline-block", padding: "2px 8px", borderRadius: 100,
                      background: "#17B892", color: "#fff", fontSize: 10, fontWeight: 700
                    }}, "DONE"),
                    !done && isTankClean && React.createElement("span", {
                      style: { display: "inline-flex", alignItems: "center", gap: 3, padding: "3px 10px", borderRadius: 100, background: "#17B892", color: "#fff", fontSize: 10, fontWeight: 700, cursor: "pointer" },
                      onClick: function(e) { e.stopPropagation(); markScheduleDone(item); }
                    }, "\u2713 Mark done"),
                    !done && item.time && React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)" } }, formatSchedTime(item.time)),
                    item.who && React.createElement("span", { style: {
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      width: 22, height: 22, borderRadius: 11,
                      background: "var(--input-bg)",
                      fontSize: 8, fontWeight: 800, color: "var(--dim)", letterSpacing: 0.5
                    }}, item.who)
                  )
                );
              })
            )
      );
    })
    );
  }

  function renderHome() {
    const ab = activeBrew(),
      fs = fridgeSummary(),
      ft = fridgeTotal();
    return /*#__PURE__*/React.createElement("div", {
      className: "scr",
      style: {
        animation: "fu .2s ease"
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "sect"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sect-top"
    }, /*#__PURE__*/React.createElement("span", {
      className: "h2"
    }, "Today"), schedule.length > 0 && React.createElement("span", {
      style: { fontSize: 12, color: "var(--blue)", cursor: "pointer", fontWeight: 600 },
      onClick: function() { setSchedWeek(0); setSub("schedule"); }
    }, "Full schedule \u203a"))), function() {
      var today = schedToday();
      if (today.length === 0) {
        // Fallback: show the original hardcoded cards when no schedule data
        return React.createElement("div", {
          style: { display: "flex", flexDirection: "column", gap: 10, marginTop: 14, marginBottom: 4 }
        },
        // BREW OFF card (original)
        React.createElement("div", {
          style: { borderRadius: 12, overflow: "hidden", border: "1px solid var(--rule)", cursor: "pointer" },
          onClick: function() {
            if (ab) {
              if (!offInProgress) {
                setOffStep(1);
                setOffLtS(""); setOffLtL(""); setOffBtS(""); setOffBtL("");
                setOffFS(nowT()); setOffFF(nowTplus(ab && ab.product === "Iced Espresso" ? 60 : 45)); setOffPS("5"); setOffPE("15"); setOffFL("");
                setOffConc(["", "", "", "", ""]); setOffDil(["", "", "", "", ""]);
                setOffWaterOk(false); setOffNagardoOk(false); setOffSorbateOk(false); setOffOverride("");
                var _offRcp = recipes.find(function(r) { return r.product === ab.product; });
                setOffNagardoRate(_offRcp && _offRcp.nagardoRate ? String(_offRcp.nagardoRate) : "0.025");
                setOffSorbateRate(_offRcp && _offRcp.sorbateRate ? String(_offRcp.sorbateRate) : "0.01");
                setOffTag(""); setOffBB(bbDef()); setOffFlavour("");
                setOffInProgress(true);
              }
              setScreen("brew"); setSub("brewOff");
            } else { setScreen("brew"); }
          }
        },
          React.createElement("div", { style: { background: AC.off, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" } },
            React.createElement("span", { style: { fontSize: 16, fontWeight: 700, color: "#fff", letterSpacing: 0.5 } }, "BREW OFF")
          ),
          React.createElement("div", { style: { background: "var(--bg)", padding: "10px 16px" } },
            ab ? React.createElement("span", { style: { display: "inline-block", padding: "2px 10px", borderRadius: 100, background: PC[ab.product] || "#bbb", color: "#fff", fontSize: 12, fontWeight: 700 } }, ab.product)
            : React.createElement("span", { style: { fontSize: 13, color: "var(--ghost)" } }, "No batch off today")
          )
        ),
        // BREW ON card (original)
        React.createElement("div", {
          style: { borderRadius: 12, overflow: "hidden", border: "1px solid var(--rule)", cursor: "pointer" },
          onClick: function() { setScreen("brew"); }
        },
          React.createElement("div", { style: { background: AC.on, padding: "12px 16px" } },
            React.createElement("span", { style: { fontSize: 16, fontWeight: 700, color: "#fff", letterSpacing: 0.5 } }, "BREW ON")
          ),
          React.createElement("div", { style: { background: "var(--bg)", padding: "10px 16px" } },
            React.createElement("span", { style: { fontSize: 13, color: "var(--ghost)" } }, onInProgress ? "Resume brew in progress" : "Tap to start a brew")
          )
        ),
        // KEGS card (original)
        React.createElement("div", {
          style: { borderRadius: 12, overflow: "hidden", border: "1px solid var(--rule)", cursor: "pointer" },
          onClick: function() { setScreen("kegs"); }
        },
          React.createElement("div", { style: { background: "var(--blue)", padding: "12px 16px" } },
            React.createElement("span", { style: { fontSize: 16, fontWeight: 700, color: "#fff", letterSpacing: 0.5 } }, "KEGS")
          ),
          React.createElement("div", { style: { background: "var(--bg)", padding: "10px 16px" } },
            React.createElement("span", { style: { fontSize: 13, color: "var(--ghost)" } }, ft > 0 ? ft + " kegs in fridge" : "No kegs ready")
          )
        )
        );
      }
      // Schedule-driven today cards
      return React.createElement("div", {
        style: { display: "flex", flexDirection: "column", gap: 8, marginTop: 14, marginBottom: 4 }
      }, today.map(function(item, idx) {
        // Day Note — collapsible banner above tasks
        if (item.task === "Note") {
          var lines = (item.detail || "").split("\n").filter(function(l) { return l.trim(); });
          var firstLine = lines[0] || "";
          var hasMore = lines.length > 1;
          var expanded = noteExpanded[item.date];
          return React.createElement("div", {
            key: idx,
            onClick: hasMore ? function() {
              var next = Object.assign({}, noteExpanded);
              next[item.date] = !expanded;
              setNoteExpanded(next);
            } : undefined,
            style: {
              borderRadius: 12, overflow: "hidden",
              background: "var(--warn-bg, #FFF8E1)",
              borderLeft: "4px solid var(--warn, #F9A825)",
              padding: "10px 14px",
              fontSize: 12, color: "var(--dim)", lineHeight: 1.5,
              cursor: hasMore ? "pointer" : "default"
            }
          },
            expanded
              ? React.createElement("div", { style: { display: "flex", gap: 8 } },
                  React.createElement("div", { style: { whiteSpace: "pre-line", flex: 1 } }, item.detail),
                  React.createElement("span", { style: { fontSize: 16, color: "var(--dim)", alignSelf: "flex-end", lineHeight: 1 } }, "▴")
                )
              : React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6 } },
                  React.createElement("span", { style: { flex: 1 } }, firstLine),
                  hasMore && React.createElement("span", { style: { fontSize: 16, color: "var(--dim)", whiteSpace: "nowrap", lineHeight: 1 } }, "▾")
                )
          );
        }

        var col = TASK_COLOURS[item.task] || "#95A5A6";
        var done = isSchedDone(item);
        var clickFn = null;
        if (!done) {
          if (item.task === "Brew Off") {
            clickFn = function() {
              if (ab) {
                if (!offInProgress) {
                  setOffStep(1);
                  setOffLtS(""); setOffLtL(""); setOffBtS(""); setOffBtL("");
                  setOffFS(nowT()); setOffFF(nowTplus(ab && ab.product === "Iced Espresso" ? 60 : 45)); setOffPS("5"); setOffPE("15"); setOffFL("");
                  setOffConc(["", "", "", "", ""]); setOffDil(["", "", "", "", ""]);
                  setOffWaterOk(false); setOffNagardoOk(false); setOffSorbateOk(false); setOffOverride("");
                  var _offRcp = recipes.find(function(r) { return r.product === ab.product; });
                  setOffNagardoRate(_offRcp && _offRcp.nagardoRate ? String(_offRcp.nagardoRate) : "0.025");
                  setOffSorbateRate(_offRcp && _offRcp.sorbateRate ? String(_offRcp.sorbateRate) : "0.01");
                  setOffTag(""); setOffBB(bbDef()); setOffFlavour("");
                  setOffInProgress(true);
                }
                setScreen("brew"); setSub("brewOff");
              } else { setScreen("brew"); }
            };
          } else if (item.task === "Brew On") {
            clickFn = function() {
              if (onInProgress) { setScreen("brew"); setSub("brewOn"); }
              else { setScreen("brew"); }
            };
          } else if (item.task === "Keg Up") {
            clickFn = function() { setScreen("kegs"); };
          } else if (item.task === "Keg Clean") {
            clickFn = function() {
              if (kegFleetCounts && kegFleetCounts.toBeWashed) {
                setW18(String(kegFleetCounts.toBeWashed.n18 || ""));
                setW9(String(kegFleetCounts.toBeWashed.n9 || ""));
              }
              setScreen("kegs"); setSub("washKegs");
            };
          }
        }
        return React.createElement("div", {
          key: idx,
          style: { borderRadius: 12, overflow: "hidden", border: "1px solid var(--rule)", cursor: clickFn ? "pointer" : "default", opacity: done ? 0.55 : 1 },
          onClick: clickFn
        },
          React.createElement("div", { style: { background: done ? "#bbb" : col, padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" } },
            React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8 } },
              done && React.createElement("span", { style: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 11, background: "rgba(255,255,255,.35)", fontSize: 14, color: "#fff" } }, "\u2713"),
              React.createElement("span", { style: { fontSize: 14, fontWeight: 700, color: "#fff", letterSpacing: 0.5 } }, item.task.toUpperCase())
            ),
            React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8 } },
              done && React.createElement("span", { style: { display: "inline-block", padding: "3px 10px", borderRadius: 100, background: "#17B892", color: "#fff", fontSize: 11, fontWeight: 700, letterSpacing: 0.5 } }, "DONE"),
              item.who && React.createElement("span", { style: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: 12, background: "rgba(255,255,255,.25)", fontSize: 9, fontWeight: 800, color: "#fff", letterSpacing: 0.5 } }, item.who),
              !done && item.time && React.createElement("span", { style: { fontSize: 12, color: "rgba(255,255,255,.7)" } }, formatSchedTime(item.time))
            )
          ),
          React.createElement("div", { style: { background: "var(--bg)", padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 } },
            function() {
              // Parse product from detail, suppress "Yes"
              var detail = schedDetail(item);
              if (!detail && !item.notes && item.task !== "Tank Clean") return null;
              var parts = detail ? detail.split(":") : [];
              var product = parts[0] ? parts[0].trim() : "";
              var rest = parts.length > 1 ? parts.slice(1).join(":").trim() : "";
              return React.createElement(React.Fragment, null,
                product && PC[product] ? React.createElement("span", { style: { display: "inline-block", padding: "2px 10px", borderRadius: 100, background: PC[product], color: "#fff", fontSize: 11, fontWeight: 700 } }, product) : null,
                (rest || (!PC[product] && detail)) && React.createElement("span", { style: { fontSize: 13, color: "var(--dim)" } }, rest || detail),
                item.notes && React.createElement("span", { style: { fontSize: 12, color: "var(--ghost)", fontStyle: "italic" } }, item.notes),
                // Mark Done button for Tank Clean
                item.task === "Tank Clean" && !done && React.createElement("span", {
                  style: { marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 14px", borderRadius: 100, background: "#17B892", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", letterSpacing: 0.3 },
                  onClick: function(e) { e.stopPropagation(); markScheduleDone(item); }
                }, "\u2713 Mark done")
              );
            }()
          )
        );
      }));
    }(), /*#__PURE__*/React.createElement("div", {
      className: "sect"
    }, /*#__PURE__*/React.createElement("div", {
      className: "h2"
    }, "Product In Tanks")), /*#__PURE__*/React.createElement("div", {
      className: "cols",
      style: {
        borderTop: "1px solid var(--rule)",
        marginTop: 10,
        paddingTop: 12
      }
    }, TANK_DEF.map(t => {
      const ts = getTank(t.id),
        has = ts && ts.product && ts.status !== "Empty",
        L = ts ? ts.currentLitres || 0 : 0,
        col = has ? PC[ts.product] || "#bbb" : "#bbb";
      return /*#__PURE__*/React.createElement("div", {
        className: "col",
        key: t.id,
        style: has ? { cursor: "pointer" } : {},
        onClick: has ? function() {
          var brew = ts.brewId ? brewLog.find(function(b) { return b.brewId === ts.brewId; }) : null;
          if (brew) { setBrewDetail(brew); setBrewEditing(false); setBrewDraft({}); }
        } : null
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1
        }
      }, t.id, " ", /*#__PURE__*/React.createElement("span", {
        style: {
          fontWeight: 400,
          color: "var(--ghost)",
          fontSize: 9
        }
      }, t.cap, "L")), /*#__PURE__*/React.createElement("div", {
        style: {
          marginTop: 4
        }
      }, has ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 36,
          fontWeight: 700
        }
      }, L), /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 16,
          fontWeight: 400,
          color: "var(--dim)"
        }
      }, "L")) : /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 36,
          fontWeight: 700,
          color: "var(--faint)"
        }
      }, "\u2014")), has ? /*#__PURE__*/React.createElement("div", {
        style: {
          marginTop: 6
        }
      }, /*#__PURE__*/React.createElement(ProductPill, { name: ts.product, size: 10 })) : /*#__PURE__*/React.createElement("div", {
        style: {
          fontSize: 12,
          color: "var(--off)",
          marginTop: 4
        }
      }, "Empty"));
    })), /*#__PURE__*/React.createElement("div", {
      className: "sect"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sect-top"
    }, /*#__PURE__*/React.createElement("span", {
      className: "h2"
    }, "Kegs in Fridge"), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 11,
        color: "var(--dim)",
        letterSpacing: 1
      }
    }, ft, " KEGS"))), function() {
      var show7 = has7LKegs();
      var gridCols = show7 ? "100px 1fr 1fr 1fr" : "100px 1fr 1fr";
      return React.createElement(React.Fragment, null,
        React.createElement("div", {
          className: "ftbl ftbl-hd",
          style: { gridTemplateColumns: gridCols }
        }, React.createElement("div", null, "\u00a0"), React.createElement("div", {
          className: "ftbl-c",
          style: { color: "var(--ghost)", fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textAlign: "center" }
        }, "18L"), React.createElement("div", {
          className: "ftbl-c",
          style: { color: "var(--ghost)", fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textAlign: "center" }
        }, "9L"), show7 && React.createElement("div", {
          className: "ftbl-c",
          style: { color: "var(--ghost)", fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textAlign: "center" }
        }, "7L")),
        Object.keys(fs).length > 0 ? Object.keys(fs).map(function(product) {
          var row = fs[product], col = PC[product] || "#bbb";
          return React.createElement("div", {
            className: "ftbl ftbl-row",
            key: product,
            style: { gridTemplateColumns: gridCols }
          }, React.createElement("div", {
            className: "row-i"
          }, React.createElement(ProductPill, { name: product, size: 10 })), React.createElement("div", {
            className: "ftbl-c fridge-tap",
            style: { fontSize: 22, fontWeight: 700 },
            onClick: function() { setFridgeModal({ product: product, size: "18L", count: row["18L"] }); }
          }, row["18L"] || 0), React.createElement("div", {
            className: "ftbl-c fridge-tap",
            style: { fontSize: 22, fontWeight: 700 },
            onClick: function() { setFridgeModal({ product: product, size: "9L", count: row["9L"] }); }
          }, row["9L"] || 0), show7 && React.createElement("div", {
            className: "ftbl-c fridge-tap",
            style: { fontSize: 22, fontWeight: 700 },
            onClick: function() { setFridgeModal({ product: product, size: "7L", count: row["7L"] }); }
          }, row["7L"] || 0));
        }) : React.createElement("div", {
          style: { padding: "16px 0", textAlign: "center", color: "var(--ghost)", fontSize: 13 }
        }, "No kegs in fridge"),
        Object.keys(fs).length > 0 && function() {
          var t18 = 0, t9 = 0, t7 = 0;
          Object.keys(fs).forEach(function(p) {
            t18 += fs[p]["18L"];
            t9 += fs[p]["9L"];
            t7 += fs[p]["7L"];
          });
          return React.createElement("div", {
            className: "ftbl ftbl-total",
            style: { gridTemplateColumns: gridCols }
          }, React.createElement("div", {
            className: "lbl",
            style: { color: "var(--dim)", letterSpacing: 1.5 }
          }, "Total"), React.createElement("div", {
            className: "ftbl-c",
            style: { fontSize: 16, fontWeight: 700 }
          }, t18), React.createElement("div", {
            className: "ftbl-c",
            style: { fontSize: 16, fontWeight: 700 }
          }, t9), show7 && React.createElement("div", {
            className: "ftbl-c",
            style: { fontSize: 16, fontWeight: 700 }
          }, t7));
        }()
      );
    }());
  }

  // ============================================================
  // LIVE
  // ============================================================
  function renderLive() {
    var fs = fridgeSummary(), ft = fridgeTotal();
    var ab = activeBrew();

    // Determine brew phase for visual states
    // Phases: idle | grinding | brewing | filtering | ready
    var brewPhase = "idle";
    var liveProduct = null;
    var liveTank = null;

    if (ab) {
      if (ab.status === "Brewing" || ab.status === "ON") {
        brewPhase = "brewing";
        liveProduct = ab.product;
        liveTank = ab.brightTank;
      }
    }

    // Brew-on in progress (unsaved) — grinding phase
    if (!ab && onInProgress && onGrindStartOk) {
      brewPhase = "grinding";
      liveProduct = onProduct;
      liveTank = onTank;
    }

    // Brew-off in progress — filtering phase
    if (ab && offInProgress) {
      brewPhase = "filtering";
      liveProduct = ab.product;
      liveTank = ab.brightTank;
    }

    // Tank states from data
    var ltCol = liveProduct ? (PC[liveProduct] || "#bbb") : "var(--rule)";
    var ltHas = brewPhase !== "idle";

    // Lauter Tun fill + status per phase
    var ltPct = 0;
    var ltStatus = "Empty";
    var ltLabel = "";
    if (brewPhase === "grinding") { ltPct = 0; ltStatus = "Grinding"; ltLabel = "Grinding"; }
    else if (brewPhase === "brewing") { ltPct = 100; ltStatus = "Brewing"; ltLabel = "Brewing"; }
    else if (brewPhase === "filtering") { ltPct = 50; ltStatus = "Filtering"; ltLabel = "Filtering"; }

    // Bright tank visual overrides per phase
    function bbtVisPct(t, ts) {
      if (!liveTank || t.id !== liveTank) return null;
      if (brewPhase === "grinding") return 0; // highlighted border only
      if (brewPhase === "brewing") return 0; // highlighted border only
      if (brewPhase === "filtering") return 50; // half full during filtering
      return null;
    }
    function bbtVisStatus(t, ts) {
      if (!liveTank || t.id !== liveTank) return null;
      if (brewPhase === "grinding") return "Grinding";
      if (brewPhase === "brewing") return "Brewing";
      if (brewPhase === "filtering") return "Filtering";
      return null;
    }

    var ltSS = tankSS(ltStatus);
    if (brewPhase === "grinding") ltSS = { bg: "rgba(29,78,216,.08)", c: "var(--blue)" };
    if (brewPhase === "filtering") ltSS = { bg: "rgba(196,62,42,.08)", c: "#C43E2A" };
    return /*#__PURE__*/React.createElement("div", {
      className: "scr",
      style: {
        animation: "fu .2s ease"
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "map-floor"
    },
    // v2.1 — inline fills + brew phase
    // LAUTER TUN — top
    /*#__PURE__*/React.createElement("div", {
      style: { display: "flex", justifyContent: "center", marginBottom: 0 }
    }, /*#__PURE__*/React.createElement("div", { className: "mt" },
      /*#__PURE__*/React.createElement("div", { className: "mt-c", style: {
        width: 80, height: 80,
        borderColor: ltHas ? ltCol : "var(--rule)",
        boxShadow: brewPhase !== "idle" ? "0 0 0 3px " + ltCol + "33" : "none",
        background: ltPct > 0 ? "linear-gradient(to top, " + ltCol + " " + ltPct + "%, #fff " + ltPct + "%)" : "#fff"
      } },
        /*#__PURE__*/React.createElement("div", { className: "mt-v", style: { fontSize: 13, color: ltPct >= 50 ? "#fff" : (ltHas ? ltCol : "var(--ghost)"), fontWeight: 700 } }, ltLabel || ""),
        (brewPhase !== "idle" && liveProduct) && /*#__PURE__*/React.createElement("div", { className: "mt-p", style: { color: ltPct >= 50 ? "rgba(255,255,255,.85)" : ltCol, fontSize: 8 } }, PS[liveProduct] || liveProduct)
      ),
      /*#__PURE__*/React.createElement("div", { className: "mt-n" }, "Lauter Tun"),
      /*#__PURE__*/React.createElement("div", { className: "mt-s", style: { background: ltSS.bg, color: ltSS.c, fontSize: 10, padding: "4px 12px", letterSpacing: 1 } },
        brewPhase !== "idle" && /*#__PURE__*/React.createElement("span", { className: "pulse", style: { width: 5, height: 5, marginRight: 3 } }),
        ltStatus
      )
    )),
    // Pulsing dot between Lauter and tanks
    (brewPhase !== "idle") && /*#__PURE__*/React.createElement("div", { style: { textAlign: "center", margin: "4px 0" } },
      /*#__PURE__*/React.createElement("span", { className: "pulse", style: { display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: brewPhase === "filtering" ? ltCol : "var(--success)" } })
    ),
    // TANKS section (no label)
    /*#__PURE__*/React.createElement("div", {
      className: "map-tr"
    }, TANK_DEF.map(function(t) {
      var ts = getTank(t.id);
      var has = ts && ts.product && ts.status !== "Empty";
      var L = ts ? ts.currentLitres || 0 : 0;
      var pct = Math.min(100, Math.round(L / t.cap * 100));
      var col = has ? PC[ts.product] || "#bbb" : "var(--rule)";

      // Override visual fill for target tank during brew phases
      var visPct = bbtVisPct(t, ts);
      var visStatus = bbtVisStatus(t, ts);
      var isTarget = liveTank && t.id === liveTank;
      if (visPct !== null && !has) {
        pct = visPct;
        col = ltCol;
        has = true;
      } else if (visPct !== null && has) {
        // Tank already has product — use its real fill but override status
        // During filtering, show half full if currently empty from data
      }
      var displayStatus = visStatus || (ts ? ts.status || "Empty" : "Empty");
      var ss = tankSS(displayStatus);
      if (isTarget && brewPhase === "grinding") ss = { bg: "rgba(29,78,216,.08)", c: "var(--blue)" };
      if (isTarget && brewPhase === "filtering") ss = { bg: "rgba(196,62,42,.08)", c: "#C43E2A" };
      var isActive = isTarget && brewPhase !== "idle";

      // Tap behaviour:
      //   Filling  → Cancel Fill modal (which also offers Empty Tank)
      //   Ready    → Empty Tank modal, for residual left behind after kegging
      // Brewing / Filtering / Empty tanks aren't tappable — nothing safe to do mid-process.
      var canCancelFill = displayStatus === "Filling";
      var canEmpty = !canCancelFill && displayStatus === "Ready" && !!(ts && ts.product) && L > 0;
      var tankTap = canCancelFill
        ? function() { setCancelFillTank({ id: t.id, product: ts ? ts.product : "", litres: L, brewId: ts ? ts.brewId || "" : "" }); }
        : (canEmpty
            ? function() { setEmptyReason(""); setEmptyTank({ id: t.id, product: ts ? ts.product : "", litres: L, brewId: ts ? ts.brewId || "" : "" }); }
            : null);

      return /*#__PURE__*/React.createElement("div", {
        className: "mt",
        key: t.id,
        onClick: tankTap,
        style: { cursor: tankTap ? "pointer" : "default" }
      }, /*#__PURE__*/React.createElement("div", {
        className: "mt-c",
        style: {
          width: 80, height: 80,
          borderColor: has ? col : (isTarget ? ltCol : "var(--rule)"),
          boxShadow: isTarget && brewPhase !== "idle" ? "0 0 0 3px " + ltCol + "33" : "none",
          background: pct > 0 ? "linear-gradient(to top, " + col + " " + pct + "%, #fff " + pct + "%)" : "#fff"
        }
      },
        // Product pill — positioned at top of circle
        has && /*#__PURE__*/React.createElement("div", {
          style: { position: "relative", zIndex: 1 }
        }, /*#__PURE__*/React.createElement("span", {
          style: { display: "inline-block", padding: "1px 6px", borderRadius: 50, background: col, color: "#fff", fontSize: 7, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }
        }, isTarget && liveProduct ? PS[liveProduct] || liveProduct : (ts && ts.product ? PS[ts.product] || ts.product : ""))),
        // Litres value — positioned below product pill, just above bottom
        /*#__PURE__*/React.createElement("div", {
          className: "mt-v",
          style: { fontSize: 16, color: pct > 50 ? "#fff" : "var(--text)", marginTop: 4 }
        }, has ? (L > 0 ? L : "") : "")
      ), /*#__PURE__*/React.createElement("div", {
        className: "mt-n"
      }, t.id, " \xB7 ", t.cap, "L"), /*#__PURE__*/React.createElement("div", {
        className: "mt-s",
        style: { background: ss.bg, color: ss.c, fontSize: isActive ? 10 : 8, padding: isActive ? "4px 12px" : "3px 10px", letterSpacing: isActive ? 1 : 0.8 }
      }, (isActive || displayStatus === "Filling" || displayStatus === "Brewing" || displayStatus === "Filtering") && /*#__PURE__*/React.createElement("span", {
        className: "pulse",
        style: { width: isActive ? 5 : 4, height: isActive ? 5 : 4, marginRight: isActive ? 3 : 2 }
      }), displayStatus));
    })),
    // Arrow between tanks and fridge — visible only during keg filling
    function() {
      var anyFilling = TANK_DEF.some(function(t) { var ts = getTank(t.id); return ts && ts.status === "Filling"; });
      if (!anyFilling) return /*#__PURE__*/React.createElement("div", { style: { height: 16 } });
      return /*#__PURE__*/React.createElement("div", { className: "flow", style: { margin: "6px 0" } },
        /*#__PURE__*/React.createElement("span", { className: "pulse", style: { display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "var(--success)" } })
      );
    }(),
    // KEGS IN FRIDGE — bottom
    /*#__PURE__*/React.createElement("div", { className: "mfridge" },
      /*#__PURE__*/React.createElement("div", { className: "row-i", style: { gap: 10 } },
        /*#__PURE__*/React.createElement("div", null,
          /*#__PURE__*/React.createElement("div", { className: "lbl", style: { color: "var(--ghost)" } }, "Kegs in Fridge"),
          /*#__PURE__*/React.createElement("div", { style: { fontSize: 26, fontWeight: 800 } }, ft, " ", /*#__PURE__*/React.createElement("span", { style: { fontSize: 12, fontWeight: 500, color: "var(--dim)" } }, "kegs"))
        )
      ),
      React.createElement("div", { style: { marginTop: 10 } },
        function() {
          var show7 = has7LKegs();
          var gridCols = show7 ? "100px 1fr 1fr 1fr" : "100px 1fr 1fr";
          return React.createElement(React.Fragment, null,
            React.createElement("div", { className: "ftbl ftbl-hd", style: { marginTop: 0, gridTemplateColumns: gridCols } },
              React.createElement("div", null, "\u00a0"),
              React.createElement("div", { className: "ftbl-c", style: { color: "var(--ghost)", fontSize: 12, fontWeight: 700, letterSpacing: 0.5 } }, "18L"),
              React.createElement("div", { className: "ftbl-c", style: { color: "var(--ghost)", fontSize: 12, fontWeight: 700, letterSpacing: 0.5 } }, "9L"),
              show7 && React.createElement("div", { className: "ftbl-c", style: { color: "var(--ghost)", fontSize: 12, fontWeight: 700, letterSpacing: 0.5 } }, "7L")
            ),
            Object.keys(fs).length > 0 ? Object.keys(fs).map(function(product) {
              var row = fs[product];
              return React.createElement("div", { className: "ftbl ftbl-row", key: product, style: { gridTemplateColumns: gridCols } },
                React.createElement("div", { className: "row-i" }, React.createElement(ProductPill, { name: product, size: 10 })),
                React.createElement("div", { className: "ftbl-c fridge-tap", style: { fontSize: 18, fontWeight: 700, cursor: "pointer" },
                  onClick: function() { setFridgeModal({ product: product, size: "18L", count: row["18L"] }); }
                }, row["18L"] || 0),
                React.createElement("div", { className: "ftbl-c fridge-tap", style: { fontSize: 18, fontWeight: 700, cursor: "pointer" },
                  onClick: function() { setFridgeModal({ product: product, size: "9L", count: row["9L"] }); }
                }, row["9L"] || 0),
                show7 && React.createElement("div", { className: "ftbl-c fridge-tap", style: { fontSize: 18, fontWeight: 700, cursor: "pointer" },
                  onClick: function() { setFridgeModal({ product: product, size: "7L", count: row["7L"] }); }
                }, row["7L"] || 0)
              );
            }) : React.createElement("div", { style: { padding: "16px 0", textAlign: "center", color: "var(--ghost)", fontSize: 13 } }, "No kegs in fridge"),
            Object.keys(fs).length > 0 && function() {
              var t18 = 0, t9 = 0, t7 = 0;
              Object.keys(fs).forEach(function(p) { t18 += fs[p]["18L"]; t9 += fs[p]["9L"]; t7 += fs[p]["7L"]; });
              return React.createElement("div", { className: "ftbl ftbl-total", style: { gridTemplateColumns: gridCols } },
                React.createElement("div", { className: "lbl", style: { color: "var(--dim)", letterSpacing: 1.5 } }, "Total"),
                React.createElement("div", { className: "ftbl-c", style: { fontSize: 14, fontWeight: 700 } }, t18),
                React.createElement("div", { className: "ftbl-c", style: { fontSize: 14, fontWeight: 700 } }, t9),
                show7 && React.createElement("div", { className: "ftbl-c", style: { fontSize: 14, fontWeight: 700 } }, t7)
              );
            }()
          );
        }()
      )
    )
    ));
  }

  // ============================================================
  // BREW DEFAULT (log + buttons)
  // ============================================================
  function renderBrewDefault() {
    const ab = activeBrew();
    return /*#__PURE__*/React.createElement("div", {
      className: "scr",
      style: {
        animation: "fu .2s ease"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 20
      }
    }, ab && /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 12
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 100,
        background: PC[ab.product] || "#bbb",
        color: "#fff",
        fontSize: 13,
        fontWeight: 700
      }
    }, ab.product), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 13,
        fontWeight: 600
      }
    }, " brewing", ab.brightTank ? " → " + ab.brightTank : "")), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 10
      }
    }, !ab && /*#__PURE__*/React.createElement("button", {
      className: "btn",
      style: {
        flex: 1,
        background: AC.on,
        color: "#fff"
      },
      onClick: () => {
        if (!onInProgress) {
          setOnStep(1);
          setOnProduct("Classic");
          setOnTotalKg("34");
          setOnGrindSize("");
          setOnRoastDate("");
          var rcp = recipes.find(function(r) { return r.product === "Classic"; });
          if (rcp) {
            var dw = rcp.doseWeight ? String(rcp.doseWeight) : "8.5";
            var dc = rcp.doseCount ? Number(rcp.doseCount) : 4;
            var doses = [];
            for (var di = 0; di < dc; di++) doses.push(dw);
            setOnDoses(doses);
            if (rcp.defaultGrind) setOnGrindSize(String(rcp.defaultGrind));
            if (rcp.fillTargetL) setOnFillTarget(String(rcp.fillTargetL));
          } else {
            setOnDoses(["8.5", "8.5", "8.5", "8.5"]);
            setOnFillTarget("");
          }
          setOnGrindStart(nowT());
          setOnGrindFinish("");
          setOnTank("");
          setOnTimeOn("");
          setOnTargetOff("");
          setOnAmbient("");
          setOnStartTemp("");
          setOnTargetTemp("8.3");
          setOnParticle(["", "", ""]);
          setOnDoseDone([false, false, false, false]);
          setOnGrindStartOk(false);
          setOnGrindFinishOk(false);
          setOnChecklist([false, false, false, false, false, false, false, false]);
          setOnPhotoFlow(false);
          setOnPhotoTank(false);
          setOnPreGrind([false, false]);
          setOnInProgress(true);
        }
        setSub("brewOn");
      }
    }, /*#__PURE__*/React.createElement(IcoBrew, null), onInProgress ? " Resume Brew" : " Brew On"), ab && /*#__PURE__*/React.createElement("button", {
      className: "btn",
      style: {
        flex: 1,
        background: AC.off,
        color: "#fff"
      },
      onClick: () => {
        if (!offInProgress) {
          setOffStep(1);
          setOffLtS("");
          setOffLtL("");
          setOffBtS("");
          setOffBtL("");
          setOffFS(nowT());
          setOffFF(nowTplus(ab && ab.product === "Iced Espresso" ? 60 : 45));
          setOffPS("5");
          setOffPE("15");
          setOffFL("");
          setOffConc(["", "", "", "", ""]);
          setOffDil(["", "", "", "", ""]);
          setOffWaterOk(false);
          setOffNagardoOk(false);
          setOffSorbateOk(false);
          // Removed: setOffNagardoAdjust / setOffSorbateAdjust / setOffNagardoGrams /
          // setOffSorbateGrams. None of those four are declared anywhere in this file,
          // so this line threw a ReferenceError and aborted the handler before
          // setOffInProgress(true) and setSub("brewOff") — the Brew tab's Brew Off
          // button did nothing. The Home card path never had this line, which is why
          // brew offs still worked from there.
          var _offRcp2 = recipes.find(function(r) { return r.product === (ab ? ab.product : "Classic"); });
          setOffNagardoRate(_offRcp2 && _offRcp2.nagardoRate ? String(_offRcp2.nagardoRate) : "0.025");
          setOffSorbateRate(_offRcp2 && _offRcp2.sorbateRate ? String(_offRcp2.sorbateRate) : "0.01");
          setOffOverride("");
          setOffTag("");
          setOffBB(bbDef());
          setOffFlavour("");
          setOffInProgress(true);
        }
        setSub("brewOff");
      }
    }, /*#__PURE__*/React.createElement(IcoBrew, null), " Brew Off"))), /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 20
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "btn",
      style: {
        background: "#D4870A",
        color: "#fff"
      },
      onClick: () => setSub("recipes")
    }, /*#__PURE__*/React.createElement(IcoBook, null), " Recipes")), /*#__PURE__*/React.createElement("div", {
      className: "sect"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sect-top"
    }, /*#__PURE__*/React.createElement("span", {
      className: "h2"
    }, "Brew Log"), /*#__PURE__*/React.createElement("span", {
      className: "lbl",
      style: {
        color: "var(--ghost)"
      }
    }, "Latest"))), brewLog.length === 0 ? /*#__PURE__*/React.createElement("div", {
      style: {
        padding: "20px 0",
        textAlign: "center",
        color: "var(--ghost)",
        fontSize: 13
      }
    }, "No brews yet \u2014 tap Brew On to start") : brewLog.slice().reverse().slice(0, 10).map(b => {
      const col = PC[b.product] || "#bbb",
        isOn = b.status === "ON" || b.status === "Brewing";
      return /*#__PURE__*/React.createElement("div", {
        className: "brew-entry",
        key: b.brewId,
        onClick: () => setBrewDetail(b)
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline"
        }
      }, /*#__PURE__*/React.createElement("span", {
        className: "lbl",
        style: {
          color: isOn ? "var(--blue)" : col
        }
      }, "Brew ", b.brewId), /*#__PURE__*/React.createElement("span", {
        style: { fontSize: 13, color: "var(--dim)" }
      }, fmtDate(b.date), " \xB7 ", b.status || "")), /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 6
        }
      }, /*#__PURE__*/React.createElement("span", {
        style: {
          display: "inline-block",
          padding: "2px 10px",
          borderRadius: 100,
          background: col,
          color: "#fff",
          fontSize: 12,
          fontWeight: 700
        }
      }, b.product), /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 13,
          fontWeight: 700
        }
      }, b.totalKg || "", " kg"),
      b.tdsConcAvg && /*#__PURE__*/React.createElement("span", {
        style: { fontSize: 13, fontWeight: 700, color: "var(--blue)" }
      }, "TDS ", b.tdsConcAvg)), /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: 12,
          marginTop: 6,
          fontSize: 13,
          color: "var(--dim)"
        }
      }, /*#__PURE__*/React.createElement("span", null, b.brightTank || ""), b.totalLitres && /*#__PURE__*/React.createElement("span", null, b.totalLitres, " L"),
      b.kegTag && /*#__PURE__*/React.createElement("span", { style: { fontWeight: 700 } }, b.kegTag),
      b.bestBefore && /*#__PURE__*/React.createElement("span", null, "BB ", fmtDate(b.bestBefore))));
    }));
  }

  // ============================================================
  // BREW ON FLOW
  // ============================================================
  function renderBrewOn() {
    var doseTotal = onDoses.reduce(function(a, v) { return a + (parseFloat(v) || 0); }, 0);
    var tgtKg = parseFloat(onTotalKg) || 0;
    var doseCol = tgtKg <= 0 || doseTotal === 0 ? "var(--ghost)" : Math.abs(doseTotal - tgtKg) < 0.1 ? "var(--success)" : "var(--danger)";
    var allDosesDone = onDoseDone.length > 0 && onDoseDone.length >= onDoses.length && onDoseDone.every(function(d) { return d; });
    // Each step gates on the fields that actually live on it, so nothing can be
    // glided past and only discovered at save. Ambient / Target / Time-Dose used to
    // be absent from these checks, which let Continue stay live on a blank Ambient.
    // Keep in step with the required list in saveBrewOn().
    var step1Missing = [];
    if (!String(onProduct || "").trim()) step1Missing.push("Product");
    if (!String(onTank || "").trim()) step1Missing.push("Bright Tank");
    if (!String(onTotalKg || "").trim()) step1Missing.push("Total KG");
    if (!String(onGrindSize || "").trim()) step1Missing.push("Grind Size");
    if (!String(onRoastDate || "").trim()) step1Missing.push("Roast Date");
    if (!String(onTimePerDose || "").trim()) step1Missing.push("Time/Dose");
    if (!onGrindStartOk) step1Missing.push("Grind Start");
    if (!onGrindFinishOk) step1Missing.push("Grind Finish");
    if (!String(onParticle[0] || "").trim()) step1Missing.push("Particle 0-400");
    if (!String(onParticle[1] || "").trim()) step1Missing.push("Particle 400-800");
    if (!String(onParticle[2] || "").trim()) step1Missing.push("Particle 800+");
    var step1Valid = step1Missing.length === 0;

    var step2Missing = [];
    if (!String(onFillTarget || "").trim()) step2Missing.push("Fill Target");
    if (!String(onTimeOn || "").trim()) step2Missing.push("Time On");
    if (!String(onTargetOff || "").trim()) step2Missing.push("Target Time Off");
    if (!String(onAmbient || "").trim()) step2Missing.push("Ambient");
    if (!String(onTargetTemp || "").trim()) step2Missing.push("Target");
    var step2Valid = step2Missing.length === 0;

    // Step 3 holds Starting Temp only — steps 1 and 2 cover the rest.
    var step3Missing = [];
    if (!String(onStartTemp || "").trim()) step3Missing.push("Starting Temp");
    var step3Valid = step3Missing.length === 0;

    function missingHint(list) {
      if (!list.length) return null;
      return React.createElement("div", {
        style: { fontSize: 11, color: "var(--danger)", fontWeight: 600, textAlign: "center", marginTop: 8 }
      }, "Still needed: " + list.join(", "));
    }

    function selectProduct(p) {
      setOnProduct(p);
      setOnTotalKg(PKG[p] || "34");
      var rcp = recipes.find(function(r) { return r.product === p; });
      if (rcp) {
        if (rcp.grind || rcp.defaultGrind) setOnGrindSize(String(rcp.grind || rcp.defaultGrind));
        if (rcp.fillTargetL) setOnFillTarget(String(rcp.fillTargetL));
        var dw = rcp.doseWeight ? Number(rcp.doseWeight) : (p === "Classic" ? 8.5 : 9);
        var dc = rcp.doseCount ? Number(rcp.doseCount) : (p === "Classic" ? 4 : 6);
        var totalKg = rcp.defaultKg ? Number(rcp.defaultKg) : Number(PKG[p] || 34);
        var doses = [], usedKg = 0;
        for (var di = 0; di < dc; di++) {
          if (di === dc - 1) { doses.push(String(Math.round((totalKg - usedKg) * 10) / 10)); }
          else { doses.push(String(dw)); usedKg += dw; }
        }
        setOnDoses(doses);
        setOnDoseDone(doses.map(function() { return false; }));
      } else {
        var defDw = p === "Classic" ? 8.5 : 9;
        var defDc = p === "Classic" ? 4 : 6;
        var defTotal = Number(PKG[p] || 34);
        var defDoses = [], defUsed = 0;
        for (var dj = 0; dj < defDc; dj++) {
          if (dj === defDc - 1) { defDoses.push(String(Math.round((defTotal - defUsed) * 10) / 10)); }
          else { defDoses.push(String(defDw)); defUsed += defDw; }
        }
        setOnDoses(defDoses);
        setOnDoseDone(defDoses.map(function() { return false; }));
      }
    }

    function confirmableTime(label, value, setValue, confirmed, setConfirmed) {
      return React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 10, padding: "10px 0" } },
        React.createElement("div", { style: { flex: 1, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: confirmed ? "var(--success)" : "var(--ghost)" } }, label),
        React.createElement("input", { className: "input", type: "time", value: value, placeholder: nowT(), onChange: function(e) { setValue(e.target.value); }, style: { width: 140, textAlign: "center", fontSize: 14, fontWeight: 700, padding: "6px 10px" } }),
        React.createElement("div", { style: { width: 32, height: 32, borderRadius: 8, border: confirmed ? "none" : "2px solid var(--rule)", background: confirmed ? "var(--success)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, transition: "all .2s" }, onClick: function() {
          if (!confirmed) { if (!value) setValue(nowT()); setConfirmed(true); }
          else { setConfirmed(false); }
        } },
          confirmed ? React.createElement("svg", { style: { width: 14, height: 14, stroke: "#fff", fill: "none", strokeWidth: 3, strokeLinecap: "round", strokeLinejoin: "round" }, viewBox: "0 0 24 24" },
            React.createElement("polyline", { points: "20 6 9 17 4 12" })
          ) : null
        )
      );
    }

    return React.createElement("div", { className: "scr", style: { animation: "fu .2s ease" } },
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 14 } },
        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flex: 1 }, onClick: function() { setSub(null); } },
          React.createElement(IcoBack, null),
          React.createElement("span", { className: "h2", style: { color: AC.on } }, "Brew On"),
          React.createElement("span", { style: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 13, background: AC.on, fontSize: 10, fontWeight: 800, color: "#fff", letterSpacing: 0.5 } }, userInitials(user.name))
        ),
        React.createElement("button", { style: { background: "none", border: "1.5px solid var(--danger)", borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 700, color: "var(--danger)", cursor: "pointer" }, onClick: function() {
          if (confirm("Cancel this brew on? Progress will be lost.")) {
            setOnInProgress(false); setSub(null);
          }
        } }, "\u2715 Cancel")
      ),
      React.createElement(Steps, { total: 3, current: onStep }),

      // === STEP 1: Product & Grinding ===
      onStep === 1 && React.createElement(React.Fragment, null,
        React.createElement("div", { className: "sect", style: { marginTop: 0 } },
          React.createElement("div", { className: "h2" }, "1. Product & Grinding")
        ),
        // Pre-Grind Checklist (#1)
        React.createElement("div", { className: "rule" },
          React.createElement("div", { className: "fsect-title" }, "Pre-Grind Checklist"),
          [
            "Sanitise grinder, catch tub and buckets",
            "Dial in grind size"
          ].map(function(label, i) {
            var done = onPreGrind[i] || false;
            return React.createElement("div", { key: i, style: { display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: i < 1 ? "1px solid var(--rule-light)" : "none", cursor: "pointer" }, onClick: function() { var c = onPreGrind.slice(); c[i] = !c[i]; setOnPreGrind(c); } },
              React.createElement("div", { style: { width: 24, height: 24, borderRadius: 6, border: done ? "none" : "2px solid var(--rule)", background: done ? "var(--success)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .2s" } },
                done ? React.createElement("svg", { style: { width: 12, height: 12, stroke: "#fff", fill: "none", strokeWidth: 3, strokeLinecap: "round", strokeLinejoin: "round" }, viewBox: "0 0 24 24" },
                  React.createElement("polyline", { points: "20 6 9 17 4 12" })
                ) : null
              ),
              React.createElement("span", { style: { fontSize: 13, fontWeight: 600, color: done ? "var(--success)" : "var(--text)", textDecoration: done ? "line-through" : "none" } }, label)
            );
          })
        ),
        // Product + Brew Date
        React.createElement("div", { style: { display: "flex", gap: 10, marginTop: 12, alignItems: "flex-end" } },
          React.createElement("div", { style: { display: "flex", gap: 8 } },
            ["Classic", "Iced Espresso"].map(function(p) {
              return React.createElement("div", { key: p, className: "popt" + (onProduct === p ? " sel" : ""), style: { background: PC[p], padding: "10px 14px" }, onClick: function() { selectProduct(p); } }, PS[p] || p);
            })
          ),
          React.createElement("div", { className: "ffield", style: { flex: 1 } },
            React.createElement("div", { className: "flabel" }, "Brew Date"),
            React.createElement("input", { className: "input", type: "date", value: toISODate(onBrewDate), onChange: function(e) { setOnBrewDate(fmtDate(e.target.value)); }, style: { fontSize: 13, padding: "8px 10px" } })
          )
        ),
        // Bright Tank selector (#9 — moved to step 1)
        React.createElement("div", { className: "rule" },
          React.createElement("div", { className: "fsect-title" }, "Bright Tank"),
          React.createElement("div", { style: { display: "flex", gap: 8 } },
            TANK_DEF.map(function(t) {
              return React.createElement("div", { key: t.id, className: "bsel" + (onTank === t.id ? " on" : ""), onClick: function() { setOnTank(t.id); } }, t.id);
            })
          )
        ),
        // Total KG + Grind Size
        React.createElement("div", { className: "frow", style: { marginTop: 14 } },
          React.createElement("div", { className: "ffield" },
            React.createElement("div", { className: "flabel" }, "Total KG"),
            React.createElement("input", { className: "input input-mono", type: "number", inputMode: "decimal", value: onTotalKg, onChange: function(e) { setOnTotalKg(e.target.value); } })
          ),
          React.createElement("div", { className: "ffield" },
            React.createElement("div", { className: "flabel" }, "Grind Size"),
            React.createElement("input", { className: "input input-mono", type: "text", inputMode: "decimal", placeholder: "6.4", value: onGrindSize, onChange: function(e) { setOnGrindSize(e.target.value); } })
          )
        ),
        // Roast Date (half width)
        React.createElement("div", { className: "frow" },
          React.createElement("div", { className: "ffield" },
            React.createElement("div", { className: "flabel" }, "Roast Date"),
            React.createElement("input", { className: "input", type: "date", value: toISODate(onRoastDate), onChange: function(e) { setOnRoastDate(fmtDate(e.target.value)); } })
          ),
          React.createElement("div", { className: "ffield" })
        ),
        // Grind Start (confirmable)
        React.createElement("div", { className: "rule" },
          confirmableTime("Grind Start", onGrindStart, setOnGrindStart, onGrindStartOk, setOnGrindStartOk)
        ),
        // Grind Batches
        React.createElement("div", { className: "rule" },
          React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "baseline" } },
            React.createElement("div", { className: "fsect-title", style: { marginBottom: 0 } }, "Grind Batches"),
            React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6 } },
              React.createElement("span", { style: { fontSize: 9, fontWeight: 700, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: 1 } }, "Time/Dose"),
              React.createElement("input", { className: "input", type: "text", inputMode: "numeric", placeholder: "m:ss", value: onTimePerDose, onChange: function(e) {
              var raw = e.target.value.replace(/[^0-9]/g, "");
              if (raw.length <= 1) { setOnTimePerDose(raw); }
              else { setOnTimePerDose(raw.slice(0, -2) + ":" + raw.slice(-2)); }
            }, style: { width: 64, textAlign: "center", fontSize: 13, fontWeight: 700, padding: "4px 6px" } })
            )
          ),
          React.createElement("div", { style: { fontSize: 12, color: "var(--ghost)", marginTop: 6, marginBottom: 8 } }, "Tap \u2713 when ground \u00b7 edit kg to adjust"),
          React.createElement("div", { className: "dgrid" },
            onDoses.map(function(d, i) {
              var done = onDoseDone[i] || false;
              return React.createElement("div", {
                key: i,
                className: "dchip",
                style: done ? { background: "var(--success)", color: "#fff", display: "flex", alignItems: "center", gap: 4, padding: "6px 8px" } : { background: "var(--input-bg)", color: "var(--text)", display: "flex", alignItems: "center", gap: 4, padding: "6px 8px" }
              },
                React.createElement("input", {
                  type: "text", inputMode: "decimal",
                  value: d,
                  onChange: function(e) { var ds = onDoses.slice(); ds[i] = e.target.value; setOnDoses(ds); },
                  style: { width: 36, border: "none", background: "transparent", textAlign: "center", fontSize: 14, fontWeight: 700, color: "inherit", outline: "none", padding: 0, fontFamily: "var(--sans)" }
                }),
                React.createElement("div", {
                  style: { width: 20, height: 20, borderRadius: 4, border: done ? "none" : "1.5px solid " + (done ? "transparent" : "rgba(0,0,0,.15)"), background: done ? "rgba(255,255,255,.3)" : "rgba(0,0,0,.06)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 },
                  onClick: function(e) {
                    e.stopPropagation();
                    var dd = onDoseDone.slice();
                    while (dd.length < onDoses.length) dd.push(false);
                    dd[i] = !dd[i];
                    setOnDoseDone(dd);
                  }
                }, done ? React.createElement("svg", { style: { width: 10, height: 10, stroke: "#fff", fill: "none", strokeWidth: 3, strokeLinecap: "round", strokeLinejoin: "round" }, viewBox: "0 0 24 24" },
                  React.createElement("polyline", { points: "20 6 9 17 4 12" })
                ) : null)
              );
            }),
            React.createElement("div", { className: "dadd", onClick: function() { setOnDoses(onDoses.concat(["8.5"])); setOnDoseDone(onDoseDone.concat([false])); } }, "+")
          ),
          function() {
            var doneKg = 0;
            for (var dk = 0; dk < onDoses.length; dk++) { if (onDoseDone[dk]) doneKg += (parseFloat(onDoses[dk]) || 0); }
            var col = doneKg === 0 ? "var(--ghost)" : Math.abs(doneKg - tgtKg) < 0.1 ? "var(--success)" : "var(--text)";
            return React.createElement("div", { style: { marginTop: 8, fontSize: 14, fontWeight: 700, color: col } }, doneKg.toFixed(1), " / ", tgtKg > 0 ? tgtKg : doseTotal.toFixed(1), " kg");
          }()
        ),
        // Grind Finish (confirmable)
        React.createElement("div", { className: "rule" },
          confirmableTime("Grind Finish", onGrindFinish, setOnGrindFinish, onGrindFinishOk, setOnGrindFinishOk)
        ),
        // Particle Distribution
        React.createElement("div", { className: "rule" },
          React.createElement("div", { className: "fsect-title" }, "Particle Distribution"),
          React.createElement("div", { className: "frow" },
            ["\u2013", "\u2013", "+"].map(function(sep, i) {
              var labels = ["0\u2013400\u00b5m", "400\u2013800\u00b5m", "800+\u00b5m"];
              return React.createElement("div", { className: "ffield", key: i },
                React.createElement("div", { className: "flabel" }, labels[i]),
                React.createElement("input", { className: "input", type: "text", inputMode: "decimal", placeholder: "g", value: onParticle[i], onChange: function(e) { var p = onParticle.slice(); p[i] = e.target.value; setOnParticle(p); } })
              );
            })
          )
        ),
        // Continue
        React.createElement("div", { className: "btn-row" },
          React.createElement("button", {
            className: "btn btn-blue",
            style: { opacity: step1Valid ? 1 : 0.4, pointerEvents: step1Valid ? "auto" : "none" },
            onClick: function() {
              if (step1Valid) {
                if (!onTimeOn) setOnTimeOn(nowT());
                if (!onTargetOff) {
                  var brewHrs = onProduct === "Iced Espresso" ? 19 : 17;
                  var d = new Date(); d.setHours(d.getHours() + brewHrs);
                  setOnTargetOff(String(d.getHours()).padStart(2,"0") + ":" + String(d.getMinutes()).padStart(2,"0"));
                }
                setOnStep(2);
              }
            }
          }, "Continue ", React.createElement(IcoArrow, null))
        ),
        missingHint(step1Missing)
      ),

      // === STEP 2: Brewing ===
      onStep === 2 && React.createElement(React.Fragment, null,
        React.createElement("div", { className: "sect", style: { marginTop: 0 } },
          React.createElement("div", { className: "h2" }, "2. Brewing")
        ),
        React.createElement("div", { className: "frow", style: { marginTop: 12 } },
          React.createElement("div", { className: "ffield" },
            React.createElement("div", { className: "flabel" }, "Fill Target (L)"),
            React.createElement("input", { className: "input input-mono", type: "number", inputMode: "numeric", placeholder: "420", value: onFillTarget, onChange: function(e) { setOnFillTarget(e.target.value); } })
          )
        ),
        React.createElement("div", { className: "frow", style: { gap: 16 } },
          React.createElement("div", { className: "ffield" },
            React.createElement("div", { className: "flabel" }, "Time On"),
            React.createElement("input", { className: "input", type: "time", value: onTimeOn, onChange: function(e) {
              var v = e.target.value;
              setOnTimeOn(v);
              if (v) {
                var parts = v.split(":");
                var brewHrs = onProduct === "Iced Espresso" ? 19 : 17;
                var h = (parseInt(parts[0]) || 0) + brewHrs;
                var m = parseInt(parts[1]) || 0;
                if (h >= 24) h -= 24;
                setOnTargetOff(String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0"));
              }
            } })
          ),
          React.createElement("div", { className: "ffield" },
            React.createElement("div", { className: "flabel" }, "Target Time Off"),
            React.createElement("input", { className: "input", type: "time", value: onTargetOff, onChange: function(e) { setOnTargetOff(e.target.value); } })
          )
        ),
        React.createElement("div", { className: "rule" },
          React.createElement("div", { className: "fsect-title" }, "Temps"),
          React.createElement("div", { className: "frow" },
            React.createElement("div", { className: "ffield" },
              React.createElement("div", { className: "flabel" }, "Ambient"),
              React.createElement("input", { className: "input input-mono", type: "text", inputMode: "decimal", placeholder: "\u00b0C", value: onAmbient, onChange: function(e) { setOnAmbient(e.target.value); } })
            ),
            React.createElement("div", { className: "ffield" },
              React.createElement("div", { className: "flabel" }, "Target"),
              React.createElement("input", { className: "input input-mono", type: "text", inputMode: "decimal", value: onTargetTemp, onChange: function(e) { setOnTargetTemp(e.target.value); } })
            )
          )
        ),
        React.createElement("div", { className: "btn-row" },
          React.createElement("button", { className: "btn btn-out", onClick: function() { setOnStep(1); } },
            React.createElement(IcoBack, null), " Back"
          ),
          React.createElement("button", {
            className: "btn btn-blue",
            style: { opacity: step2Valid ? 1 : 0.4, pointerEvents: step2Valid ? "auto" : "none" },
            onClick: function() { if (step2Valid) setOnStep(3); }
          }, "Continue ", React.createElement(IcoArrow, null))
        ),
        missingHint(step2Missing)
      ),

      // === STEP 3: Review & Save ===
      onStep === 3 && React.createElement(React.Fragment, null,
        React.createElement("div", { className: "sect", style: { marginTop: 0 } },
          React.createElement("div", { className: "h2" }, "3. Review & Save")
        ),
        // Photos
        React.createElement("div", { className: "rule" },
          React.createElement("div", { className: "fsect-title" }, "Photos"),
          React.createElement("div", { style: { display: "flex", gap: 10, marginTop: 6 } },
            [["Flow Meter", onPhotoFlow, setOnPhotoFlow], ["Filled Tank", onPhotoTank, setOnPhotoTank]].map(function(p) {
              var label = p[0], done = p[1], setDone = p[2];
              return React.createElement("div", { key: label, style: { flex: 1, border: done ? "2px solid var(--success)" : "2px dashed var(--rule)", borderRadius: 10, padding: "16px 10px", textAlign: "center", cursor: "pointer", background: done ? "rgba(42,139,106,.06)" : "transparent", transition: "all .2s" }, onClick: function() { setDone(!done); } },
                done ? React.createElement("svg", { style: { width: 28, height: 28, stroke: "var(--success)", fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", marginBottom: 4 }, viewBox: "0 0 24 24" },
                  React.createElement("polyline", { points: "20 6 9 17 4 12" })
                ) : React.createElement("svg", { style: { width: 28, height: 28, stroke: "var(--ghost)", fill: "none", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round", marginBottom: 4 }, viewBox: "0 0 24 24" },
                  React.createElement("path", { d: "M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" }),
                  React.createElement("circle", { cx: "12", cy: "13", r: "4" })
                ),
                React.createElement("div", { style: { fontSize: 11, fontWeight: 700, color: done ? "var(--success)" : "var(--text)" } }, label),
                React.createElement("div", { style: { fontSize: 9, color: "var(--ghost)", marginTop: 2 } }, done ? "Done" : "Tap to capture")
              );
            })
          )
        ),
        // Finish Checklist
        React.createElement("div", { className: "rule" },
          React.createElement("div", { className: "fsect-title" }, "Finish Checklist"),
          React.createElement("div", { style: { marginTop: 6 } },
            ['Rake OFF', 'Glycol ON for Lauter Tun', 'Lauter Tun gas flushed', 'All valves closed', 'Filters sanitised', 'Bright tank sanitised & closed', 'Filter stand & hose setup', 'UV Filter OFF'].map(function(label, i) {
              var done = onChecklist[i] || false;
              return React.createElement("div", { key: i, style: { display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: i < 7 ? "1px solid var(--rule-light)" : "none", cursor: "pointer" }, onClick: function() { var c = onChecklist.slice(); c[i] = !c[i]; setOnChecklist(c); } },
                React.createElement("div", { style: { width: 24, height: 24, borderRadius: 6, border: done ? "none" : "2px solid var(--rule)", background: done ? "var(--success)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .2s" } },
                  done ? React.createElement("svg", { style: { width: 12, height: 12, stroke: "#fff", fill: "none", strokeWidth: 3, strokeLinecap: "round", strokeLinejoin: "round" }, viewBox: "0 0 24 24" },
                    React.createElement("polyline", { points: "20 6 9 17 4 12" })
                  ) : null
                ),
                React.createElement("span", { style: { fontSize: 13, color: done ? "var(--success)" : "var(--text)", textDecoration: done ? "line-through" : "none" } }, label)
              );
            })
          )
        ),
        // Starting Temp (taken at the end, after brew is running)
        React.createElement("div", { className: "rule" },
          React.createElement("div", { className: "fsect-title" }, "Starting Temp"),
          React.createElement("div", { style: { fontSize: 11, color: "var(--ghost)", marginBottom: 8 } }, "Record the tank temp once brew is running"),
          React.createElement("div", { className: "frow" },
            React.createElement("div", { className: "ffield", style: { maxWidth: 140 } },
              React.createElement("div", { className: "flabel" }, "Starting \u00b0C"),
              React.createElement("input", { className: "input input-mono", type: "text", inputMode: "decimal", placeholder: "\u00b0C", value: onStartTemp, onChange: function(e) { setOnStartTemp(e.target.value); } })
            )
          )
        ),
        // Summary
        React.createElement("div", { className: "rule" },
          React.createElement("div", { className: "fsect-title" }, "Summary"),
          [["Product", onProduct], ["Brew Date", onBrewDate], ["Total KG", onTotalKg + " kg"], ["Grind Size", onGrindSize], ["Roast Date", onRoastDate], ["Grind Start", onGrindStart], ["Grind Finish", onGrindFinish], ["Time/Dose", onTimePerDose], ["Tank", onTank], ["Fill Target", onFillTarget + " L"], ["Time On", onTimeOn], ["Target Off", onTargetOff], ["Ambient", onAmbient + "\u00b0C"], ["Target", onTargetTemp + "\u00b0C"], ["Starting", onStartTemp ? onStartTemp + "\u00b0C" : ""]].map(function(pair) {
            return React.createElement("div", { key: pair[0], style: { display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--rule-light)" } },
              React.createElement("span", { style: { fontSize: 12, color: "var(--dim)" } }, pair[0]),
              React.createElement("span", { style: { fontSize: 13, fontWeight: 600 } }, pair[1])
            );
          })
        ),
        React.createElement("div", { className: "btn-row" },
          React.createElement("button", { className: "btn btn-out", onClick: function() { setOnStep(2); } },
            React.createElement(IcoBack, null), " Back"
          ),
          React.createElement("button", {
            className: "btn",
            style: { background: AC.on, color: "#fff", opacity: (syncing || !step3Valid) ? 0.4 : 1, pointerEvents: (syncing || !step3Valid) ? "none" : "auto" },
            onClick: syncing ? null : saveBrewOn
          }, syncing ? React.createElement(Spinner, null) : React.createElement(React.Fragment, null, React.createElement(IcoCheck, null), " Save Brew On"))
        ),
        missingHint(step3Missing)
      )
    );
  }

  // ============================================================
  // BREW OFF FLOW
  // ============================================================
  function renderBrewOff() {
    const prod = offProd(),
      needsDil = offNeedsDil(),
      tgt = offTgt();
    const cA = calcAvg(offConc),
      fL = parseFloat(offFL) || 0,
      ft = offFT(),
      water = Math.max(0, ft - fL);
    const estTds = fL > 0 && cA ? fL * cA / ft : null;
    const concFull = offConc.filter(v => v).length === 5;
    return /*#__PURE__*/React.createElement("div", {
      className: "scr",
      style: {
        animation: "fu .2s ease"
      }
    },
    // BREW OFF banner
    React.createElement("div", {
      style: { background: AC.off, borderRadius: 12, padding: "14px 16px", marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }
    },
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 10 } },
        React.createElement("span", { style: { fontSize: 18, fontWeight: 700, color: "#fff", letterSpacing: 0.5 } }, "BREW OFF"),
        React.createElement("span", { style: { display: "inline-block", padding: "2px 10px", borderRadius: 100, background: PC[prod] || "rgba(255,255,255,.25)", color: "#fff", fontSize: 12, fontWeight: 700 } }, prod),
        React.createElement("span", { style: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 13, background: "rgba(255,255,255,.25)", fontSize: 10, fontWeight: 800, color: "#fff", letterSpacing: 0.5 } }, userInitials(user.name))
      ),
      activeBrew() && activeBrew().brightTank && React.createElement("span", { style: { fontSize: 14, color: "rgba(255,255,255,.7)" } }, activeBrew().brightTank)
    ),
    // Back + Save & Close + Cancel row
    React.createElement("div", {
      style: { display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }
    },
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, cursor: "pointer", flex: 1 }, onClick: function() { setSub(null); } },
        React.createElement(IcoBack, null), React.createElement("span", { style: { fontSize: 13, color: "var(--dim)" } }, "Brewing")
      ),
      React.createElement("button", { style: { background: "none", border: "1.5px solid var(--blue)", borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 700, color: "var(--blue)", cursor: "pointer", marginRight: 6 }, onClick: function() {
        var ab2 = activeBrew();
        if (!ab2) { toast("No active brew to save", "var(--danger)"); return; }
        setSyncing(true);
        api.post("updateBrewLog", { data: {
          brewId: ab2.brewId,
          ltEndTempStart: offLtS || undefined,
          ltEndTempLow: offLtL || undefined,
          btEndTempStart: offBtS || undefined,
          btEndTempLow: offBtL || undefined,
          filterStart: offFS || undefined,
          filterFinish: offFF || undefined,
          pumpSpeedStart: offPS || undefined,
          pumpSpeedEnd: offPE || undefined,
          filteredLitres: offFL || undefined,
          loggedBy: user.name
        } }).then(function(res) {
          setSyncing(false);
          if (res.error) { toast(res.error, "var(--danger)"); return; }
          toast("Progress saved", "var(--success)");
          setSub(null);
          loadData();
        }).catch(function(err) { setSyncing(false); toast("Error: " + err.message, "var(--danger)"); });
      } }, "Save & Close"),
      React.createElement("button", { style: { background: "none", border: "1.5px solid var(--danger)", borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 700, color: "var(--danger)", cursor: "pointer" }, onClick: function() {
        if (confirm("Cancel this brew off? Progress will be lost.")) {
          setOffInProgress(false); setSub(null);
        }
      } }, "\u2715 Cancel")
    ),
    /*#__PURE__*/React.createElement(Steps, {
      total: 3,
      current: offStep
    }), offStep === 1 && /*#__PURE__*/React.createElement(React.Fragment, null,

    // --- UNIFIED START | FINISH two-column layout ---
    React.createElement("div", { style: { display: "flex", gap: 0, marginTop: 14 } },
      // LEFT: START column
      React.createElement("div", { style: { flex: 1, paddingRight: 14, borderRight: "1.5px solid var(--rule)" } },
        React.createElement("div", { className: "h2", style: { textAlign: "center", marginBottom: 16 } }, "Start"),

        // Filter Time
        React.createElement("div", { style: { marginBottom: 16 } },
          React.createElement("div", { style: { fontSize: 13, fontWeight: 700, letterSpacing: 0.3, marginBottom: 6 } }, "Filter Time"),
          React.createElement("input", {
            className: "input",
            type: "time",
            style: { textAlign: "center", fontSize: 18, fontWeight: 700, padding: "10px 6px", width: "100%" },
            value: offFS,
            onChange: function(e) {
              var v = e.target.value;
              setOffFS(v);
              if (v) {
                var parts = v.split(":");
                var h = parseInt(parts[0]) || 0;
                var filterMins = prod === "Iced Espresso" ? 60 : 45;
                var m = (parseInt(parts[1]) || 0) + filterMins;
                if (m >= 60) { h += Math.floor(m / 60); m = m % 60; }
                if (h >= 24) h -= 24;
                setOffFF(String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0"));
              }
            }
          })
        ),

        // Lauter Tun Temp
        React.createElement("div", { style: { marginBottom: 16 } },
          React.createElement("div", { style: { fontSize: 13, fontWeight: 700, letterSpacing: 0.3, marginBottom: 6 } }, "Lauter Tun (\u00b0C)"),
          React.createElement("input", {
            className: "input input-mono",
            type: "text",
            inputMode: "decimal",
            placeholder: "\u00b0C",
            style: { padding: 10, fontSize: 20, textAlign: "center", width: "100%" },
            value: offLtS,
            onChange: function(e) { setOffLtS(e.target.value); }
          })
        ),

        // Bright Tank Temp
        React.createElement("div", { style: { marginBottom: 16 } },
          React.createElement("div", { style: { fontSize: 13, fontWeight: 700, letterSpacing: 0.3, marginBottom: 6 } }, "Bright Tank (\u00b0C)"),
          React.createElement("input", {
            className: "input input-mono",
            type: "text",
            inputMode: "decimal",
            placeholder: "\u00b0C",
            style: { padding: 10, fontSize: 20, textAlign: "center", width: "100%" },
            value: offBtS,
            onChange: function(e) { setOffBtS(e.target.value); }
          })
        ),

        // Pump Speed
        React.createElement("div", null,
          React.createElement("div", { style: { fontSize: 13, fontWeight: 700, letterSpacing: 0.3, marginBottom: 6 } }, "Pump Speed"),
          React.createElement("input", {
            className: "input input-mono",
            type: "number",
            inputMode: "decimal",
            style: { padding: 10, fontSize: 20, textAlign: "center", width: "100%" },
            value: offPS,
            onChange: function(e) { setOffPS(e.target.value); }
          })
        )
      ),
      // RIGHT: FINISH column
      React.createElement("div", { style: { flex: 1, paddingLeft: 14 } },
        React.createElement("div", { className: "h2", style: { textAlign: "center", marginBottom: 16 } }, "Finish"),

        // Filter Time
        React.createElement("div", { style: { marginBottom: 16 } },
          React.createElement("div", { style: { fontSize: 13, fontWeight: 700, letterSpacing: 0.3, marginBottom: 6 } }, "Filter Time"),
          React.createElement("input", {
            className: "input",
            type: "time",
            style: { textAlign: "center", fontSize: 18, fontWeight: 700, padding: "10px 6px", width: "100%" },
            value: offFF,
            onChange: function(e) { setOffFF(e.target.value); }
          })
        ),

        // Lauter Tun Temp
        React.createElement("div", { style: { marginBottom: 16 } },
          React.createElement("div", { style: { fontSize: 13, fontWeight: 700, letterSpacing: 0.3, marginBottom: 6 } }, "Lauter Tun (\u00b0C)"),
          React.createElement("input", {
            className: "input input-mono",
            type: "text",
            inputMode: "decimal",
            placeholder: "\u00b0C",
            style: { padding: 10, fontSize: 20, textAlign: "center", width: "100%" },
            value: offLtL,
            onChange: function(e) { setOffLtL(e.target.value); }
          })
        ),

        // Bright Tank Temp
        React.createElement("div", { style: { marginBottom: 16 } },
          React.createElement("div", { style: { fontSize: 13, fontWeight: 700, letterSpacing: 0.3, marginBottom: 6 } }, "Bright Tank (\u00b0C)"),
          React.createElement("input", {
            className: "input input-mono",
            type: "text",
            inputMode: "decimal",
            placeholder: "\u00b0C",
            style: { padding: 10, fontSize: 20, textAlign: "center", width: "100%" },
            value: offBtL,
            onChange: function(e) { setOffBtL(e.target.value); }
          })
        ),

        // Pump Speed
        React.createElement("div", null,
          React.createElement("div", { style: { fontSize: 13, fontWeight: 700, letterSpacing: 0.3, marginBottom: 6 } }, "Pump Speed"),
          React.createElement("input", {
            className: "input input-mono",
            type: "number",
            inputMode: "decimal",
            style: { padding: 10, fontSize: 20, textAlign: "center", width: "100%" },
            value: offPE,
            onChange: function(e) { setOffPE(e.target.value); }
          })
        )
      )
    ),

    // --- FILTERED LITRES (bottom) ---
    React.createElement("div", { style: { marginTop: 20 } },
      React.createElement("div", { className: "flabel" }, "Filtered Litres in Bright Tank"),
      React.createElement("input", {
        className: "input input-mono",
        type: "number",
        inputMode: "numeric",
        value: offFL,
        onChange: function(e) { setOffFL(e.target.value); }
      })
    ),

    /*#__PURE__*/React.createElement("div", {
      className: "btn-row"
    }, /*#__PURE__*/React.createElement("button", {
      className: "btn btn-blue",
      style: {
        opacity: fL > 0 ? 1 : .4,
        pointerEvents: fL > 0 ? "auto" : "none"
      },
      onClick: () => setOffStep(2)
    }, "Continue ", /*#__PURE__*/React.createElement(IcoArrow, null)))), offStep === 2 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "sect",
      style: {
        marginTop: 0
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "h2"
    }, "2. QC")), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 14px",
        background: "var(--input-bg)",
        borderRadius: 8,
        marginBottom: 16
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12,
        color: "var(--dim)",
        fontWeight: 600
      }
    }, "Filtered Litres in Tank"), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 20,
        fontWeight: 800
      }
    }, fL)), /*#__PURE__*/React.createElement("div", {
      className: "rule"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fsect-title"
    }, "TDS \u2014 Concentrate"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6,
        marginTop: 8
      }
    }, offConc.map(function(v, i) {
      var numV = parseFloat(v);
      var outOfRange = v && !isNaN(numV) && tgt && Math.abs(numV - tgt) > 0.50;
      return React.createElement("input", {
        key: i,
        className: "input",
        type: "text",
        inputMode: "numeric",
        maxLength: "4",
        style: {
          textAlign: "center",
          fontWeight: 700,
          flex: 1,
          fontSize: 14,
          border: outOfRange ? "2px solid var(--danger)" : "2px solid transparent",
          background: outOfRange ? "rgba(196,62,42,.12)" : "var(--input-bg)",
          color: outOfRange ? "var(--danger)" : "var(--text)"
        },
        placeholder: "",
        value: v,
        onChange: function(e) {
          var f = tdsF(e.target.value),
            a = offConc.slice();
          a[i] = f;
          setOffConc(a);
          if (f.replace(/\./g, "").length >= 3 && i < 4) {
            var nx = e.target.parentElement.children[i + 1];
            if (nx) nx.focus();
          }
        }
      });
    })), function() {
      var anyOutOfRange = offConc.some(function(v) { var n = parseFloat(v); return v && !isNaN(n) && tgt && Math.abs(n - tgt) > 0.50; });
      return React.createElement(React.Fragment, null,
        anyOutOfRange && React.createElement("div", { style: { fontSize: 11, color: "var(--danger)", fontWeight: 600, marginTop: 6, textAlign: "center" } }, "Outside expected range \u2014 test again"),
        React.createElement("div", {
          className: "tdsavg",
          style: anyOutOfRange ? { background: "var(--danger)" } : {}
        }, React.createElement("div", null, React.createElement("div", {
          className: "lbl",
          style: { color: "rgba(255,255,255,.5)", fontSize: 8 }
        }, "Average"), React.createElement("div", {
          style: { fontSize: 22, fontWeight: 700 }
      }, cA ? cA.toFixed(2) : "—")),
      anyOutOfRange && React.createElement("div", { style: { fontSize: 10, color: "#fff", marginLeft: 8, maxWidth: 120, lineHeight: 1.3 } }, "\xb10.50 of ", tgt.toFixed(2))
        )
      );
    }()), needsDil && /*#__PURE__*/React.createElement("div", {
      className: "rule"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fsect-title"
    }, "Dilution"), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10,
        background: concFull ? "var(--blue)" : "var(--input-bg)",
        borderRadius: 8,
        padding: 16,
        transition: "background .3s"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between"
      }
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: "lbl",
      style: {
        color: concFull ? "rgba(255,255,255,.5)" : "var(--ghost)",
        fontSize: 8,
        marginBottom: 2
      }
    }, "Add Water"), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 22,
        fontWeight: 800,
        color: concFull ? "#fff" : "var(--ghost)"
      }
    }, water > 0 ? water : "—"), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 14,
        color: "var(--ghost)",
        marginLeft: 4
      }
    }, "litres")), /*#__PURE__*/React.createElement("div", {
      style: {
        textAlign: "right"
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "lbl",
      style: {
        color: concFull ? "rgba(255,255,255,.5)" : "var(--ghost)",
        fontSize: 8,
        marginBottom: 2
      }
    }, "Target Fill"), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 32,
        fontWeight: 800,
        color: concFull ? "#fff" : "var(--ghost)"
      }
    }, ft, "L")))), estTds && /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10,
        padding: "12px 14px",
        background: "var(--input-bg)",
        borderRadius: 8
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between"
      }
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: "lbl",
      style: {
        color: "var(--ghost)",
        fontSize: 8,
        marginBottom: 4
      }
    }, "Est. TDS"), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 20,
        fontWeight: 800,
        color: Math.abs(estTds - tgt) <= .10 ? "var(--success)" : Math.abs(estTds - tgt) <= .20 ? "#FF9800" : "var(--danger)"
      }
    }, estTds.toFixed(2))), /*#__PURE__*/React.createElement("div", {
      style: {
        textAlign: "right"
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "lbl",
      style: {
        color: "var(--ghost)",
        fontSize: 8,
        marginBottom: 4
      }
    }, "Target TDS"), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 20,
        fontWeight: 800
      }
    }, tgt.toFixed(2))))), concFull && /*#__PURE__*/React.createElement(ConfirmCheck, {
      checked: offWaterOk,
      onClick: () => setOffWaterOk(!offWaterOk),
      label: "Water added"
    }), offWaterOk && /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 16
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "fsect-title"
    }, "TDS \u2014 Diluted"), /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 10,
        color: "var(--ghost)",
        marginBottom: 8
      }
    }, "Take 5 new readings after dilution"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6
      }
    }, offDil.map(function(v, i) {
      var numV = parseFloat(v);
      var outOfRange = v && !isNaN(numV) && tgt && Math.abs(numV - tgt) > 0.50;
      return React.createElement("input", {
        key: i,
        className: "input",
        type: "text",
        inputMode: "numeric",
        maxLength: "4",
        style: {
          textAlign: "center",
          fontWeight: 700,
          flex: 1,
          fontSize: 14,
          border: outOfRange ? "2px solid var(--danger)" : "2px solid transparent",
          background: outOfRange ? "rgba(196,62,42,.12)" : "var(--input-bg)",
          color: outOfRange ? "var(--danger)" : "var(--text)"
        },
        value: v,
        onChange: function(e) {
          var f = tdsF(e.target.value),
            a = offDil.slice();
          a[i] = f;
          setOffDil(a);
          if (f.replace(/\./g, "").length >= 3 && i < 4) {
            var nx = e.target.parentElement.children[i + 1];
            if (nx) nx.focus();
          }
        }
      });
    })), (function() {
      var dA = calcAvg(offDil);
      var dilOutOfRange = offDil.some(function(v) { var n = parseFloat(v); return v && !isNaN(n) && tgt && Math.abs(n - tgt) > 0.50; });
      return /*#__PURE__*/React.createElement("div", {
        className: "tdsavg",
        style: {
          background: dilOutOfRange ? "var(--danger)" : "var(--success)",
          marginTop: 10
        }
      }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
        className: "lbl",
        style: {
          color: "rgba(255,255,255,.5)",
          fontSize: 8
        }
      }, "Average"), /*#__PURE__*/React.createElement("div", {
        style: {
          fontSize: 22,
          fontWeight: 700
        }
      }, dA ? dA.toFixed(2) : "—")), dilOutOfRange ? React.createElement("div", {
        style: { fontSize: 10, color: "#fff", marginLeft: 8, maxWidth: 120, lineHeight: 1.3 }
      }, "TDS outside expected range (\xb10.50 of ", tgt.toFixed(2), ")") : /*#__PURE__*/React.createElement("div", {
        style: {
          marginLeft: "auto"
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "lbl",
        style: {
          color: "rgba(255,255,255,.5)",
          fontSize: 8
        }
      }, "Target"), /*#__PURE__*/React.createElement("div", {
        style: {
          fontSize: 22,
          fontWeight: 700
        }
      }, tgt.toFixed(2))));
    })())), !needsDil && /*#__PURE__*/React.createElement("div", {
      className: "rule"
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10,
        padding: "12px 16px",
        background: "var(--input-bg)",
        borderRadius: 8,
        fontSize: 13,
        color: "var(--dim)",
        textAlign: "center",
        fontWeight: 600
      }
    }, "No dilution for Iced Espresso")), /*#__PURE__*/React.createElement("div", {
      className: "rule"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fsect-title"
    }, "Preservatives"),
    function() {
      var nagRate = parseFloat(offNagardoRate) || 0;
      var sorRate = parseFloat(offSorbateRate) || 0;
      var preservL = needsDil ? ft : fL; // Iced Espresso uses filtered litres (no dilution)
      var nagCalc = (preservL * nagRate).toFixed(1);
      var sorCalc = (preservL * sorRate).toFixed(1);

      function preservRow(name, rate, calcGrams, ok, setOk) {
        return React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 12, padding: "14px 0", borderBottom: "1px solid var(--rule-light)", cursor: "pointer" }, onClick: function() { setOk(!ok); } },
          React.createElement("div", { style: { width: 28, height: 28, borderRadius: 6, border: ok ? "none" : "2px solid var(--rule)", background: ok ? "var(--success)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .2s" } },
            ok ? React.createElement(TickSvg, { size: 12 }) : null
          ),
          React.createElement("div", { style: { flex: 1 } },
            React.createElement("div", { style: { fontSize: 14, fontWeight: 700, color: ok ? "var(--success)" : "var(--text)" } }, name),
            React.createElement("div", { style: { fontSize: 12, color: "var(--ghost)", marginTop: 2 } }, "Rate ", React.createElement("span", { style: { fontWeight: 700, color: "var(--dim)" } }, rate), " g/L")
          ),
          React.createElement("div", { style: { textAlign: "right" } },
            React.createElement("div", { style: { fontSize: 22, fontWeight: 800, color: ok ? "var(--success)" : "var(--text)" } }, calcGrams),
            React.createElement("div", { style: { fontSize: 9, fontWeight: 700, color: "var(--ghost)", letterSpacing: 1, textTransform: "uppercase" } }, "grams")
          )
        );
      }

      return React.createElement("div", { style: { marginTop: 10 } },
        React.createElement("div", { style: { fontSize: 11, color: "var(--ghost)", marginBottom: 4 } }, "Based on ", React.createElement("strong", { style: { color: "var(--dim)" } }, preservL, "L"), needsDil ? " (diluted)" : " (undiluted)"),
        preservRow("Nagardo", offNagardoRate, nagCalc, offNagardoOk, setOffNagardoOk),
        preservRow("Sorbate", offSorbateRate, sorCalc, offSorbateOk, setOffSorbateOk)
      );
    }()), (() => {
      let ready = concFull && offNagardoOk && offSorbateOk;
      if (needsDil) ready = ready && offWaterOk && offDil.filter(v => v).length === 5;
      return /*#__PURE__*/React.createElement("div", {
        className: "btn-row"
      }, /*#__PURE__*/React.createElement("button", {
        className: "btn btn-out",
        onClick: () => setOffStep(1)
      }, /*#__PURE__*/React.createElement(IcoBack, null), " Back"), /*#__PURE__*/React.createElement("button", {
        className: "btn btn-blue",
        style: {
          opacity: ready ? 1 : .4,
          pointerEvents: ready ? "auto" : "none"
        },
        onClick: () => setOffStep(3)
      }, "Continue ", /*#__PURE__*/React.createElement(IcoArrow, null)));
    })()), offStep === 3 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "sect",
      style: {
        marginTop: 0
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "h2"
    }, "3. Tag & Save")), /*#__PURE__*/React.createElement("div", {
      className: "rule"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fsect-title"
    }, "Keg Tag"), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 10
      }
    }, ["Solid", "Stripe"].map(t => /*#__PURE__*/React.createElement("div", {
      key: t,
      className: "tag" + (offTag === t ? " sel" : ""),
      style: {
        background: PC[prod] || "var(--blue)",
        opacity: offTag === t ? 1 : .5
      },
      onClick: () => setOffTag(t)
    }, /*#__PURE__*/React.createElement("span", {
      className: "tag-t"
    }, t), t === "Stripe" && /*#__PURE__*/React.createElement("div", {
      style: { position: "absolute", top: "50%", left: 0, right: 0, height: 6, background: "#fff", opacity: 0.6, transform: "translateY(-50%)", pointerEvents: "none" }
    }))))), /*#__PURE__*/React.createElement("div", {
      className: "frow",
      style: {
        marginTop: 16
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "ffield"
    }, /*#__PURE__*/React.createElement("div", {
      className: "flabel"
    }, "Best Before ", React.createElement("span", { style: { fontWeight: 400, opacity: 0.6, letterSpacing: 0 } }, "(10 weeks from brew date)")), /*#__PURE__*/React.createElement("input", {
      className: "input",
      type: "date",
      value: toISODate(offBB),
      onChange: e => setOffBB(fmtDate(e.target.value))
    }))), /*#__PURE__*/React.createElement("div", {
      className: "frow"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ffield"
    }, /*#__PURE__*/React.createElement("div", {
      className: "flabel"
    }, "Flavour Notes"), /*#__PURE__*/React.createElement("textarea", {
      className: "input",
      style: {
        minHeight: 60,
        resize: "vertical",
        fontFamily: "var(--sans)",
        fontSize: 13
      },
      placeholder: "choc, caramel, almond\u2026",
      value: offFlavour,
      onChange: e => setOffFlavour(e.target.value)
    }))), /*#__PURE__*/React.createElement("div", {
      className: "btn-row"
    }, /*#__PURE__*/React.createElement("button", {
      className: "btn btn-out",
      onClick: () => setOffStep(2)
    }, /*#__PURE__*/React.createElement(IcoBack, null), " Back"), /*#__PURE__*/React.createElement("button", {
      className: "btn",
      style: {
        background: AC.off,
        color: "#fff",
        opacity: syncing ? .5 : 1
      },
      onClick: syncing ? null : saveBrewOff
    }, syncing ? /*#__PURE__*/React.createElement(Spinner, null) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(IcoCheck, null), " Save Brew Off")))));
  }

  // ============================================================
  // KEGS DEFAULT
  // ============================================================
  function renderKegsDefault() {
    return React.createElement("div", { className: "scr", style: { animation: "fu .2s ease" } },
      React.createElement("div", { style: { marginBottom: 10 } },
        React.createElement("div", { style: { display: "flex", gap: 10 } },
          React.createElement("button", { className: "btn btn-blue", style: { flex: 1 }, onClick: function() {
            // If there's an in-memory active session, just navigate back into it (don't wipe)
            if (fkTank) { setSub("fillKegs"); return; }
            // If a session is persisted but not currently in memory, restore it
            var saved = loadFillSession();
            if (saved && saved.tankId) {
              var ts = tankState.find(function(t) { return t.tankId === saved.tankId; });
              if (ts && ts.product) {
                resumeFillSession(saved);
                return;
              }
            }
            // No active or saved session — fresh start
            setSub("fillKegs");
            setFkTank(null); setFkScanned([]); setFk18(""); setFk9(""); setFk7(""); setFkRemainOverride(""); setFkLastKegPartial(null); setFkLastKegScannedId("");
            setFkChecks([false,false,false,false,false]); setFkLimited(null);
            setFkLtd18(""); setFkLtd9(""); setFkLtdScanned([]);
            setFkLtdChecks([false,false,false,false,false]); setFkRecipeConfirmed(false);
            setFkLtdTag(""); setFkPhase("setup"); setFkPhotoCalcs(false);
            setFkLtdPhotoCalcs(false); setFkDrainOpen(false); setFkDrainReason("");
          } }, React.createElement(IcoBrew, null), " Fill Kegs"),
          React.createElement("button", { className: "btn", style: { flex: 1, background: "var(--text)", color: "#fff" }, onClick: function() {
            setSub("moveKegs"); setMvKeg(null); setMvDest(null); setMvDigits(""); setMvSize("18"); setMvNotes("");
            setMvTab("move"); setMvTfKind(null); setMvTfDests([]); setMvTfResidual(null); setMvTfProduct(null);
          } },
            React.createElement("svg", { className: "ico", style: { stroke: "#fff" }, viewBox: "0 0 24 24" },
              React.createElement("polyline", { points: "15 3 21 3 21 9" }),
              React.createElement("line", { x1: 21, y1: 3, x2: 14, y2: 10 }),
              React.createElement("polyline", { points: "9 21 3 21 3 15" }),
              React.createElement("line", { x1: 3, y1: 21, x2: 10, y2: 14 })
            ), " Move Kegs"
          )
        ),
        React.createElement("button", { className: "btn btn-out", style: { width: "100%", marginTop: 8, fontSize: 13, padding: "10px 0" }, onClick: function() {
          setSub("fillLog"); setFlFilter("week"); setFlExpanded(null);
        } },
          React.createElement("svg", { className: "ico", viewBox: "0 0 24 24" },
            React.createElement("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" }),
            React.createElement("polyline", { points: "14 2 14 8 20 8" }),
            React.createElement("line", { x1: 8, y1: 13, x2: 16, y2: 13 }),
            React.createElement("line", { x1: 8, y1: 17, x2: 16, y2: 17 }),
            React.createElement("line", { x1: 8, y1: 9, x2: 11, y2: 9 })
          ), " View Fill Log"
        )
        ,
        (user && user.username === "brendan") && React.createElement("button", {
          className: "btn btn-out",
          style: { width: "100%", marginTop: 8, fontSize: 13, padding: "10px 0", borderColor: "var(--blue)", color: "var(--blue)" },
          onClick: function() { setSub("reconcile"); loadReconcile(); }
        },
          React.createElement("svg", { className: "ico", viewBox: "0 0 24 24" },
            React.createElement("path", { d: "M21 12a9 9 0 1 1-3-6.7" }),
            React.createElement("polyline", { points: "21 3 21 9 15 9" })
          ), " Reconcile KOH \u2194 Register"
        )
      ),
      React.createElement("div", { className: "sect", style: { marginTop: 0 } },
        React.createElement("div", { className: "h2" }, "Keg Fleet")
      ),
      function() {
        var fridge18 = 0, fridge9 = 0;
        var fs = fridgeSummary();
        Object.keys(fs).forEach(function(p) { fridge18 += fs[p]["18L"]; fridge9 += fs[p]["9L"] + (fs[p]["7L"] || 0); });
        var fleetRows = [
          { label: "At Customer", status: "At Customer", sub: "Currently deployed", color: "var(--text)", n18: kegFleetCounts.atCustomer.n18, n9: kegFleetCounts.atCustomer.n9 },
          { label: "To Be Washed", status: "To Be Washed", sub: "Collected, awaiting wash", color: "var(--danger)", n18: kegFleetCounts.toBeWashed.n18, n9: kegFleetCounts.toBeWashed.n9 },
          { label: "Clean", status: "Clean", sub: "Washed and ready to fill", color: "var(--success)", n18: kegFleetCounts.clean.n18, n9: kegFleetCounts.clean.n9 },
          { label: "In Fridge", status: "In Fridge", sub: "Filled, stored, ready for delivery", color: "var(--blue)", n18: fridge18, n9: fridge9 },
          { label: "Staff / Market", status: "Staff/Market", sub: "Internal use or farmers market", color: "var(--warning, #C17817)", n18: kegFleetCounts.staffMarket.n18, n9: kegFleetCounts.staffMarket.n9 }
        ];
        // Helper: get kegs from register matching a status
        function kegsForStatus(status) {
          return kegRegister.filter(function(k) { return k.status === status; })
            .sort(function(a, b) { return a.kegId.localeCompare(b.kegId); });
        }
        // Render expanded content per status — 3 rows visible, scrollable
        function renderExpandedContent(r, matchingKegs) {
          if (matchingKegs.length === 0) {
            return React.createElement("div", { style: { padding: "8px 4px", fontSize: 12, color: "var(--ghost)" } }, "No individual kegs tracked");
          }
          // ROW_H must match actual rendered row height (minHeight + padding 6+6)
          var ROW_H = 46;
          var MAX_ROWS = 3;
          var scrollStyle = {
            maxHeight: ROW_H * MAX_ROWS,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            touchAction: "pan-y",
            // Ensure iOS treats this as the scroll target, not the page
            position: "relative",
            zIndex: 1
          };

          // AT CUSTOMER: group by customer name, show count per size
          if (r.status === "At Customer") {
            var byC = {};
            matchingKegs.forEach(function(k) {
              var c = k.location || "Unknown";
              if (!byC[c]) byC[c] = { n18: 0, n9: 0 };
              if (String(k.size).indexOf("18") > -1) byC[c].n18++; else byC[c].n9++;
            });
            var cKeys = Object.keys(byC).sort();
            return React.createElement("div", { style: scrollStyle },
              cKeys.map(function(c) {
                var v = byC[c], parts = [];
                if (v.n18) parts.push(v.n18 + "\u00d718L");
                if (v.n9) parts.push(v.n9 + "\u00d79L");
                return React.createElement("div", { key: c, style: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 4px", borderBottom: "1px solid var(--rule-light)", minHeight: ROW_H - 1 } },
                  React.createElement("span", { style: { fontSize: 13, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }, c),
                  React.createElement("span", { style: { fontSize: 12, color: "var(--dim)", fontWeight: 700, whiteSpace: "nowrap", marginLeft: 8 } }, parts.join(" + "))
                );
              })
            );
          }

          // TO BE WASHED / CLEAN: keg ID + size only
          if (r.status === "To Be Washed" || r.status === "Clean") {
            return React.createElement("div", { style: scrollStyle },
              matchingKegs.map(function(k) {
                return React.createElement("div", { key: k.kegId, style: { display: "flex", alignItems: "center", gap: 8, padding: "6px 4px", borderBottom: "1px solid var(--rule-light)", minHeight: ROW_H - 1 } },
                  React.createElement("span", { style: { fontWeight: 700, fontFamily: "monospace", fontSize: 13 } }, k.kegId),
                  React.createElement("span", { style: { fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: "rgba(29,78,216,.06)", color: "var(--blue)" } }, k.size)
                );
              })
            );
          }

          // IN FRIDGE: keg ID + size + product + keg tag (batch)
          if (r.status === "In Fridge") {
            return React.createElement("div", { style: scrollStyle },
              matchingKegs.map(function(k) {
                var prodNorm = normProduct(k.product);
                return React.createElement("div", { key: k.kegId, style: { display: "flex", alignItems: "center", gap: 6, padding: "6px 4px", borderBottom: "1px solid var(--rule-light)", minHeight: ROW_H - 1 } },
                  React.createElement("span", { style: { fontWeight: 700, fontFamily: "monospace", fontSize: 12, minWidth: 86 } }, k.kegId),
                  k.product && React.createElement("span", { style: { display: "flex", alignItems: "center", gap: 3, fontSize: 11, color: "var(--dim)", flex: 1, overflow: "hidden" } },
                    React.createElement("span", { style: { width: 6, height: 6, borderRadius: 3, background: PC[prodNorm] || "var(--ghost)", flexShrink: 0 } }),
                    React.createElement("span", { style: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }, prodNorm)
                  ),
                  k.kegTag && React.createElement("span", { style: { fontSize: 10, color: "var(--ghost)", whiteSpace: "nowrap", marginLeft: "auto" } }, k.kegTag)
                );
              })
            );
          }

          // STAFF / MARKET: keg ID + size + product
          if (r.status === "Staff/Market") {
            return React.createElement("div", { style: scrollStyle },
              matchingKegs.map(function(k) {
                var prodNorm = normProduct(k.product);
                return React.createElement("div", { key: k.kegId, style: { display: "flex", alignItems: "center", gap: 6, padding: "6px 4px", borderBottom: "1px solid var(--rule-light)", minHeight: ROW_H - 1 } },
                  React.createElement("span", { style: { fontWeight: 700, fontFamily: "monospace", fontSize: 12, minWidth: 86 } }, k.kegId),
                  k.product && React.createElement("span", { style: { display: "flex", alignItems: "center", gap: 3, fontSize: 11, color: "var(--dim)" } },
                    React.createElement("span", { style: { width: 6, height: 6, borderRadius: 3, background: PC[prodNorm] || "var(--ghost)", flexShrink: 0 } }),
                    prodNorm
                  )
                );
              })
            );
          }
          return null;
        }
        return React.createElement(React.Fragment, null,
          fleetRows.map(function(r) {
            var canExpand = r.status !== "At Customer";
            var isExpanded = canExpand && fleetExpanded === r.label;
            var matchingKegs = isExpanded ? kegsForStatus(r.status) : [];
            var total = (r.n18 || 0) + (r.n9 || 0);
            // When expanded, neutralise the :active scale transform so it doesn't
            // interfere with touch-scroll on the inner keg list (iOS Safari issue).
            var kstatStyle = { marginTop: 8, cursor: canExpand && total > 0 ? "pointer" : "default" };
            if (isExpanded) { kstatStyle.transform = "none"; }
            return React.createElement("div", { className: "kstat", key: r.label, style: kstatStyle,
              onClick: function() { if (canExpand && total > 0) setFleetExpanded(isExpanded ? null : r.label); }
            },
              React.createElement("div", { className: "kstat-top" },
                React.createElement("div", { style: { flex: 1 } },
                  React.createElement("div", { className: "kstat-lbl", style: { color: r.color, display: "flex", alignItems: "center", gap: 6 } },
                    r.label,
                    canExpand && total > 0 && React.createElement("span", { style: { fontSize: 10, color: "var(--ghost)", transition: "transform .15s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" } }, "\u203A")
                  ),
                  React.createElement("div", { className: "kstat-sub" }, r.sub)
                ),
                React.createElement("div", { style: { textAlign: "center", width: 60 } },
                  React.createElement("div", { style: { fontSize: 9, fontWeight: 700, color: "var(--ghost)", letterSpacing: 1 } }, "18L"),
                  React.createElement("div", { className: "kstat-n" }, r.n18)
                ),
                React.createElement("div", { style: { textAlign: "center", width: 60 } },
                  React.createElement("div", { style: { fontSize: 9, fontWeight: 700, color: "var(--ghost)", letterSpacing: 1 } }, "9L"),
                  React.createElement("div", { className: "kstat-n" }, r.n9)
                )
              ),
              // Expanded — status-specific content, 3 rows max with scroll
              isExpanded && React.createElement("div", {
                style: { borderTop: "1px solid var(--rule-light)", marginTop: 6, paddingTop: 2, touchAction: "pan-y" },
                onClick: function(e) { e.stopPropagation(); }
              }, renderExpandedContent(r, matchingKegs)),
              r.label === "To Be Washed" && React.createElement("button", {
                style: { background: "var(--success)", color: "#fff", padding: "6px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, border: "none", cursor: "pointer", marginLeft: "auto", marginRight: 8, marginTop: 4 },
                onClick: function(e) { e.stopPropagation(); setW18(String(r.n18 || "")); setW9(String(r.n9 || "")); setSub("washKegs"); }
              }, "WASH KEGS")
            );
          }),
          React.createElement("div", { className: "sect" }, React.createElement("div", { className: "h2" }, "Total Kegs")),
          function() {
            var total18 = 0, total9 = 0;
            fleetRows.forEach(function(r) { total18 += (r.n18 || 0); total9 += (r.n9 || 0); });
            return React.createElement("div", { style: { display: "flex", justifyContent: "flex-end", gap: 0 } },
              React.createElement("div", { style: { textAlign: "center", width: 60 } },
                React.createElement("div", { className: "lbl", style: { color: "var(--ghost)" } }, "18L"),
                React.createElement("div", { style: { marginTop: 4 } }, React.createElement("span", { style: { fontSize: 26, fontWeight: 700 } }, total18))
              ),
              React.createElement("div", { style: { textAlign: "center", width: 60 } },
                React.createElement("div", { className: "lbl", style: { color: "var(--ghost)" } }, "9L"),
                React.createElement("div", { style: { marginTop: 4 } }, React.createElement("span", { style: { fontSize: 26, fontWeight: 700 } }, total9))
              )
            );
          }(),
          React.createElement("div", { style: { marginTop: 14, display: "flex", gap: 8 } },
            React.createElement("button", { className: "btn btn-out", style: { flex: 1 }, onClick: function() {
              setSub("registerKeg"); setAddFleetSize("18"); setAddFleetDigits(""); setAddFleetStaged([]);
            } },
              React.createElement("svg", { className: "ico", viewBox: "0 0 24 24" },
                React.createElement("path", { d: "M9 11l3 3L22 4" }),
                React.createElement("path", { d: "M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" })
              ), " Register Keg"
            ),
            React.createElement("button", { className: "btn btn-out", style: { flex: 1 }, onClick: function() {
              setSub("addToFleet"); setAddFleetChecks([false,false,false,false,false,false]); setAddFleetSize("18"); setAddFleetDigits(""); setAddFleetLog([]); setAddFleetStaged([]);
            } },
              React.createElement("svg", { className: "ico", viewBox: "0 0 24 24" },
                React.createElement("line", { x1: 12, y1: 5, x2: 12, y2: 19 }),
                React.createElement("line", { x1: 5, y1: 12, x2: 19, y2: 12 })
              ), " Add New Keg"
            )
          )
        );
      }()
    );
  }

  // ============================================================
  // FILL KEGS FLOW
  // ============================================================
  // ============================================================
  // FILL LOG  (Aaron's keg-fill summary view)
  // ------------------------------------------------------------
  // Reviews recent keg-fill activity grouped into "sessions" — a
  // session is one fill operation (one brew batch + tag + day +
  // createdAt minute). Each row shows product, tag, BB, count and
  // expands to show the keg IDs and where each went (Fridge vs
  // Staff/Market).
  // ============================================================
  function renderFillLog() {
    // Date filter pills
    var FILTERS = [
      { id: "today",     label: "Today",    days: 0 },
      { id: "week",      label: "7 Days",   days: 7 },
      { id: "fortnight", label: "14 Days",  days: 14 },
      { id: "all",       label: "All",      days: -1 }
    ];

    // Build a date cutoff (ISO yyyy-mm-dd) based on the active filter
    function cutoffISO(days) {
      if (days < 0) return "";
      var d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - days);
      return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
    }
    var activeFilter = FILTERS.find(function(f) { return f.id === flFilter; }) || FILTERS[1];
    var cutoff = cutoffISO(activeFilter.days);
    var todayStr = (function() {
      var d = new Date();
      return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
    })();

    // Build a map of kegId -> current register entry (for destination + status lookups)
    var regMap = {};
    kegRegister.forEach(function(k) { regMap[String(k.kegId).toUpperCase()] = k; });

    // Filter fills by date and group into sessions.
    // Session key = brewId + kegTag + date + (createdAt rounded to minute) — covers
    // edge case where the same batch is keg-up'd in two passes on the same day.
    var sessions = {};
    var order = [];
    for (var i = kegFilling.length - 1; i >= 0; i--) {
      var f = kegFilling[i];
      if (!f || !f.date) continue;
      var fIso = dateToISO(f.date);
      if (cutoff && fIso < cutoff) continue;

      // Round createdAt to the minute for session grouping (avoid seconds drift across kegs)
      var createdMin = "";
      if (f.createdAt) {
        var t = new Date(f.createdAt);
        if (!isNaN(t.getTime())) {
          createdMin = t.getFullYear() + "-" + (t.getMonth() + 1) + "-" + t.getDate() + " " + t.getHours() + ":" + t.getMinutes();
        }
      }
      var key = (f.brewId || "?") + "|" + (f.kegTag || "") + "|" + fIso + "|" + createdMin;
      if (!sessions[key]) {
        sessions[key] = {
          key: key,
          date: f.date,
          dateIso: fIso,
          brewId: f.brewId || "",
          brightTank: f.brightTank || "",
          product: f.product || "",
          kegTag: f.kegTag || "",
          bestBefore: f.bestBefore || "",
          loggedBy: f.loggedBy || "",
          createdAt: f.createdAt || "",
          kegs: []
        };
        order.push(key);
      }
      // Determine destination: if lastKegLitres is set, it's a partial → Staff/Market.
      // Otherwise check the current register status (fallback to Fridge if absent).
      var hasLast = f.lastKegLitres !== "" && f.lastKegLitres !== null && f.lastKegLitres !== undefined && Number(f.lastKegLitres) > 0;
      var dest = "fridge";
      if (hasLast) dest = "staff";
      else {
        var reg = regMap[String(f.kegId).toUpperCase()];
        if (reg && reg.status === "Staff/Market") dest = "staff";
      }
      sessions[key].kegs.push({
        kegId: f.kegId,
        size: f.size || "",
        dest: dest,
        lastKegLitres: hasLast ? f.lastKegLitres : "",
        currentStatus: (regMap[String(f.kegId).toUpperCase()] || {}).status || ""
      });
    }

    // Sort sessions newest first (by date desc, then createdAt desc)
    order.sort(function(a, b) {
      var sa = sessions[a], sb = sessions[b];
      if (sa.dateIso !== sb.dateIso) return sa.dateIso < sb.dateIso ? 1 : -1;
      if (sa.createdAt && sb.createdAt) return sa.createdAt < sb.createdAt ? 1 : -1;
      return 0;
    });

    // Aggregate totals across the filtered window
    var totKegs = 0, tot18 = 0, tot9 = 0, tot7 = 0, totFridge = 0, totStaff = 0;
    order.forEach(function(k) {
      sessions[k].kegs.forEach(function(kg) {
        totKegs++;
        if (String(kg.size).indexOf("18") > -1) tot18++;
        else if (String(kg.size).indexOf("7L") > -1 && String(kg.size).indexOf("17") === -1) tot7++;
        else tot9++;
        if (kg.dest === "staff") totStaff++; else totFridge++;
      });
    });

    return React.createElement("div", { className: "scr", style: { animation: "fu .2s ease" } },
      // Header with back arrow
      React.createElement("div", {
        style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 14, cursor: "pointer" },
        onClick: function() { setSub(null); }
      },
        React.createElement(IcoBack, null),
        React.createElement("span", { className: "h2" }, "Keg Fill Log")
      ),
      React.createElement("div", { style: { fontSize: 12, color: "var(--ghost)", marginBottom: 12 } },
        "Review recent keg fills \u2014 product, batch, best before, and where each keg went."
      ),

      // Filter pills
      React.createElement("div", { style: { display: "flex", gap: 6, marginBottom: 14 } },
        FILTERS.map(function(f) {
          var sel = flFilter === f.id;
          return React.createElement("button", {
            key: f.id,
            style: {
              flex: 1, padding: "8px 0", fontSize: 12, fontWeight: 700,
              border: sel ? "2px solid var(--blue)" : "1.5px solid var(--rule)",
              borderRadius: 8,
              background: sel ? "rgba(29,78,216,.08)" : "transparent",
              color: sel ? "var(--blue)" : "var(--dim)",
              cursor: "pointer",
              letterSpacing: 0.3
            },
            onClick: function() { setFlFilter(f.id); setFlExpanded(null); }
          }, f.label);
        })
      ),

      // Summary card
      totKegs > 0 && React.createElement("div", {
        style: { background: "var(--input-bg)", borderRadius: 10, padding: "12px 14px", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between" }
      },
        React.createElement("div", null,
          React.createElement("div", { style: { fontSize: 11, fontWeight: 700, color: "var(--ghost)", letterSpacing: 1, textTransform: "uppercase" } }, activeFilter.label),
          React.createElement("div", { style: { fontSize: 12, color: "var(--dim)", marginTop: 2 } },
            order.length, " session", order.length === 1 ? "" : "s",
            " \u00b7 ", totKegs, " keg", totKegs === 1 ? "" : "s"
          )
        ),
        React.createElement("div", { style: { display: "flex", gap: 16, textAlign: "right" } },
          React.createElement("div", null,
            React.createElement("div", { style: { fontSize: 9, fontWeight: 700, color: "var(--ghost)", letterSpacing: 1 } }, "FRIDGE"),
            React.createElement("div", { style: { fontSize: 18, fontWeight: 700, color: "var(--blue)", fontFamily: "var(--mono)" } }, totFridge)
          ),
          React.createElement("div", null,
            React.createElement("div", { style: { fontSize: 9, fontWeight: 700, color: "var(--ghost)", letterSpacing: 1 } }, "STAFF"),
            React.createElement("div", { style: { fontSize: 18, fontWeight: 700, color: "var(--warning, #C17817)", fontFamily: "var(--mono)" } }, totStaff)
          )
        )
      ),

      // Empty state
      order.length === 0 && React.createElement("div", {
        style: { padding: "40px 20px", textAlign: "center", color: "var(--ghost)", border: "1.5px dashed var(--rule)", borderRadius: 12 }
      },
        React.createElement("div", { style: { fontSize: 13, fontWeight: 600, marginBottom: 4 } }, "No fills in this window"),
        React.createElement("div", { style: { fontSize: 12 } }, "Try a wider date range.")
      ),

      // Session cards
      order.map(function(key) {
        var s = sessions[key];
        var prodCol = PC[normProduct(s.product)] || "var(--ghost)";
        var n18 = 0, n9 = 0, n7 = 0, nFridge = 0, nStaff = 0;
        s.kegs.forEach(function(kg) {
          if (String(kg.size).indexOf("18") > -1) n18++;
          else if (kg.size === "7L") n7++;
          else n9++;
          if (kg.dest === "staff") nStaff++; else nFridge++;
        });
        var isExpanded = flExpanded === key;
        var isToday = s.dateIso === todayStr;

        // Build a compact keg-count line
        var countParts = [];
        if (n18) countParts.push(n18 + "\u00d718L");
        if (n9) countParts.push(n9 + "\u00d79L");
        if (n7) countParts.push(n7 + "\u00d77L");

        return React.createElement("div", {
          key: key,
          style: {
            marginBottom: 10,
            border: isExpanded ? "2px solid " + prodCol : "1px solid var(--rule)",
            borderRadius: 12,
            overflow: "hidden",
            background: "var(--card, #fff)",
            transition: "border-color .15s"
          }
        },
          // Top summary row (tap to expand)
          React.createElement("div", {
            style: { padding: "12px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 },
            onClick: function() { setFlExpanded(isExpanded ? null : key); }
          },
            // Product colour dot
            React.createElement("div", {
              style: { width: 10, height: 10, borderRadius: 5, background: prodCol, flexShrink: 0 }
            }),
            // Main info
            React.createElement("div", { style: { flex: 1, minWidth: 0 } },
              React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" } },
                React.createElement("span", { style: { fontSize: 14, fontWeight: 700, color: "var(--text)" } }, s.product || "Unknown"),
                s.kegTag && React.createElement("span", {
                  style: { position: "relative", overflow: "hidden", padding: "2px 8px", borderRadius: 4, background: prodCol, color: "#fff", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }
                },
                  s.kegTag === "Stripe" && React.createElement("div", {
                    style: { position: "absolute", top: "50%", left: 0, right: 0, height: 2, background: "#fff", opacity: 0.6, transform: "translateY(-50%)", pointerEvents: "none" }
                  }),
                  React.createElement("span", { style: { position: "relative", zIndex: 1 } }, s.kegTag)
                )
              ),
              React.createElement("div", { style: { fontSize: 11, color: "var(--dim)", marginTop: 3, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" } },
                React.createElement("span", null, isToday ? "Today" : fmtDateShort(s.date)),
                s.brewId && React.createElement("span", { style: { color: "var(--ghost)" } }, "\u00b7"),
                s.brewId && React.createElement("span", { style: { fontFamily: "var(--mono)" } }, s.brewId),
                s.bestBefore && React.createElement("span", { style: { color: "var(--ghost)" } }, "\u00b7"),
                s.bestBefore && React.createElement("span", null, "BB ", fmtDateShort(s.bestBefore))
              )
            ),
            // Right-side count + chevron
            React.createElement("div", { style: { textAlign: "right", display: "flex", alignItems: "center", gap: 8 } },
              React.createElement("div", null,
                React.createElement("div", { style: { fontSize: 16, fontWeight: 700, fontFamily: "var(--mono)" } }, s.kegs.length),
                React.createElement("div", { style: { fontSize: 9, color: "var(--ghost)", fontWeight: 700, letterSpacing: 0.5 } }, countParts.join(" + "))
              ),
              React.createElement("span", {
                style: { fontSize: 14, color: "var(--ghost)", transition: "transform .15s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }
              }, "\u203A")
            )
          ),

          // Expanded detail
          isExpanded && React.createElement("div", {
            style: { borderTop: "1px solid var(--rule-light)", padding: "10px 14px 12px", background: "var(--input-bg)" }
          },
            // Destination summary row
            React.createElement("div", { style: { display: "flex", gap: 8, marginBottom: 10 } },
              nFridge > 0 && React.createElement("div", {
                style: { flex: 1, padding: "6px 10px", borderRadius: 6, background: "rgba(29,78,216,.08)", border: "1px solid rgba(29,78,216,.15)" }
              },
                React.createElement("div", { style: { fontSize: 9, fontWeight: 700, color: "var(--blue)", letterSpacing: 0.5 } }, "FRIDGE STOCK"),
                React.createElement("div", { style: { fontSize: 14, fontWeight: 700, color: "var(--blue)", fontFamily: "var(--mono)" } }, nFridge + " keg" + (nFridge === 1 ? "" : "s"))
              ),
              nStaff > 0 && React.createElement("div", {
                style: { flex: 1, padding: "6px 10px", borderRadius: 6, background: "rgba(193,120,23,.08)", border: "1px solid rgba(193,120,23,.18)" }
              },
                React.createElement("div", { style: { fontSize: 9, fontWeight: 700, color: "var(--warning, #C17817)", letterSpacing: 0.5 } }, "STAFF / MARKET"),
                React.createElement("div", { style: { fontSize: 14, fontWeight: 700, color: "var(--warning, #C17817)", fontFamily: "var(--mono)" } }, nStaff + " keg" + (nStaff === 1 ? "" : "s"))
              )
            ),

            // Keg list (sorted: fridge first, then staff; within each by ID)
            React.createElement("div", {
              style: { background: "#fff", borderRadius: 8, border: "1px solid var(--rule-light)", overflow: "hidden" }
            },
              s.kegs.slice().sort(function(a, b) {
                if (a.dest !== b.dest) return a.dest === "fridge" ? -1 : 1;
                return String(a.kegId).localeCompare(String(b.kegId));
              }).map(function(kg, idx) {
                var moved = kg.currentStatus && (
                  (kg.dest === "fridge" && kg.currentStatus !== "In Fridge") ||
                  (kg.dest === "staff" && kg.currentStatus !== "Staff/Market")
                );
                return React.createElement("div", {
                  key: kg.kegId + "-" + idx,
                  style: { display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderTop: idx === 0 ? "none" : "1px solid var(--rule-light)" }
                },
                  // Keg ID
                  React.createElement("span", { style: { fontFamily: "var(--mono)", fontSize: 12, fontWeight: 700, minWidth: 86 } }, kg.kegId),
                  // Size badge
                  React.createElement("span", {
                    style: { fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: "rgba(0,0,0,.05)", color: "var(--dim)" }
                  }, kg.size),
                  // Last keg litres (if partial)
                  kg.lastKegLitres && React.createElement("span", {
                    style: { fontSize: 10, color: "var(--ghost)", fontFamily: "var(--mono)" }
                  }, kg.lastKegLitres + "L"),
                  // Destination pill
                  React.createElement("span", {
                    style: {
                      marginLeft: "auto",
                      fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10, letterSpacing: 0.3,
                      background: kg.dest === "staff" ? "rgba(193,120,23,.12)" : "rgba(29,78,216,.1)",
                      color: kg.dest === "staff" ? "var(--warning, #C17817)" : "var(--blue)"
                    }
                  }, kg.dest === "staff" ? "Staff" : "Fridge"),
                  // Moved-since indicator
                  moved && React.createElement("span", {
                    title: "Currently: " + kg.currentStatus,
                    style: { fontSize: 9, fontWeight: 700, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: 0.3 }
                  }, "moved")
                );
              })
            ),

            // Footer metadata
            (s.brightTank || s.loggedBy) && React.createElement("div", {
              style: { fontSize: 10, color: "var(--ghost)", marginTop: 8, display: "flex", gap: 10, flexWrap: "wrap" }
            },
              s.brightTank && React.createElement("span", null, "Tank: ", React.createElement("strong", { style: { color: "var(--dim)" } }, s.brightTank)),
              s.loggedBy && React.createElement("span", null, "By: ", React.createElement("strong", { style: { color: "var(--dim)" } }, s.loggedBy))
            )
          )
        );
      })
    );
  }

  // ============================================================
  // MOVE KEGS (with Move | Transfer tabs)
  // ============================================================
  function renderMoveKegs() {
    var DESTS = [
      { id: "wash", name: "To Be Washed", desc: "Returned dirty, needs cleaning", color: "var(--danger)" },
      { id: "clean", name: "Clean", desc: "Washed and ready to fill", color: "var(--success)" },
      { id: "fridge", name: "In Fridge", desc: "Filled keg stored in fridge", color: "var(--blue)" },
      { id: "staff", name: "Staff / Market", desc: "Personal use or farmers market", color: "var(--warning, #C17817)" }
    ];
    var statusToDest = { "To Be Washed": "wash", "Clean": "clean", "In Fridge": "fridge", "Staff/Market": "staff" };

    var isTransfer = mvTab === "transfer";
    var PURPLE = "#7C3AED";
    var accentColor = isTransfer ? PURPLE : "var(--blue)";

    var srcVol = mvKeg ? lookupSourceVolume(mvKeg.kegId) : null;

    // ── Tab pill row ──
    var tabRow = React.createElement("div", {
      style: { display: "flex", gap: 4, background: "var(--input-bg)", padding: 4, borderRadius: 10, marginBottom: 14 }
    },
      [
        { id: "move", label: "Move", color: "var(--blue)" },
        { id: "transfer", label: "Transfer", color: PURPLE }
      ].map(function(t) {
        var sel = mvTab === t.id;
        return React.createElement("button", { key: t.id, style: {
          flex: 1, padding: "10px 14px", borderRadius: 8, fontSize: 13, fontWeight: 800,
          background: sel ? "#fff" : "transparent",
          color: sel ? t.color : "var(--dim)",
          border: sel ? "1px solid var(--rule)" : "1px solid transparent",
          boxShadow: sel ? "0 1px 2px rgba(0,0,0,.04)" : "none",
          cursor: "pointer", transition: "all .15s"
        }, onClick: function() {
          if (mvTab === t.id) return;
          setMvTab(t.id);
          // Reset transient state when switching tabs (but keep loaded source keg)
          setMvDest(null);
          setMvTfKind(null);
          setMvTfDests([]);
          setMvTfResidual(null); setMvTfProduct(null);
        } }, t.label);
      })
    );

    // ── Keg ID input (shared between tabs) ──
    var kegIdInput = !mvKeg && React.createElement(React.Fragment, null,
      React.createElement("div", { className: "sect", style: { marginTop: 0 } },
        React.createElement("div", { className: "lbl", style: { color: "var(--ghost)" } }, "Keg ID")
      ),
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, marginTop: 10 } },
        React.createElement("span", { style: { fontSize: 18, fontWeight: 800, color: "var(--dim)", whiteSpace: "nowrap" } }, "KEG-"),
        ["18", "09"].map(function(sz) {
          var sel = mvSize === sz;
          return React.createElement("button", { key: sz, style: {
            padding: "10px 16px", borderRadius: 8, fontSize: 16, fontWeight: 800,
            border: sel ? "2px solid " + accentColor : "2px solid var(--rule)",
            background: sel ? (isTransfer ? "rgba(124,58,237,.08)" : "rgba(29,78,216,.08)") : "transparent",
            color: sel ? accentColor : "var(--ghost)", cursor: "pointer"
          }, onClick: function() { setMvSize(sz); } }, sz);
        }),
        React.createElement("span", { style: { fontSize: 18, fontWeight: 800, color: "var(--dim)" } }, "-"),
        React.createElement("input", {
          style: { width: 80, fontSize: 22, fontWeight: 800, textAlign: "center", padding: "10px 8px", borderRadius: 8, border: "2px solid var(--rule)", background: "var(--input-bg)", color: "var(--text)", outline: "none", fontFamily: "var(--sans)" },
          type: "text", inputMode: "numeric", maxLength: 3, placeholder: "001",
          value: mvDigits,
          onChange: function(e) { var v = e.target.value.replace(/[^0-9]/g, "").slice(0, 3); setMvDigits(v); }
        })
      ),
      React.createElement("button", {
        className: "btn",
        style: {
          marginTop: 12, background: accentColor, color: "#fff",
          opacity: mvDigits.length > 0 ? 1 : 0.4,
          pointerEvents: mvDigits.length > 0 ? "auto" : "none"
        },
        onClick: function() {
          mvLookupKeg();
        }
      },
        React.createElement("svg", { className: "ico", style: { stroke: "#fff" }, viewBox: "0 0 24 24" },
          React.createElement("circle", { cx: 11, cy: 11, r: 8 }), React.createElement("line", { x1: 21, y1: 21, x2: 16.65, y2: 16.65 })
        ), " Look Up"
      ),
      React.createElement("div", { style: { textAlign: "center", margin: "10px 0 0" } },
        React.createElement("span", { style: { fontSize: 10, color: "var(--ghost)", fontWeight: 600 } }, "or")
      ),
      React.createElement("button", { className: "btn btn-out", style: { marginTop: 8, fontSize: 12 }, onClick: function() {
        bscOpen({
          mode: "move",
          expected: 1,
          label: isTransfer ? "SCAN SOURCE KEG" : "SCAN KEG TO MOVE",
          color: accentColor,
          onDone: function(ids) {
            if (ids.length === 0) return;
            var keg = kegLookup(ids[0]);
            if (!keg) { toast("Not found: " + ids[0], "var(--danger)"); return; }
            setMvKeg(keg); setMvDest(null); setMvNotes("");
          }
        });
      } },
        React.createElement("svg", { style: { width: 16, height: 16, stroke: "var(--text)", fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", marginRight: 6 }, viewBox: "0 0 24 24" },
          React.createElement("path", { d: "M4 7V4h3M20 7V4h-3M4 17v3h3M20 17v3h-3" }),
          React.createElement("rect", { x: 9, y: 9, width: 6, height: 6, rx: 1 })
        ), "Scan QR Code"
      )
    );

    // ── Shared keg info card ──
    var kegInfoCard = mvKeg && React.createElement("div", {
      style: { border: "2px solid " + accentColor, borderRadius: 12, padding: 18, background: "#fff", marginTop: 6 }
    },
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 } },
        React.createElement("span", { style: { fontSize: 20, fontWeight: 800, letterSpacing: 0.5 } }, mvKeg.kegId),
        React.createElement("span", { style: { fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 4, background: isTransfer ? "rgba(124,58,237,.1)" : "rgba(29,78,216,.08)", color: accentColor, letterSpacing: 0.5 } }, mvKeg.size)
      ),
      React.createElement("div", { style: { display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid var(--rule-light)" } },
        React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)", fontWeight: 600 } }, "Current Location"),
        React.createElement("span", { style: { fontSize: 13, fontWeight: 700 } }, (mvKeg.status === "At Customer" && mvKeg.location) ? mvKeg.location : (mvKeg.status || "Unknown"))
      ),
      mvKeg.product ? React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderTop: "1px solid var(--rule-light)" } },
        React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)", fontWeight: 600 } }, "Product"),
        React.createElement("span", { style: { fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 } },
          React.createElement("span", { style: { width: 8, height: 8, borderRadius: 4, background: PC[normProduct(mvKeg.product)] || "var(--ghost)" } }),
          mvKeg.product
        )
      ) : null,
      isTransfer && srcVol != null ? React.createElement("div", { style: { display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid var(--rule-light)" } },
        React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)", fontWeight: 600 } }, "Estimated Volume"),
        React.createElement("span", { style: { fontSize: 13, fontWeight: 700, fontFamily: "var(--mono)" } }, "~" + srcVol + "L (from last fill)")
      ) : null,
      mvKeg.lastMovement ? React.createElement("div", { style: { display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid var(--rule-light)" } },
        React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)", fontWeight: 600 } }, "Last Moved"),
        React.createElement("span", { style: { fontSize: 13, fontWeight: 700 } }, mvKeg.lastMovement)
      ) : null,
      mvKeg.bestBefore ? React.createElement("div", { style: { display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid var(--rule-light)" } },
        React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)", fontWeight: 600 } }, "Best Before"),
        React.createElement("span", { style: { fontSize: 13, fontWeight: 700 } }, mvKeg.bestBefore)
      ) : null
    );

    // ── MOVE TAB body (post-lookup) ──
    var moveBody = mvKeg && !isTransfer && React.createElement(React.Fragment, null,
      React.createElement("div", { className: "sect" }, React.createElement("div", { className: "lbl", style: { color: "var(--ghost)" } }, "Move To")),
      React.createElement("div", { style: { marginTop: 10 } },
        DESTS.map(function(d) {
          var isCurrent = statusToDest[mvKeg.status] === d.id;
          var isSel = mvDest === d.id;
          return React.createElement("div", { key: d.id, style: {
            border: isSel ? "2px solid var(--blue)" : "1.5px solid var(--rule)",
            borderRadius: 10, padding: "14px 16px", cursor: isCurrent ? "default" : "pointer",
            opacity: isCurrent ? 0.35 : 1, marginTop: 8,
            background: isSel ? "rgba(29,78,216,.04)" : "transparent",
            display: "flex", alignItems: "center", gap: 12, transition: "all .15s"
          }, onClick: isCurrent ? null : function() { setMvDest(d.id); } },
            React.createElement("div", { style: { width: 36, height: 36, borderRadius: 8, background: d.color + "20", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 } },
              React.createElement("div", { style: { width: 10, height: 10, borderRadius: 5, background: d.color } })
            ),
            React.createElement("div", null,
              React.createElement("div", { style: { fontSize: 14, fontWeight: 700 } }, d.name, isCurrent ? React.createElement("span", { style: { fontSize: 10, color: "var(--ghost)", fontWeight: 400, marginLeft: 6 } }, "(current)") : null),
              React.createElement("div", { style: { fontSize: 11, color: "var(--ghost)", marginTop: 2 } }, d.desc)
            )
          );
        })
      ),

      React.createElement("div", { style: { display: "flex", gap: 10, marginTop: 16 } },
        React.createElement("button", { className: "btn btn-out", style: { flex: "0 0 auto", width: "auto", padding: "10px 16px" }, onClick: function() { setMvKeg(null); setMvDest(null); } },
          React.createElement(IcoBack, null), " Back"
        ),
        React.createElement("button", { className: "btn btn-blue", style: { flex: 1, opacity: mvDest ? 1 : 0.4, pointerEvents: mvDest ? "auto" : "none" }, onClick: syncing ? null : saveMoveKeg },
          syncing ? React.createElement(Spinner, null) : React.createElement(React.Fragment, null, React.createElement(IcoCheck, null), " Confirm Move")
        )
      )
    );

    // ── TRANSFER TAB body (post-lookup) ──
    var transferEmpty = mvKeg && isTransfer && !mvKeg.product;
    var transferKindPicker = mvKeg && isTransfer && mvKeg.product && !mvTfKind && React.createElement(React.Fragment, null,
      React.createElement("div", { className: "sect" }, React.createElement("div", { className: "lbl", style: { color: "var(--ghost)" } }, "What are you doing?")),
      React.createElement("div", { style: { marginTop: 10 } },
        [
          { id: "split", label: "Split", desc: "Divide contents across 2+ clean kegs", icon: "split" },
          { id: "transfer", label: "Transfer", desc: "Move contents (whole or partial) into another keg", icon: "transfer" }
        ].map(function(opt) {
          return React.createElement("div", { key: opt.id, style: {
            border: "1.5px solid var(--rule)", borderRadius: 10, padding: "16px 18px", cursor: "pointer",
            marginTop: 8, background: "#fff", display: "flex", alignItems: "center", gap: 14, transition: "all .15s"
          }, onClick: function() { setMvTfKind(opt.id); initTransferDests(opt.id); } },
            React.createElement("div", { style: { width: 44, height: 44, borderRadius: 10, background: "rgba(124,58,237,.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 } },
              opt.id === "split"
                ? React.createElement("svg", { style: { width: 24, height: 24, stroke: PURPLE, fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }, viewBox: "0 0 24 24" },
                    React.createElement("path", { d: "M12 3v6" }),
                    React.createElement("path", { d: "M12 9l-5 6" }),
                    React.createElement("path", { d: "M12 9l5 6" }),
                    React.createElement("rect", { x: 3, y: 15, width: 6, height: 6, rx: 1 }),
                    React.createElement("rect", { x: 15, y: 15, width: 6, height: 6, rx: 1 })
                  )
                : React.createElement("svg", { style: { width: 24, height: 24, stroke: PURPLE, fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }, viewBox: "0 0 24 24" },
                    React.createElement("rect", { x: 3, y: 4, width: 7, height: 14, rx: 1 }),
                    React.createElement("rect", { x: 14, y: 4, width: 7, height: 14, rx: 1 }),
                    React.createElement("path", { d: "M10 11h4" }),
                    React.createElement("polyline", { points: "12 9 14 11 12 13" })
                  )
            ),
            React.createElement("div", null,
              React.createElement("div", { style: { fontSize: 15, fontWeight: 800, color: "var(--text)" } }, opt.label),
              React.createElement("div", { style: { fontSize: 11, color: "var(--ghost)", marginTop: 2 } }, opt.desc)
            )
          );
        })
      ),
      React.createElement("button", { className: "btn btn-out", style: { width: "auto", padding: "10px 16px", marginTop: 14 }, onClick: function() { setMvKeg(null); } },
        React.createElement(IcoBack, null), " Back"
      )
    );

    // ── Transfer/Split allocation panel ──
    var transferAllocPanel = mvKeg && isTransfer && mvTfKind && React.createElement("div", {
      style: { marginTop: 16, border: "2px solid " + PURPLE, borderRadius: 12, padding: 16, background: "rgba(124,58,237,.03)" }
    },
      React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 } },
        React.createElement("div", { style: { fontSize: 11, fontWeight: 800, color: PURPLE, textTransform: "uppercase", letterSpacing: 1.2 } },
          mvTfKind === "split" ? "Split into Kegs" : "Transfer to Keg"
        ),
        React.createElement("button", { style: { background: "none", border: "none", fontSize: 11, color: "var(--ghost)", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }, onClick: function() { setMvTfKind(null); setMvTfDests([]); setMvTfResidual(null); setMvTfProduct(null); } },
          "Change"
        )
      ),
      React.createElement("div", { style: { fontSize: 12, color: "var(--dim)", marginBottom: 14 } },
        mvTfKind === "split"
          ? "Each destination is prefilled with an even share. Adjust the litres or keg numbers as needed."
          : "Destination is prefilled with the source's full volume. Adjust the litres if it\u2019s a partial transfer."
      ),

      // Product relabel (split only) — convert e.g. Classic into a Limited.
      // Rare; defaults to keeping the source product. Source keg keeps its own product.
      mvTfKind === "split" ? React.createElement("div", { style: { marginBottom: 14 } },
        React.createElement("div", { style: { fontSize: 10, fontWeight: 700, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 } }, "Fill Destinations As"),
        React.createElement("div", { style: { display: "flex", flexWrap: "wrap", gap: 6 } },
          [{ product: null, label: "Keep \u2014 " + (mvKeg.product || "source") }].concat(
            (limitedRecipes || []).map(function(lr) { return { product: lr.product, label: lr.product }; })
          ).map(function(opt) {
            var sel = (mvTfProduct || null) === (opt.product || null);
            return React.createElement("button", { key: opt.label, style: {
              padding: "7px 12px", borderRadius: 8, fontSize: 12, fontWeight: 800,
              border: sel ? "2px solid " + PURPLE : "2px solid var(--rule)",
              background: sel ? "rgba(124,58,237,.08)" : "transparent",
              color: sel ? PURPLE : "var(--ghost)", cursor: "pointer"
            }, onClick: function() { setMvTfProduct(opt.product); } }, opt.label);
          })
        ),
        mvTfProduct ? React.createElement("div", { style: { fontSize: 11, color: PURPLE, fontWeight: 700, marginTop: 8 } },
          "Destination kegs will be labelled " + mvTfProduct + " (converted from " + (mvKeg.product || "source") + ")."
        ) : null
      ) : null,

      // Destination rows
      React.createElement("div", { style: { fontSize: 10, fontWeight: 700, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 } }, "Destination Kegs"),
      mvTfDests.map(function(row, idx) {
        // Compute inline validation status for this row
        var rowValid = null;
        var rowKegId = null;
        if (row.digitsInput && row.digitsInput.length > 0) {
          rowKegId = "KEG-" + row.sizeInput + "-" + row.digitsInput.padStart(3, "0");
          if (mvKeg && rowKegId === mvKeg.kegId) {
            rowValid = { kind: "error", text: "Same as source" };
          } else {
            // Check for duplicate against other rows
            var dup = mvTfDests.find(function(other) {
              if (other.id === row.id) return false;
              if (!other.digitsInput || other.digitsInput.length === 0) return false;
              return ("KEG-" + other.sizeInput + "-" + other.digitsInput.padStart(3, "0")) === rowKegId;
            });
            if (dup) {
              rowValid = { kind: "error", text: "Listed twice" };
            } else {
              var lookup = kegLookup(rowKegId);
              if (!lookup) {
                rowValid = { kind: "error", text: "Not in fleet" };
              } else if (lookup.status === "Clean") {
                rowValid = { kind: "ok", text: "Clean \u2713" };
              } else {
                rowValid = { kind: "warn", text: lookup.status + " \u2014 not clean" };
              }
            }
          }
        }
        var badgeColor = rowValid ? (rowValid.kind === "ok" ? "var(--success)" : rowValid.kind === "warn" ? "var(--warning, #C17817)" : "var(--danger)") : null;
        var badgeBg = rowValid ? (rowValid.kind === "ok" ? "rgba(34,197,94,.1)" : rowValid.kind === "warn" ? "rgba(193,120,23,.1)" : "rgba(220,38,38,.1)") : null;

        return React.createElement("div", { key: row.id, style: { background: "#fff", border: "1.5px solid var(--rule)", borderRadius: 8, padding: 10, marginBottom: 8 } },
          React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 } },
            React.createElement("div", { style: { fontSize: 10, fontWeight: 700, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: 1 } }, "Destination " + (idx + 1)),
            mvTfDests.length > 1 ? React.createElement("button", { style: { background: "none", border: "none", color: "var(--danger)", fontSize: 11, fontWeight: 700, cursor: "pointer", padding: "0 4px", lineHeight: 1 }, onClick: function() { tfRemoveDest(row.id); } }, "Remove") : null
          ),
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 6 } },
            React.createElement("span", { style: { fontSize: 13, fontWeight: 800, color: "var(--dim)" } }, "KEG-"),
            ["18", "09"].map(function(sz) {
              var sel = row.sizeInput === sz;
              return React.createElement("button", { key: sz, style: {
                padding: "6px 10px", borderRadius: 6, fontSize: 12, fontWeight: 800,
                border: sel ? "2px solid " + PURPLE : "2px solid var(--rule)",
                background: sel ? "rgba(124,58,237,.08)" : "transparent",
                color: sel ? PURPLE : "var(--ghost)", cursor: "pointer"
              }, onClick: function() { tfUpdateDest(row.id, "sizeInput", sz); } }, sz);
            }),
            React.createElement("span", { style: { fontSize: 13, fontWeight: 800, color: "var(--dim)" } }, "-"),
            React.createElement("input", {
              style: { width: 60, fontSize: 14, fontWeight: 800, textAlign: "center", padding: "6px 4px", borderRadius: 6, border: "2px solid var(--rule)", background: "var(--input-bg)", color: "var(--text)", outline: "none", fontFamily: "var(--sans)" },
              type: "text", inputMode: "numeric", maxLength: 3, placeholder: "001",
              value: row.digitsInput,
              onChange: function(e) { tfUpdateDest(row.id, "digitsInput", e.target.value.replace(/[^0-9]/g, "").slice(0, 3)); }
            }),
            React.createElement("button", { className: "btn btn-out", style: { padding: "6px 8px", fontSize: 11, marginLeft: "auto" }, onClick: function() { tfScanDest(row.id); } },
              React.createElement("svg", { style: { width: 12, height: 12, stroke: "var(--text)", fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", marginRight: 4 }, viewBox: "0 0 24 24" },
                React.createElement("path", { d: "M4 7V4h3M20 7V4h-3M4 17v3h3M20 17v3h-3" }),
                React.createElement("rect", { x: 9, y: 9, width: 6, height: 6, rx: 1 })
              ), "Scan"
            )
          ),
          // Inline validation badge
          rowValid ? React.createElement("div", {
            style: {
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "3px 8px", borderRadius: 4, marginBottom: 8,
              fontSize: 10, fontWeight: 800, letterSpacing: 0.3,
              background: badgeBg, color: badgeColor
            }
          }, rowValid.text) : null,
          // Litres + location toggle
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" } },
            React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)", fontWeight: 600 } }, "Litres"),
            React.createElement("input", {
              style: { width: 70, fontSize: 14, fontWeight: 800, textAlign: "center", padding: "6px 4px", borderRadius: 6, border: "2px solid var(--rule)", background: "var(--input-bg)", color: "var(--text)", outline: "none", fontFamily: "var(--mono)" },
              type: "text", inputMode: "decimal", placeholder: "0",
              value: row.litres,
              onChange: function(e) { tfUpdateDest(row.id, "litres", e.target.value.replace(/[^0-9.]/g, "").slice(0, 5)); }
            }),
            React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)", fontWeight: 600, marginLeft: 6 } }, "\u2192"),
            // Location segmented toggle
            React.createElement("div", { style: { display: "flex", gap: 2, background: "var(--input-bg)", padding: 2, borderRadius: 6 } },
              [
                { id: "fridge", label: "Fridge Stock", color: "var(--blue)" },
                { id: "staff", label: "Staff/Market", color: "var(--warning, #C17817)" }
              ].map(function(loc) {
                var sel = (row.destination || "fridge") === loc.id;
                return React.createElement("button", { key: loc.id, style: {
                  padding: "5px 9px", borderRadius: 4, fontSize: 10, fontWeight: 800,
                  background: sel ? "#fff" : "transparent",
                  color: sel ? loc.color : "var(--ghost)",
                  border: sel ? "1px solid var(--rule)" : "1px solid transparent",
                  boxShadow: sel ? "0 1px 2px rgba(0,0,0,.04)" : "none",
                  cursor: "pointer", transition: "all .15s"
                }, onClick: function() { tfUpdateDest(row.id, "destination", loc.id); } }, loc.label);
              })
            )
          )
        );
      }),
      // Add more destination
      React.createElement("button", { className: "btn btn-out", style: { width: "100%", fontSize: 12, marginTop: 4, marginBottom: 14 }, onClick: tfAddDest },
        "+ Add another destination keg"
      ),

      // Original keg outcome
      React.createElement("div", { style: { fontSize: 10, fontWeight: 700, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 } }, "Original keg: " + mvKeg.kegId + " (" + (mvKeg.status || "—") + ")"),
      (function() {
        var opts = [
          { id: "wash", label: "Original keg \u2192 To wash", desc: "Original keg is empty after the transfer", color: "var(--danger)", recommendedFor: "split" },
          { id: "staff", label: "Original keg \u2192 Residual to Staff/Market Fridge", desc: "Leftover product, original keg moves to Staff/Market", color: "var(--warning, #C17817)", recommendedFor: "transfer" },
          { id: "stockResidual", label: "Original keg \u2192 Residual in Stock", desc: "Rare \u2014 leftover product, original keg returned to Fridge Stock", color: "var(--blue)" }
        ];
        return opts.map(function(o) {
          var sel = mvTfResidual === o.id;
          var isRecommended = o.recommendedFor === mvTfKind;
          return React.createElement("div", { key: o.id, style: {
            border: sel ? "2px solid " + o.color : "1.5px solid var(--rule)",
            borderRadius: 8, padding: "10px 12px", cursor: "pointer", marginBottom: 6,
            background: sel ? o.color + "10" : "#fff",
            display: "flex", alignItems: "flex-start", gap: 10, transition: "all .15s"
          }, onClick: function() { setMvTfResidual(o.id); } },
            React.createElement("div", { style: { width: 18, height: 18, borderRadius: 9, border: "2px solid " + (sel ? o.color : "var(--rule)"), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 } },
              sel ? React.createElement("div", { style: { width: 8, height: 8, borderRadius: 4, background: o.color } }) : null
            ),
            React.createElement("div", { style: { flex: 1 } },
              React.createElement("div", { style: { fontSize: 12, fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" } },
                o.label,
                isRecommended ? React.createElement("span", { style: { fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 3, background: "rgba(124,58,237,.1)", color: PURPLE, letterSpacing: 0.5 } }, "SUGGESTED") : null
              ),
              React.createElement("div", { style: { fontSize: 10, color: "var(--ghost)", marginTop: 2 } }, o.desc)
            )
          );
        });
      })()
    );

    // ── Transfer action buttons ──
    var transferActions = mvKeg && isTransfer && mvTfKind && React.createElement("div", { style: { display: "flex", gap: 10, marginTop: 16 } },
      React.createElement("button", { className: "btn btn-out", style: { flex: "0 0 auto", width: "auto", padding: "10px 16px" }, onClick: function() {
        setMvKeg(null); setMvTfKind(null); setMvTfDests([]); setMvTfResidual(null); setMvTfProduct(null);
      } },
        React.createElement(IcoBack, null), " Back"
      ),
      React.createElement("button", { className: "btn", style: { flex: 1, background: PURPLE, color: "#fff", opacity: (mvTfDests.length > 0 && mvTfResidual) ? 1 : 0.4, pointerEvents: (mvTfDests.length > 0 && mvTfResidual) ? "auto" : "none" }, onClick: syncing ? null : saveTransferKeg },
        syncing ? React.createElement(Spinner, null) : React.createElement(React.Fragment, null, React.createElement(IcoCheck, null), " Confirm " + (mvTfKind === "split" ? "Split" : "Transfer"))
      )
    );

    // Description text under heading
    var pageDesc = isTransfer
      ? "Move product from one keg into one or more clean kegs."
      : "Scan a keg to see where it is and what\u2019s in it, then choose where to move it.";

    return React.createElement("div", { className: "scr", style: { animation: "fu .2s ease" } },
      React.createElement("div", { style: { fontSize: 12, color: "var(--dim)", marginBottom: 16 } }, pageDesc),

      // Tab pill row
      tabRow,

      // Keg ID input (when no source loaded)
      kegIdInput,

      // Loaded source keg
      kegInfoCard,

      // Move tab body
      moveBody,

      // Transfer tab: empty-source warning
      transferEmpty && React.createElement("div", { style: { marginTop: 16, padding: "16px 18px", background: "rgba(220,38,38,.06)", border: "1.5px solid rgba(220,38,38,.2)", borderRadius: 10 } },
        React.createElement("div", { style: { fontSize: 13, fontWeight: 700, color: "var(--danger)", marginBottom: 4 } }, "Source keg is empty"),
        React.createElement("div", { style: { fontSize: 12, color: "var(--dim)" } }, "There's nothing to transfer from " + mvKeg.kegId + ". Switch to the Move tab to change its status."),
        React.createElement("button", { className: "btn btn-out", style: { marginTop: 10, fontSize: 12 }, onClick: function() { setMvKeg(null); } },
          React.createElement(IcoBack, null), " Pick another keg"
        )
      ),

      // Transfer tab: kind picker
      transferKindPicker,

      // Transfer tab: allocation panel
      transferAllocPanel,
      transferActions,

      // ── Session log ──
      React.createElement("div", { className: "sect", style: { marginTop: 20 } },
        React.createElement("div", { className: "sect-top" },
          React.createElement("span", { className: "lbl", style: { color: "var(--ghost)" } }, "Moved This Session"),
          React.createElement("span", { style: { fontSize: 14, fontWeight: 700, color: "var(--blue)" } }, mvLog.length)
        )
      ),
      mvLog.length === 0
        ? React.createElement("div", { style: { fontSize: 12, color: "var(--ghost)", padding: "8px 0" } }, "No kegs moved yet")
        : React.createElement("div", { style: { marginTop: 6 } }, mvLog.map(function(m, i) {
            return React.createElement("div", { key: i, style: { padding: "10px 0", borderBottom: "1px solid var(--rule-light)" } },
              React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 10 } },
                React.createElement("span", { style: { fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 4, background: "rgba(29,78,216,.08)", color: "var(--blue)" } }, m.size),
                React.createElement("span", { style: { fontSize: 13, fontWeight: 700 } }, m.kegId),
                React.createElement("span", { style: { marginLeft: "auto", fontSize: 11, color: "var(--dim)" } }, m.from, " \u2192 ", m.to)
              ),
              m.notes ? React.createElement("div", { style: { fontSize: 11, color: "var(--ghost)", marginTop: 4, paddingLeft: 50 } }, m.notes) : null
            );
          }))
    );
  }

  // ============================================================
  // ADD TO FLEET
  // ============================================================
  function renderAddToFleet() {
    var nxt = nextKegIds();
    var checkLabels = ["Add Filament sticker", "Replace o-rings", "Tighten keg posts", "Add Keg ID QR code", "Colour IN and OUT posts", "Wash"];
    var allChecked = addFleetChecks.every(function(c) { return c; });

    return React.createElement("div", { className: "scr", style: { animation: "fu .2s ease" } },
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 4, cursor: "pointer" }, onClick: function() { setSub(null); } },
        React.createElement(IcoBack, null), React.createElement("span", { className: "h2" }, "Add Kegs to Fleet")
      ),
      React.createElement("div", { style: { fontSize: 12, color: "var(--dim)", marginBottom: 16 } }, "Prep, scan and register new kegs. Kegs are added as Clean."),

      // ── Next ID suggestion ──
      React.createElement("div", { style: { background: "rgba(29,78,216,.06)", border: "1.5px solid rgba(29,78,216,.15)", borderRadius: 10, padding: "14px 16px" } },
        React.createElement("div", { style: { fontSize: 9, fontWeight: 700, color: "var(--blue)", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 } }, "Next Available IDs"),
        React.createElement("div", { style: { display: "flex", gap: 16 } },
          React.createElement("div", null,
            React.createElement("div", { style: { fontSize: 22, fontWeight: 800, color: "var(--blue)", letterSpacing: 0.5 } }, "KEG-18-" + nxt.next18),
            React.createElement("div", { style: { fontSize: 10, color: "var(--ghost)", marginTop: 4 } }, "Next 18L")
          ),
          React.createElement("div", null,
            React.createElement("div", { style: { fontSize: 22, fontWeight: 800, color: "var(--blue)", letterSpacing: 0.5 } }, "KEG-09-" + nxt.next9),
            React.createElement("div", { style: { fontSize: 10, color: "var(--ghost)", marginTop: 4 } }, "Next 9L")
          )
        )
      ),

      // ── Prep checklist ──
      React.createElement("div", { className: "sect", style: { marginTop: 16 } },
        React.createElement("div", { className: "lbl", style: { color: "var(--ghost)" } }, "Prep Checklist")
      ),
      React.createElement("div", { style: { marginTop: 10 } },
        checkLabels.map(function(label, idx) {
          var done = addFleetChecks[idx];
          return React.createElement("div", { key: idx, className: "prep-item" + (done ? " done" : ""), style: { display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: idx < 5 ? "1px solid var(--rule-light)" : "none", fontSize: 14, fontWeight: 600, cursor: "pointer" }, onClick: function() {
            setAddFleetChecks(function(prev) { var n = prev.slice(); n[idx] = !n[idx]; return n; });
          } },
            React.createElement("div", { style: { width: 24, height: 24, borderRadius: 6, border: done ? "2px solid var(--success)" : "2px solid var(--rule)", background: done ? "var(--success)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", transition: "all .15s", flexShrink: 0 } },
              done ? React.createElement(IcoCheck, null) : null
            ),
            React.createElement("span", { style: done ? { color: "var(--dim)", textDecoration: "line-through" } : {} }, label)
          );
        })
      ),

      // ── Keg ID entry ──
      React.createElement("div", { className: "sect" },
        React.createElement("div", { className: "lbl", style: { color: "var(--ghost)" } }, "Keg ID")
      ),
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, marginTop: 10, opacity: allChecked ? 1 : 0.4, pointerEvents: allChecked ? "auto" : "none" } },
        React.createElement("span", { style: { fontSize: 18, fontWeight: 800, color: "var(--dim)", whiteSpace: "nowrap" } }, "KEG-"),
        ["18", "09"].map(function(sz) {
          var sel = addFleetSize === sz;
          return React.createElement("button", { key: sz, style: {
            padding: "10px 16px", borderRadius: 8, fontSize: 16, fontWeight: 800,
            border: sel ? "2px solid var(--success)" : "2px solid var(--rule)",
            background: sel ? "rgba(42,139,106,.08)" : "transparent",
            color: sel ? "var(--success)" : "var(--ghost)", cursor: "pointer"
          }, onClick: function() { setAddFleetSize(sz); } }, sz);
        }),
        React.createElement("span", { style: { fontSize: 18, fontWeight: 800, color: "var(--dim)" } }, "-"),
        React.createElement("input", {
          style: { width: 80, fontSize: 22, fontWeight: 800, textAlign: "center", padding: "10px 8px", borderRadius: 8, border: "2px solid var(--rule)", background: "var(--input-bg)", color: "var(--text)", outline: "none", fontFamily: "var(--sans)" },
          type: "text", inputMode: "numeric", maxLength: 3,
          placeholder: addFleetSize === "18" ? nxt.next18 : nxt.next9,
          value: addFleetDigits,
          onChange: function(e) { var v = e.target.value.replace(/[^0-9]/g, "").slice(0, 3); setAddFleetDigits(v); },
          onKeyDown: function(e) { if (e.key === "Enter" && addFleetDigits.length > 0 && allChecked) stageKegForBulk("add"); }
        }),
        React.createElement("button", {
          style: {
            width: 44, height: 44, borderRadius: 8, border: "none",
            background: "var(--success)", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", opacity: addFleetDigits.length > 0 ? 1 : 0.4,
            pointerEvents: addFleetDigits.length > 0 ? "auto" : "none"
          },
          onClick: function() { stageKegForBulk("add"); }
        }, React.createElement(IcoCheck, null))
      ),
      !allChecked ? React.createElement("div", { style: { fontSize: 11, color: "var(--ghost)", marginTop: 6 } }, "Complete checklist to enable") : null,

      // ── Staged list ──
      React.createElement("div", { className: "sect", style: { marginTop: 18 } },
        React.createElement("div", { className: "sect-top" },
          React.createElement("span", { className: "lbl", style: { color: "var(--ghost)" } }, "To Add"),
          React.createElement("span", { style: { fontSize: 14, fontWeight: 700, color: "var(--success)" } }, addFleetStaged.length)
        )
      ),
      addFleetStaged.length === 0
        ? React.createElement("div", { style: { fontSize: 12, color: "var(--ghost)", padding: "8px 0" } }, "No kegs added yet — type a number and tap \u2713")
        : React.createElement("div", { style: { marginTop: 6 } }, addFleetStaged.map(function(k, i) {
            var is18 = k.size === "18L";
            return React.createElement("div", { key: i, style: { padding: "10px 0", borderBottom: "1px solid var(--rule-light)", display: "flex", alignItems: "center", gap: 10 } },
              React.createElement("span", { style: { fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 4, background: is18 ? "rgba(29,78,216,.08)" : "rgba(212,89,138,.08)", color: is18 ? "var(--blue)" : "var(--summer)" } }, k.size),
              React.createElement("span", { style: { fontSize: 13, fontWeight: 700 } }, k.kegId),
              React.createElement("button", { style: { marginLeft: "auto", background: "transparent", border: "none", color: "var(--danger)", fontSize: 18, fontWeight: 700, cursor: "pointer", padding: "0 6px" }, onClick: function() { unstageKeg(i); } }, "\u00d7")
            );
          })),
      React.createElement("button", { className: "btn", style: { marginTop: 12, background: "var(--success)", color: "#fff", opacity: allChecked && addFleetStaged.length > 0 ? 1 : 0.4, pointerEvents: allChecked && addFleetStaged.length > 0 ? "auto" : "none" }, onClick: syncing ? null : function() { submitStagedKegs("add"); } },
        syncing ? React.createElement(Spinner, null) : React.createElement(React.Fragment, null, React.createElement(IcoCheck, null), " Add " + (addFleetStaged.length > 0 ? addFleetStaged.length + " " : "") + "Keg" + (addFleetStaged.length === 1 ? "" : "s"))
      ),
      React.createElement("div", { style: { textAlign: "center", margin: "10px 0 0" } },
        React.createElement("span", { style: { fontSize: 10, color: "var(--ghost)", fontWeight: 600 } }, "or")
      ),
      React.createElement("button", { className: "btn btn-out", style: { marginTop: 8, fontSize: 12, opacity: allChecked ? 1 : 0.4, pointerEvents: allChecked ? "auto" : "none" }, onClick: function() {
        bscOpen({
          mode: "addFleet",
          expected: 0,
          label: "SCAN NEW KEGS",
          color: "var(--success)",
          onConfirm: function(kegId, size) {
            // Auto-save each keg as it's scanned
            api.post("addKegToFleet", {
              data: { kegId: kegId, size: size, loggedBy: user.name }
            }).then(function(res) {
              if (res.error) { toast(res.error, "var(--danger)"); return; }
              setAddFleetLog(function(prev) { return [{ kegId: kegId, size: size }].concat(prev); });
              loadData();
            }).catch(function(err) { toast("Error: " + err.message, "var(--danger)"); });
          },
          onDone: function(ids) {
            if (ids.length > 0) toast(ids.length + " keg" + (ids.length > 1 ? "s" : "") + " added to fleet", "var(--success)");
          }
        });
      } },
        React.createElement("svg", { style: { width: 16, height: 16, stroke: "var(--text)", fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", marginRight: 6 }, viewBox: "0 0 24 24" },
          React.createElement("path", { d: "M4 7V4h3M20 7V4h-3M4 17v3h3M20 17v3h-3" }),
          React.createElement("rect", { x: 9, y: 9, width: 6, height: 6, rx: 1 })
        ), "Scan QR Codes"
      ),

      // ── Session log ──
      React.createElement("div", { className: "sect", style: { marginTop: 20 } },
        React.createElement("div", { className: "sect-top" },
          React.createElement("span", { className: "lbl", style: { color: "var(--ghost)" } }, "Added This Session"),
          React.createElement("span", { style: { fontSize: 14, fontWeight: 700, color: "var(--success)" } }, addFleetLog.length)
        )
      ),
      addFleetLog.length === 0
        ? React.createElement("div", { style: { fontSize: 12, color: "var(--ghost)", padding: "8px 0" } }, "No kegs added yet")
        : React.createElement("div", { style: { marginTop: 6 } }, addFleetLog.map(function(k, i) {
            var is18 = k.size === "18L";
            return React.createElement("div", { key: i, style: { padding: "10px 0", borderBottom: "1px solid var(--rule-light)", display: "flex", alignItems: "center", gap: 10 } },
              React.createElement("span", { style: { fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 4, background: is18 ? "rgba(29,78,216,.08)" : "rgba(212,89,138,.08)", color: is18 ? "var(--blue)" : "var(--summer)" } }, k.size),
              React.createElement("span", { style: { fontSize: 13, fontWeight: 700 } }, k.kegId),
              React.createElement("span", { style: { marginLeft: "auto", fontSize: 9, color: "var(--success)", fontWeight: 700 } }, "NEW")
            );
          }))
    );
  }

  // ============================================================
  // REGISTER KEG — activate a pre-printed Unregistered label
  // ============================================================
  function renderRegisterKeg() {
    var nxt = nextKegIds();
    return React.createElement("div", { className: "scr", style: { animation: "fu .2s ease" } },
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 4, cursor: "pointer" }, onClick: function() { setSub(null); } },
        React.createElement(IcoBack, null), React.createElement("span", { className: "h2" }, "Register Keg")
      ),
      React.createElement("div", { style: { fontSize: 12, color: "var(--dim)", marginBottom: 16 } }, "Activate a pre-printed label. The keg moves from Unregistered \u2192 Clean."),

      // ── Next Unregistered suggestion ──
      React.createElement("div", { style: { background: "rgba(29,78,216,.06)", border: "1.5px solid rgba(29,78,216,.15)", borderRadius: 10, padding: "14px 16px" } },
        React.createElement("div", { style: { fontSize: 9, fontWeight: 700, color: "var(--blue)", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 } }, "Next Unregistered"),
        React.createElement("div", { style: { display: "flex", gap: 16 } },
          React.createElement("div", null,
            React.createElement("div", { style: { fontSize: 22, fontWeight: 800, color: "var(--blue)", letterSpacing: 0.5 } }, "KEG-18-" + nxt.next18),
            React.createElement("div", { style: { fontSize: 10, color: "var(--ghost)", marginTop: 4 } }, "Next 18L")
          ),
          React.createElement("div", null,
            React.createElement("div", { style: { fontSize: 22, fontWeight: 800, color: "var(--blue)", letterSpacing: 0.5 } }, "KEG-09-" + nxt.next9),
            React.createElement("div", { style: { fontSize: 10, color: "var(--ghost)", marginTop: 4 } }, "Next 9L")
          )
        )
      ),

      // ── Keg ID entry ──
      React.createElement("div", { className: "sect" },
        React.createElement("div", { className: "lbl", style: { color: "var(--ghost)" } }, "Keg ID")
      ),
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, marginTop: 10 } },
        React.createElement("span", { style: { fontSize: 18, fontWeight: 800, color: "var(--dim)", whiteSpace: "nowrap" } }, "KEG-"),
        ["18", "09"].map(function(sz) {
          var sel = addFleetSize === sz;
          return React.createElement("button", { key: sz, style: {
            padding: "10px 16px", borderRadius: 8, fontSize: 16, fontWeight: 800,
            border: sel ? "2px solid var(--blue)" : "2px solid var(--rule)",
            background: sel ? "rgba(29,78,216,.08)" : "transparent",
            color: sel ? "var(--blue)" : "var(--ghost)", cursor: "pointer"
          }, onClick: function() { setAddFleetSize(sz); } }, sz);
        }),
        React.createElement("span", { style: { fontSize: 18, fontWeight: 800, color: "var(--dim)" } }, "-"),
        React.createElement("input", {
          style: { width: 80, fontSize: 22, fontWeight: 800, textAlign: "center", padding: "10px 8px", borderRadius: 8, border: "2px solid var(--rule)", background: "var(--input-bg)", color: "var(--text)", outline: "none", fontFamily: "var(--sans)" },
          type: "text", inputMode: "numeric", maxLength: 3,
          placeholder: addFleetSize === "18" ? nxt.next18 : nxt.next9,
          value: addFleetDigits,
          onChange: function(e) { var v = e.target.value.replace(/[^0-9]/g, "").slice(0, 3); setAddFleetDigits(v); },
          onKeyDown: function(e) { if (e.key === "Enter" && addFleetDigits.length > 0) stageKegForBulk("register"); }
        }),
        React.createElement("button", {
          style: {
            width: 44, height: 44, borderRadius: 8, border: "none",
            background: "var(--success)", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", opacity: addFleetDigits.length > 0 ? 1 : 0.4,
            pointerEvents: addFleetDigits.length > 0 ? "auto" : "none"
          },
          onClick: function() { stageKegForBulk("register"); }
        }, React.createElement(IcoCheck, null))
      ),

      // ── Staged list ──
      React.createElement("div", { className: "sect", style: { marginTop: 18 } },
        React.createElement("div", { className: "sect-top" },
          React.createElement("span", { className: "lbl", style: { color: "var(--ghost)" } }, "To Register"),
          React.createElement("span", { style: { fontSize: 14, fontWeight: 700, color: "var(--blue)" } }, addFleetStaged.length)
        )
      ),
      addFleetStaged.length === 0
        ? React.createElement("div", { style: { fontSize: 12, color: "var(--ghost)", padding: "8px 0" } }, "No kegs added yet — type a number and tap \u2713")
        : React.createElement("div", { style: { marginTop: 6 } }, addFleetStaged.map(function(k, i) {
            var is18 = k.size === "18L";
            return React.createElement("div", { key: i, style: { padding: "10px 0", borderBottom: "1px solid var(--rule-light)", display: "flex", alignItems: "center", gap: 10 } },
              React.createElement("span", { style: { fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 4, background: is18 ? "rgba(29,78,216,.08)" : "rgba(212,89,138,.08)", color: is18 ? "var(--blue)" : "var(--summer)" } }, k.size),
              React.createElement("span", { style: { fontSize: 13, fontWeight: 700 } }, k.kegId),
              React.createElement("button", { style: { marginLeft: "auto", background: "transparent", border: "none", color: "var(--danger)", fontSize: 18, fontWeight: 700, cursor: "pointer", padding: "0 6px" }, onClick: function() { unstageKeg(i); } }, "\u00d7")
            );
          })),
      React.createElement("button", { className: "btn", style: { marginTop: 12, background: "var(--blue)", color: "#fff", opacity: addFleetStaged.length > 0 ? 1 : 0.4, pointerEvents: addFleetStaged.length > 0 ? "auto" : "none" }, onClick: syncing ? null : function() { submitStagedKegs("register"); } },
        syncing ? React.createElement(Spinner, null) : React.createElement(React.Fragment, null, React.createElement(IcoCheck, null), " Register " + (addFleetStaged.length > 0 ? addFleetStaged.length + " " : "") + "Keg" + (addFleetStaged.length === 1 ? "" : "s"))
      )
    );
  }

    // Manual keg ID submit (from fill flow modal)
  function fkSubmitManual() {
    var st = fkManualOpen;
    if (!st || !fkManualDigits || fkManualDigits.length < 1) return;
    var padded = fkManualDigits.padStart(3, "0");
    var kegId = "KEG-" + st.size + "-" + padded;
    var sizeLabel = st.size === "18" ? "18L" : st.size === "09" ? "9L" : "7L";

    // If the keg is in the register and currently in an active state (At Customer,
    // In Fridge, To Be Washed, Staff/Market), warn before overwriting — a misread
    // handwritten ID shouldn't silently mark someone else's keg as freshly filled.
    // Unregistered or unknown kegs are allowed through: the backend auto-registers
    // them via upsertKegRegister.
    var existingKeg = kegLookup(kegId);
    if (existingKeg && !isKegFillable(existingKeg.status)) {
      var locText = existingKeg.location ? " at " + existingKeg.location : "";
      var msg = kegId + " is currently marked \"" + existingKeg.status + "\"" + locText + ".\n\n" +
                "Filling will overwrite this and mark the keg as freshly filled. " +
                "Double-check you read the ID correctly.\n\nContinue?";
      if (!window.confirm(msg)) return;
    }

    // LAST KEG path: write the partial keg's ID separately, not into fkScanned/fkLtdScanned.
    if (st.isLast) {
      // Reject duplicates against both lists
      var existsInScanned = (st.phase === "limited" ? fkLtdScanned : fkScanned).indexOf(kegId) !== -1;
      if (existsInScanned || fkLastKegScannedId === kegId) {
        toast(kegId + " already added", "var(--warning, #C17817)");
        return;
      }
      setFkLastKegScannedId(kegId);
      toast(kegId + " set as last keg", "var(--success)");
      setFkManualDigits("");
      setFkManualOpen(null);
      return;
    }

    if (st.phase === "limited") {
      setFkLtdScanned(function(prev) {
        var existing = prev.length > 0 && prev[0] !== "confirmed" ? prev : [];
        if (existing.indexOf(kegId) !== -1) { toast(kegId + " already added", "var(--warning, #C17817)"); return prev; }
        if (fkLastKegScannedId === kegId) { toast(kegId + " is already the last keg", "var(--warning, #C17817)"); return prev; }
        var merged = existing.concat([kegId]);
        // Only update the count for the size that was actually added — don't clobber others
        if (sizeLabel === "18L") {
          var c18 = merged.filter(function(k) { return parseSizeFromId(k) === "18L"; }).length;
          // Don't include the partial in this count: partial is tracked separately.
          var ltdAdj = (fkLastKegPartial && fkLastKegPartial.size === "18L") ? 1 : 0;
          setFkLtd18(String((Math.max(st.target || 0, c18) + ltdAdj) || ""));
        } else if (sizeLabel === "9L") {
          var c9 = merged.filter(function(k) { return parseSizeFromId(k) === "9L"; }).length;
          var ltdAdj9 = (fkLastKegPartial && fkLastKegPartial.size === "9L") ? 1 : 0;
          setFkLtd9(String((Math.max(st.target || 0, c9) + ltdAdj9) || ""));
        }
        toast(kegId + " added", "var(--success)");
        return merged;
      });
    } else {
      setFkScanned(function(prev) {
        var existing = prev.length > 0 && prev[0] !== "confirmed" ? prev : [];
        if (existing.indexOf(kegId) !== -1) { toast(kegId + " already added", "var(--warning, #C17817)"); return prev; }
        if (fkLastKegScannedId === kegId) { toast(kegId + " is already the last keg", "var(--warning, #C17817)"); return prev; }
        var merged = existing.concat([kegId]);
        // Only update the count for the size that was actually added — don't clobber others.
        // When a partial exists for this size, add 1 back so the keg count reflects the partial too.
        if (sizeLabel === "18L") {
          var c18b = merged.filter(function(k) { return parseSizeFromId(k) === "18L"; }).length;
          var adj18 = (fkLastKegPartial && fkLastKegPartial.size === "18L") ? 1 : 0;
          setFk18(String((Math.max(st.target || 0, c18b) + adj18) || ""));
        } else if (sizeLabel === "9L") {
          var c9b = merged.filter(function(k) { return parseSizeFromId(k) === "9L"; }).length;
          var adj9 = (fkLastKegPartial && fkLastKegPartial.size === "9L") ? 1 : 0;
          setFk9(String((Math.max(st.target || 0, c9b) + adj9) || ""));
        } else if (sizeLabel === "7L") {
          var c7b = merged.filter(function(k) { return parseSizeFromId(k) === "7L"; }).length;
          var adj7 = (fkLastKegPartial && fkLastKegPartial.size === "7L") ? 1 : 0;
          setFk7(String((Math.max(st.target || 0, c7b) + adj7) || ""));
        }
        toast(kegId + " added", "var(--success)");
        return merged;
      });
    }
    setFkManualDigits("");
    // Refocus the input so keyboard stays up and ready for the next 3 digits
    if (fkManualInputRef.current) {
      var el = fkManualInputRef.current;
      // requestAnimationFrame ensures the re-render with cleared value has flushed
      requestAnimationFrame(function() { try { el.focus(); } catch(e) {} });
    }
  }

  function renderFillKegs() {
    var isIcedEsp = fkTank && fkTank.product === "Iced Espresso";
    var n18 = parseInt(fk18) || 0, n9 = parseInt(fk9) || 0, n7 = isIcedEsp ? (parseInt(fk7) || 0) : 0, total = n18 + n9 + n7;
    var classicL = n18 * 18 + n9 * 9 + n7 * 7;
    var tankL = fkTank ? fkTank.currentLitres || 0 : 0;
    var calcRemainL = Math.max(0, tankL - classicL);
    // User can override the calculated remaining (Aaron's actual measured leftover may differ slightly per fill)
    var remainL = fkRemainOverride !== "" && !isNaN(parseFloat(fkRemainOverride))
      ? Math.max(0, parseFloat(fkRemainOverride))
      : calcRemainL;
    var isClassic = fkTank && fkTank.product === "Classic";
    var ltdN18 = parseInt(fkLtd18) || 0, ltdN9 = parseInt(fkLtd9) || 0, ltdTotal = ltdN18 + ltdN9;
    var ltdL = ltdN18 * 18 + ltdN9 * 9;
    var afterLtdL = Math.max(0, remainL - ltdL);
    var ltdRecipe = fkLimited ? limitedRecipes.find(function(r) { return r.product === fkLimited; }) : null;
    var prodCol = fkTank ? PC[fkTank.product] || "var(--blue)" : "var(--blue)";
    var ltdCol = fkLimited ? PC[fkLimited] || "var(--blue)" : "var(--blue)";

    function suggestKegs(litres) {
      if (litres <= 0) return { s18: 0, s9: 0 };
      var s18 = Math.floor(litres / 18 * 2 / 3);
      var used18 = s18 * 18;
      var s9 = Math.floor((litres - used18) / 9);
      return { s18: s18, s9: s9 };
    }
    var classicSugg = suggestKegs(fkLimited ? Math.floor(tankL * 0.7) : tankL);
    var placeholder18 = String(classicSugg.s18);
    var placeholder9 = String(classicSugg.s9);

    var fillLabels = []
      .concat(n18 > 0 ? [n18 + "x 18L kegs filled"] : [])
      .concat(n9 > 0 ? [n9 + "x 9L kegs filled"] : [])
      .concat(n7 > 0 ? [n7 + "x 7L kegs filled"] : [])
      .concat([
      "Kegs at pressure",
      "Keg posts lubed",
      "Keg tags added"
    ]);
    var ltdFillLabels = []
      .concat(ltdN18 > 0 ? [ltdN18 + "x 18L kegs filled"] : [])
      .concat(ltdN9 > 0 ? [ltdN9 + "x 9L kegs filled"] : [])
      .concat([
      "Kegs at pressure",
      "Keg posts lubed",
      "Keg tags added"
    ]);
    var allCk = fillLabels.length > 0 && fillLabels.every(function(lbl, i) { return !!fkChecks[i]; });
    // Gate on the VISIBLE limited labels, not the full 5-slot check array. An 18L-only
    // (or 9L-only) limited fill renders only 4 checkboxes, so requiring all 5 array
    // slots left "Add to Fridge" permanently greyed. Mirrors the classic allCk logic.
    var allLtdCk = ltdFillLabels.length > 0 && ltdFillLabels.every(function(lbl, i) { return !!fkLtdChecks[i]; });

    // Header row: back/label on left, "Save & Close" pill on right.
    // backHandler is optional — if null, the left side just shows the label (no arrow / not clickable).
    // showSaveClose defaults true but can be disabled per-phase if needed.
    function fillHeader(label, backHandler, showSaveClose) {
      var showClose = showSaveClose !== false && !!fkTank;
      return React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 14 } },
        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, cursor: backHandler ? "pointer" : "default", flex: 1, minWidth: 0 }, onClick: backHandler || null },
          backHandler ? React.createElement(IcoBack, null) : null,
          React.createElement("span", { className: "h2", style: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } }, label)
        ),
        showClose ? React.createElement("button", {
          onClick: saveAndCloseFill,
          style: {
            background: "none",
            border: "1.5px solid var(--rule)",
            borderRadius: 8,
            padding: "6px 12px",
            fontSize: 11,
            fontWeight: 700,
            color: "var(--dim)",
            cursor: "pointer",
            whiteSpace: "nowrap",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 6
          }
        },
          React.createElement("svg", { style: { width: 12, height: 12, stroke: "var(--dim)", fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }, viewBox: "0 0 24 24" },
            React.createElement("path", { d: "M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" }),
            React.createElement("polyline", { points: "17 21 17 13 7 13 7 21" }),
            React.createElement("polyline", { points: "7 3 7 8 15 8" })
          ),
          "Save & Close"
        ) : null
      );
    }

    // Tag + BB info bar (reused in setup and classic phases)
    var tagBBBar = fkTank ? React.createElement("div", { style: { display: "flex", gap: 14, padding: "8px 12px", background: "var(--input-bg)", borderRadius: 8, alignItems: "center", marginTop: 8 } },
      fkTank.kegTag && React.createElement("div", { style: { position: "relative", overflow: "hidden", padding: "5px 14px", borderRadius: 6, background: prodCol, color: "#fff", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 } },
        fkTank.kegTag === "Stripe" && React.createElement("div", { style: { position: "absolute", top: "50%", left: 0, right: 0, height: 4, background: "#fff", opacity: 0.6, transform: "translateY(-50%)", pointerEvents: "none" } }),
        React.createElement("span", { style: { position: "relative", zIndex: 1 } }, fkTank.kegTag, " tags")
      ),
      fkTank.bestBefore && React.createElement("div", { style: { fontSize: 11, color: "var(--dim)" } }, "BB: ", React.createElement("strong", null, fmtDate(fkTank.bestBefore)))
    ) : null;

    return React.createElement("div", { className: "scr", style: { animation: "fu .2s ease" } },

      // === SETUP PHASE ===
      fkPhase === "setup" && React.createElement(React.Fragment, null,
        fillHeader("Fill Kegs", function() {
          // If there's meaningful progress, arm the resume banner so user can pick up where they left off
          if (fkTank && (fk18 || fk9 || fk7 || fkLimited)) {
            var saved = loadFillSession();
            if (saved && saved.tankId) setFkResumeSession(saved);
          }
          setSub(null);
        }),

        // SELECT BRIGHT TANK
        React.createElement("div", { className: "sect", style: { marginTop: 0 } },
          React.createElement("div", { className: "h2", style: { fontSize: 18 } }, "Select Bright Tank")
        ),
        React.createElement("div", { style: { display: "flex", gap: 10, borderTop: "1px solid var(--rule)", marginTop: 10, paddingTop: 12 } },
          TANK_DEF.map(function(t) {
            var ts = getTank(t.id);
            var has = ts && ts.product && ts.status === "Ready";
            var col = has ? PC[ts.product] || "#bbb" : "var(--rule)";
            var L = ts ? ts.currentLitres || 0 : 0;
            var pct = Math.min(100, Math.round(L / t.cap * 100));
            var sel = fkTank && fkTank.tankId === t.id;
            return React.createElement("div", {
              key: t.id,
              style: { flex: 1, textAlign: "center", cursor: has ? "pointer" : "not-allowed", opacity: has ? 1 : 0.35, border: "2px solid " + (sel ? col : "var(--rule)"), borderRadius: 10, padding: 12 },
              onClick: has ? function() { setFkTank(ts); setFkLimited(null); setFk18(""); setFk9(""); setFkLtd18(""); setFkLtd9(""); setFkRemainOverride(""); setFkLastKegPartial(null); setFkLastKegScannedId(""); } : null
            },
              React.createElement("div", { className: "mt-c", style: { width: 60, height: 60, borderColor: col, margin: "0 auto", background: pct > 0 ? "linear-gradient(to top, " + col + " " + pct + "%, #fff " + pct + "%)" : "#fff" } },
                React.createElement("div", { className: "mt-v", style: { fontSize: 14 } }, has ? L : ""),
                has && React.createElement("div", { style: { position: "relative", zIndex: 1 } },
                  React.createElement("span", { style: { display: "inline-block", padding: "1px 5px", borderRadius: 50, background: col, color: "#fff", fontSize: 6, fontWeight: 700, textTransform: "uppercase" } }, PS[ts.product] || ts.product)
                )
              ),
              React.createElement("div", { style: { marginTop: 6, fontWeight: 700, fontSize: 11 } }, t.id),
              React.createElement("div", { style: { fontSize: 9, color: has ? "var(--success)" : "var(--ghost)", fontWeight: 700, marginTop: 2 } }, has ? "READY" : "EMPTY")
            );
          })
        ),

        // LIMITED RELEASE TOGGLE (Classic only)
        fkTank && isClassic && React.createElement("div", { className: "sect" },
          React.createElement("div", { className: "h2", style: { fontSize: 18 } }, "Limited Release?"),
          React.createElement("div", { style: { display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" } },
            React.createElement("div", {
              style: { padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", border: !fkLimited ? "2px solid var(--blue)" : "2px solid var(--rule)", background: !fkLimited ? "rgba(29,78,216,.08)" : "transparent", color: !fkLimited ? "var(--blue)" : "var(--dim)" },
              onClick: function() { setFkLimited(null); setFkLtd18(""); setFkLtd9(""); }
            }, "Classic Only"),
            limitedRecipes.map(function(lr) {
              var sel = fkLimited === lr.product;
              var lrCol = PC[lr.product] || "#bbb";
              return React.createElement("div", {
                key: lr.product,
                style: { padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", border: sel ? "2px solid " + lrCol : "2px solid var(--rule)", background: sel ? lrCol : "transparent", color: sel ? "#fff" : "var(--dim)" },
                onClick: function() { setFkLimited(sel ? null : lr.product); setFkLtd18(""); setFkLtd9(""); setFkLtdScanned([]); setFkLtdChecks([false,false,false,false,false]); setFkRecipeConfirmed(false); setFkLtdTag(""); }
              }, lr.product);
            })
          )
        ),

        // CLASSIC KEG COUNTS (with tag+BB inside)
        fkTank && React.createElement("div", { className: "sect" },
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 4 } },
            React.createElement(ProductPill, { name: fkTank.product, size: 13 }),
            React.createElement("span", { className: "h2", style: { fontSize: 18 } }, "Keg Counts")
          ),
          tagBBBar,
          React.createElement("div", { className: "frow", style: { marginTop: 10 } },
            React.createElement("div", { className: "ffield" },
              React.createElement("div", { className: "flabel" }, "18L Kegs"),
              React.createElement("input", { className: "input input-mono", type: "number", inputMode: "numeric", placeholder: placeholder18, value: fk18, onChange: function(e) { setFk18(e.target.value); }, style: { color: fk18 === "" ? "var(--ghost)" : "var(--text)" } })
            ),
            React.createElement("div", { className: "ffield" },
              React.createElement("div", { className: "flabel" }, "9L Kegs"),
              React.createElement("input", { className: "input input-mono", type: "number", inputMode: "numeric", placeholder: placeholder9, value: fk9, onChange: function(e) { setFk9(e.target.value); }, style: { color: fk9 === "" ? "var(--ghost)" : "var(--text)" } })
            ),
            isIcedEsp && React.createElement("div", { className: "ffield" },
              React.createElement("div", { className: "flabel" }, "7L Kegs"),
              React.createElement("input", { className: "input input-mono", type: "number", inputMode: "numeric", placeholder: "0", value: fk7, onChange: function(e) { setFk7(e.target.value); }, style: { color: fk7 === "" ? "var(--ghost)" : "var(--text)" } })
            )
          ),
          total > 0 && React.createElement("div", { style: { marginTop: 8, fontSize: 12, color: "var(--dim)" } },
            "Using ", React.createElement("strong", null, classicL, "L"), " of ", tankL, "L"
          )
        ),

        // LIMITED KEG COUNTS (shows immediately when limited selected)
        fkTank && fkLimited && React.createElement("div", { className: "sect" },
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 4 } },
            React.createElement(ProductPill, { name: fkLimited, size: 13 }),
            React.createElement("span", { className: "h2", style: { fontSize: 18 } }, "Keg Counts")
          ),
          React.createElement("div", { style: { fontSize: 28, fontWeight: 800, color: PC[fkLimited] || "var(--blue)", marginTop: 4 } }, remainL, React.createElement("span", { style: { fontSize: 14, fontWeight: 500, color: "var(--dim)" } }, "L available")),
          // KEG TAG + BB bar for limited
          function() {
            var lastTag = "", lastFillId = "";
            for (var ki = kegFilling.length - 1; ki >= 0; ki--) {
              if (kegFilling[ki].product === fkLimited && kegFilling[ki].kegTag) { lastTag = kegFilling[ki].kegTag; lastFillId = kegFilling[ki].brewId; break; }
            }
            var ltdProdCol = PC[fkLimited] || "var(--blue)";
            return React.createElement("div", { style: { display: "flex", gap: 14, padding: "8px 12px", background: "var(--input-bg)", borderRadius: 8, alignItems: "center", marginTop: 10 } },
              React.createElement("div", { style: { display: "flex", gap: 6 } },
                function() {
                  var selS = fkLtdTag === "Solid";
                  var selT = fkLtdTag === "Stripe";
                  var base = { position: "relative", overflow: "hidden", padding: "5px 14px", borderRadius: 6, background: ltdProdCol, color: "#fff", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, cursor: "pointer", transition: "all .2s" };
                  return [
                    React.createElement("div", { key: "Solid", style: Object.assign({}, base, { border: selS ? "2px solid rgba(255,255,255,.7)" : "2px solid transparent", filter: selS ? "none" : "brightness(0.7)" }), onClick: function() { setFkLtdTag("Solid"); } },
                      React.createElement("span", null, "Solid tags")
                    ),
                    React.createElement("div", { key: "Stripe", style: Object.assign({}, base, { border: selT ? "2px solid rgba(255,255,255,.7)" : "2px solid transparent", filter: selT ? "none" : "brightness(0.7)" }), onClick: function() { setFkLtdTag("Stripe"); } },
                      React.createElement("div", { style: { position: "absolute", top: "50%", left: 0, right: 0, height: 4, background: "#fff", opacity: 0.7, transform: "translateY(-50%)", pointerEvents: "none", zIndex: 0 } }),
                      React.createElement("span", { style: { position: "relative", zIndex: 1 } }, "Stripe tags")
                    )
                  ];
                }()
              ),
              React.createElement("div", { style: { marginLeft: "auto", textAlign: "right" } },
                fkTank.bestBefore && React.createElement("div", { style: { fontSize: 11, color: "var(--dim)" } }, "BB: ", React.createElement("strong", null, fmtDate(fkTank.bestBefore))),
                React.createElement("div", { style: { fontSize: 10, color: "var(--ghost)" } },
                  lastTag ? "Last fill was " + lastTag : "No history yet"
                )
              )
            );
          }(),
          function() {
            var ltdSugg = total > 0 ? suggestKegs(remainL) : { s18: 0, s9: 0 };
            return React.createElement("div", { className: "frow", style: { marginTop: 10 } },
              React.createElement("div", { className: "ffield" },
                React.createElement("div", { className: "flabel" }, "18L Kegs"),
                React.createElement("input", { className: "input input-mono", type: "number", inputMode: "numeric", placeholder: total > 0 ? String(ltdSugg.s18) : "0", value: fkLtd18, onChange: function(e) { setFkLtd18(e.target.value); }, style: { color: fkLtd18 === "" ? "var(--ghost)" : "var(--text)" } })
              ),
              React.createElement("div", { className: "ffield" },
                React.createElement("div", { className: "flabel" }, "9L Kegs"),
                React.createElement("input", { className: "input input-mono", type: "number", inputMode: "numeric", placeholder: total > 0 ? String(ltdSugg.s9) : "0", value: fkLtd9, onChange: function(e) { setFkLtd9(e.target.value); }, style: { color: fkLtd9 === "" ? "var(--ghost)" : "var(--text)" } })
              )
            );
          }()
        ),

        // LIMITED RECIPE CARD (preview on setup screen)
        fkTank && fkLimited && ltdRecipe && React.createElement("div", { className: "sect" },
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8 } },
            React.createElement(ProductPill, { name: fkLimited, size: 13 }),
            React.createElement("span", { className: "h2", style: { fontSize: 18 } }, "Recipe")
          ),
          React.createElement("div", { style: { border: "1.5px solid " + ltdCol, borderRadius: 10, padding: 14, marginTop: 6 } },
            React.createElement("div", { style: { fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "var(--ghost)", marginBottom: 8 } }, "Flavour Addition for ", ltdL > 0 ? ltdL : remainL, "L"),
            (ltdRecipe.ingredients || []).map(function(ing, idx) {
              var mlTotal = (parseFloat(ing.rate) || 0) * (ltdL > 0 ? ltdL : remainL);
              return React.createElement("div", { key: "setup-ing-" + idx, style: { display: "flex", justifyContent: "space-between", padding: "6px 0", borderTop: idx > 0 ? "1px solid var(--rule-light)" : "none" } },
                React.createElement("span", { style: { fontSize: 13, fontWeight: 600 } }, ing.name),
                React.createElement("span", { style: { fontSize: 14, fontWeight: 700 } }, mlTotal.toFixed(0), React.createElement("span", { style: { fontSize: 10, color: "var(--ghost)", marginLeft: 2 } }, "ml"))
              );
            }),
            ltdRecipe.method && React.createElement("div", { style: { marginTop: 12 } },
              React.createElement("div", { style: { fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "var(--ghost)", marginBottom: 6 } }, "Method"),
              React.createElement("div", { style: { fontSize: 13, color: "var(--dim)", whiteSpace: "pre-line" } }, ltdRecipe.method)
            )
          )
        ),

        // REMAINING LITRES — editable input (calc value is placeholder/default)
        fkTank && total > 0 && function() {
          var displayRemain = afterLtdL > 0 || (fkLimited && ltdTotal > 0) ? afterLtdL : remainL;
          var calcDisplay = afterLtdL > 0 || (fkLimited && ltdTotal > 0) ? Math.max(0, calcRemainL - ltdL) : calcRemainL;
          var isOverridden = fkRemainOverride !== "" && !fkLimited;
          return React.createElement("div", { style: { textAlign: "center", marginTop: 16, padding: "12px 0" } },
            // Editable when no limited split, otherwise show computed afterLtdL
            !fkLimited
              ? React.createElement("div", { style: { display: "flex", alignItems: "baseline", justifyContent: "center", gap: 4 } },
                  React.createElement("input", {
                    type: "text",
                    inputMode: "decimal",
                    value: fkRemainOverride,
                    placeholder: String(calcDisplay),
                    onChange: function(e) {
                      var v = e.target.value.replace(/[^0-9.]/g, "");
                      // only allow one decimal point
                      var parts = v.split(".");
                      if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
                      setFkRemainOverride(v);
                    },
                    style: {
                      fontSize: 36, fontWeight: 800, color: "var(--text)",
                      width: 110, textAlign: "right",
                      background: "transparent",
                      border: "none",
                      borderBottom: "2px solid " + (isOverridden ? prodCol : "var(--rule)"),
                      outline: "none",
                      padding: "2px 4px",
                      fontFamily: "var(--sans)"
                    }
                  }),
                  React.createElement("span", { style: { fontSize: 14, fontWeight: 500, color: "var(--dim)" } }, "L")
                )
              : React.createElement("div", { style: { fontSize: 36, fontWeight: 800, color: "var(--text)" } }, displayRemain, React.createElement("span", { style: { fontSize: 14, fontWeight: 500, color: "var(--dim)" } }, "L")),
            React.createElement("div", { style: { fontSize: 11, color: "var(--ghost)", marginTop: 2 } },
              "remaining in tank",
              !fkLimited && isOverridden ? React.createElement("span", null,
                " \u00b7 calc: " + calcDisplay + "L \u00b7 ",
                React.createElement("span", { style: { color: prodCol, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }, onClick: function() { setFkRemainOverride(""); setFkLastKegPartial(null); setFkLastKegScannedId(""); } }, "reset")
              ) : null
            )
          );
        }(),

        // START FILLING BUTTON
        fkTank && (total > 0 || (fkLimited && (parseInt(fkLtd18) || 0) + (parseInt(fkLtd9) || 0) > 0)) && (function() {
          var classicHasKegs = total > 0;
          var ltdHasKegs = !!fkLimited && ((parseInt(fkLtd18) || 0) + (parseInt(fkLtd9) || 0)) > 0;
          // If only the limited section has counts, skip the classic phase entirely
          var skipClassic = !classicHasKegs && ltdHasKegs;
          var label = skipClassic ? "Start Filling " + fkLimited : "Start Filling " + fkTank.product;
          return React.createElement("div", { style: { marginTop: 12 } },
            React.createElement("button", {
              className: "btn btn-blue",
              style: { background: skipClassic ? "var(--accent2, #d8542e)" : prodCol },
              onClick: function() {
                if (skipClassic) {
                  setFkPhase("limited");
                  // Don't re-lock an already-completed checklist when returning
                  // to an in-progress fill (e.g. after tapping Back to setup).
                  if (!allLtdCk) setFkLtdChecks([false,false,false,false,false]);
                } else {
                  setFkPhase("classic");
                  if (!allCk) setFkChecks([false,false,false,false,false]);
                }
              }
            }, label)
          );
        })()
      ),

      // === CLASSIC FILL PHASE ===
      fkPhase === "classic" && fkTank && React.createElement(React.Fragment, null,
        fillHeader("Fill " + fkTank.product, function() { setFkPhase("setup"); }),
        // Summary bar with tag+BB
        React.createElement("div", { style: { padding: "10px 14px", background: "var(--input-bg)", borderRadius: 8, marginBottom: 14 } },
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 10 } },
            React.createElement(ProductPill, { name: fkTank.product, size: 11 }),
            React.createElement("span", { style: { fontSize: 13, fontWeight: 700 } }, n18, "x 18L + ", n9, "x 9L", n7 > 0 ? " + " + n7 + "x 7L" : ""),
            React.createElement("span", { style: { fontSize: 11, color: "var(--dim)", marginLeft: "auto" } }, total, " kegs")
          ),
          tagBBBar ? React.createElement("div", { style: { marginTop: 6 } }, tagBBBar) : null
        ),

        // Editable keg counts — adjust on the fly
        React.createElement("div", { className: "sect", style: { marginTop: 0 } },
          React.createElement("div", { className: "h2", style: { fontSize: 18 } }, "Keg Counts"),
          React.createElement("div", { style: { fontSize: 11, color: "var(--ghost)", marginBottom: 8 } }, "Adjust if needed based on available kegs or sediment")
        ),
        React.createElement("div", { style: { display: "flex", gap: 10 } },
          React.createElement("div", { style: { flex: 1 } },
            React.createElement("div", { className: "flabel" }, "18L"),
            React.createElement("input", { className: "input input-mono", type: "number", inputMode: "numeric", value: fk18, onChange: function(e) { setFk18(e.target.value); }, style: { textAlign: "center", fontSize: 18, fontWeight: 700 } })
          ),
          React.createElement("div", { style: { flex: 1 } },
            React.createElement("div", { className: "flabel" }, "9L"),
            React.createElement("input", { className: "input input-mono", type: "number", inputMode: "numeric", value: fk9, onChange: function(e) { setFk9(e.target.value); }, style: { textAlign: "center", fontSize: 18, fontWeight: 700 } })
          ),
          isIcedEsp && React.createElement("div", { style: { flex: 1 } },
            React.createElement("div", { className: "flabel" }, "7L"),
            React.createElement("input", { className: "input input-mono", type: "number", inputMode: "numeric", value: fk7, onChange: function(e) { setFk7(e.target.value); }, style: { textAlign: "center", fontSize: 18, fontWeight: 700 } })
          )
        ),

        // Remaining litres + drain option (editable — Aaron may measure a different actual remainder)
        React.createElement("div", { style: { textAlign: "center", marginTop: 12, padding: "10px 0" } },
          React.createElement("div", { style: { display: "flex", alignItems: "baseline", justifyContent: "center", gap: 4, fontSize: 14, color: "var(--dim)" } },
            React.createElement("input", {
              type: "text",
              inputMode: "decimal",
              value: fkRemainOverride,
              placeholder: String(calcRemainL),
              onChange: function(e) {
                var v = e.target.value.replace(/[^0-9.]/g, "");
                var parts = v.split(".");
                if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
                setFkRemainOverride(v);
              },
              style: {
                fontSize: 16, fontWeight: 800, color: "var(--text)",
                width: 70, textAlign: "right",
                background: "transparent",
                border: "none",
                borderBottom: "2px solid " + (fkRemainOverride !== "" ? prodCol : "var(--rule)"),
                outline: "none",
                padding: "2px 4px",
                fontFamily: "var(--sans)"
              }
            }),
            React.createElement("strong", { style: { fontSize: 14, fontWeight: 700 } }, "L"),
            React.createElement("span", null, " remaining in tank")
          ),
          fkRemainOverride !== "" && parseFloat(fkRemainOverride) !== calcRemainL ? React.createElement("div", { style: { fontSize: 10, color: "var(--ghost)", marginTop: 3 } },
            "Calculated: " + calcRemainL + "L \u00b7 ",
            React.createElement("span", { style: { color: prodCol, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }, onClick: function() { setFkRemainOverride(""); setFkLastKegPartial(null); setFkLastKegScannedId(""); } }, "reset to calc")
          ) : null,
          remainL > 0 && total > 0 && !fkLimited && React.createElement("div", { style: { marginTop: 8 } },
            !fkDrainOpen ? React.createElement("button", {
              style: { background: "none", border: "1.5px solid var(--rule)", borderRadius: 8, padding: "6px 14px", fontSize: 11, fontWeight: 700, color: "var(--dim)", cursor: "pointer" },
              onClick: function() { setFkDrainOpen(true); setFkDrainReason(""); }
            }, "Empty Tank \u2014 ", remainL, "L") :
            React.createElement("div", { style: { textAlign: "left", marginTop: 6, padding: "12px 14px", background: "var(--input-bg)", borderRadius: 10 } },
              React.createElement("div", { style: { fontSize: 12, fontWeight: 700, marginBottom: 6 } }, "Drain ", remainL, "L from ", fkTank.tankId),
              React.createElement("div", { className: "flabel", style: { marginBottom: 4 } }, "Reason"),
              React.createElement("input", {
                className: "input",
                type: "text",
                placeholder: "e.g. Too coarse to keg, sediment, etc.",
                value: fkDrainReason,
                onChange: function(e) { setFkDrainReason(e.target.value); },
                style: { fontSize: 13, marginBottom: 8 }
              }),
              React.createElement("div", { style: { display: "flex", gap: 8 } },
                React.createElement("button", {
                  className: "btn",
                  style: { flex: 1, background: fkDrainReason.trim() ? "var(--danger)" : "var(--rule)", color: "#fff", fontSize: 12, opacity: fkDrainReason.trim() ? 1 : 0.5 },
                  onClick: fkDrainReason.trim() ? function() { drainTankRemaining(remainL); } : null
                }, syncing ? React.createElement(Spinner, null) : "Drain " + remainL + "L"),
                React.createElement("button", {
                  style: { background: "none", border: "1.5px solid var(--rule)", borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 700, color: "var(--dim)", cursor: "pointer" },
                  onClick: function() { setFkDrainOpen(false); }
                }, "Cancel")
              )
            )
          )
        ),
        // Last Keg — appears BEFORE Fill Checklist on the final fill phase only (no Limited queued)
        !fkLimited && total > 0 && (function() {
          var partial = fkLastKegPartial;
          // Determine the planned last size — when partial set, use partial.size; else smallest with at least 1 keg
          var lastSize = null;
          if (partial && partial.size) {
            lastSize = partial.size;
          } else {
            if (isIcedEsp && n7 > 0) lastSize = "7L";
            else if (n9 > 0) lastSize = "9L";
            else if (n18 > 0) lastSize = "18L";
          }
          if (!lastSize) return null;
          // Display: "9L (planned)" until updated, "12.3L (partial)" after
          var displayPrimary = partial ? (partial.litres + "L") : lastSize;
          var displaySecondary = partial
            ? "partial \u00b7 " + partial.size + " keg \u2192 Staff/Market"
            : "planned";
          var accent = partial ? "var(--warning, #C17817)" : prodCol;
          var bg = partial ? "rgba(193, 120, 23, 0.08)" : "var(--input-bg)";
          var border = partial ? "1px solid rgba(193,120,23,.3)" : "1px solid transparent";
          return React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: bg, borderRadius: 10, marginBottom: 12, border: border } },
            React.createElement("div", { style: { minWidth: 0 } },
              React.createElement("div", { style: { fontSize: 11, fontWeight: 700, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 0.5 } }, "Last keg"),
              React.createElement("div", { style: { fontSize: 16, fontWeight: 800, marginTop: 2, color: partial ? accent : "var(--text)" } },
                displayPrimary,
                React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: partial ? accent : "var(--ghost)", marginLeft: 6 } },
                  partial ? "(partial)" : "(planned)"
                )
              ),
              partial ? React.createElement("div", { style: { fontSize: 10, fontWeight: 600, color: accent, marginTop: 1 } }, displaySecondary) : null
            ),
            React.createElement("button", {
              onClick: function() {
                setFkLastKegInput(partial && partial.litres ? String(partial.litres) : "");
                setFkLastKegOpen(true);
              },
              style: { background: "none", border: "1.5px solid " + accent, borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, color: accent, cursor: "pointer", flexShrink: 0 }
            }, partial ? "Edit" : "Update \u2192")
          );
        })(),

        // Fill Checklist
        React.createElement("div", { className: "sect", style: { marginTop: 0 } },
          React.createElement("div", { className: "h2", style: { fontSize: 18 } }, "Fill Checklist")
        ),
        fillLabels.map(function(lbl, i) {
          return React.createElement(CheckBox, { key: i, checked: fkChecks[i], label: lbl, onClick: function() { var c = fkChecks.slice(); c[i] = !c[i]; setFkChecks(c); } });
        }),

        // Add to Fridge — always visible, greyed until checklist done
        React.createElement("div", { className: "sect" },
          React.createElement("div", { className: "h2", style: { fontSize: 18, opacity: allCk ? 1 : 0.35 } }, "Add to Fridge")
        ),
        React.createElement("div", { style: { opacity: allCk ? 1 : 0.35, pointerEvents: allCk ? "auto" : "none" } },
          // Keg count summary
          React.createElement("div", { style: { padding: "14px 16px", background: "var(--input-bg)", borderRadius: 10, marginTop: 8 } },
            React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" } },
              React.createElement("div", null,
                n18 > 0 && React.createElement("div", { style: { fontSize: 18, fontWeight: 800 } }, n18, " \u00d7 18L"),
                n9 > 0 && React.createElement("div", { style: { fontSize: 18, fontWeight: 800, marginTop: n18 > 0 ? 4 : 0 } }, n9, " \u00d7 9L"),
                n7 > 0 && React.createElement("div", { style: { fontSize: 18, fontWeight: 800, marginTop: 4 } }, n7, " \u00d7 7L")
              ),
              React.createElement("div", { style: { fontSize: 28, fontWeight: 800, color: prodCol } }, total, React.createElement("span", { style: { fontSize: 13, fontWeight: 500, color: "var(--dim)", marginLeft: 4 } }, "kegs"))
            )
          ),
          // Scan buttons + manual entry — FIRST (above fridge tick)
          React.createElement("div", { style: { marginTop: 14 } },
            (function() {
              // Partial adjustments — if a last keg is partial, that 1 keg is tracked separately
              var partial = fkLastKegPartial;
              var pAdj18 = (partial && partial.size === "18L") ? 1 : 0;
              var pAdj9 = (partial && partial.size === "9L") ? 1 : 0;
              var pAdj7 = (partial && partial.size === "7L") ? 1 : 0;
              // Regular row counts EXCLUDE the partial keg (it gets its own row below)
              var reg18 = Math.max(0, n18 - pAdj18);
              var reg9 = Math.max(0, n9 - pAdj9);
              var reg7 = Math.max(0, n7 - pAdj7);
              // Build batch list based on regular (non-partial) keg counts
              var batches = [];
              if (reg18 > 0) batches.push({ size: "18", label: "18L", count: reg18, color: prodCol });
              if (reg9 > 0) batches.push({ size: "09", label: "9L", count: reg9, color: prodCol });
              if (reg7 > 0) batches.push({ size: "07", label: "7L", count: reg7, color: prodCol });
              // Figure out which IDs are already scanned per size (regular only — partial is separate)
              var realIds = fkScanned.length > 0 && fkScanned[0] !== "confirmed" ? fkScanned : [];
              var scanned18 = realIds.filter(function(k) { return parseSizeFromId(k) === "18L"; });
              var scanned09 = realIds.filter(function(k) { return parseSizeFromId(k) === "9L"; });
              var scanned07 = realIds.filter(function(k) { return parseSizeFromId(k) === "7L"; });
              var scannedMap = { "18": scanned18, "09": scanned09, "07": scanned07 };

              if (batches.length === 0 && !partial) return React.createElement("div", { style: { fontSize: 11, color: "var(--ghost)", textAlign: "center" } }, "Enter keg counts to enable scanning");

              return React.createElement(React.Fragment, null,
                batches.map(function(b) {
                  var done = scannedMap[b.size].length >= b.count;
                  var partialScan = scannedMap[b.size].length > 0 && scannedMap[b.size].length < b.count;
                  var label = done
                    ? b.label + " \u2713 " + scannedMap[b.size].length + "/" + b.count
                    : partialScan
                      ? "Scan " + b.label + " (" + scannedMap[b.size].length + "/" + b.count + ")"
                      : "Scan " + b.label + " Kegs (" + b.count + ")";
                  return React.createElement("div", { key: b.size, style: { display: "flex", gap: 6, marginBottom: 6 } },
                    React.createElement("button", {
                      className: done ? "btn" : "btn btn-blue",
                      style: { fontSize: 12, flex: 3, whiteSpace: "nowrap", background: done ? "var(--success)" : undefined, color: "#fff", opacity: done ? 0.7 : 1 },
                      onClick: done ? null : function() {
                        var remaining = b.count - scannedMap[b.size].length;
                        bscOpen({
                          mode: "fill",
                          expected: remaining,
                          label: "SCAN " + b.label + " KEGS (" + remaining + ")",
                          color: b.color,
                          onDone: function(ids) {
                            if (ids.length === 0) return;
                            // Merge new IDs with existing scanned IDs
                            setFkScanned(function(prev) {
                              var existing = prev.length > 0 && prev[0] !== "confirmed" ? prev : [];
                              // Filter out any that are already the last keg (defensive)
                              var safeIds = ids.filter(function(id) { return id !== fkLastKegScannedId; });
                              var merged = existing.concat(safeIds);
                              // Only update the count for the size that was just scanned — don't clobber others.
                              // Add the partial adjustment back so the count reflects total (regular + partial).
                              if (b.size === "18") {
                                var c18 = merged.filter(function(k) { return parseSizeFromId(k) === "18L"; }).length;
                                setFk18(String((Math.max(b.count, c18) + pAdj18) || ""));
                              } else if (b.size === "09") {
                                var c9 = merged.filter(function(k) { return parseSizeFromId(k) === "9L"; }).length;
                                setFk9(String((Math.max(b.count, c9) + pAdj9) || ""));
                              } else if (b.size === "07" && isIcedEsp) {
                                var c7 = merged.filter(function(k) { return parseSizeFromId(k) === "7L"; }).length;
                                setFk7(String((Math.max(b.count, c7) + pAdj7) || ""));
                              }
                              return merged;
                            });
                            toast(ids.length + " " + b.label + " kegs scanned", "var(--success)");
                          }
                        });
                      }
                    },
                      React.createElement("svg", { style: { width: 16, height: 16, stroke: "#fff", fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", marginRight: 6 }, viewBox: "0 0 24 24" },
                        done
                          ? React.createElement("polyline", { points: "20 6 9 17 4 12" })
                          : React.createElement(React.Fragment, null,
                              React.createElement("path", { d: "M4 7V4h3M20 7V4h-3M4 17v3h3M20 17v3h-3" }),
                              React.createElement("rect", { x: 9, y: 9, width: 6, height: 6, rx: 1 })
                            )
                      ), label
                    ),
                    // Manual entry button — disabled when batch is done
                    React.createElement("button", {
                      className: "btn btn-out",
                      style: { fontSize: 10, flex: 1, padding: "0 6px", whiteSpace: "normal", lineHeight: 1.15, opacity: done ? 0.4 : 1, pointerEvents: done ? "none" : "auto" },
                      onClick: done ? null : function() {
                        setFkManualDigits("");
                        setFkManualOpen({ size: b.size, target: b.count, phase: "classic", color: b.color, label: b.label });
                      }
                    }, "Enter Manually")
                  );
                }),
                // LAST KEG row — distinct row when partial is set
                partial ? (function() {
                  var lastSizeCode = partial.size === "18L" ? "18" : (partial.size === "9L" ? "09" : "07");
                  var lastColor = "var(--warning, #C17817)";
                  var lastDone = !!fkLastKegScannedId;
                  var lastLabel = lastDone
                    ? "Last Keg \u2713 " + partial.litres + "L"
                    : "Scan Last Keg \u2014 " + partial.size + " \u00b7 " + partial.litres + "L";
                  return React.createElement("div", { key: "last", style: { display: "flex", gap: 6, marginBottom: 6, marginTop: 4 } },
                    React.createElement("button", {
                      className: lastDone ? "btn" : "btn",
                      style: { fontSize: 12, flex: 3, whiteSpace: "nowrap", background: lastDone ? "var(--success)" : lastColor, color: "#fff", opacity: lastDone ? 0.7 : 1, border: "none" },
                      onClick: lastDone ? null : function() {
                        bscOpen({
                          mode: "fill",
                          expected: 1,
                          label: "SCAN LAST KEG (" + partial.size + " \u00b7 " + partial.litres + "L)",
                          color: lastColor,
                          onDone: function(ids) {
                            if (ids.length === 0) return;
                            // Take the first scanned ID; reject if it conflicts with regular scanned IDs
                            var lid = ids[0];
                            if (fkScanned.indexOf(lid) !== -1) { toast(lid + " is already a regular scanned keg", "var(--warning, #C17817)"); return; }
                            // Validate size matches partial.size
                            if (parseSizeFromId(lid) !== partial.size) {
                              toast("Last keg must be " + partial.size + " — scanned " + parseSizeFromId(lid), "var(--warning, #C17817)");
                              return;
                            }
                            setFkLastKegScannedId(lid);
                            toast("Last keg set: " + lid, "var(--success)");
                          }
                        });
                      }
                    },
                      React.createElement("svg", { style: { width: 16, height: 16, stroke: "#fff", fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", marginRight: 6 }, viewBox: "0 0 24 24" },
                        lastDone
                          ? React.createElement("polyline", { points: "20 6 9 17 4 12" })
                          : React.createElement(React.Fragment, null,
                              React.createElement("path", { d: "M4 7V4h3M20 7V4h-3M4 17v3h3M20 17v3h-3" }),
                              React.createElement("rect", { x: 9, y: 9, width: 6, height: 6, rx: 1 })
                            )
                      ),
                      lastLabel
                    ),
                    React.createElement("button", {
                      className: "btn btn-out",
                      style: { fontSize: 10, flex: 1, padding: "0 6px", whiteSpace: "normal", lineHeight: 1.15, opacity: lastDone ? 0.4 : 1, pointerEvents: lastDone ? "none" : "auto", borderColor: lastColor, color: lastColor },
                      onClick: lastDone ? null : function() {
                        setFkManualDigits("");
                        setFkManualOpen({ size: lastSizeCode, target: 1, phase: "classic", color: lastColor, label: "Last Keg " + partial.size, isLast: true });
                      }
                    }, "Enter Manually"),
                    lastDone ? React.createElement("button", {
                      style: { background: "none", border: "none", fontSize: 14, color: "var(--ghost)", cursor: "pointer", padding: "0 4px" },
                      onClick: function() { setFkLastKegScannedId(""); toast("Last keg cleared", "var(--dim)"); }
                    }, "\u00d7") : null
                  );
                })() : null,
                // Summary of all scanned (regular + partial)
                (realIds.length > 0 || fkLastKegScannedId) && React.createElement("div", { style: { fontSize: 11, color: "var(--success)", fontWeight: 700, textAlign: "center", marginTop: 4 } },
                  (realIds.length + (fkLastKegScannedId ? 1 : 0)) + " of " + total + " kegs scanned"
                ),
                // List of scanned IDs grouped by size (with remove × per ID)
                (realIds.length > 0 || fkLastKegScannedId) && React.createElement("div", { style: { marginTop: 8, padding: "8px 10px", background: "var(--input-bg)", borderRadius: 8, fontSize: 10 } },
                  ["18L", "9L", "7L"].map(function(sz) {
                    var ids = realIds.filter(function(k) { return parseSizeFromId(k) === sz; });
                    if (ids.length === 0) return null;
                    return React.createElement("div", { key: sz, style: { marginBottom: 6 } },
                      React.createElement("div", { style: { fontSize: 9, fontWeight: 700, color: "var(--dim)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 } }, sz + " (" + ids.length + ")"),
                      React.createElement("div", { style: { display: "flex", flexWrap: "wrap", gap: 4 } },
                        ids.map(function(kid) {
                          return React.createElement("span", { key: kid, style: { display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 6px", background: "var(--bg)", borderRadius: 4, fontFamily: "var(--mono, monospace)", fontSize: 10, fontWeight: 600 } },
                            kid,
                            React.createElement("button", {
                              style: { background: "none", border: "none", color: "var(--ghost)", cursor: "pointer", padding: 0, fontSize: 11, lineHeight: 1, fontWeight: 700 },
                              onClick: function() {
                                setFkScanned(function(prev) {
                                  var existing = prev.length > 0 && prev[0] !== "confirmed" ? prev : [];
                                  var next = existing.filter(function(x) { return x !== kid; });
                                  // Recompute count for THIS size only
                                  var c = next.filter(function(k) { return parseSizeFromId(k) === sz; }).length;
                                  // Add back partial count if applicable to keep totals consistent
                                  var adj = (fkLastKegPartial && fkLastKegPartial.size === sz) ? 1 : 0;
                                  if (sz === "18L") setFk18(String((c + adj) || ""));
                                  else if (sz === "9L") setFk9(String((c + adj) || ""));
                                  else if (sz === "7L") setFk7(String((c + adj) || ""));
                                  toast(kid + " removed", "var(--dim)");
                                  return next;
                                });
                              }
                            }, "\u00d7")
                          );
                        })
                      )
                    );
                  }),
                  // Partial keg ID (if set)
                  fkLastKegScannedId && fkLastKegPartial ? React.createElement("div", { style: { marginTop: 4, paddingTop: 6, borderTop: "1px dashed var(--rule)" } },
                    React.createElement("div", { style: { fontSize: 9, fontWeight: 700, color: "var(--warning, #C17817)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 } }, "Last keg \u00b7 " + fkLastKegPartial.litres + "L \u2192 Staff"),
                    React.createElement("span", { style: { display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 6px", background: "rgba(193,120,23,.1)", borderRadius: 4, fontFamily: "var(--mono, monospace)", fontSize: 10, fontWeight: 600, color: "var(--warning, #C17817)" } },
                      fkLastKegScannedId,
                      React.createElement("button", {
                        style: { background: "none", border: "none", color: "var(--warning, #C17817)", cursor: "pointer", padding: 0, fontSize: 11, lineHeight: 1, fontWeight: 700 },
                        onClick: function() { setFkLastKegScannedId(""); toast("Last keg cleared", "var(--dim)"); }
                      }, "\u00d7")
                    )
                  ) : null
                )
              );
            })()
          ),
          // Confirm added to fridge tick — now BELOW scan buttons
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 12, padding: "14px 0", marginTop: 8, cursor: "pointer" }, onClick: function() {
            setFkFridgeChecked(function(v) { return !v; });
          } },
            React.createElement("div", { style: { width: 28, height: 28, borderRadius: 6, border: fridgeChecked ? "none" : "2px solid var(--rule)", background: fridgeChecked ? "var(--success)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .2s" } },
              fridgeChecked ? React.createElement(TickSvg, { size: 12 }) : null
            ),
            React.createElement("span", { style: { fontSize: 14, fontWeight: 600, color: fridgeChecked ? "var(--success)" : "var(--text)" } }, "Kegs filled and added to fridge")
          ),
          // Photo confirmation — ONLY on final step (no Limited queued)
          !fkLimited && React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 12, padding: "14px 0", marginTop: 4, cursor: "pointer", borderTop: "1px solid var(--rule-light)" }, onClick: function() { setFkPhotoCalcs(!fkPhotoCalcs); } },
            React.createElement("div", { style: { width: 28, height: 28, borderRadius: 6, border: fkPhotoCalcs ? "none" : "2px solid var(--rule)", background: fkPhotoCalcs ? "var(--success)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .2s" } },
              fkPhotoCalcs ? React.createElement(TickSvg, { size: 12 }) : null
            ),
            React.createElement("div", null,
              React.createElement("div", { style: { fontSize: 14, fontWeight: 600, color: fkPhotoCalcs ? "var(--success)" : "var(--text)" } }, "Photo taken of manual calcs"),
              React.createElement("div", { style: { fontSize: 10, color: "var(--ghost)", marginTop: 1 } }, "Stored on your device — not uploaded")
            )
          ),
          // Complete / Continue button — ALWAYS visible, greyed out until ready
          React.createElement("div", { style: { marginTop: 12 } },
            (function() {
              var scannedReal = fkScanned.length > 0 && fkScanned[0] !== "confirmed" ? fkScanned.length : 0;
              var totalScanned = scannedReal + (fkLastKegScannedId ? 1 : 0);
              // If a partial is set, the partial keg counts toward total — it must also be scanned.
              var partialReady = !fkLastKegPartial || !!fkLastKegScannedId;
              var allScanned = total > 0 && totalScanned >= total && partialReady;
              // Photo only required when this is the final step (no Limited queued)
              var ready = allScanned && fridgeChecked && (fkLimited || fkPhotoCalcs) && !fkSaving;
              return React.createElement("button", {
                className: "btn",
                style: { background: fkLimited ? prodCol : "var(--success)", color: "#fff", opacity: ready ? 1 : 0.4, pointerEvents: ready ? "auto" : "none" },
                onClick: ready ? saveFillKegs : null
              }, fkSaving ? React.createElement(Spinner, null) : (fkLimited ? "Save " + fkTank.product + " \u2014 Continue to " + fkLimited : React.createElement(React.Fragment, null, React.createElement(IcoCheck, null), " Complete \u2014 ", total, " Kegs Filled")));
            })()
          )
        )
      ),

      // === LIMITED PHASE ===
      fkPhase === "limited" && fkLimited && React.createElement(React.Fragment, null,
        fillHeader("Fill " + fkLimited, null),
        React.createElement("div", { style: { padding: "10px 14px", background: "var(--input-bg)", borderRadius: 8, marginBottom: 14 } },
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 10 } },
            React.createElement(ProductPill, { name: fkLimited, size: 11 }),
            React.createElement("span", { style: { fontSize: 13, fontWeight: 700 } }, ltdN18, "x 18L + ", ltdN9, "x 9L"),
            React.createElement("span", { style: { fontSize: 11, color: "var(--dim)", marginLeft: "auto" } }, ltdTotal, " kegs")
          ),
          tagBBBar ? React.createElement("div", { style: { marginTop: 6 } }, tagBBBar) : null
        ),
        // RECIPE CARD
        React.createElement("div", { className: "sect", style: { marginTop: 0 } },
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8 } },
            React.createElement(ProductPill, { name: fkLimited, size: 13 }),
            React.createElement("span", { className: "h2", style: { fontSize: 18 } }, "Recipe")
          )
        ),
        ltdRecipe && React.createElement("div", { style: { border: "1.5px solid " + ltdCol, borderRadius: 10, padding: 14, marginTop: 6 } },
          React.createElement("div", { style: { fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "var(--ghost)", marginBottom: 8 } }, "Flavour Addition for ", ltdL > 0 ? ltdL : remainL, "L"),
          (ltdRecipe.ingredients || []).map(function(ing, idx) {
            var mlTotal = (parseFloat(ing.rate) || 0) * (ltdL > 0 ? ltdL : remainL);
            return React.createElement("div", { key: idx, style: { display: "flex", justifyContent: "space-between", padding: "6px 0", borderTop: idx > 0 ? "1px solid var(--rule-light)" : "none" } },
              React.createElement("span", { style: { fontSize: 13, fontWeight: 600 } }, ing.name),
              React.createElement("span", { style: { fontSize: 14, fontWeight: 700 } }, mlTotal.toFixed(0), React.createElement("span", { style: { fontSize: 10, color: "var(--ghost)", marginLeft: 2 } }, "ml"))
            );
          }),
          ltdRecipe.method && React.createElement("div", { style: { marginTop: 12 } },
            React.createElement("div", { style: { fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "var(--ghost)", marginBottom: 6 } }, "Method"),
            React.createElement("div", { style: { fontSize: 13, color: "var(--dim)", whiteSpace: "pre-line" } }, ltdRecipe.method)
          )
        ),
        React.createElement("div", { style: { marginTop: 14 } },
          React.createElement(CheckBox, { checked: fkRecipeConfirmed, label: "Flavours added, mixed & tasted", onClick: function() { setFkRecipeConfirmed(!fkRecipeConfirmed); } })
        ),
        // Last Keg — limited is always the final step, so always show here
        ltdTotal > 0 && (function() {
          var partial = fkLastKegPartial;
          var lastSize = null;
          if (partial && partial.size) lastSize = partial.size;
          else if (ltdN9 > 0) lastSize = "9L";
          else if (ltdN18 > 0) lastSize = "18L";
          if (!lastSize) return null;
          var displayPrimary = partial ? (partial.litres + "L") : lastSize;
          var displaySecondary = partial ? "partial \u00b7 " + partial.size + " keg \u2192 Staff/Market" : "planned";
          var accent = partial ? "var(--warning, #C17817)" : ltdCol;
          var bg = partial ? "rgba(193, 120, 23, 0.08)" : "var(--input-bg)";
          var border = partial ? "1px solid rgba(193,120,23,.3)" : "1px solid transparent";
          return React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: bg, borderRadius: 10, marginTop: 12, border: border } },
            React.createElement("div", { style: { minWidth: 0 } },
              React.createElement("div", { style: { fontSize: 11, fontWeight: 700, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 0.5 } }, "Last keg"),
              React.createElement("div", { style: { fontSize: 16, fontWeight: 800, marginTop: 2, color: partial ? accent : "var(--text)" } },
                displayPrimary,
                React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: partial ? accent : "var(--ghost)", marginLeft: 6 } },
                  partial ? "(partial)" : "(planned)"
                )
              ),
              partial ? React.createElement("div", { style: { fontSize: 10, fontWeight: 600, color: accent, marginTop: 1 } }, displaySecondary) : null
            ),
            React.createElement("button", {
              onClick: function() {
                setFkLastKegInput(partial && partial.litres ? String(partial.litres) : "");
                setFkLastKegOpen(true);
              },
              style: { background: "none", border: "1.5px solid " + accent, borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, color: accent, cursor: "pointer", flexShrink: 0 }
            }, partial ? "Edit" : "Update \u2192")
          );
        })(),
        // Fill checklist + scan — visible but greyed until recipe confirmed
        React.createElement("div", { className: "sect" },
          React.createElement("div", { className: "h2", style: { fontSize: 18, opacity: fkRecipeConfirmed ? 1 : 0.35 } }, "Fill Checklist")
        ),
        React.createElement("div", { style: { opacity: fkRecipeConfirmed ? 1 : 0.35, pointerEvents: fkRecipeConfirmed ? "auto" : "none" } },
          ltdFillLabels.map(function(lbl, i) {
            return React.createElement(CheckBox, { key: "ltd" + i, checked: fkLtdChecks[i], label: lbl, onClick: function() { var c = fkLtdChecks.slice(); c[i] = !c[i]; setFkLtdChecks(c); } });
          })
        ),
        React.createElement("div", { className: "sect" },
          React.createElement("div", { className: "h2", style: { fontSize: 18, opacity: allLtdCk && fkRecipeConfirmed ? 1 : 0.35 } }, "Add to Fridge")
        ),
        React.createElement("div", { style: { opacity: allLtdCk && fkRecipeConfirmed ? 1 : 0.35, pointerEvents: allLtdCk && fkRecipeConfirmed ? "auto" : "none" } },
          // Keg count summary
          React.createElement("div", { style: { padding: "14px 16px", background: "var(--input-bg)", borderRadius: 10, marginTop: 8 } },
            React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" } },
              React.createElement("div", null,
                ltdN18 > 0 && React.createElement("div", { style: { fontSize: 18, fontWeight: 800 } }, ltdN18, " \u00d7 18L"),
                ltdN9 > 0 && React.createElement("div", { style: { fontSize: 18, fontWeight: 800, marginTop: ltdN18 > 0 ? 4 : 0 } }, ltdN9, " \u00d7 9L")
              ),
              React.createElement("div", { style: { fontSize: 28, fontWeight: 800, color: ltdCol } }, ltdTotal, React.createElement("span", { style: { fontSize: 13, fontWeight: 500, color: "var(--dim)", marginLeft: 4 } }, "kegs"))
            )
          ),
          // Scan buttons + manual entry — FIRST (above fridge tick)
          React.createElement("div", { style: { marginTop: 14 } },
            (function() {
              // Partial adjustments
              var partial = fkLastKegPartial;
              var pAdj18 = (partial && partial.size === "18L") ? 1 : 0;
              var pAdj9 = (partial && partial.size === "9L") ? 1 : 0;
              var reg18 = Math.max(0, ltdN18 - pAdj18);
              var reg9 = Math.max(0, ltdN9 - pAdj9);
              var ltdBatches = [];
              if (reg18 > 0) ltdBatches.push({ size: "18", label: "18L", count: reg18, color: ltdCol });
              if (reg9 > 0) ltdBatches.push({ size: "09", label: "9L", count: reg9, color: ltdCol });
              var ltdRealIds = fkLtdScanned.length > 0 && fkLtdScanned[0] !== "confirmed" ? fkLtdScanned : [];
              var ltdSc18 = ltdRealIds.filter(function(k) { return parseSizeFromId(k) === "18L"; });
              var ltdSc09 = ltdRealIds.filter(function(k) { return parseSizeFromId(k) === "9L"; });
              var ltdScMap = { "18": ltdSc18, "09": ltdSc09 };

              if (ltdBatches.length === 0 && !partial) return React.createElement("div", { style: { fontSize: 11, color: "var(--ghost)", textAlign: "center" } }, "Enter keg counts to enable scanning");

              return React.createElement(React.Fragment, null,
                ltdBatches.map(function(b) {
                  var done = ltdScMap[b.size].length >= b.count;
                  var partialScan = ltdScMap[b.size].length > 0 && ltdScMap[b.size].length < b.count;
                  var label = done
                    ? b.label + " \u2713 " + ltdScMap[b.size].length + "/" + b.count
                    : partialScan
                      ? "Scan " + b.label + " (" + ltdScMap[b.size].length + "/" + b.count + ")"
                      : "Scan " + b.label + " Kegs (" + b.count + ")";
                  return React.createElement("div", { key: b.size, style: { display: "flex", gap: 6, marginBottom: 6 } },
                    React.createElement("button", {
                      className: done ? "btn" : "btn btn-blue",
                      style: { fontSize: 12, flex: 3, whiteSpace: "nowrap", background: done ? "var(--success)" : undefined, color: "#fff", opacity: done ? 0.7 : 1 },
                      onClick: done ? null : function() {
                        var remaining = b.count - ltdScMap[b.size].length;
                        bscOpen({
                          mode: "fill",
                          expected: remaining,
                          label: "SCAN " + b.label + " " + (fkLimited || "").toUpperCase() + " (" + remaining + ")",
                          color: b.color,
                          onDone: function(ids) {
                            if (ids.length === 0) return;
                            setFkLtdScanned(function(prev) {
                              var existing = prev.length > 0 && prev[0] !== "confirmed" ? prev : [];
                              var safeIds = ids.filter(function(id) { return id !== fkLastKegScannedId; });
                              var merged = existing.concat(safeIds);
                              if (b.size === "18") {
                                var c18 = merged.filter(function(k) { return parseSizeFromId(k) === "18L"; }).length;
                                setFkLtd18(String((Math.max(b.count, c18) + pAdj18) || ""));
                              } else if (b.size === "09") {
                                var c9 = merged.filter(function(k) { return parseSizeFromId(k) === "9L"; }).length;
                                setFkLtd9(String((Math.max(b.count, c9) + pAdj9) || ""));
                              }
                              return merged;
                            });
                            toast(ids.length + " " + b.label + " kegs scanned", "var(--success)");
                          }
                        });
                      }
                    },
                      React.createElement("svg", { style: { width: 16, height: 16, stroke: "#fff", fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", marginRight: 6 }, viewBox: "0 0 24 24" },
                        done
                          ? React.createElement("polyline", { points: "20 6 9 17 4 12" })
                          : React.createElement(React.Fragment, null,
                              React.createElement("path", { d: "M4 7V4h3M20 7V4h-3M4 17v3h3M20 17v3h-3" }),
                              React.createElement("rect", { x: 9, y: 9, width: 6, height: 6, rx: 1 })
                            )
                      ), label
                    ),
                    React.createElement("button", {
                      className: "btn btn-out",
                      style: { fontSize: 10, flex: 1, padding: "0 6px", whiteSpace: "normal", lineHeight: 1.15, opacity: done ? 0.4 : 1, pointerEvents: done ? "none" : "auto" },
                      onClick: done ? null : function() {
                        setFkManualDigits("");
                        setFkManualOpen({ size: b.size, target: b.count, phase: "limited", color: b.color, label: b.label });
                      }
                    }, "Enter Manually")
                  );
                }),
                // LAST KEG row — limited variant
                partial ? (function() {
                  var lastSizeCode = partial.size === "18L" ? "18" : "09";
                  var lastColor = "var(--warning, #C17817)";
                  var lastDone = !!fkLastKegScannedId;
                  var lastLabel = lastDone
                    ? "Last Keg \u2713 " + partial.litres + "L"
                    : "Scan Last Keg \u2014 " + partial.size + " \u00b7 " + partial.litres + "L";
                  return React.createElement("div", { key: "last", style: { display: "flex", gap: 6, marginBottom: 6, marginTop: 4 } },
                    React.createElement("button", {
                      className: lastDone ? "btn" : "btn",
                      style: { fontSize: 12, flex: 3, whiteSpace: "nowrap", background: lastDone ? "var(--success)" : lastColor, color: "#fff", opacity: lastDone ? 0.7 : 1, border: "none" },
                      onClick: lastDone ? null : function() {
                        bscOpen({
                          mode: "fill",
                          expected: 1,
                          label: "SCAN LAST KEG (" + partial.size + " \u00b7 " + partial.litres + "L)",
                          color: lastColor,
                          onDone: function(ids) {
                            if (ids.length === 0) return;
                            var lid = ids[0];
                            if (fkLtdScanned.indexOf(lid) !== -1) { toast(lid + " is already a regular scanned keg", "var(--warning, #C17817)"); return; }
                            if (parseSizeFromId(lid) !== partial.size) {
                              toast("Last keg must be " + partial.size + " — scanned " + parseSizeFromId(lid), "var(--warning, #C17817)");
                              return;
                            }
                            setFkLastKegScannedId(lid);
                            toast("Last keg set: " + lid, "var(--success)");
                          }
                        });
                      }
                    },
                      React.createElement("svg", { style: { width: 16, height: 16, stroke: "#fff", fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", marginRight: 6 }, viewBox: "0 0 24 24" },
                        lastDone
                          ? React.createElement("polyline", { points: "20 6 9 17 4 12" })
                          : React.createElement(React.Fragment, null,
                              React.createElement("path", { d: "M4 7V4h3M20 7V4h-3M4 17v3h3M20 17v3h-3" }),
                              React.createElement("rect", { x: 9, y: 9, width: 6, height: 6, rx: 1 })
                            )
                      ),
                      lastLabel
                    ),
                    React.createElement("button", {
                      className: "btn btn-out",
                      style: { fontSize: 10, flex: 1, padding: "0 6px", whiteSpace: "normal", lineHeight: 1.15, opacity: lastDone ? 0.4 : 1, pointerEvents: lastDone ? "none" : "auto", borderColor: lastColor, color: lastColor },
                      onClick: lastDone ? null : function() {
                        setFkManualDigits("");
                        setFkManualOpen({ size: lastSizeCode, target: 1, phase: "limited", color: lastColor, label: "Last Keg " + partial.size, isLast: true });
                      }
                    }, "Enter Manually"),
                    lastDone ? React.createElement("button", {
                      style: { background: "none", border: "none", fontSize: 14, color: "var(--ghost)", cursor: "pointer", padding: "0 4px" },
                      onClick: function() { setFkLastKegScannedId(""); toast("Last keg cleared", "var(--dim)"); }
                    }, "\u00d7") : null
                  );
                })() : null,
                (ltdRealIds.length > 0 || fkLastKegScannedId) && React.createElement("div", { style: { fontSize: 11, color: "var(--success)", fontWeight: 700, textAlign: "center", marginTop: 4 } },
                  (ltdRealIds.length + (fkLastKegScannedId ? 1 : 0)) + " of " + ltdTotal + " kegs scanned"
                ),
                // List of scanned IDs grouped by size (limited)
                (ltdRealIds.length > 0 || fkLastKegScannedId) && React.createElement("div", { style: { marginTop: 8, padding: "8px 10px", background: "var(--input-bg)", borderRadius: 8, fontSize: 10 } },
                  ["18L", "9L"].map(function(sz) {
                    var ids = ltdRealIds.filter(function(k) { return parseSizeFromId(k) === sz; });
                    if (ids.length === 0) return null;
                    return React.createElement("div", { key: sz, style: { marginBottom: 6 } },
                      React.createElement("div", { style: { fontSize: 9, fontWeight: 700, color: "var(--dim)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 } }, sz + " (" + ids.length + ")"),
                      React.createElement("div", { style: { display: "flex", flexWrap: "wrap", gap: 4 } },
                        ids.map(function(kid) {
                          return React.createElement("span", { key: kid, style: { display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 6px", background: "var(--bg)", borderRadius: 4, fontFamily: "var(--mono, monospace)", fontSize: 10, fontWeight: 600 } },
                            kid,
                            React.createElement("button", {
                              style: { background: "none", border: "none", color: "var(--ghost)", cursor: "pointer", padding: 0, fontSize: 11, lineHeight: 1, fontWeight: 700 },
                              onClick: function() {
                                setFkLtdScanned(function(prev) {
                                  var existing = prev.length > 0 && prev[0] !== "confirmed" ? prev : [];
                                  var next = existing.filter(function(x) { return x !== kid; });
                                  var c = next.filter(function(k) { return parseSizeFromId(k) === sz; }).length;
                                  var adj = (fkLastKegPartial && fkLastKegPartial.size === sz) ? 1 : 0;
                                  if (sz === "18L") setFkLtd18(String((c + adj) || ""));
                                  else if (sz === "9L") setFkLtd9(String((c + adj) || ""));
                                  toast(kid + " removed", "var(--dim)");
                                  return next;
                                });
                              }
                            }, "\u00d7")
                          );
                        })
                      )
                    );
                  }),
                  fkLastKegScannedId && fkLastKegPartial ? React.createElement("div", { style: { marginTop: 4, paddingTop: 6, borderTop: "1px dashed var(--rule)" } },
                    React.createElement("div", { style: { fontSize: 9, fontWeight: 700, color: "var(--warning, #C17817)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 } }, "Last keg \u00b7 " + fkLastKegPartial.litres + "L \u2192 Staff"),
                    React.createElement("span", { style: { display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 6px", background: "rgba(193,120,23,.1)", borderRadius: 4, fontFamily: "var(--mono, monospace)", fontSize: 10, fontWeight: 600, color: "var(--warning, #C17817)" } },
                      fkLastKegScannedId,
                      React.createElement("button", {
                        style: { background: "none", border: "none", color: "var(--warning, #C17817)", cursor: "pointer", padding: 0, fontSize: 11, lineHeight: 1, fontWeight: 700 },
                        onClick: function() { setFkLastKegScannedId(""); toast("Last keg cleared", "var(--dim)"); }
                      }, "\u00d7")
                    )
                  ) : null
                )
              );
            })()
          ),
          // Confirm tick — now BELOW scan buttons
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 12, padding: "14px 0", marginTop: 8, cursor: "pointer" }, onClick: function() {
            setFkLtdFridgeChecked(function(v) { return !v; });
          } },
            React.createElement("div", { style: { width: 28, height: 28, borderRadius: 6, border: ltdFridgeChecked ? "none" : "2px solid var(--rule)", background: ltdFridgeChecked ? "var(--success)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .2s" } },
              ltdFridgeChecked ? React.createElement(TickSvg, { size: 12 }) : null
            ),
            React.createElement("span", { style: { fontSize: 14, fontWeight: 600, color: ltdFridgeChecked ? "var(--success)" : "var(--text)" } }, "Kegs filled and added to fridge")
          ),
          // Photo confirmation — this IS the final step
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 12, padding: "14px 0", marginTop: 4, cursor: "pointer", borderTop: "1px solid var(--rule-light)" }, onClick: function() { setFkLtdPhotoCalcs(!fkLtdPhotoCalcs); } },
            React.createElement("div", { style: { width: 28, height: 28, borderRadius: 6, border: fkLtdPhotoCalcs ? "none" : "2px solid var(--rule)", background: fkLtdPhotoCalcs ? "var(--success)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .2s" } },
              fkLtdPhotoCalcs ? React.createElement(TickSvg, { size: 12 }) : null
            ),
            React.createElement("div", null,
              React.createElement("div", { style: { fontSize: 14, fontWeight: 600, color: fkLtdPhotoCalcs ? "var(--success)" : "var(--text)" } }, "Photo taken of manual calcs"),
              React.createElement("div", { style: { fontSize: 10, color: "var(--ghost)", marginTop: 1 } }, "Stored on your device — not uploaded")
            )
          ),
          // Complete button — ALWAYS visible, greyed out until ready
          React.createElement("div", { style: { marginTop: 12 } },
            (function() {
              var ltdScannedReal = fkLtdScanned.length > 0 && fkLtdScanned[0] !== "confirmed" ? fkLtdScanned.length : 0;
              var totalScanned = ltdScannedReal + (fkLastKegScannedId ? 1 : 0);
              var partialReady = !fkLastKegPartial || !!fkLastKegScannedId;
              var allScanned = ltdTotal > 0 && totalScanned >= ltdTotal && partialReady;
              var ready = allScanned && ltdFridgeChecked && fkLtdTag && fkLtdPhotoCalcs && !fkSaving;
              return React.createElement("button", {
                className: "btn",
                style: { background: "var(--success)", color: "#fff", opacity: ready ? 1 : 0.4, pointerEvents: ready ? "auto" : "none" },
                onClick: ready ? saveFillKegsLimited : null
              }, fkSaving ? React.createElement(Spinner, null) : React.createElement(React.Fragment, null, React.createElement(IcoCheck, null), " Complete \u2014 ", ltdTotal, " ", fkLimited, " Kegs Filled"));
            })()
          )
        )
      )
    );
  }

  // ============================================================
  // WASH KEGS
  // ============================================================
  function renderWashKegs() {
    const n18 = parseInt(w18) || 0,
      n9 = parseInt(w9) || 0,
      total = n18 + n9;
    return React.createElement("div", {
      className: "scr",
      style: { animation: "fu .2s ease" }
    }, React.createElement("div", {
      style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 14, cursor: "pointer" },
      onClick: () => setSub(null)
    }, React.createElement(IcoBack, null), React.createElement("span", { className: "h2" }, "Wash Kegs")),
    React.createElement("div", { style: { fontSize: 12, color: "var(--ghost)", marginBottom: 12 } }, "Count and confirm kegs to be cleaned"),
    React.createElement("div", { className: "frow", style: { marginTop: 12 } },
      React.createElement("div", { className: "ffield" },
        React.createElement("div", { className: "flabel" }, "18L Kegs"),
        React.createElement("input", {
          className: "input input-mono", type: "number", inputMode: "numeric",
          value: w18, onChange: e => setW18(e.target.value)
        })
      ),
      React.createElement("div", { className: "ffield" },
        React.createElement("div", { className: "flabel" }, "9L Kegs"),
        React.createElement("input", {
          className: "input input-mono", type: "number", inputMode: "numeric",
          value: w9, onChange: e => setW9(e.target.value)
        })
      )
    ),
    total > 0 && React.createElement("div", { style: { marginTop: 16 } },
      React.createElement("button", {
        className: "btn btn-blue",
        style: { background: "var(--success)", opacity: syncing ? .5 : 1, width: "100%", padding: "14px 0", fontSize: 16 },
        onClick: syncing ? null : saveWash
      }, syncing ? React.createElement(Spinner, null) : React.createElement(React.Fragment, null, React.createElement(IcoCheck, null), " Wash ", total, " Kegs"))
    ));
  }

  // ============================================================
  // STOCK
  // ============================================================
  function renderStock() {
    const coffee = stock.filter(s => s.category === "Coffee"),
      other = stock.filter(s => s.category !== "Coffee");
    const totalC = coffee.reduce((a, s) => a + (Number(s.currentQty) || 0), 0);
    return /*#__PURE__*/React.createElement("div", {
      className: "scr",
      style: {
        animation: "fu .2s ease"
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "sect sect-blue",
      style: {
        marginTop: 0
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "sect-top"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: "lbl",
      style: {
        color: "var(--blue)"
      }
    }, "Total Coffee Stock"), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 4
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 36,
        fontWeight: 700
      }
    }, totalC), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 16,
        fontWeight: 400,
        color: "var(--dim)"
      }
    }, "kg"))))), coffee.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "sect"
    }, /*#__PURE__*/React.createElement("div", {
      className: "h2"
    }, "Coffee Beans")), coffee.map((s, i) => {
      const pct = s.maxLevel ? Math.min(100, Math.round(s.currentQty / s.maxLevel * 100)) : 50,
        col = PC[s.item] || "var(--blue)",
        low = s.minLevel && s.currentQty <= s.minLevel;
      return /*#__PURE__*/React.createElement("div", {
        className: "rule" + (i > 0 ? " rule-light" : ""),
        key: s.item
      }, /*#__PURE__*/React.createElement("div", {
        className: "row-i"
      }, /*#__PURE__*/React.createElement("div", {
        className: "dot dot-lg",
        style: {
          background: col
        }
      }), /*#__PURE__*/React.createElement("div", {
        style: {
          flex: 1,
          minWidth: 0
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "product-sm"
      }, s.item), /*#__PURE__*/React.createElement("div", {
        className: "sbar"
      }, /*#__PURE__*/React.createElement("div", {
        className: "sfill",
        style: {
          width: pct + "%",
          background: low ? "var(--danger)" : col
        }
      }))), /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 14,
          fontWeight: 700,
          color: low ? "var(--danger)" : "var(--text)"
        }
      }, s.currentQty, " ", /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 12,
          fontWeight: 400,
          color: "var(--dim)"
        }
      }, s.unit || "kg"))));
    })), other.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "sect"
    }, /*#__PURE__*/React.createElement("div", {
      className: "h2"
    }, "Preservatives & Supplies")), other.map((s, i) => {
      const pct = s.maxLevel ? Math.min(100, Math.round(s.currentQty / s.maxLevel * 100)) : 50,
        low = s.minLevel && s.currentQty <= s.minLevel;
      return /*#__PURE__*/React.createElement("div", {
        className: "rule" + (i > 0 ? " rule-light" : ""),
        key: s.item
      }, /*#__PURE__*/React.createElement("div", {
        className: "row-i"
      }, /*#__PURE__*/React.createElement("div", {
        className: "dot dot-lg",
        style: {
          background: "var(--blue)"
        }
      }), /*#__PURE__*/React.createElement("div", {
        style: {
          flex: 1,
          minWidth: 0
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "product-sm"
      }, s.item), /*#__PURE__*/React.createElement("div", {
        className: "sbar"
      }, /*#__PURE__*/React.createElement("div", {
        className: "sfill",
        style: {
          width: pct + "%",
          background: low ? "var(--danger)" : "var(--blue)"
        }
      }))), /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 14,
          fontWeight: 700,
          color: low ? "var(--danger)" : "var(--text)"
        }
      }, s.currentQty, " ", /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 12,
          fontWeight: 400,
          color: "var(--dim)"
        }
      }, s.unit || ""))));
    })), stock.length === 0 && /*#__PURE__*/React.createElement("div", {
      style: {
        padding: "20px 0",
        textAlign: "center",
        color: "var(--ghost)",
        fontSize: 13
      }
    }, "No stock data yet"));
  }

  // ============================================================
  // RECIPES
  // ============================================================
  function startEditRecipe(product, isLimited) {
    var src = isLimited ? limitedRecipes.find(function(r) { return r.product === product; }) : recipes.find(function(r) { return r.product === product; });
    if (!src) return;
    setRcpEditing({ product: product, isLimited: isLimited });
    setRcpDraft(JSON.parse(JSON.stringify(src)));
    setRcpReason("");
  }
  function cancelEditRecipe() { setRcpEditing(null); setRcpDraft({}); setRcpReason(""); }
  function updateDraft(key, val) { setRcpDraft(function(prev) { var n = Object.assign({}, prev); n[key] = val; return n; }); }
  function updateDraftIng(idx, key, val) {
    setRcpDraft(function(prev) {
      var n = Object.assign({}, prev);
      var ings = (n.ingredients || []).map(function(x) { return Object.assign({}, x); });
      if (ings[idx]) ings[idx][key] = val;
      n.ingredients = ings;
      return n;
    });
  }
  function saveRecipeChanges() {
    if (!rcpEditing) return;
    if (!rcpReason.trim()) { toast("Please enter a reason for the change", "var(--danger)"); return; }
    setSyncing(true);
    var action = rcpEditing.isLimited ? "saveLimitedRecipe" : "saveRecipe";
    var payload = Object.assign({}, rcpDraft, { loggedBy: user.name, reason: rcpReason.trim() });
    api.post(action, { data: payload }).then(function(res) {
      setSyncing(false);
      if (res.error) { toast(res.error, "var(--danger)"); return; }
      toast("Recipe saved", "var(--success)");
      setRcpEditing(null);
      setRcpDraft({});
      setRcpReason("");
      loadData();
    }).catch(function(err) {
      setSyncing(false);
      toast("Error: " + err.message, "var(--danger)");
    });
  }
  function renderRecipes() {
    return /*#__PURE__*/React.createElement("div", {
      className: "scr",
      style: {
        animation: "fu .2s ease"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 14,
        cursor: "pointer"
      },
      onClick: () => setSub(null)
    }, /*#__PURE__*/React.createElement(IcoBack, null), /*#__PURE__*/React.createElement("span", {
      className: "h2"
    }, "Recipes")), /*#__PURE__*/React.createElement("div", {
      className: "rcp-tabs"
    }, [{
      k: "classic",
      l: "Classic",
      col: "#C43E2A"
    }, {
      k: "ie",
      l: "Iced Esp.",
      col: "#2563A8"
    }, {
      k: "limited",
      l: "Limiteds",
      col: null
    }].map(t => /*#__PURE__*/React.createElement("div", {
      key: t.k,
      className: "rcp-tab" + (rcpTabSt === t.k ? " on" : ""),
      style: rcpTabSt === t.k && t.col ? { background: t.col, borderColor: t.col, color: "#fff" } : {},
      onClick: () => setRcpTabSt(t.k)
    }, t.l))), rcpTabSt === "classic" && recipes.filter(function(r) { return r.product === "Classic"; }).map(function(r) {
      var editing = rcpEditing && rcpEditing.product === "Classic" && !rcpEditing.isLimited;
      var d = editing ? rcpDraft : r;
      var fields = [["Default KG", "defaultKg", d.defaultKg, "kg"], ["Grind", "defaultGrind", d.defaultGrind, ""], ["Dose Weight", "doseWeight", d.doseWeight, "kg"], ["Dose Count", "doseCount", d.doseCount, ""], ["Fill Target", "fillTargetL", d.fillTargetL, "L"], ["Target TDS", "targetTds", d.targetTds, ""], ["Nagardo", "nagardoRate", d.nagardoRate, "g/L"], ["Sorbate", "sorbateRate", d.sorbateRate, "g/L"]];
      return React.createElement("div", { key: r.product, style: { borderTop: "3px solid #C43E2A", paddingTop: 12 } },
        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12 } },
          React.createElement(ProductPill, { name: "Classic", size: 14 })
        ),
        fields.map(function(f) {
          return React.createElement("div", { className: "rcp-field", key: f[0] },
            React.createElement("span", { className: "rcp-field-lbl" }, f[0]),
            editing ? React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 4 } },
              React.createElement("input", { className: "input", style: { width: 80, textAlign: "right", fontSize: 14, fontWeight: 700, padding: "4px 8px" }, value: f[2] || "", onChange: function(e) { updateDraft(f[1], e.target.value); } }),
              f[3] && React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)" } }, f[3])
            ) : React.createElement("div", null,
              React.createElement("span", { style: { fontSize: 14, fontWeight: 700 } }, f[2]),
              f[3] && React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)", marginLeft: 2 } }, f[3])
            )
          );
        }),
        React.createElement("div", { style: { marginTop: 14 } },
          editing ? React.createElement("div", null,
            React.createElement("div", { style: { marginBottom: 8 } },
              React.createElement("div", { className: "flabel", style: { marginBottom: 4 } }, "Reason for change"),
              React.createElement("input", { className: "input", type: "text", placeholder: "e.g. Updated grind after cupping", value: rcpReason, onChange: function(e) { setRcpReason(e.target.value); } })
            ),
            React.createElement("div", { style: { display: "flex", gap: 8 } },
              React.createElement("button", { className: "btn btn-out", style: { flex: 1 }, onClick: cancelEditRecipe }, "Cancel"),
              React.createElement("button", { className: "btn btn-blue", style: { flex: 1, opacity: rcpReason.trim() ? 1 : 0.4 }, onClick: saveRecipeChanges, disabled: syncing || !rcpReason.trim() }, syncing ? React.createElement(Spinner, null) : "Save")
            )
          ) : React.createElement("button", { className: "btn btn-out", style: { width: "100%" }, onClick: function() { startEditRecipe("Classic", false); } }, "Edit Recipe")
        )
      );
    }), rcpTabSt === "ie" && recipes.filter(function(r) { return r.product === "Iced Espresso"; }).map(function(r) {
      var editing = rcpEditing && rcpEditing.product === "Iced Espresso" && !rcpEditing.isLimited;
      var d = editing ? rcpDraft : r;
      var fields = [["Default KG", "defaultKg", d.defaultKg, "kg"], ["Grind", "defaultGrind", d.defaultGrind, ""], ["Dose Weight", "doseWeight", d.doseWeight, "kg"], ["Dose Count", "doseCount", d.doseCount, ""], ["Target TDS", "targetTds", d.targetTds, ""], ["Nagardo", "nagardoRate", d.nagardoRate, "g/L"], ["Sorbate", "sorbateRate", d.sorbateRate, "g/L"]];
      return React.createElement("div", { key: r.product, style: { borderTop: "3px solid #2563A8", paddingTop: 12 } },
        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12 } },
          React.createElement(ProductPill, { name: "Iced Espresso", size: 14 })
        ),
        fields.map(function(f) {
          return React.createElement("div", { className: "rcp-field", key: f[0] },
            React.createElement("span", { className: "rcp-field-lbl" }, f[0]),
            editing ? React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 4 } },
              React.createElement("input", { className: "input", style: { width: 80, textAlign: "right", fontSize: 14, fontWeight: 700, padding: "4px 8px" }, value: f[2] || "", onChange: function(e) { updateDraft(f[1], e.target.value); } }),
              f[3] && React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)" } }, f[3])
            ) : React.createElement("div", null,
              React.createElement("span", { style: { fontSize: 14, fontWeight: 700 } }, f[2]),
              f[3] && React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)", marginLeft: 2 } }, f[3])
            )
          );
        }),
        React.createElement("div", { style: { marginTop: 14 } },
          editing ? React.createElement("div", null,
            React.createElement("div", { style: { marginBottom: 8 } },
              React.createElement("div", { className: "flabel", style: { marginBottom: 4 } }, "Reason for change"),
              React.createElement("input", { className: "input", type: "text", placeholder: "e.g. Adjusted TDS target", value: rcpReason, onChange: function(e) { setRcpReason(e.target.value); } })
            ),
            React.createElement("div", { style: { display: "flex", gap: 8 } },
              React.createElement("button", { className: "btn btn-out", style: { flex: 1 }, onClick: cancelEditRecipe }, "Cancel"),
              React.createElement("button", { className: "btn btn-blue", style: { flex: 1, opacity: rcpReason.trim() ? 1 : 0.4 }, onClick: saveRecipeChanges, disabled: syncing || !rcpReason.trim() }, syncing ? React.createElement(Spinner, null) : "Save")
            )
          ) : React.createElement("button", { className: "btn btn-out", style: { width: "100%" }, onClick: function() { startEditRecipe("Iced Espresso", false); } }, "Edit Recipe")
        )
      );
    }), rcpTabSt === "limited" && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      style: {
        padding: "10px 14px",
        background: "rgba(42,139,106,.06)",
        borderRadius: 8,
        marginBottom: 14,
        fontSize: 11,
        color: "var(--dim)"
      }
    }, /*#__PURE__*/React.createElement("strong", {
      style: {
        color: "var(--success)"
      }
    }, "Limiteds are made from Classic."), " Brew a standard Classic batch, fill Classic kegs first, then use Limited Split in the Fill Kegs flow to add flavour to the residual and keg off."), limitedRecipes.map(function(r) {
      var col = PC[r.product] || "var(--blue)";
      var open = rcpOpen === r.product;
      var editing = rcpEditing && rcpEditing.product === r.product && rcpEditing.isLimited;
      var d = editing ? rcpDraft : r;
      return React.createElement("div", {
        key: r.product,
        className: "rcp-card" + (open ? " open" : "")
      }, React.createElement("div", {
        style: { display: "flex", alignItems: "center", gap: 10, marginBottom: 6, cursor: "pointer" },
        onClick: function() { if (!editing) setRcpOpen(open ? null : r.product); }
      }, React.createElement(ProductPill, { name: r.product, size: 13 }),
        React.createElement("span", { className: "ml" }, React.createElement(IcoChevD, null))
      ), React.createElement("div", {
        className: "rcp-detail"
      }, React.createElement("div", { className: "lbl", style: { color: "var(--ghost)", marginBottom: 8 } }, "Flavour Addition"),
        (d.ingredients || []).map(function(ing, idx) {
          return React.createElement("div", { className: "rcp-field", key: ing.name || idx },
            editing ? React.createElement("input", { className: "input", style: { flex: 1, fontSize: 13, padding: "4px 8px" }, value: ing.name || "", onChange: function(e) { updateDraftIng(idx, "name", e.target.value); } })
              : React.createElement("span", { className: "rcp-field-lbl" }, ing.name),
            editing ? React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 4 } },
              React.createElement("input", { className: "input", style: { width: 60, textAlign: "right", fontSize: 14, fontWeight: 700, padding: "4px 8px" }, value: ing.rate || "", onChange: function(e) { updateDraftIng(idx, "rate", e.target.value); } }),
              React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)" } }, "ml/L")
            ) : React.createElement("div", null,
              React.createElement("span", { style: { fontSize: 14, fontWeight: 700 } }, ing.rate),
              React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)", marginLeft: 2 } }, "ml/L")
            )
          );
        }),
        d.method && !editing && React.createElement(React.Fragment, null,
          React.createElement("div", { className: "lbl", style: { color: "var(--ghost)", marginTop: 14, marginBottom: 8 } }, "Method"),
          React.createElement("div", { style: { fontSize: 13, color: "var(--dim)", whiteSpace: "pre-line" } }, d.method)
        ),
        editing && React.createElement(React.Fragment, null,
          React.createElement("div", { className: "lbl", style: { color: "var(--ghost)", marginTop: 14, marginBottom: 8 } }, "Method"),
          React.createElement("textarea", { className: "input", style: { width: "100%", minHeight: 70, resize: "vertical", fontSize: 13, padding: "8px" }, value: d.method || "", onChange: function(e) { updateDraft("method", e.target.value); } })
        ),
        React.createElement("div", { style: { padding: "8px 12px", background: "var(--input-bg)", borderRadius: 6, marginTop: 10, fontSize: 11, color: "var(--dim)" } }, "No additional preservatives \u2014 already added during Classic brew off."),
        React.createElement("div", { style: { marginTop: 14 } },
          editing ? React.createElement("div", null,
            React.createElement("div", { style: { marginBottom: 8 } },
              React.createElement("div", { className: "flabel", style: { marginBottom: 4 } }, "Reason for change"),
              React.createElement("input", { className: "input", type: "text", placeholder: "e.g. Adjusted flavour rates", value: rcpReason, onChange: function(e) { setRcpReason(e.target.value); }, onClick: function(e) { e.stopPropagation(); } })
            ),
            React.createElement("div", { style: { display: "flex", gap: 8 } },
              React.createElement("button", { className: "btn btn-out", style: { flex: 1 }, onClick: function(e) { e.stopPropagation(); cancelEditRecipe(); } }, "Cancel"),
              React.createElement("button", { className: "btn btn-blue", style: { flex: 1, opacity: rcpReason.trim() ? 1 : 0.4 }, onClick: function(e) { e.stopPropagation(); saveRecipeChanges(); }, disabled: syncing || !rcpReason.trim() }, syncing ? React.createElement(Spinner, null) : "Save")
            )
          ) : React.createElement("button", { className: "btn btn-out", style: { width: "100%" }, onClick: function(e) { e.stopPropagation(); startEditRecipe(r.product, true); } }, "Edit Recipe")
        )
      ));
    })),
    // Recipe Change Log
    React.createElement("div", { className: "sect", style: { marginTop: 20 } },
      React.createElement("div", { className: "h2" }, "Change Log")
    ),
    recipeLog.length > 0 ? function() {
      // Group log entries by editId (or fallback to timestamp+product+editedBy)
      var groups = [];
      var groupMap = {};
      recipeLog.forEach(function(entry) {
        var key = entry.editId || (entry.timestamp + "|" + entry.product + "|" + entry.editedBy);
        if (!groupMap[key]) {
          groupMap[key] = { key: key, product: entry.product, editedBy: entry.editedBy, timestamp: entry.timestamp, reason: entry.reason || "", changes: [] };
          groups.push(groupMap[key]);
        }
        groupMap[key].changes.push({ field: entry.field, oldValue: entry.oldValue, newValue: entry.newValue });
      });
      return groups.slice(0, 20).map(function(g, i) {
        var open = rcpLogOpen === g.key;
        var col = PC[g.product] || "#bbb";
        return React.createElement("div", { key: i, style: { padding: "10px 0", borderBottom: "1px solid var(--rule)", cursor: "pointer" }, onClick: function() { setRcpLogOpen(open ? null : g.key); } },
          React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" } },
            React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8 } },
              React.createElement(ProductPill, { name: g.product, size: 11 }),
              React.createElement("span", { style: { fontSize: 12, color: "var(--dim)" } }, "by ", g.editedBy)
            ),
            React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)" } }, g.timestamp)
          ),
          g.reason && React.createElement("div", { style: { fontSize: 12, color: "var(--dim)", marginTop: 4, fontStyle: "italic" } }, g.reason),
          !g.reason && React.createElement("div", { style: { fontSize: 11, color: "var(--ghost)", marginTop: 3 } }, g.changes.length, " field", g.changes.length > 1 ? "s" : "", " changed"),
          open && React.createElement("div", { style: { marginTop: 8, padding: "8px 10px", background: "var(--input-bg)", borderRadius: 6, fontSize: 12 } },
            g.changes.map(function(c, ci) {
              return React.createElement("div", { key: ci, style: { padding: "3px 0", color: "var(--dim)" } },
                React.createElement("span", { style: { fontWeight: 600 } }, c.field), ": ",
                c.oldValue || "(empty)", " \u2192 ", c.newValue || "(empty)"
              );
            })
          )
        );
      });
    }() : React.createElement("div", { style: { padding: "12px 0", textAlign: "center", color: "var(--ghost)", fontSize: 12 } }, "No changes yet"),
    recipes.length === 0 && limitedRecipes.length === 0 && /*#__PURE__*/React.createElement("div", {
      style: {
        padding: "20px 0",
        textAlign: "center",
        color: "var(--ghost)",
        fontSize: 13
      }
    }, "No recipes loaded yet"));
  }

  // ============================================================
  // BREW DETAIL MODAL
  // ============================================================
  function saveBrewEdit() {
    if (!brewDetail || !brewEditing) return;
    setSyncing(true);
    api.post("updateBrewLog", { data: Object.assign({}, brewDraft, { brewId: brewDetail.brewId, loggedBy: user.name }) }).then(function(res) {
      setSyncing(false);
      if (res.error) { toast(res.error, "var(--danger)"); return; }
      toast("Brew updated", "var(--success)");
      setBrewEditing(false); setBrewDraft({});
      setBrewDetail(null);
      loadData();
    }).catch(function(err) {
      setSyncing(false);
      toast("Error: " + err.message, "var(--danger)");
    });
  }
  function renderBrewModal() {
    if (!brewDetail) return null;
    var b = brewEditing ? Object.assign({}, brewDetail, brewDraft) : brewDetail;
    var col = PC[b.product] || "#bbb";
    var isBrewing = b.status === "Brewing" || b.status === "ON";
    // #11: Show different field sets based on brew status
    var editableFields = isBrewing ? [
      ["Date", "date", b.date],
      ["Status", "status", b.status],
      ["Tank", "brightTank", b.brightTank],
      ["Total KG", "totalKg", b.totalKg],
      ["Grind Size", "grindSize", b.grindSize],
      ["Roast Date", "roastDate", b.roastDate],
      ["Grind Start", "grindStart", b.grindStart],
      ["Grind Finish", "grindFinish", b.grindFinish],
      ["Fill Target", "fillTargetL", b.fillTargetL],
      ["Time On", "timeOn", b.timeOn],
      ["Target Off", "targetTimeOff", b.targetTimeOff],
      ["Ambient", "ambientTemp", b.ambientTemp],
      ["Target Temp", "targetTemp", b.targetTemp],
    ] : [
      ["Date", "date", b.date],
      ["Status", "status", b.status],
      ["Tank", "brightTank", b.brightTank],
      ["Total KG", "totalKg", b.totalKg],
      ["Grind Size", "grindSize", b.grindSize],
      ["Filtered L", "filteredLitres", b.filteredLitres],
      ["Filter Start", "filterStart", b.filterStart],
      ["Filter Finish", "filterFinish", b.filterFinish],
      ["Conc TDS", "tdsConcAvg", b.tdsConcAvg],
      ["Dil TDS", "tdsDilAvg", b.tdsDilAvg],
      ["Final L", "totalLitres", b.totalLitres],
      ["Tag", "kegTag", b.kegTag],
      ["Best Before", "bestBefore", b.bestBefore],
      ["Flavour Notes", "flavourNotes", b.flavourNotes],
    ];
    // Check if this brew's tank is ready for filling
    var tankReady = false;
    if (brewDetail.brightTank) {
      var ts = getTank(brewDetail.brightTank);
      tankReady = ts && ts.status === "Ready" && ts.brewId === brewDetail.brewId;
    }
    return React.createElement("div", {
      style: { position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,.5)", padding: 20, overflowY: "auto" },
      onClick: function() { setBrewDetail(null); setBrewEditing(false); setBrewDraft({}); }
    }, React.createElement("div", {
      style: { background: "var(--bg)", borderRadius: 14, maxWidth: 430, margin: "40px auto", padding: 24 },
      onClick: function(e) { e.stopPropagation(); }
    },
      // Header
      React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 } },
        React.createElement("div", null,
          React.createElement("span", { style: { fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: col } }, "Brew ", b.brewId),
          React.createElement("div", { style: { marginTop: 6 } }, React.createElement(ProductPill, { name: b.product, size: 13 }))
        ),
        React.createElement("div", { style: { cursor: "pointer", padding: 4 }, onClick: function() { setBrewDetail(null); setBrewEditing(false); setBrewDraft({}); } }, React.createElement(IcoClose, null))
      ),

      // Fill Kegs button (only if tank ready)
      tankReady && React.createElement("button", {
        className: "btn btn-blue", style: { marginBottom: 14 },
        onClick: function() {
          setBrewDetail(null); setBrewEditing(false); setBrewDraft({});
          setFkTank(getTank(brewDetail.brightTank));
          setFkLimited(null); setFkScanned([]); setFk18(""); setFk9(""); setFkRemainOverride(""); setFkLastKegPartial(null); setFkLastKegScannedId("");
          setFkChecks([false, false, false, false, false]); setFkLtd18(""); setFkLtd9("");
          setFkLtdScanned([]); setFkLtdChecks([false, false, false, false, false]);
          setFkRecipeConfirmed(false); setFkLtdTag(""); setFkPhase("setup");
      setFkPhotoCalcs(false); setFkLtdPhotoCalcs(false);
          setScreen("kegs"); setSub("fillKegs");
        }
      }, "Fill Kegs from ", brewDetail.brightTank),

      // Fields
      editableFields.map(function(f) {
        var label = f[0], key = f[1], val = f[2];
        if (!brewEditing && !val && val !== 0) return null;
        var displayVal = val;
        if ((key === "bestBefore" || key === "date" || key === "roastDate") && val && !brewEditing) displayVal = fmtDate(val);
        return React.createElement("div", { key: label, style: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--rule-light)" } },
          React.createElement("span", { style: { fontSize: 12, color: "var(--dim)" } }, label),
          brewEditing ? React.createElement("input", {
            className: "input", style: { width: 120, textAlign: "right", fontSize: 13, fontWeight: 600, padding: "4px 8px" },
            value: brewDraft[key] !== undefined ? brewDraft[key] : (val || ""),
            onChange: function(e) { var d = Object.assign({}, brewDraft); d[key] = e.target.value; setBrewDraft(d); }
          }) : React.createElement("span", { style: { fontSize: 13, fontWeight: 600 } }, displayVal)
        );
      }),

      // Edit / Save / Cancel buttons
      React.createElement("div", { style: { marginTop: 16 } },
        brewEditing ? React.createElement("div", { style: { display: "flex", gap: 8 } },
          React.createElement("button", { className: "btn btn-out", style: { flex: 1 }, onClick: function() { setBrewEditing(false); setBrewDraft({}); } }, "Cancel"),
          React.createElement("button", { className: "btn btn-blue", style: { flex: 1, opacity: syncing ? 0.5 : 1 }, onClick: syncing ? null : saveBrewEdit, disabled: syncing }, syncing ? React.createElement(Spinner, null) : "Save")
        ) : React.createElement("button", { className: "btn btn-out", style: { width: "100%" }, onClick: function() {
          setBrewEditing(true);
          setBrewDraft({});
        } }, "Edit Brew")
      )
    ));
  }

  // ============================================================
  // FRIDGE MODAL
  // ============================================================
  function renderFridgeModal() {
    if (!fridgeModal) return null;
    const m = fridgeModal,
      col = PC[m.product] || "var(--blue)";
    const sz = m.size === "9L" ? "9" : (m.size === "7L" ? "7" : "18");
    const kegs = fridgeStock.filter(k => normProduct(k.product) === m.product && String(k.size).indexOf(sz) > -1)
      .sort(function(a, b) { return String(a.kegId || "").localeCompare(String(b.kegId || "")); });
    // Group by batch. The keg tag (Solid / Stripe) is what distinguishes batches of
    // the same product on the fridge floor, so it's collected per batch and shown
    // beside the batch ID. Normally one tag per batch — if a batch somehow carries
    // both, show both rather than silently picking one.
    const byB = {};
    kegs.forEach(k => {
      const bid = k.brewId || "Unknown";
      if (!byB[bid]) byB[bid] = {
        count: 0,
        bb: k.bestBefore || "",
        tags: [],
        kegs: []
      };
      byB[bid].count++;
      byB[bid].kegs.push(k);
      if (!byB[bid].bb && k.bestBefore) byB[bid].bb = k.bestBefore;
      const tg = String(k.kegTag || "").trim();
      if (tg && byB[bid].tags.indexOf(tg) === -1) byB[bid].tags.push(tg);
    });

    // Oldest batch first — that's the whole point of this list, so the kegs to pull
    // are at the top. Batch IDs are issued sequentially (B013 before B014), so the
    // numeric part is the age order. Anything without a parseable ID sorts last.
    function batchNum(bid) {
      const mm = String(bid).match(/(\d+)/);
      return mm ? parseInt(mm[1], 10) : Number.MAX_SAFE_INTEGER;
    }
    const batchIds = Object.keys(byB).sort(function(a, b) {
      const na = batchNum(a), nb = batchNum(b);
      if (na !== nb) return na - nb;
      return String(a).localeCompare(String(b));
    });

    // Keg tag pill — solid block of the product colour, with a white bar through it
    // for Stripe. Mirrors the tag pill in the Fill Log.
    function tagPill(tag) {
      return React.createElement("span", {
        key: tag,
        style: { position: "relative", overflow: "hidden", padding: "2px 8px", borderRadius: 4, background: col, color: "#fff", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }
      },
        tag === "Stripe" ? React.createElement("div", {
          style: { position: "absolute", top: "50%", left: 0, right: 0, height: 2, background: "#fff", opacity: 0.6, transform: "translateY(-50%)", pointerEvents: "none" }
        }) : null,
        React.createElement("span", { style: { position: "relative", zIndex: 1 } }, tag)
      );
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "modal-overlay",
      onClick: () => setFridgeModal(null)
    }, /*#__PURE__*/React.createElement("div", {
      className: "modal-card",
      onClick: e => e.stopPropagation(),
      style: { maxHeight: "80vh", overflowY: "auto" }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 14
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 8
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 100,
        background: col,
        color: "#fff",
        fontSize: 13,
        fontWeight: 700
      }
    }, PS[m.product] || m.product), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 18,
        fontWeight: 700
      }
    }, m.size)), /*#__PURE__*/React.createElement("div", {
      style: {
        cursor: "pointer",
        padding: 4
      },
      onClick: () => setFridgeModal(null)
    }, /*#__PURE__*/React.createElement(IcoClose, null))),
    // Batches, oldest first — each batch heads its own block with the keg IDs
    // belonging to it underneath, so you can read a batch and go find those kegs.
    batchIds.length === 0 ? React.createElement("div", {
      style: { padding: "16px 0", textAlign: "center", color: "var(--ghost)", fontSize: 13 }
    }, "No batch data") : batchIds.map(function(bid, bi) {
      var b = byB[bid];
      var isOldest = bi === 0 && batchIds.length > 1;
      return React.createElement("div", {
        key: bid,
        style: { padding: "14px 0", borderBottom: "1px solid var(--rule-light)" }
      },
        // Batch heading — ID, tag(s), count
        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" } },
          React.createElement("span", { style: { fontSize: 15, fontWeight: 700 } }, "Batch ", bid),
          b.tags.map(tagPill),
          isOldest ? React.createElement("span", {
            style: { padding: "2px 7px", borderRadius: 4, background: "rgba(42,139,106,.12)", color: "var(--success)", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }
          }, "Take first") : null,
          React.createElement("span", { style: { marginLeft: "auto", fontSize: 22, fontWeight: 700 } }, b.count)
        ),
        b.bb ? React.createElement("div", {
          style: { fontSize: 11, color: "var(--ghost)", marginTop: 2 }
        }, "BB ", fmtDate(b.bb)) : null,
        // Keg IDs for this batch
        React.createElement("div", { style: { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 } },
          b.kegs.map(function(k) {
            return React.createElement("span", {
              key: k.kegId,
              style: {
                display: "inline-block", padding: "3px 8px", borderRadius: 4, fontSize: 12, fontWeight: 700, fontFamily: "monospace",
                background: "var(--bg)", border: "1px solid var(--rule)", color: "var(--text)"
              }
            }, k.kegId || "?");
          })
        )
      );
    }), React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 0",
        borderTop: "1.5px solid var(--rule-heavy)",
        marginTop: 2
      }
    }, React.createElement("div", {
      className: "lbl",
      style: { color: "var(--dim)", letterSpacing: 1.5 }
    }, "Total"), React.createElement("div", {
      style: { fontSize: 18, fontWeight: 800 }
    }, m.count))));
  }

  // ============================================================
  // NAV
  // ============================================================
  const TABS = [{
    id: "home",
    l: "Home",
    I: IcoHome
  }, {
    id: "live",
    l: "Live",
    I: IcoLive
  }, {
    id: "brew",
    l: "Brew",
    I: IcoBrew
  }, {
    id: "kegs",
    l: "Kegs",
    I: IcoKeg
  }, {
    id: "stock",
    l: "Stock",
    I: IcoStock
  }];

  // ============================================================
  // MAIN RENDER
  // ============================================================
  let content;

  // Resume fill session banner
  var resumeBanner = fkResumeSession && sub !== "fillKegs" ? React.createElement("div", {
    style: { margin: "0 24px 12px", padding: "14px 16px", background: "var(--blue)", borderRadius: 10, color: "#fff", cursor: "pointer" },
    onClick: function() { resumeFillSession(fkResumeSession); }
  },
    React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" } },
      React.createElement("div", null,
        React.createElement("div", { style: { fontSize: 13, fontWeight: 700 } }, fkResumeSession._stale ? "Unfinished fill from earlier" : "Resume Fill Session"),
        React.createElement("div", { style: { fontSize: 11, opacity: 0.7, marginTop: 2 } },
          (function() {
            var prod = fkResumeSession.fkPhase === "limited"
              ? (fkResumeSession.fkLimited || "Limited")
              : ((fkResumeSession.tankSnapshot && fkResumeSession.tankSnapshot.product) ||
                 (function() { var ts = tankState.find(function(t) { return t.tankId === fkResumeSession.tankId; }); return ts ? ts.product : "Fill"; })());
            var scanned = fkResumeSession.fkPhase === "limited" ? (fkResumeSession.fkLtdScanned || []) : (fkResumeSession.fkScanned || []);
            var n = scanned.filter(function(k) { return k && k !== "confirmed"; }).length;
            return prod + " \u2014 " + fkResumeSession.tankId + (n ? " (" + n + " keg" + (n !== 1 ? "s" : "") + " scanned)" : "");
          })()
        )
      ),
      React.createElement("div", { style: { display: "flex", gap: 8 } },
        React.createElement("div", {
          style: { padding: "6px 12px", background: "rgba(255,255,255,.2)", borderRadius: 6, fontSize: 11, fontWeight: 700 },
          onClick: function(e) { e.stopPropagation(); dismissFillSession(); }
        }, "Discard"),
        React.createElement("div", {
          style: { padding: "6px 12px", background: "#fff", color: "var(--blue)", borderRadius: 6, fontSize: 11, fontWeight: 700 }
        }, "Resume \u2192")
      )
    )
  ) : null;

  // Residual tank prompt.
  // A keg-up that leaves a few litres behind parks the tank at "Filling" with a small
  // balance, and it sits there until somebody goes looking for it. This surfaces it as
  // a banner — the fill flow already drops you on the Kegs screen when it saves, so it
  // appears right after the fill, then follows you around until it's cleared. Tapping
  // it opens the Empty Tank modal that already exists, with the reason pre-filled.
  //
  // Reads tankState only. It does not touch the fill flow, the fill session, or any
  // fk* state.
  var residualTank = (function() {
    if (sub === "fillKegs" || sub === "fillKegsLimited") return null;   // mid-fill
    if (fkResumeSession) return null;                                   // resume banner owns the screen
    for (var ri = 0; ri < tankState.length; ri++) {
      var rt = tankState[ri];
      if (!rt || rt.status !== "Filling") continue;
      var rL = Number(rt.currentLitres) || 0;
      if (rL <= 0) continue;
      if (rL > 20) continue;                                            // >20L is a real part-batch, not dregs
      if (fkTank && fkTank.tankId === rt.tankId) continue;              // fill still open in memory
      return { id: rt.tankId, product: rt.product, litres: rL, brewId: rt.brewId || "" };
    }
    return null;
  })();

  var residualBanner = residualTank ? React.createElement("div", {
    style: { margin: "0 24px 12px", padding: "14px 16px", background: "#C17817", borderRadius: 10, color: "#fff", cursor: "pointer" },
    onClick: function() {
      setEmptyReason("Residual left after keg up");
      setEmptyTank(residualTank);
    }
  },
    React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 } },
      React.createElement("div", { style: { minWidth: 0 } },
        React.createElement("div", { style: { fontSize: 13, fontWeight: 700 } }, residualTank.litres + "L still showing in " + residualTank.id),
        React.createElement("div", { style: { fontSize: 11, opacity: 0.85, marginTop: 2 } }, "Clear the tank balance?")
      ),
      React.createElement("div", {
        style: { padding: "6px 12px", background: "#fff", color: "#C17817", borderRadius: 6, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }
      }, "Empty Tank \u2192")
    )
  ) : null;

  // Manual keg ID entry modal (in fill flow)
  var fkManualModal = fkManualOpen ? (function() {
    // Compute live progress for this size in the current phase
    var sizeLabel = fkManualOpen.size === "18" ? "18L" : fkManualOpen.size === "09" ? "9L" : "7L";
    var listForPhase = fkManualOpen.phase === "limited" ? fkLtdScanned : fkScanned;
    var realIds = listForPhase.length > 0 && listForPhase[0] !== "confirmed" ? listForPhase : [];
    // For the Last Keg row, progress is just whether the partial keg ID has been recorded
    // (1 if set, 0 if not) — NOT the count of all kegs of that size in the regular scan list.
    var addedForSize = fkManualOpen.isLast
      ? (fkLastKegScannedId ? 1 : 0)
      : realIds.filter(function(k) { return parseSizeFromId(k) === sizeLabel; }).length;
    var target = fkManualOpen.target || 0;
    var atTarget = target > 0 && addedForSize >= target;
    var canSubmit = !atTarget && fkManualDigits.length > 0;

    return React.createElement("div", {
      style: { position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
      onClick: function() { setFkManualOpen(null); setFkManualDigits(""); }
    },
      React.createElement("div", {
        style: { background: "var(--bg)", borderRadius: 14, padding: 24, maxWidth: 360, width: "100%" },
        onClick: function(e) { e.stopPropagation(); }
      },
        React.createElement("div", { style: { fontSize: 18, fontWeight: 700, marginBottom: 4 } }, "Enter ", fkManualOpen.label, " Keg ID"),
        // Live progress line — replaces the helper text
        React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, padding: "8px 12px", background: atTarget ? "rgba(42,139,106,.08)" : "var(--input-bg)", borderRadius: 8 } },
          React.createElement("span", { style: { fontSize: 12, color: "var(--dim)", fontWeight: 600 } }, atTarget ? "All kegs added" : "Type the last 3 digits"),
          React.createElement("span", { style: { fontSize: 14, fontWeight: 800, color: atTarget ? "var(--success)" : (fkManualOpen.color || "var(--blue)") } },
            atTarget
              ? React.createElement(React.Fragment, null,
                  React.createElement("svg", { style: { width: 14, height: 14, stroke: "var(--success)", fill: "none", strokeWidth: 3, strokeLinecap: "round", strokeLinejoin: "round", marginRight: 4, verticalAlign: "-2px" }, viewBox: "0 0 24 24" },
                    React.createElement("polyline", { points: "20 6 9 17 4 12" })
                  ),
                  addedForSize + "/" + target
                )
              : addedForSize + " / " + target
          )
        ),
        React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 16, opacity: atTarget ? 0.4 : 1 } },
          React.createElement("span", { style: { fontSize: 20, fontWeight: 800, color: "var(--dim)" } }, "KEG-"),
          React.createElement("span", { style: {
            fontSize: 20, fontWeight: 800, padding: "10px 14px", borderRadius: 8,
            border: "2px solid " + (fkManualOpen.color || "var(--blue)"),
            background: "rgba(29,78,216,.06)",
            color: fkManualOpen.color || "var(--blue)"
          } }, fkManualOpen.size),
          React.createElement("span", { style: { fontSize: 20, fontWeight: 800, color: "var(--dim)" } }, "-"),
          React.createElement("input", {
            autoFocus: true,
            ref: fkManualInputRef,
            disabled: atTarget,
            style: { width: 100, fontSize: 22, fontWeight: 800, textAlign: "center", padding: "10px 8px", borderRadius: 8, border: "2px solid var(--rule)", background: "var(--input-bg)", color: "var(--text)", outline: "none", fontFamily: "var(--sans)", pointerEvents: atTarget ? "none" : "auto" },
            type: "text", inputMode: "numeric", maxLength: 3, placeholder: "001",
            value: fkManualDigits,
            onChange: function(e) { if (!atTarget) setFkManualDigits(e.target.value.replace(/[^0-9]/g, "").slice(0, 3)); },
            onKeyDown: function(e) { if (e.key === "Enter" && canSubmit) fkSubmitManual(); }
          })
        ),
        React.createElement("div", { style: { display: "flex", gap: 10 } },
          React.createElement("button", {
            className: "btn btn-out",
            style: { flex: 1 },
            onClick: function() { setFkManualOpen(null); setFkManualDigits(""); }
          }, atTarget ? "Close" : "Cancel"),
          !atTarget && React.createElement("button", {
            className: "btn",
            style: { flex: 1, background: fkManualOpen.color || "var(--blue)", color: "#fff", opacity: canSubmit ? 1 : 0.4, pointerEvents: canSubmit ? "auto" : "none" },
            onClick: canSubmit ? fkSubmitManual : null
          }, "Add Keg")
        )
      )
    );
  })() : null;

  // Last Keg Update modal — appears when user taps "Update" on the Last Keg band
  var fkLastKegModal = fkLastKegOpen ? (function() {
    // Determine the active phase context (limited if we're on that screen, else classic)
    var inLtd = fkPhase === "limited";
    var aN18 = inLtd ? (parseInt(fkLtd18) || 0) : (parseInt(fk18) || 0);
    var aN9 = inLtd ? (parseInt(fkLtd9) || 0) : (parseInt(fk9) || 0);
    var aN7 = !inLtd && fkTank && fkTank.product === "Iced Espresso" ? (parseInt(fk7) || 0) : 0;
    var lastSize = aN7 > 0 ? "7L" : (aN9 > 0 ? "9L" : (aN18 > 0 ? "18L" : null));
    var lastSizeL = lastSize === "18L" ? 18 : (lastSize === "9L" ? 9 : 7);
    var color = inLtd ? "var(--accent2, #d8542e)" : (fkTank ? PC[fkTank.product] || "var(--blue)" : "var(--blue)");
    var raw = fkLastKegInput.replace(/[^0-9.]/g, "");
    // single decimal point guard
    var parts = raw.split(".");
    if (parts.length > 2) raw = parts[0] + "." + parts.slice(1).join("");
    var litres = raw === "" ? null : parseFloat(raw);
    var validInput = litres !== null && !isNaN(litres) && litres > 0;
    var diff = validInput ? +(litres - lastSizeL).toFixed(2) : null;
    var EPS = 0.5;
    var status = !validInput ? "empty" :
                 Math.abs(diff) <= EPS ? "equal" :
                 diff > EPS ? "over" : "under";

    // If user is RE-EDITING an existing partial, undo its prior count adjustment first,
    // then re-apply based on the new entered value. Otherwise tapping Update twice would
    // compound the count changes.
    function revertPriorDelta() {
      var prev = fkLastKegPartial;
      if (!prev || !prev.delta) return;
      var d = prev.delta;
      if (inLtd) {
        if (d.n18) setFkLtd18(String(Math.max(0, aN18 - d.n18)));
        if (d.n9) setFkLtd9(String(Math.max(0, aN9 - d.n9)));
      } else {
        if (d.n18) setFk18(String(Math.max(0, aN18 - d.n18)));
        if (d.n9) setFk9(String(Math.max(0, aN9 - d.n9)));
        if (d.n7) setFk7(String(Math.max(0, aN7 - d.n7)));
      }
    }
    // Effective base counts after reverting prior delta (used so new delta builds on clean baseline)
    function effN(size) {
      var raw = size === "18L" ? aN18 : size === "9L" ? aN9 : aN7;
      var prev = fkLastKegPartial;
      if (!prev || !prev.delta) return raw;
      var d = prev.delta;
      var key = size === "18L" ? "n18" : size === "9L" ? "n9" : "n7";
      return Math.max(0, raw - (d[key] || 0));
    }

    function applyPartial() {
      // Under-fill: planned keg is partial. Counts unchanged.
      revertPriorDelta();
      setFkLastKegPartial({ size: lastSize, litres: litres, delta: { n18: 0, n9: 0, n7: 0 } });
      setFkLastKegScannedId("");
      setFkLastKegOpen(false);
    }
    function applySplit() {
      // Over-fill, split: planned keg full + new overflow keg same size. Counts +1 in planned size.
      var overflow = +(litres - lastSizeL).toFixed(2);
      revertPriorDelta();
      var base18 = effN("18L"), base9 = effN("9L"), base7 = effN("7L");
      var delta = { n18: 0, n9: 0, n7: 0 };
      if (lastSize === "18L") { delta.n18 = 1; if (inLtd) setFkLtd18(String(base18 + 1)); else setFk18(String(base18 + 1)); }
      else if (lastSize === "9L") { delta.n9 = 1; if (inLtd) setFkLtd9(String(base9 + 1)); else setFk9(String(base9 + 1)); }
      else if (lastSize === "7L") { delta.n7 = 1; setFk7(String(base7 + 1)); }
      setFkLastKegPartial({ size: lastSize, litres: overflow, delta: delta });
      setFkLastKegScannedId("");
      setFkLastKegOpen(false);
    }
    function applyUpsize() {
      // Over-fill, upsize: swap 1×lastSize → 1×upSize. The upsized keg is the partial.
      var upSize = lastSize === "9L" ? "18L" : (lastSize === "7L" ? "9L" : null);
      if (!upSize) { setFkLastKegOpen(false); return; }
      revertPriorDelta();
      var base18 = effN("18L"), base9 = effN("9L"), base7 = effN("7L");
      var delta = { n18: 0, n9: 0, n7: 0 };
      if (lastSize === "9L") {
        delta.n9 = -1; delta.n18 = 1;
        if (inLtd) { setFkLtd9(String(Math.max(0, base9 - 1))); setFkLtd18(String(base18 + 1)); }
        else { setFk9(String(Math.max(0, base9 - 1))); setFk18(String(base18 + 1)); }
      } else if (lastSize === "7L") {
        delta.n7 = -1; delta.n9 = 1;
        setFk7(String(Math.max(0, base7 - 1))); setFk9(String(base9 + 1));
      }
      setFkLastKegPartial({ size: upSize, litres: litres, delta: delta });
      setFkLastKegScannedId("");
      setFkLastKegOpen(false);
    }
    function applyEqual() {
      // Within tolerance — full keg, clear partial flag (revert any prior delta).
      revertPriorDelta();
      setFkLastKegPartial(null); setFkLastKegScannedId("");
      setFkLastKegOpen(false);
    }

    return React.createElement("div", {
      style: { position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
      onClick: function() { setFkLastKegOpen(false); }
    },
      React.createElement("div", {
        style: { background: "var(--bg)", borderRadius: 14, padding: 24, maxWidth: 400, width: "100%", maxHeight: "85vh", overflowY: "auto" },
        onClick: function(e) { e.stopPropagation(); }
      },
        React.createElement("div", { style: { fontSize: 18, fontWeight: 700, marginBottom: 4 } }, "Update Last Keg"),
        React.createElement("div", { style: { fontSize: 12, color: "var(--dim)", marginBottom: 16 } },
          "Planned last keg: ", React.createElement("strong", null, lastSize || "—"), ". Enter the actual volume filled."
        ),
        // Volume input
        React.createElement("div", { style: { display: "flex", alignItems: "baseline", justifyContent: "center", gap: 6, marginBottom: 14 } },
          React.createElement("input", {
            autoFocus: true,
            type: "text",
            inputMode: "decimal",
            value: fkLastKegInput,
            placeholder: String(lastSizeL),
            onChange: function(e) {
              var v = e.target.value.replace(/[^0-9.]/g, "");
              var p = v.split(".");
              if (p.length > 2) v = p[0] + "." + p.slice(1).join("");
              setFkLastKegInput(v);
            },
            style: { fontSize: 32, fontWeight: 800, textAlign: "center", width: 130, padding: "8px 4px", borderRadius: 8, border: "2px solid " + color, background: "var(--input-bg)", color: "var(--text)", outline: "none", fontFamily: "var(--sans)" }
          }),
          React.createElement("span", { style: { fontSize: 18, fontWeight: 700, color: "var(--dim)" } }, "L")
        ),
        // Status messages + actions
        status === "empty" && React.createElement("div", { style: { fontSize: 12, color: "var(--ghost)", textAlign: "center", marginBottom: 14 } }, "Enter the actual measured volume of the last keg."),

        status === "equal" && React.createElement("div", { style: { padding: 14, background: "rgba(42,139,106,.08)", borderRadius: 10, marginBottom: 14, textAlign: "center" } },
          React.createElement("div", { style: { fontSize: 13, fontWeight: 700, color: "var(--success)", marginBottom: 4 } }, "✓ Matches planned ", lastSize),
          React.createElement("div", { style: { fontSize: 11, color: "var(--dim)" } }, "No changes needed.")
        ),

        status === "over" && React.createElement("div", null,
          React.createElement("div", { style: { fontSize: 11, fontWeight: 700, color: "var(--dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 } }, "Choose how the extra " + diff + "L was handled:"),
          // Option 1: Split into planned + overflow
          React.createElement("button", {
            onClick: applySplit,
            style: { width: "100%", padding: 12, marginBottom: 8, background: "var(--input-bg)", border: "1.5px solid var(--rule)", borderRadius: 10, cursor: "pointer", textAlign: "left", fontFamily: "var(--sans)" }
          },
            React.createElement("div", { style: { fontSize: 13, fontWeight: 700, marginBottom: 2 } }, "Split: full ", lastSize, " + " + diff + "L overflow keg"),
            React.createElement("div", { style: { fontSize: 11, color: "var(--dim)" } }, "Adds 1× ", lastSize, " keg. The overflow keg (" + diff + "L) goes to Staff/Market fridge.")
          ),
          // Option 2: Upsize (only if there's a bigger size available)
          (lastSize === "9L" || lastSize === "7L") && React.createElement("button", {
            onClick: applyUpsize,
            style: { width: "100%", padding: 12, marginBottom: 8, background: "var(--input-bg)", border: "1.5px solid var(--rule)", borderRadius: 10, cursor: "pointer", textAlign: "left", fontFamily: "var(--sans)" }
          },
            React.createElement("div", { style: { fontSize: 13, fontWeight: 700, marginBottom: 2 } }, "Upsize: filled into a ", lastSize === "9L" ? "18L" : "9L", " keg"),
            React.createElement("div", { style: { fontSize: 11, color: "var(--dim)" } }, "All " + litres + "L went into a single ", lastSize === "9L" ? "18L" : "9L", " keg. Swaps 1× ", lastSize, " → 1× ", lastSize === "9L" ? "18L" : "9L", ".")
          )
        ),

        status === "under" && React.createElement("div", null,
          React.createElement("div", { style: { fontSize: 11, fontWeight: 700, color: "var(--dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 } }, "Tank ran short — last keg is partial"),
          React.createElement("button", {
            onClick: applyPartial,
            style: { width: "100%", padding: 12, marginBottom: 8, background: "rgba(193,120,23,.08)", border: "1.5px solid rgba(193,120,23,.4)", borderRadius: 10, cursor: "pointer", textAlign: "left", fontFamily: "var(--sans)" }
          },
            React.createElement("div", { style: { fontSize: 13, fontWeight: 700, marginBottom: 2, color: "var(--warning, #C17817)" } }, "Mark as " + litres + "L partial → Staff/Market"),
            React.createElement("div", { style: { fontSize: 11, color: "var(--dim)" } }, "Last keg is short of full ", lastSize, " (only " + litres + "L). Routes to Staff/Market fridge instead of main fridge.")
          )
        ),

        React.createElement("div", { style: { display: "flex", gap: 10, marginTop: 4 } },
          React.createElement("button", {
            className: "btn btn-out",
            style: { flex: 1 },
            onClick: function() { setFkLastKegOpen(false); }
          }, "Cancel"),
          status === "equal" && React.createElement("button", {
            className: "btn",
            style: { flex: 1, background: "var(--success)", color: "#fff" },
            onClick: applyEqual
          }, "Confirm")
        )
      )
    );
  })() : null;

  // Save progress overlay — shown during fillKegs writes so the user sees what's happening.
  // Driven by saveProgress state. Steps animate from pending → active (spinner) → done (tick).
  var saveProgressOverlay = saveProgress ? React.createElement("div", {
    style: { position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, backdropFilter: "blur(2px)" }
  },
    React.createElement("div", {
      style: { background: "var(--bg)", borderRadius: 14, padding: "24px 22px", maxWidth: 360, width: "100%", boxShadow: "0 10px 40px rgba(0,0,0,.3)" }
    },
      // Header
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 10, marginBottom: 16 } },
        saveProgress.error
          ? React.createElement("div", { style: { width: 28, height: 28, borderRadius: "50%", background: "var(--danger)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18, fontWeight: 800 } }, "!")
          : saveProgress.done
            ? React.createElement("div", { style: { width: 28, height: 28, borderRadius: "50%", background: "var(--success)", display: "flex", alignItems: "center", justifyContent: "center" } },
                React.createElement(TickSvg, { size: 14 })
              )
            : React.createElement("div", { style: { width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" } },
                React.createElement(Spinner, null)
              ),
        React.createElement("div", { style: { fontSize: 16, fontWeight: 800 } },
          saveProgress.error ? "Sync error" : (saveProgress.done ? (saveProgress.doneMessage || "Done") : "Saving fill")
        )
      ),
      // Step list
      saveProgress.error
        ? React.createElement("div", { style: { fontSize: 12, color: "var(--danger)", lineHeight: 1.4 } }, saveProgress.error)
        : React.createElement("div", null,
            saveProgress.steps.map(function(s, idx) {
              var color = s.status === "done" ? "var(--success)" : (s.status === "active" ? "var(--text)" : "var(--ghost)");
              var weight = s.status === "active" ? 700 : 600;
              return React.createElement("div", { key: idx, style: { display: "flex", alignItems: "center", gap: 10, padding: "5px 0", fontSize: 12, color: color, fontWeight: weight } },
                React.createElement("div", { style: { width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 } },
                  s.status === "done"
                    ? React.createElement("svg", { style: { width: 14, height: 14, stroke: "var(--success)", fill: "none", strokeWidth: 3, strokeLinecap: "round", strokeLinejoin: "round" }, viewBox: "0 0 24 24" },
                        React.createElement("polyline", { points: "20 6 9 17 4 12" })
                      )
                    : s.status === "active"
                      ? React.createElement("div", { style: { width: 12, height: 12, borderRadius: "50%", border: "2px solid var(--text)", borderTopColor: "transparent", animation: "fkspin 0.8s linear infinite" } })
                      : React.createElement("div", { style: { width: 8, height: 8, borderRadius: "50%", background: "var(--rule)" } })
                ),
                React.createElement("span", null, s.label)
              );
            })
          ),
      (!saveProgress.done && !saveProgress.error) ? React.createElement("div", {
        style: { marginTop: 16, padding: "10px 12px", background: "rgba(193,120,23,0.12)", borderRadius: 8, fontSize: 12.5, fontWeight: 700, color: "var(--warning, #C17817)", textAlign: "center", lineHeight: 1.45 }
      }, "Please leave the app open while the keg register is updated.") : null
    ),
    // Inline keyframes (avoid global CSS edits)
    React.createElement("style", null, "@keyframes fkspin{to{transform:rotate(360deg)}}")
  ) : null;

  // Cancel fill confirmation modal
  var cancelFillModal = cancelFillTank ? React.createElement("div", {
    style: { position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
    onClick: function() { setCancelFillTank(null); }
  },
    React.createElement("div", {
      style: { background: "var(--bg)", borderRadius: 14, padding: 24, maxWidth: 360, width: "100%" },
      onClick: function(e) { e.stopPropagation(); }
    },
      React.createElement("div", { style: { fontSize: 18, fontWeight: 700, marginBottom: 8 } }, "Cancel Fill?"),
      React.createElement("div", { style: { fontSize: 13, color: "var(--dim)", marginBottom: 16, lineHeight: 1.4 } },
        "This will reset ", React.createElement("strong", null, cancelFillTank.id), " from Filling back to Ready.",
        cancelFillTank.litres > 0 ? React.createElement("span", null, " The ", React.createElement("strong", null, cancelFillTank.litres, "L"), " of ", cancelFillTank.product, " will remain in the tank.") : null
      ),
      React.createElement("div", { style: { display: "flex", gap: 10 } },
        React.createElement("button", {
          className: "btn btn-out",
          style: { flex: 1 },
          onClick: function() { setCancelFillTank(null); }
        }, "Keep Filling"),
        React.createElement("button", {
          className: "btn",
          style: { flex: 1, background: "var(--danger)", color: "#fff", opacity: cancelFillSaving ? 0.5 : 1 },
          onClick: cancelFillSaving ? null : function() { cancelFillFromLive(cancelFillTank.id); }
        }, cancelFillSaving ? React.createElement(Spinner, null) : "Cancel Fill")
      ),
      // Escape hatch — a tank left part-full after kegging sits at "Filling", so the
      // "left 3L behind" case lands here first. Let them empty it without a detour.
      cancelFillTank.litres > 0 ? React.createElement("div", { style: { textAlign: "center", marginTop: 14 } },
        React.createElement("button", {
          style: { background: "none", border: "none", fontSize: 12, fontWeight: 700, color: "var(--dim)", textDecoration: "underline", cursor: "pointer", padding: 4 },
          onClick: function() {
            var t = cancelFillTank;
            setCancelFillTank(null);
            setEmptyReason("");
            setEmptyTank({ id: t.id, product: t.product, litres: t.litres, brewId: t.brewId || "" });
          }
        }, "Or empty the tank \u2014 discard " + cancelFillTank.litres + "L \u2192")
      ) : null
    )
  ) : null;

  // Empty Tank modal — reason required, drains the full remaining volume and
  // returns the tank to Empty. Reached from the Live view (Ready tank with
  // residual) or from the Cancel Fill modal.
  var emptyTankModal = emptyTank ? React.createElement("div", {
    style: { position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
    onClick: function() { if (!emptySaving) { setEmptyTank(null); setEmptyReason(""); } }
  },
    React.createElement("div", {
      style: { background: "var(--bg)", borderRadius: 14, padding: 24, maxWidth: 360, width: "100%" },
      onClick: function(e) { e.stopPropagation(); }
    },
      React.createElement("div", { style: { fontSize: 18, fontWeight: 700, marginBottom: 8 } }, "Empty ", emptyTank.id, "?"),
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10 } },
        emptyTank.product ? React.createElement(ProductPill, { name: emptyTank.product, size: 11 }) : null,
        React.createElement("span", { style: { fontSize: 20, fontWeight: 800 } }, emptyTank.litres, React.createElement("span", { style: { fontSize: 12, fontWeight: 500, color: "var(--dim)", marginLeft: 2 } }, "L"))
      ),
      React.createElement("div", { style: { fontSize: 13, color: "var(--dim)", marginBottom: 14, lineHeight: 1.4 } },
        "This discards the remaining ", React.createElement("strong", null, emptyTank.litres, "L"), " and sets the tank back to Empty, ready for the next brew. It\u2019s logged to the Stock Log as a drain."
      ),
      React.createElement("div", { className: "flabel", style: { marginBottom: 4 } }, "Reason"),
      React.createElement("input", {
        className: "input",
        type: "text",
        autoFocus: true,
        placeholder: "e.g. Residual left after keg up",
        value: emptyReason,
        onChange: function(e) { setEmptyReason(e.target.value); },
        onKeyDown: function(e) { if (e.key === "Enter" && emptyReason.trim() && !emptySaving) emptyTankFromLive(); },
        style: { fontSize: 13, marginBottom: 16 }
      }),
      React.createElement("div", { style: { display: "flex", gap: 10 } },
        React.createElement("button", {
          className: "btn btn-out",
          style: { flex: 1 },
          onClick: function() { if (!emptySaving) { setEmptyTank(null); setEmptyReason(""); } }
        }, "Cancel"),
        React.createElement("button", {
          className: "btn",
          style: { flex: 1, background: "var(--danger)", color: "#fff", opacity: (emptyReason.trim() && !emptySaving) ? 1 : 0.4, pointerEvents: (emptyReason.trim() && !emptySaving) ? "auto" : "none" },
          onClick: emptySaving ? null : emptyTankFromLive
        }, emptySaving ? React.createElement(Spinner, null) : "Empty Tank")
      )
    )
  ) : null;
  // ============================================================
  // ITEM 4 — KEG RECONCILE (Brendan-only, read-only)
  // ============================================================
  function loadReconcile() {
    setReconLoading(true);
    setReconErr("");
    api.get("loadKegVariances").then(function(res) {
      if (res && res.error) { setReconErr(res.error); setReconData(null); }
      else { setReconData(res); }
      setReconLoading(false);
    }).catch(function(e) {
      setReconErr("Could not load reconcile data. Check the connection and try Refresh again.");
      setReconLoading(false);
    });
  }

  function renderReconcile() {
    // Gate: Brendan only. If somehow reached otherwise, show nothing actionable.
    if (!user || user.username !== "brendan") {
      return React.createElement("div", { className: "scr" },
        React.createElement("div", { style: { padding: 24, color: "var(--ghost)" } }, "Not available.")
      );
    }

    function sizePill(size) {
      return React.createElement("span", { style: { fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: "rgba(29,78,216,.08)", color: "var(--blue)", marginLeft: 6 } }, size);
    }

    function varCard(v, accent) {
      return React.createElement("div", { key: (v.kegId + "|" + v.customer), style: { border: "1px solid var(--rule)", borderLeft: "3px solid " + accent, borderRadius: 10, padding: "12px 14px", marginBottom: 8, background: "#fff" } },
        React.createElement("div", { style: { display: "flex", alignItems: "center", flexWrap: "wrap", marginBottom: 6 } },
          React.createElement("span", { style: { fontWeight: 700, fontFamily: "monospace", fontSize: 14 } }, v.kegId),
          v.size ? sizePill(v.size) : null,
          v.product ? React.createElement("span", { style: { fontSize: 12, color: "var(--dim)", marginLeft: 8 } }, v.product) : null
        ),
        React.createElement("div", { style: { fontSize: 12, color: "var(--text)", marginBottom: 6 } },
          React.createElement("strong", null, v.customer || "\u2014"),
          v.regStatus ? React.createElement("span", { style: { fontSize: 11, color: "var(--ghost)" } }, "  \u00b7  register: " + v.regStatus + (v.regLocation ? " (" + v.regLocation + ")" : "")) : null
        ),
        React.createElement("div", { style: { fontSize: 12, color: "var(--dim)", lineHeight: 1.45 } }, v.suggestion)
      );
    }

    var body;
    if (reconLoading) {
      body = React.createElement("div", { style: { display: "flex", justifyContent: "center", padding: 30 } }, React.createElement(Spinner, null));
    } else if (reconErr) {
      body = React.createElement("div", { style: { padding: "14px", fontSize: 13, color: "var(--danger)", background: "rgba(196,62,42,.06)", borderRadius: 10 } }, reconErr);
    } else if (!reconData) {
      body = React.createElement("div", { style: { fontSize: 13, color: "var(--ghost)", padding: "10px 0" } }, "Tap Refresh to run the reconcile.");
    } else {
      var a = reconData.typeA || [], b = reconData.typeB || [];
      var total = (reconData.counts && reconData.counts.total) || (a.length + b.length);
      if (total === 0) {
        body = React.createElement("div", { style: { padding: "18px 14px", fontSize: 14, color: "var(--success)", background: "rgba(45,122,79,.07)", borderRadius: 10, fontWeight: 600 } }, "\u2713 No variances. The register and Kegs On Hand agree for all standard-ID kegs.");
      } else {
        body = React.createElement(React.Fragment, null,
          React.createElement("div", { style: { display: "flex", gap: 8, marginBottom: 14 } },
            React.createElement("div", { style: { flex: 1, textAlign: "center", padding: "10px 0", border: "1px solid var(--rule)", borderRadius: 10 } },
              React.createElement("div", { style: { fontSize: 20, fontWeight: 800 } }, a.length),
              React.createElement("div", { style: { fontSize: 11, color: "var(--ghost)" } }, "Missing from KOH")
            ),
            React.createElement("div", { style: { flex: 1, textAlign: "center", padding: "10px 0", border: "1px solid var(--rule)", borderRadius: 10 } },
              React.createElement("div", { style: { fontSize: 20, fontWeight: 800 } }, b.length),
              React.createElement("div", { style: { fontSize: 11, color: "var(--ghost)" } }, "Register disagrees")
            )
          ),
          a.length ? React.createElement("div", { style: { marginBottom: 6 } },
            React.createElement("div", { className: "h2", style: { fontSize: 14, marginBottom: 8 } }, "Register says At Customer, but not in their KOH"),
            a.map(function(v) { return varCard(v, "var(--warning, #C17817)"); })
          ) : null,
          b.length ? React.createElement("div", { style: { marginTop: a.length ? 14 : 0 } },
            React.createElement("div", { className: "h2", style: { fontSize: 14, marginBottom: 8 } }, "In a customer's KOH, but the register disagrees"),
            b.map(function(v) { return varCard(v, "var(--blue)"); })
          ) : null
        );
      }
    }

    return React.createElement("div", { className: "scr", style: { animation: "fu .2s ease" } },
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 14, cursor: "pointer" }, onClick: function() { setSub(null); } },
        React.createElement(IcoBack, null),
        React.createElement("span", { className: "h2" }, "Keg Reconcile")
      ),
      React.createElement("div", { style: { fontSize: 12, color: "var(--ghost)", marginBottom: 12 } },
        "Read-only drift check between the Keg Register and Kegs On Hand (standard-ID kegs only; legacy IDs ignored). Fixes are applied by hand \u2014 nothing here writes."
      ),
      React.createElement("button", {
        className: "btn btn-out",
        style: { width: "100%", marginBottom: 12, fontSize: 13, padding: "10px 0" },
        onClick: reconLoading ? null : function() { loadReconcile(); }
      }, reconLoading ? React.createElement(Spinner, null) : "\u21bb Refresh"),
      (reconData && reconData.generatedAt) ? React.createElement("div", { style: { fontSize: 11, color: "var(--ghost)", marginBottom: 10, textAlign: "right" } }, "As of " + reconData.generatedAt) : null,
      body
    );
  }

    if (sub === "brewOn") content = renderBrewOn();else if (sub === "brewOff") content = renderBrewOff();else if (sub === "recipes") content = renderRecipes();else if (sub === "fillKegs") content = renderFillKegs();else if (sub === "washKegs") content = renderWashKegs();else if (sub === "moveKegs") content = renderMoveKegs();else if (sub === "addToFleet") content = renderAddToFleet();else if (sub === "registerKeg") content = renderRegisterKeg();else if (sub === "fillLog") content = renderFillLog();else if (sub === "schedule") content = renderSchedule();else if (sub === "reconcile") content = renderReconcile();else if (screen === "home") content = renderHome();else if (screen === "live") content = renderLive();else if (screen === "brew") content = renderBrewDefault();else if (screen === "kegs") content = renderKegsDefault();else if (screen === "stock") content = renderStock();else content = renderHome();
  const title = sub === "brewOn" ? "Brew On" : sub === "brewOff" ? "Brew Off" : sub === "recipes" ? "Recipes" : sub === "fillKegs" ? "Fill Kegs" : sub === "washKegs" ? "Wash Kegs" : sub === "moveKegs" ? (mvTab === "transfer" ? "Transfer" : "Move Kegs") : sub === "addToFleet" ? "Add to Fleet" : sub === "registerKeg" ? "Register Keg" : sub === "schedule" ? "Schedule" : sub === "reconcile" ? "Keg Reconcile" : TITLES[screen] || "Dashboard";
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    id: "brewery-toast",
    className: "toast-box"
  }), /*#__PURE__*/React.createElement("div", {
    className: "hdr"
  }, /*#__PURE__*/React.createElement("div", {
    className: "hdr-top"
  }, /*#__PURE__*/React.createElement("span", {
    className: "brand"
  }, "Filament Brewery"), /*#__PURE__*/React.createElement("div", {
    style: { display: "flex", alignItems: "center", gap: 8 }
  }, /*#__PURE__*/React.createElement("span", {
    className: "date-hdr"
  }, todayHdr()), React.createElement("div", {
    style: { width: 28, height: 28, borderRadius: "50%", border: "1.5px solid var(--rule)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, transition: "all .15s" },
    onClick: function() { window.location.href = window.location.pathname + "?_=" + Date.now(); }
  }, React.createElement("svg", { width: 14, height: 14, viewBox: "0 0 24 24", fill: "none", stroke: "var(--dim)", strokeWidth: 2.5, strokeLinecap: "round", strokeLinejoin: "round" },
    React.createElement("polyline", { points: "23 4 23 10 17 10" }),
    React.createElement("path", { d: "M20.49 15a9 9 0 1 1-2.12-9.36L23 10" })
  )))), /*#__PURE__*/React.createElement("div", {
    className: "page-title"
  }, title)),
  // #4 — Live brew-on banner (shows across all screens when brew in progress)
  onInProgress && sub !== "brewOn" && React.createElement("div", {
    style: { margin: "0 24px 8px", padding: "10px 14px", background: AC.on, borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", animation: "fu .2s ease" },
    onClick: function() { setScreen("brew"); setSub("brewOn"); }
  },
    React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8 } },
      React.createElement("span", { className: "pulse", style: { background: "#fff" } }),
      React.createElement("span", { style: { fontSize: 13, fontWeight: 700, color: "#fff" } }, "Brew On"),
      React.createElement(ProductPill, { name: onProduct, size: 11 }),
      React.createElement("span", { style: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: 12, background: "rgba(255,255,255,.25)", fontSize: 9, fontWeight: 800, color: "#fff", letterSpacing: 0.5 } }, userInitials(user.name))
    ),
    React.createElement("span", { style: { fontSize: 11, color: "rgba(255,255,255,.7)" } }, onTank || "", " \u203a")
  ),
  // Brew-off banner (shows across all screens when brew off in progress)
  offInProgress && sub !== "brewOff" && React.createElement("div", {
    style: { margin: "0 24px 8px", padding: "10px 14px", background: AC.off, borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", animation: "fu .2s ease" },
    onClick: function() { setScreen("brew"); setSub("brewOff"); }
  },
    React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8 } },
      React.createElement("span", { className: "pulse", style: { background: "#fff" } }),
      React.createElement("span", { style: { fontSize: 13, fontWeight: 700, color: "#fff" } }, "Brew Off"),
      React.createElement(ProductPill, { name: offProd(), size: 11 }),
      React.createElement("span", { style: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: 12, background: "rgba(255,255,255,.25)", fontSize: 9, fontWeight: 800, color: "#fff", letterSpacing: 0.5 } }, userInitials(user.name))
    ),
    React.createElement("span", { style: { fontSize: 11, color: "rgba(255,255,255,.7)" } }, activeBrew() && activeBrew().brightTank ? activeBrew().brightTank : "", " \u203a")
  ),
  resumeBanner,
  residualBanner,
  // Schedule changed while the app was open. Stays until tapped or dismissed.
  schedBanner && React.createElement("div", {
    style: { margin: "0 24px 12px", padding: "12px 14px", background: "#fff", border: "1.5px solid var(--blue)", borderLeft: "5px solid var(--blue)", borderRadius: 10, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", animation: "fu .2s ease" },
    onClick: function() { setSchedBanner(null); setSchedWeek(0); setSub("schedule"); }
  },
    React.createElement("div", { style: { flex: 1, minWidth: 0 } },
      React.createElement("div", { style: { fontSize: 13, fontWeight: 700, color: "var(--blue)" } }, "Schedule updated", schedBanner.at ? " \u00b7 " + schedBanner.at : ""),
      React.createElement("div", { style: { fontSize: 11, color: "var(--dim)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } },
        schedBanner.changes.slice(0, 3).join(" \u00b7 ") + (schedBanner.changes.length > 3 ? " +" + (schedBanner.changes.length - 3) + " more" : "")
      )
    ),
    React.createElement("span", { style: { fontSize: 11, fontWeight: 700, color: "var(--blue)", whiteSpace: "nowrap" } }, "View \u203a"),
    React.createElement("button", {
      style: { background: "none", border: "none", fontSize: 16, color: "var(--ghost)", cursor: "pointer", padding: "0 2px" },
      onClick: function(e) { e.stopPropagation(); setSchedBanner(null); }
    }, "\u00d7")
  ),
  // Partial load — some Ops-sheet data couldn't be read. Stays until a clean load.
  loadWarn && React.createElement("div", {
    style: { margin: "0 24px 8px", padding: "10px 14px", background: "#FFF4E0", border: "1px solid #E8B45A", borderRadius: 10, fontSize: 12, color: "#7A4B00", lineHeight: 1.4 }
  },
    React.createElement("div", { style: { fontWeight: 700, marginBottom: 4 } }, "Some data didn't load"),
    loadWarn.map(function(w, i) { return React.createElement("div", { key: i }, w); }),
    React.createElement("button", {
      className: "btn",
      style: { marginTop: 8, padding: "6px 12px", fontSize: 12, width: "auto" },
      onClick: function() { loadData(true); }
    }, "Retry")
  ),
  content, renderBrewModal(), renderFridgeModal(), cancelFillModal, emptyTankModal, fkManualModal, fkLastKegModal, saveProgressOverlay, /*#__PURE__*/React.createElement("div", {
    className: "nav"
  }, TABS.map(t => /*#__PURE__*/React.createElement("button", {
    key: t.id,
    className: "ni" + (screen === t.id && !sub ? " on" : ""),
    onClick: () => nav(t.id)
  }, /*#__PURE__*/React.createElement(t.I, null), t.l))));
}
ReactDOM.render(/*#__PURE__*/React.createElement(App, null), document.getElementById("root"));